import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { useColumnCustomizer } from '../components/ColumnCustomizer';
import QRCode from 'qrcode';
import InvoiceLetterhead from '../components/InvoiceLetterhead';
import { ReceiptLetterhead, QuotationLetterhead, CreditNoteLetterhead, IncomeStatementLetterhead } from '../components/DocumentLetterheads';
import { printComponent } from '../utils/printComponent';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Every fetch in this file must go through BASE, never a raw literal — a
// hardcoded literal URL works in local dev but is unreachable
// from a real visitor's browser in production, silently failing.
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Normalises whatever someone typed for a date onto plain 'YYYY-MM-DD'.
// Same parsing rules as the Client import (Clients.js's toISODate) — an
// Excel date cell, DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY (2- or 4-digit year),
// or YYYY-MM-DD/YYYY-MM-DD. Returns '' when it can't make sense of the
// value, so the import warns on that row rather than failing outright.
function toISODateLoose(v) {
  if (v === null || v === undefined || v === '') return '';
  if (v instanceof Date && !isNaN(v)) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  const s = String(v).trim();
  if (!s) return '';
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (m) {
    let d = parseInt(m[1], 10), mo = parseInt(m[2], 10), y = parseInt(m[3], 10);
    if (mo > 12 && d <= 12) { [d, mo] = [mo, d]; }
    if (y < 100) y += (2000 + y > new Date().getFullYear()) ? 1900 : 2000;
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return '';
}

// Shared by Invoice import and Receipt import — looks up a client by
// (whitespace/case-normalised) name against the already-loaded client list,
// creating a bare-minimum client record on the fly when no match exists.
// Mutates `clientCache` (name.toLowerCase() -> id) and `clientsArr` (pushes
// the newly-created client) so repeated rows for the same client within one
// import only ever create it once.
async function resolveOrCreateClient(name, token, clientCache, clientsArr) {
  const key = (name || '').replace(/\s+/g, ' ').trim().toLowerCase();
  if (!key) return null;
  if (clientCache.has(key)) return clientCache.get(key);
  const existing = clientsArr.find(c => (c.client_name || '').replace(/\s+/g, ' ').trim().toLowerCase() === key);
  if (existing) { clientCache.set(key, existing.id); return existing.id; }
  const res = await fetch(`${BASE}/api/clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ client_name: name.trim() }),
  });
  const d = await res.json().catch(() => ({}));
  if (res.ok && d.id) {
    clientCache.set(key, d.id);
    clientsArr.push(d);
    return d.id;
  }
  if (res.status === 409 && d.duplicates?.[0]?.id) {
    clientCache.set(key, d.duplicates[0].id);
    return d.duplicates[0].id;
  }
  return null;
}

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  partially_paid: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500'
};

const INVOICE_COLUMNS = [
  { key: 'invoice_number', label: 'Invoice No.', default: true, sortType: 'string' },
  { key: 'client_name', label: 'Client', default: true, sortType: 'string' },
  { key: 'invoice_date', label: 'Date', default: true, sortType: 'date' },
  { key: 'due_date', label: 'Due Date', default: true, sortType: 'date' },
  { key: 'total_amount', label: 'Amount', default: true, sortType: 'number' },
  { key: 'amount_paid', label: 'Paid', default: true, sortType: 'number' },
  { key: 'balance_due', label: 'Balance', default: true, sortType: 'number' },
  { key: 'status', label: 'Status', default: true, sortType: 'string' },
];

const RECEIPT_COLUMNS = [
  { key: 'receipt_number', label: 'Receipt No.', default: true, sortType: 'string' },
  { key: 'payment_date',   label: 'Date',         default: true, sortType: 'date' },
  { key: 'client_name',   label: 'Client',        default: true, sortType: 'string' },
  { key: 'invoice_numbers', label: 'Invoice No.', default: true, sortType: 'string' },
  { key: 'received_amount', label: 'Amount',      default: true, sortType: 'number', get: r => Number(r.received_amount ?? r.amount ?? 0) },
  { key: 'allocation_status', label: 'Allocation', default: true, sortType: 'number',
    get: r => Math.max(0, Number(r.available_amount ?? r.received_amount ?? 0) - Number(r.settled_amount ?? 0)) },
  { key: 'mode',           label: 'Mode',          default: true, sortType: 'string' },
  { key: 'bank_name',      label: 'Bank',          default: true, sortType: 'string', get: r => r.bank_name ? `${r.bank_name}${r.bank_nickname ? ` — ${r.bank_nickname}` : ''}` : 'Cash' },
  { key: 'reference_number', label: 'Reference',  default: true, sortType: 'string' },
  { key: 'recorded_by_name', label: 'Recorded By', default: true, sortType: 'string' },
];

const FALLBACK_PAYMENT_MODES = ['cash','bank_transfer','upi','cheque','card','online','other']
  .map(v => ({ value: v, name: v.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()) }));

// PAYMENT MODE DROPDOWN — same shell as Clients.js's Client Type picker and
// Tasks.js's own Payment Mode picker (rounded panel, checkmark on the
// selected row, inline "+ Add New Mode" at the bottom) instead of a plain
// OS-styled <select>, and lets a firm's own custom modes be added without
// leaving the form.
// ── BANK ACCOUNT SELECT — the mandatory "where did the money actually move"
// field on Receipts and Expenses. Independent of Payment Mode (which is just
// a descriptive note on HOW — NEFT/UPI/Cheque/Card — and stays optional).
// "Cash" is always the first option and maps to bank_account_id = null; any
// real bank selected here is what makes the entry auto-appear on that bank's
// Bank Statements ledger (see utils/bankSync.js on the backend). Bank
// Accounts themselves are managed in Settings → Billing & Finance → Bank
// Accounts, not here.
function BankAccountSelect({ accounts, value, onChange, darkMode = false, T: theme }) {
  const T = theme || { inputBg:'#fff', inputBorder:'#e2e8f0', text:'#0f1f45' };
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={is}>
      <option value="">Cash</option>
      {accounts.map(a => (
        <option key={a.id} value={a.id}>{a.bank_name}{a.nickname ? ` — ${a.nickname}` : ''}{a.account_last4 ? ` (••${a.account_last4})` : ''}</option>
      ))}
    </select>
  );
}

function PaymentModeDropdown({ modes, value, onChange, onCreate, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const list = modes && modes.length ? modes : FALLBACK_PAYMENT_MODES;
  const selected = list.find(m => m.value === value);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.value) onChange(created.value);
      setNewName(''); setAdding(false); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38,
          transition:'border-color 0.15s', boxSizing:'border-box',
        }}>
        <span style={{ color: selected ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {selected?.name || 'Select mode...'}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:60, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {list.map(m => {
              const sel = m.value === value;
              return (
                <button key={m.id||m.value} type="button" onClick={() => { onChange(m.value); setOpen(false); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
          </div>

          <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            {adding ? (
              <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                <input autoFocus type="text" value={newName} placeholder="e.g. Wallet, NEFT"
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                  style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add New Mode
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const CREDIT_NOTE_SORT_COLUMNS = [
  { key: 'credit_date', label: 'Date', sortType: 'date' },
  { key: 'credit_note_number', label: 'Credit Note No.', sortType: 'string' },
  { key: 'client_name', label: 'Client', sortType: 'string' },
  { key: 'original_invoice_number', label: 'Original Invoice', sortType: 'string' },
  { key: 'reason', label: 'Reason', sortType: 'string' },
  { key: 'total_amount', label: 'Total Amount', sortType: 'number' },
];

const QUOTE_SORT_COLUMNS = [
  { key: 'quote_number', label: 'Quote No.', sortType: 'string' },
  { key: 'client_name', label: 'Client', sortType: 'string' },
  { key: 'title', label: 'Title', sortType: 'string' },
  { key: 'created_at', label: 'Date', sortType: 'date' },
  { key: 'valid_till', label: 'Valid Till', sortType: 'date' },
  { key: 'total_amount', label: 'Amount', sortType: 'number', get: q => Number(q.total_amount ?? q.amount ?? 0) },
  { key: 'source', label: 'Source', sortType: 'string', get: q => q.source || 'crm' },
  { key: 'status', label: 'Status', sortType: 'string' },
];

// ─────────────────────────────────────────────────────────────────
// Sortable column headers — shared across Invoices / Receipts / Credit Notes /
// Quotations. Click a header to sort ascending, click again for descending;
// `sortType` on each column config decides how values compare (date/number/string).
// ─────────────────────────────────────────────────────────────────
function sortValueFor(row, col) {
  const raw = col.get ? col.get(row) : row[col.key];
  if (raw == null || raw === '') return null;
  if (col.sortType === 'date') {
    const t = new Date(raw).getTime();
    return isNaN(t) ? null : t;
  }
  if (col.sortType === 'number') {
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }
  return String(raw).toLowerCase();
}

function applySort(rows, columns, sortKey, sortDir) {
  if (!sortKey) return rows;
  const col = columns.find(c => c.key === sortKey);
  if (!col) return rows;
  const sorted = [...rows].sort((a, b) => {
    const va = sortValueFor(a, col), vb = sortValueFor(b, col);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;   // nulls/blank sort to the end regardless of direction
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return va - vb;
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
  });
  return sortDir === 'desc' ? sorted.reverse() : sorted;
}

function makeSortToggle(sortKey, setSortKey, setSortDir) {
  return (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };
}

function SortableTh({ label, sortKey: key, activeKey, dir, onSort, align = 'left', style }) {
  const isActive = activeKey === key;
  return (
    <th onClick={() => onSort(key)} title="Click to sort" style={{
      padding: '10px 16px', textAlign: align, fontSize: 11, fontWeight: 700, color: '#fff',
      textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', cursor: 'pointer',
      userSelect: 'none', ...style,
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {label}
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, lineHeight: 0 }}>
          <svg width="7" height="5" viewBox="0 0 8 6"><polygon points="4,0 8,6 0,6" fill={isActive && dir === 'asc' ? '#fff' : 'rgba(255,255,255,0.4)'} /></svg>
          <svg width="7" height="5" viewBox="0 0 8 6"><polygon points="0,0 8,0 4,6" fill={isActive && dir === 'desc' ? '#fff' : 'rgba(255,255,255,0.4)'} /></svg>
        </span>
      </span>
    </th>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared FilterButton — used by Invoices / Receipts / Credit Notes / Quotation
// Identical to the CRM tab's filter UX
// ─────────────────────────────────────────────────────────────────
function FilterButton({ activeCount = 0, onClick, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', border2:'#e2e8f0', text2:'#4a5568' };
  const active = activeCount > 0;
  return (
    <button onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:7, padding:'8px 14px',
      borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer', transition:'all 0.15s',
      border: active ? '1.5px solid #3b82f6' : `1.5px solid ${T.border2}`,
      background: active ? (darkMode?'rgba(59,130,246,0.15)':'#eff6ff') : T.bg2,
      color: active ? '#3b82f6' : T.text2,
    }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
      </svg>
      Filters
      {active && (
        <span style={{ background:'#3b82f6', color:'#fff', fontSize:10, fontWeight:700, borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {activeCount}
        </span>
      )}
    </button>
  );
}

// Searchable floating-panel dropdown for filter fields with long option
// lists (Client Group, etc.) — a native <select> makes a firm with 50+
// groups scroll a bare unstyled browser list with no way to type-to-find.
// options: [[value, label], ...], first entry is the "All ..." option.
function FilterSelect({ value, onChange, options, placeholder = 'Select...', darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const [allOption, ...restOptions] = options;
  const selected = options.find(([v]) => String(v) === String(value));
  const filtered = search.trim()
    ? restOptions.filter(([, label]) => label?.toLowerCase().includes(search.trim().toLowerCase()))
    : restOptions;

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
          border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9, padding:'8px 12px', fontSize:13,
          background:T.inputBg, color: (selected && selected[0]) ? T.text : T.text3, cursor:'pointer', boxSizing:'border-box',
          textAlign:'left', transition:'border-color 0.15s',
        }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected ? selected[1] : placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ flexShrink:0, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          {restOptions.length > 6 && (
            <div style={{ padding:8, borderBottom:`1px solid ${T.border}` }}>
              <input autoFocus type="text" value={search} placeholder="Search..."
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
            </div>
          )}
          <div style={{ maxHeight:260, overflowY:'auto', padding:5 }}>
            <button type="button" onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              style={{ width:'100%', textAlign:'left', padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight: !value ? 700 : 500, color: !value ? '#2563eb' : T.text, background: !value ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent' }}
              onMouseEnter={e => { if (value) e.currentTarget.style.background = T.bg3; }}
              onMouseLeave={e => { if (value) e.currentTarget.style.background = 'transparent'; }}>
              {allOption[1]}
            </button>
            {filtered.map(([v, label]) => {
              const sel = String(v) === String(value);
              return (
                <button key={v} type="button" onClick={() => { onChange(String(v)); setOpen(false); setSearch(''); }}
                  style={{ width:'100%', textAlign:'left', padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight: sel ? 700 : 500, color: sel ? '#2563eb' : T.text, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  {label}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>No match for "{search}"</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Default icon is the delete/trash glyph (this modal started life as a delete-only
// confirm) — callers doing anything else (edit, cancel, etc.) MUST pass their own
// icon/confirmLabel/confirmColor via showConfirm's opts, or every confirmation in
// the app reads as "Delete" regardless of what it actually does.
const CONFIRM_ICON_DELETE = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>;
const CONFIRM_ICON_EDIT = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const CONFIRM_ICON_CANCEL = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>;

function ConfirmModal({ open, title, message, onConfirm, onCancel, confirmLabel='Delete', confirmColor='#ef4444', iconBg='#fef2f2', icon }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, width:'min(92vw,420px)', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:iconBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {icon || CONFIRM_ICON_DELETE}
            </div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1f45', margin:0 }}>{title}</h3>
          </div>
          <p style={{ fontSize:13, color:'#64748b', margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', gap:10, padding:'16px 24px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#4a5568', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'none', background:confirmColor, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function Finance({ darkMode = false, T: theme }) {
  const T = theme || { bg:'#f4f7ff', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm, confirmLabel, confirmColor, iconBg, icon }
  // opts lets a caller override the button label/color/icon — without it every
  // confirmation would render as the delete-styled default (trash icon, red "Delete"),
  // which is wrong for anything that isn't actually a delete.
  const showConfirm = (title, message, onConfirm, opts = {}) => setConfirm({ title, message, onConfirm, ...opts });
  const [activeTab, setActiveTab] = useState(() => {
    if (localStorage.getItem('openReceiptId')) return 'receipts';
    if (localStorage.getItem('openInvoiceId')) return 'invoices';
    // "+ Create Receipt" from a Client Dashboard's Ledger tab hands off via
    // this same key ReceiptsTab already reads to prefill "record against an
    // invoice" — here it only carries client_id, so the form opens blank
    // except for the client.
    if (localStorage.getItem('pendingReceiptFromInvoice')) return 'receipts';
    return 'invoices';
  });
  // Each tab used to fully unmount on switch (conditional render), so its
  // list/data was thrown away and re-fetched from scratch every time you
  // came back to it — "Invoices → Receipts → Invoices" re-showed "Loading…"
  // for Invoices even though nothing there had changed. Once a tab has been
  // visited, keep it mounted (just hidden via CSS) so switching back is
  // instant; only the first visit to a tab pays the network cost.
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));
  useEffect(() => { setVisitedTabs(prev => prev.has(activeTab) ? prev : new Set(prev).add(activeTab)); }, [activeTab]);
  const token = localStorage.getItem('token');

  return (
    <div style={{
      height:'100vh', padding:24, display:'flex', flexDirection:'column', overflow:'hidden', boxSizing:'border-box',
      background: darkMode
        ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),#0a0f1e'
        : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),#f4f7ff',
    }}>
      {/* Tabs */}
      <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:24, background:T.bg2, border:`1px solid ${T.border}`, borderRadius:16, padding:6, width:'fit-content', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.3)':'0 1px 4px rgba(15,31,69,0.06)' }}>
        {[
          { key:'invoices',     label:'Invoices',      icon:<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>, icon2:<><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></> },
          { key:'receipts',     label:'Receipts',      icon:<><path d="M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="1" y1="10" x2="23" y2="10"/></> },
          { key:'credit_notes', label:'Credit Notes',  icon:<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></> },
          { key:'quotations',   label:'Quotations',    icon:<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></> },
          { key:'ledger',       label:'Client Ledger', icon:<><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></> },
          { key:'income_statement', label:'Income Statement', icon:<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></> },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            display:'flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:12,
            fontSize:13, fontWeight:600, cursor:'pointer', border:'none', transition:'all 0.15s',
            background: activeTab===tab.key ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent',
            color: activeTab===tab.key ? '#fff' : T.text3,
            boxShadow: activeTab===tab.key ? '0 2px 8px rgba(15,31,69,0.3)' : 'none',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{tab.icon}{tab.icon2}</svg>
            {tab.label}
          </button>
        ))}
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        onConfirm={() => { confirm?.onConfirm(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        iconBg={confirm?.iconBg}
        icon={confirm?.icon}
      />
      {visitedTabs.has('invoices')     && <div style={{ flex:1, minHeight:0, display: activeTab==='invoices'     ? 'flex' : 'none', flexDirection:'column' }}><InvoicesTab     token={token} setActiveTab={setActiveTab} showConfirm={showConfirm} darkMode={darkMode} T={T} /></div>}
      {visitedTabs.has('receipts')     && <div style={{ flex:1, minHeight:0, display: activeTab==='receipts'     ? 'flex' : 'none', flexDirection:'column' }}><ReceiptsTab     token={token} showConfirm={showConfirm} darkMode={darkMode} T={T} /></div>}
      {visitedTabs.has('credit_notes') && <div style={{ flex:1, minHeight:0, display: activeTab==='credit_notes' ? 'flex' : 'none', flexDirection:'column' }}><CreditNotesTab  token={token} showConfirm={showConfirm} darkMode={darkMode} T={T} /></div>}
      {visitedTabs.has('ledger')       && <div style={{ flex:1, minHeight:0, display: activeTab==='ledger'       ? 'flex' : 'none', flexDirection:'column' }}><ClientLedgerTab token={token} darkMode={darkMode} T={T} /></div>}
      {visitedTabs.has('income_statement') && <div style={{ flex:1, minHeight:0, overflowY:'auto', display: activeTab==='income_statement' ? 'block' : 'none' }}><IncomeStatementTab token={token} darkMode={darkMode} T={T} /></div>}
      {visitedTabs.has('quotations')   && <div style={{ flex:1, minHeight:0, display: activeTab==='quotations'   ? 'flex' : 'none', flexDirection:'column' }}><QuotationsTab   token={token} showConfirm={showConfirm} darkMode={darkMode} T={T} /></div>}
    </div>
  );
}

// INVOICES TAB
function InvoicesTab({ token, setActiveTab, showConfirm, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const isAdmin = (JSON.parse(localStorage.getItem('user') || '{}').role) === 'admin';
  const [invoices, setInvoices] = useState([]);
  const [clients,      setClients]      = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active) : [])).catch(() => {});
  }, [token]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editInvoiceId, setEditInvoiceId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingData, setPendingData] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [openActionInvoiceId, setOpenActionInvoiceId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [sortKey, setSortKey] = useState('invoice_date');
  const [sortDir, setSortDir] = useState('desc');
  const onSort = makeSortToggle(sortKey, setSortKey, setSortDir);
  const cc = useColumnCustomizer(INVOICE_COLUMNS, { visKey: 'invoiceVisibleColumns', orderKey: 'invoiceColumnOrder', T, darkMode });
  const { visibleColumns, orderedVisibleKeys } = cc;

  const fetchInvoices = async () => {
    try {
      const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const h = { 'Authorization': `Bearer ${token}` };
      const [invRes, statsRes, clientRes, modesRes] = await Promise.all([
        fetch(`${BASE_URL}/api/invoices`, { headers: h }),
        fetch(`${BASE_URL}/api/invoices/stats`, { headers: h }),
        fetch(`${BASE_URL}/api/clients`, { headers: h }),
        fetch(`${BASE_URL}/api/payment-modes`, { headers: h }),
      ]);
      const invData    = await invRes.json();
      const statsData  = await statsRes.json();
      const clientData = await clientRes.json();
      const modesData  = await modesRes.json();
      setInvoices(Array.isArray(invData) ? invData : []);
      setStats(statsData);
      setClients(Array.isArray(clientData) ? clientData : []);
      setPaymentModes(Array.isArray(modesData) ? modesData.filter(m => m.is_active) : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchInvoices();
    const openId = localStorage.getItem('openInvoiceId');
    if (openId) {
      localStorage.removeItem('openInvoiceId');
      setSelectedInvoice(Number(openId));
      return;
    }
     // Check if navigated from CRM/Tasks with pending invoice data
    const pending = localStorage.getItem('pendingInvoice');
    if (pending) {
      try { setPendingData(JSON.parse(pending)); } catch (_) {}
      localStorage.removeItem('pendingInvoice');
      setShowCreate(true);
    }
    // Refresh list when any invoice's status changes (e.g. Mark as Sent)
    const onStatusChanged = () => fetchInvoices();
    window.addEventListener('invoice-status-changed', onStatusChanged);
    return () => window.removeEventListener('invoice-status-changed', onStatusChanged);
  }, []);
  
  const creatorOptions = Array.from(new Set(invoices.map(inv => inv.created_by_name).filter(Boolean))).sort();
  const clientGroupById = new Map(clients.map(c => [String(c.id), c.group_id ? String(c.group_id) : '']));
  const groupOptions = Array.from(new Map(clients.filter(c => c.group_id).map(c => [String(c.group_id), c.group_name])).entries());

  const filtered = applySort(invoices.filter(inv => {
    const matchSearch = !searchTerm ||
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !filterStatus || inv.status === filterStatus;
    const matchClient = !filterClient || String(inv.client_id) === String(filterClient);
    const matchGroup = !filterGroup || clientGroupById.get(String(inv.client_id)) === String(filterGroup);
    const matchCreatedBy = !filterCreatedBy || inv.created_by_name === filterCreatedBy;
    // Independent of workflow Status (Draft/Sent/Cancelled etc.) — a "Sent"
    // invoice can be Paid, Partial, or fully Unpaid, and this reads straight
    // off the real amounts rather than the status field so it always agrees
    // with what the Paid/Balance columns actually show.
    const paid = Number(inv.amount_paid || 0);
    const paymentState = paid <= 0 ? 'unpaid' : (Number(inv.balance_due || 0) > 0.01 ? 'partial' : 'paid');
    const matchPaymentStatus = !filterPaymentStatus || paymentState === filterPaymentStatus;
    const matchDateFrom = !filterDateFrom || new Date(inv.invoice_date) >= new Date(filterDateFrom);
    const matchDateTo = !filterDateTo || new Date(inv.invoice_date) <= new Date(filterDateTo + 'T23:59:59');
    return matchSearch && matchStatus && matchClient && matchGroup && matchCreatedBy && matchPaymentStatus && matchDateFrom && matchDateTo;
  }), INVOICE_COLUMNS, sortKey, sortDir);

  // Stat cards reflect whatever the filters currently narrow the table down
  // to — matching `stats.*` from the backend (which is always the firm's
  // all-time total) made the cards silently ignore Status/Client/Date
  // filters, defeating the point of filtering. Cancelled invoices are
  // excluded from the money totals the same way the backend's own /stats
  // endpoint excludes them.
  const isFilterActive = !!(searchTerm || filterStatus || filterClient || filterGroup || filterCreatedBy || filterPaymentStatus || filterDateFrom || filterDateTo);
  const filteredStats = (() => {
    const counted = filtered.filter(inv => inv.status !== 'cancelled');
    return {
      total_value: counted.reduce((s, inv) => s + Number(inv.total_amount || 0), 0),
      total_collected: counted.reduce((s, inv) => s + Number(inv.amount_paid || 0), 0),
      total_outstanding: counted.reduce((s, inv) => s + Number(inv.balance_due || 0), 0),
      overdue: filtered.filter(inv => inv.status === 'overdue').length,
    };
  })();
  const displayStats = isFilterActive ? filteredStats : stats;

  const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

    const renderInvoiceCell = (inv, key) => {
      if (key === 'invoice_number') {
        return (
          <button onClick={() => setSelectedInvoice(inv.id)}
            className="text-blue-600 font-medium text-sm hover:underline whitespace-nowrap">
            {inv.invoice_number}
          </button>
        );
      }

      if (key === 'invoice_date') {
        return inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-IN') : '-';
      }

      if (key === 'due_date') {
        return inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-IN') : '-';
      }

      if (key === 'total_amount') {
        return <span className="font-medium text-gray-800">{formatCurrency(inv.total_amount)}</span>;
      }

      if (key === 'amount_paid') {
        return <span className="font-medium text-green-600">{formatCurrency(inv.amount_paid)}</span>;
      }

      if (key === 'balance_due') {
        return <span className="font-medium text-red-600">{formatCurrency(inv.balance_due)}</span>;
      }

      if (key === 'status') {
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[inv.status]}`}>
            {inv.status?.replace('_', ' ')}
          </span>
        );
      }

      return inv[key] || '-';
    };

  const exportInvoicesToCSV = () => {
    const headers = ['Invoice No.', 'Client', 'Date', 'Due Date', 'Amount', 'Paid', 'Balance', 'Status'];
    const rows = filtered.map(inv => [
      inv.invoice_number || '',
      inv.client_name || '',
      inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-IN') : '',
      inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-IN') : '',
      inv.total_amount || 0,
      inv.amount_paid || 0,
      inv.balance_due || 0,
      inv.status || ''
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ChanakyaX_Invoices_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreateReceiptForInvoice = (inv) => {
    localStorage.setItem('pendingReceiptFromInvoice', JSON.stringify({
      client_id: inv.client_id,
      invoice_id: inv.id,
      invoice_number: inv.invoice_number,
      total_amount: inv.total_amount,
      amount_paid: inv.amount_paid,
      balance_due: inv.balance_due,
      payment_date: new Date().toISOString().split('T')[0]
    }));

    setOpenActionInvoiceId(null);
    setActiveTab('receipts');
  };

  const handleDelete = async (inv, force = false) => {
    const doDelete = async () => {
      const res = await fetch(`${BASE}/api/invoices/${inv.id}${force ? '?force=true' : ''}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { fetchInvoices(); return; }
      const d = await res.json().catch(() => ({}));
      if (d.requires_force && isAdmin) {
        showConfirm('Delete Linked Receipts Too?', d.message, () => handleDelete(inv, true));
      } else {
        alert(d.message || 'Delete failed');
      }
    };
    if (force) { doDelete(); return; }
    showConfirm('Delete Invoice', `Delete invoice ${inv.invoice_number}? This cannot be undone.`, doDelete);
  };

  // Editing a draft is routine; editing anything already sent/paid/etc. rewrites
  // numbers the client may already have in hand, so confirm that explicitly.
  const handleEditFromList = (inv) => {
    if (inv.status !== 'draft') {
      showConfirm(
        'Edit Invoice',
        `This invoice is marked as "${(inv.status||'').replace('_',' ')}". Editing it will recalculate its line items and totals. Do you want to continue?`,
        () => setEditInvoiceId(inv.id),
        { confirmLabel: 'Continue', confirmColor: '#f97316', iconBg: '#fff7ed', icon: CONFIRM_ICON_EDIT }
      );
    } else {
      setEditInvoiceId(inv.id);
    }
  };

  const handleCancelInvoice = (inv) => {
    showConfirm('Cancel Invoice', `Cancel invoice ${inv.invoice_number}? It will be frozen — no edits and no new receipts can be adjusted against it.`, async () => {
      const res = await fetch(`${BASE}/api/invoices/${inv.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: 'cancelled' })
      });
      if (res.ok) fetchInvoices();
      else { const d = await res.json().catch(() => ({})); alert(d.message || 'Cancel failed'); }
    }, { confirmLabel: 'Cancel Invoice', confirmColor: '#64748b', iconBg: '#f8fafc', icon: CONFIRM_ICON_CANCEL });
  };

  if (showCreate) {
    return <CreateInvoice token={token} prefillData={pendingData}
      onClose={() => { setShowCreate(false); setPendingData(null); }}
      onSuccess={(createdInvoice) => {
        fetchInvoices();
        setShowCreate(false);
        setPendingData(null);
        if (createdInvoice?.id) setSelectedInvoice(createdInvoice.id);
      }}
      darkMode={darkMode} T={T} />;
  }

  if (editInvoiceId) {
    return <EditInvoice token={token} invoiceId={editInvoiceId}
      onClose={() => setEditInvoiceId(null)}
      onSuccess={() => { fetchInvoices(); setEditInvoiceId(null); }}
      darkMode={darkMode} T={T} />;
  }

  if (selectedInvoice) {
    return <InvoiceDetail invoiceId={selectedInvoice} token={token}
      onClose={() => setSelectedInvoice(null)}
      onEdit={(id) => { setSelectedInvoice(null); setEditInvoiceId(id); }}
      onSuccess={() => { fetchInvoices(); setSelectedInvoice(null); }}
      showConfirm={showConfirm}
      darkMode={darkMode} T={T} />;
  }

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };

  return (
    <div style={{ flex:1, minHeight:0, display:"flex", flexDirection:"column" }}>
      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
        {[
          { label:'Total Invoiced',  value: formatCurrency(displayStats.total_value),       color:'#6366f1', glow:'rgba(99,102,241,0.12)' },
          { label:'Total Collected', value: formatCurrency(displayStats.total_collected),   color:'#10b981', glow:'rgba(16,185,129,0.12)' },
          { label:'Outstanding',     value: formatCurrency(displayStats.total_outstanding), color:'#f59e0b', glow:'rgba(245,158,11,0.12)' },
          { label:'Overdue',         value: `${displayStats.overdue||0} invoices`,          color:'#ef4444', glow:'rgba(239,68,68,0.12)'  },
        ].map(s => (
          <div key={s.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:'16px 20px', boxShadow:`0 4px 16px ${s.glow}`, position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:-16, right:-16, width:56, height:56, borderRadius:'50%', background:s.glow, filter:'blur(12px)' }}/>
            <p style={{ fontSize:20, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
            <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <input type="text" placeholder="Search invoice no., client..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ ...is, width:280 }} />
          <FilterButton activeCount={[filterStatus,filterClient,filterGroup,filterCreatedBy,filterPaymentStatus,filterDateFrom,filterDateTo].filter(Boolean).length} onClick={() => setShowFilters(s => !s)} darkMode={darkMode} T={T} />
          {(filterStatus||filterClient||filterGroup||filterCreatedBy||filterPaymentStatus||filterDateFrom||filterDateTo) && (
            <button onClick={() => { setFilterStatus(''); setFilterClient(''); setFilterGroup(''); setFilterCreatedBy(''); setFilterPaymentStatus(''); setFilterDateFrom(''); setFilterDateTo(''); }}
              style={{ fontSize:12, color:'#ef4444', padding:'6px 10px', borderRadius:8, border:'1px solid #fecaca', background:'none', cursor:'pointer' }}>
              Clear
            </button>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={exportInvoicesToCSV} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
            borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2,
            color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#f97316'; e.currentTarget.style.color='#f97316'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button onClick={() => setShowImport(true)} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
            borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2,
            color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#0f1f45'; e.currentTarget.style.color='#0f1f45'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import
          </button>
          <button onClick={() => setShowCreate(true)} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 18px',
            borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)',
            color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer',
            boxShadow:'0 4px 12px rgba(15,31,69,0.3)',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Invoice
          </button>
        </div>
      </div>
      {showImport && (
        <InvoiceImportModal token={token} clients={clients} onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); fetchInvoices(); }} darkMode={darkMode} T={T} />
      )}

      {/* Filter panel */}
      {showFilters && (
        <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, padding:16, marginBottom:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {[
              { label:'Status', type:'select', value:filterStatus, onChange:e=>setFilterStatus(e.target.value),
                options:[['','All Status'],['draft','Draft'],['sent','Sent'],['partially_paid','Partially Paid'],['paid','Paid'],['overdue','Overdue'],['cancelled','Cancelled']] },
              { label:'Client', type:'select', value:filterClient, onChange:e=>setFilterClient(e.target.value),
                options:[['','All Clients'],...clients.map(c=>[String(c.id),c.client_name])] },
              { label:'Group', type:'searchselect', value:filterGroup, onChange:setFilterGroup,
                options:[['','All Groups'],...groupOptions] },
              { label:'Created By', type:'select', value:filterCreatedBy, onChange:e=>setFilterCreatedBy(e.target.value),
                options:[['','All Users'],...creatorOptions.map(n=>[n,n])] },
              { label:'Payment Status', type:'select', value:filterPaymentStatus, onChange:e=>setFilterPaymentStatus(e.target.value),
                options:[['','All'],['paid','Paid'],['partial','Partial'],['unpaid','Unpaid']] },
            ].map(f => (
              <div key={f.label}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>{f.label}</label>
                {f.type === 'searchselect' ? (
                  <FilterSelect value={f.value} onChange={f.onChange} options={f.options} placeholder={f.options[0][1]} darkMode={darkMode} T={T} />
                ) : (
                  <select value={f.value} onChange={f.onChange} style={is}>
                    {f.options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                )}
              </div>
            ))}
            {[
              { label:'From Date', value:filterDateFrom, onChange:e=>setFilterDateFrom(e.target.value) },
              { label:'To Date',   value:filterDateTo,   onChange:e=>setFilterDateTo(e.target.value) },
            ].map(f => (
              <div key={f.label}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>{f.label}</label>
                <input type="date" value={f.value} onChange={f.onChange} style={is} />
              </div>
            ))}
          </div>
          <p style={{ fontSize:11, color:T.text3, marginTop:10 }}>{filtered.length} invoice(s) match the filters</p>
        </div>
      )}

      {/* Bulk actions bar — appears once at least one row is checked */}
      {selectedIds.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, padding:'8px 14px', borderRadius:10, background: darkMode?'rgba(239,68,68,0.1)':'#fef2f2', border:`1px solid ${darkMode?'rgba(239,68,68,0.25)':'#fecaca'}` }}>
          <span style={{ fontSize:13, fontWeight:600, color:'#dc2626' }}>{selectedIds.length} invoice{selectedIds.length===1?'':'s'} selected</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setSelectedIds([])} style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:'transparent', color:T.text2, fontSize:12, fontWeight:600, cursor:'pointer' }}>
              Clear selection
            </button>
            <button onClick={() => setShowBulkDelete(true)} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'#dc2626', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              Delete Selected
            </button>
          </div>
        </div>
      )}
      {showBulkDelete && (
        <BulkDeleteInvoicesModal token={token} isAdmin={isAdmin}
          invoices={filtered.filter(inv => selectedIds.includes(inv.id))}
          onClose={() => setShowBulkDelete(false)}
          onDone={() => { setShowBulkDelete(false); setSelectedIds([]); fetchInvoices(); }}
          darkMode={darkMode} T={T} />
      )}

      {/* Table */}
      <div style={{ flex:1, minHeight:0, background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
        <div style={{ flex:1, minHeight:0, overflowX:'auto', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
            <tr>
              <th style={{ padding:'10px 12px', width:36 }}>
                <input type="checkbox"
                  checked={filtered.length > 0 && selectedIds.length === filtered.length}
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < filtered.length; }}
                  onChange={e => setSelectedIds(e.target.checked ? filtered.map(inv => inv.id) : [])}
                  style={{ cursor:'pointer' }} />
              </th>
              {orderedVisibleKeys.map(key => {
                const col = INVOICE_COLUMNS.find(c => c.key === key);
                const align = ['total_amount','amount_paid','balance_due'].includes(key) ? 'right' : 'left';
                return <SortableTh key={key} label={col?.label} sortKey={key} activeKey={sortKey} dir={sortDir} onSort={onSort} align={align} />;
              })}
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>Actions</th>
              <th style={{ padding:'10px 8px', width:40, background:'transparent' }}>
                {cc.menu}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleColumns.length + 3} style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + 3} style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>No invoices yet. Click "+ New Invoice" to create one!</td></tr>
            ) : filtered.map((inv, idx) => (
              <tr key={inv.id} style={{ borderBottom:`1px solid ${T.border}`, background: selectedIds.includes(inv.id) ? (darkMode?'rgba(239,68,68,0.08)':'#fef2f2') : darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                onMouseEnter={e => { if (!selectedIds.includes(inv.id)) e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = selectedIds.includes(inv.id) ? (darkMode?'rgba(239,68,68,0.08)':'#fef2f2') : darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'); }}>
                <td style={{ padding:'10px 12px' }}>
                  <input type="checkbox" checked={selectedIds.includes(inv.id)}
                    onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, inv.id] : prev.filter(id => id !== inv.id))}
                    style={{ cursor:'pointer' }} />
                </td>
                {orderedVisibleKeys.map(key => (
                  <td key={key} style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{renderInvoiceCell(inv, key)}</td>
                ))}
                <td style={{ padding:'10px 16px', position:'relative' }}>
                  <button onClick={() => setOpenActionInvoiceId(openActionInvoiceId===inv.id?null:inv.id)}
                    style={{ width:32, height:32, borderRadius:8, border:'none', background:'none', color:T.text3, fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                    onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                    onMouseLeave={e => e.currentTarget.style.background='none'}>⋮</button>
                  {openActionInvoiceId===inv.id && (
                    <div onMouseLeave={() => setOpenActionInvoiceId(null)}
                      style={{ position:'absolute', right:0, top:40, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:999, width:200, padding:6 }}>
                      {[
                        { label:'View', color:'#3b82f6', hbg: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', onClick:()=>{setOpenActionInvoiceId(null);setSelectedInvoice(inv.id);} },
                        // Cancelled invoices are frozen — no edits, no new receipts against them.
                        ...(inv.status!=='cancelled'?[{ label:'Edit', color:'#f97316', hbg: darkMode?'rgba(249,115,22,0.1)':'#fff7ed', onClick:()=>{setOpenActionInvoiceId(null);handleEditFromList(inv);} }]:[]),
                        ...(inv.status!=='cancelled'&&Number(inv.balance_due||0)>0?[{ label:'Create Receipt', color:'#16a34a', hbg: darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', onClick:()=>handleCreateReceiptForInvoice(inv) }]:[]),
                        ...(inv.status!=='paid'&&inv.status!=='cancelled'&&Number(inv.amount_paid||0)<=0?[{ label:'Cancel Invoice', color:T.text3, hbg: T.bg3, onClick:()=>{setOpenActionInvoiceId(null);handleCancelInvoice(inv);} }]:[]),
                        { label:'Delete', color:'#ef4444', hbg: darkMode?'rgba(239,68,68,0.1)':'#fef2f2', onClick:()=>{setOpenActionInvoiceId(null);handleDelete(inv);} },
                      ].map(btn => (
                        <button key={btn.label} onClick={btn.onClick}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:btn.color, fontSize:13, fontWeight:500, cursor:'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background=btn.hbg}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
                <td />
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

// BULK DELETE INVOICES — same confirm → progress → report shape as the
// import modals (deliberately, since this is the "I imported the wrong
// batch, undo it" escape hatch). Reuses the exact single-invoice DELETE
// endpoint per row, so every existing safety rule still applies: a non-admin
// can only delete drafts, and an admin hitting a "this invoice has linked
// receipts" block gets offered the same force-delete-with-cascade step the
// single-invoice delete already has — bulk delete doesn't get a shortcut
// around either rule.
function BulkDeleteInvoicesModal({ token, isAdmin, invoices, onClose, onDone, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  useEffect(() => { ensureImportStyles(); }, []);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total, ok, fail }
  const [result, setResult] = useState(null);

  const deleteOne = async (inv, force = false) => {
    const res = await fetch(`${BASE}/api/invoices/${inv.id}${force ? '?force=true' : ''}`, {
      method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
    });
    if (res.ok) return { ok: true };
    const d = await res.json().catch(() => ({}));
    if (!force && d.requires_force && isAdmin) return deleteOne(inv, true);
    return { ok: false, message: d.message || 'delete failed' };
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    setResult(null);
    let ok = 0;
    const errors = [];
    const tick = (i) => setProgress({ current: i + 1, total: invoices.length, ok, fail: errors.length });
    setProgress({ current: 0, total: invoices.length, ok: 0, fail: 0 });
    for (const [i, inv] of invoices.entries()) {
      try {
        const r = await deleteOne(inv);
        if (r.ok) ok++;
        else errors.push(`${inv.invoice_number || 'Invoice #' + inv.id}: ${r.message}`);
      } catch (e) {
        errors.push(`${inv.invoice_number || 'Invoice #' + inv.id}: ${e.message}`);
      }
      tick(i);
    }
    setResult({ success: ok, errors, total: invoices.length });
    setDeleting(false);
    setProgress(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full shadow-2xl" style={{ maxWidth:'520px' }}>

        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold" style={{ color:'#0f1f45' }}>Delete Invoices</h3>
            <p className="text-xs text-gray-400 mt-0.5">This cannot be undone</p>
          </div>
          <button onClick={() => { if (deleting) return; onClose(); }} disabled={deleting}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400 transition disabled:opacity-30 disabled:cursor-not-allowed">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="p-7 space-y-5">
          {(!deleting && !result) && (
            <div className="border border-red-100 bg-red-50 rounded-2xl p-5 flex items-start gap-3">
              <div className="w-10 h-10 bg-white border border-red-200 rounded-xl flex items-center justify-center text-red-600 flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Delete {invoices.length} invoice{invoices.length === 1 ? '' : 's'}?</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  {isAdmin
                    ? 'Draft invoices delete outright. Any with linked receipts will have those receipts deleted too, and payments they settled on other invoices reversed.'
                    : 'Only draft invoices can be deleted. Anything already sent, paid, or otherwise finalised will show as failed below — ask an admin to remove those.'}
                </p>
              </div>
            </div>
          )}

          {/* Progress */}
          {deleting && progress && (() => {
            const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
            return (
              <div className="rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                      <span className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"/>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Deleting invoices</p>
                      <p className="text-xs text-gray-400">Please keep this window open</p>
                    </div>
                  </div>
                  <p className="text-lg font-extrabold" style={{ color:'#0f1f45', fontVariantNumeric:'tabular-nums' }}>
                    {progress.current}<span className="text-gray-300 font-semibold text-sm"> / {progress.total}</span>
                  </p>
                </div>
                <div style={{ height: 12, width: '100%', borderRadius: 999, background: '#eef1f6', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 999, width: `${Math.max(pct, 2)}%`,
                    background: 'linear-gradient(90deg,#991b1b,#dc2626)', transition: 'width 0.35s ease-out',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{
                      position: 'absolute', inset: 0,
                      backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.22) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.22) 75%, transparent 75%, transparent)',
                      backgroundSize: '28px 28px', animation: 'cx-stripe 0.7s linear infinite',
                    }} />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-4 text-xs font-semibold" style={{ fontVariantNumeric:'tabular-nums' }}>
                    <span className="text-green-600">✓ {progress.ok || 0} deleted</span>
                    <span className={progress.fail ? 'text-red-600' : 'text-gray-300'}>✕ {progress.fail || 0} failed</span>
                  </div>
                  <p className="text-xs font-bold text-gray-500" style={{ fontVariantNumeric:'tabular-nums' }}>{pct}%</p>
                </div>
              </div>
            );
          })()}

          {/* Report */}
          {result && (
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100" style={{ background:'#f8faff' }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: result.errors.length ? '#fef2f2' : '#f0fdf4' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={result.errors.length ? '#dc2626' : '#16a34a'} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <p className="text-sm font-bold" style={{ color:'#0f1f45' }}>Delete complete</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                {[
                  { label:'Deleted', value: result.success,        color:'#16a34a', bg:'#f0fdf4' },
                  { label:'Failed',  value: result.errors.length,  color:'#dc2626', bg:'#fef2f2' },
                ].map(s => (
                  <div key={s.label} className="p-4 text-center" style={{ background: s.value ? s.bg : '#fff' }}>
                    <p className="text-2xl font-extrabold" style={{ color: s.value ? s.color : '#94a3b8', fontVariantNumeric:'tabular-nums' }}>{s.value}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              {result.errors.length > 0 && (
                <div className="border-t border-gray-100 p-4 space-y-1.5 max-h-40 overflow-y-auto bg-gray-50">
                  {result.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600 flex gap-1.5"><span className="flex-shrink-0">✕</span><span>{e}</span></p>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-100 px-4 py-2.5 bg-white">
                <p className="text-xs text-gray-500">
                  {result.success > 0
                    ? `${result.success} of ${result.total} invoice${result.total === 1 ? '' : 's'} deleted.`
                    : 'No invoices were deleted.'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={() => { if (deleting) return; result ? onDone() : onClose(); }} disabled={deleting}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition disabled:opacity-40 disabled:cursor-not-allowed">
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (
            <button onClick={handleDeleteAll} disabled={deleting}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
              style={{ background:'#dc2626' }}>
              {deleting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
              {deleting
                ? (progress ? `Deleting… ${progress.current}/${progress.total}` : 'Deleting…')
                : `Delete ${invoices.length} Invoice${invoices.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Generic bulk-delete progress/report modal — same shell as
// BulkDeleteInvoicesModal (striped progress bar, ok/fail report) but
// parameterized so Receipts / Quotations / Credit Notes can each get bulk
// delete without three near-duplicate 150-line components. `skipIf` lets a
// caller reject an item before ever hitting the network (e.g. a quotation
// already converted to an invoice) with its own reason string.
function BulkDeleteModal({ token, entityName, items, getLabel, buildDeleteUrl, warningText, skipIf, onClose, onDone, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  useEffect(() => { ensureImportStyles(); }, []);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total, ok, fail }
  const [result, setResult] = useState(null);
  const plural = `${entityName}s`;

  const handleDeleteAll = async () => {
    setDeleting(true);
    setResult(null);
    let ok = 0;
    const errors = [];
    const tick = (i) => setProgress({ current: i + 1, total: items.length, ok, fail: errors.length });
    setProgress({ current: 0, total: items.length, ok: 0, fail: 0 });
    for (const [i, item] of items.entries()) {
      const skipReason = skipIf?.(item);
      if (skipReason) { errors.push(`${getLabel(item)}: ${skipReason}`); tick(i); continue; }
      try {
        const res = await fetch(buildDeleteUrl(item), { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) { ok++; }
        else { const d = await res.json().catch(() => ({})); errors.push(`${getLabel(item)}: ${d.message || 'delete failed'}`); }
      } catch (e) {
        errors.push(`${getLabel(item)}: ${e.message}`);
      }
      tick(i);
    }
    setResult({ success: ok, errors, total: items.length });
    setDeleting(false);
    setProgress(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full shadow-2xl" style={{ maxWidth:'520px' }}>

        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold" style={{ color:'#0f1f45' }}>Delete {plural}</h3>
            <p className="text-xs text-gray-400 mt-0.5">This cannot be undone</p>
          </div>
          <button onClick={() => { if (deleting) return; onClose(); }} disabled={deleting}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400 transition disabled:opacity-30 disabled:cursor-not-allowed">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="p-7 space-y-5">
          {(!deleting && !result) && (
            <div className="border border-red-100 bg-red-50 rounded-2xl p-5 flex items-start gap-3">
              <div className="w-10 h-10 bg-white border border-red-200 rounded-xl flex items-center justify-center text-red-600 flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Delete {items.length} {items.length === 1 ? entityName : plural}?</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{warningText}</p>
              </div>
            </div>
          )}

          {/* Progress */}
          {deleting && progress && (() => {
            const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
            return (
              <div className="rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                      <span className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"/>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Deleting {plural.toLowerCase()}</p>
                      <p className="text-xs text-gray-400">Please keep this window open</p>
                    </div>
                  </div>
                  <p className="text-lg font-extrabold" style={{ color:'#0f1f45', fontVariantNumeric:'tabular-nums' }}>
                    {progress.current}<span className="text-gray-300 font-semibold text-sm"> / {progress.total}</span>
                  </p>
                </div>
                <div style={{ height: 12, width: '100%', borderRadius: 999, background: '#eef1f6', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 999, width: `${Math.max(pct, 2)}%`,
                    background: 'linear-gradient(90deg,#991b1b,#dc2626)', transition: 'width 0.35s ease-out',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{
                      position: 'absolute', inset: 0,
                      backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.22) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.22) 75%, transparent 75%, transparent)',
                      backgroundSize: '28px 28px', animation: 'cx-stripe 0.7s linear infinite',
                    }} />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-4 text-xs font-semibold" style={{ fontVariantNumeric:'tabular-nums' }}>
                    <span className="text-green-600">✓ {progress.ok || 0} deleted</span>
                    <span className={progress.fail ? 'text-red-600' : 'text-gray-300'}>✕ {progress.fail || 0} failed</span>
                  </div>
                  <p className="text-xs font-bold text-gray-500" style={{ fontVariantNumeric:'tabular-nums' }}>{pct}%</p>
                </div>
              </div>
            );
          })()}

          {/* Report */}
          {result && (
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100" style={{ background:'#f8faff' }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: result.errors.length ? '#fef2f2' : '#f0fdf4' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={result.errors.length ? '#dc2626' : '#16a34a'} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <p className="text-sm font-bold" style={{ color:'#0f1f45' }}>Delete complete</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                {[
                  { label:'Deleted', value: result.success,        color:'#16a34a', bg:'#f0fdf4' },
                  { label:'Failed',  value: result.errors.length,  color:'#dc2626', bg:'#fef2f2' },
                ].map(s => (
                  <div key={s.label} className="p-4 text-center" style={{ background: s.value ? s.bg : '#fff' }}>
                    <p className="text-2xl font-extrabold" style={{ color: s.value ? s.color : '#94a3b8', fontVariantNumeric:'tabular-nums' }}>{s.value}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              {result.errors.length > 0 && (
                <div className="border-t border-gray-100 p-4 space-y-1.5 max-h-40 overflow-y-auto bg-gray-50">
                  {result.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600 flex gap-1.5"><span className="flex-shrink-0">✕</span><span>{e}</span></p>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-100 px-4 py-2.5 bg-white">
                <p className="text-xs text-gray-500">
                  {result.success > 0
                    ? `${result.success} of ${result.total} ${result.total === 1 ? entityName : plural} deleted.`
                    : `No ${plural.toLowerCase()} were deleted.`}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={() => { if (deleting) return; result ? onDone() : onClose(); }} disabled={deleting}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition disabled:opacity-40 disabled:cursor-not-allowed">
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (
            <button onClick={handleDeleteAll} disabled={deleting}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
              style={{ background:'#dc2626' }}>
              {deleting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
              {deleting
                ? (progress ? `Deleting… ${progress.current}/${progress.total}` : 'Deleting…')
                : `Delete ${items.length} ${items.length === 1 ? entityName : plural}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Header names accepted from an uploaded file, mapped onto our field keys —
// lets someone paste in a raw export (Practice-style headers included)
// without first renaming every column to match our template exactly.
const INVOICE_IMPORT_HEADER_MAP = {
  'old invoice no': 'reference_number', 'old invoice no.': 'reference_number',
  'old invoice number': 'reference_number', 'invoice no': 'reference_number',
  'invoice no.': 'reference_number', 'invoice number': 'reference_number',
  'reference no': 'reference_number', 'reference no.': 'reference_number', 'reference number': 'reference_number',
  'client name': 'client_name', 'client': 'client_name', 'name': 'client_name',
  'client gstin': 'client_gstin', 'gstin': 'client_gstin',
  'client mobile': 'client_mobile', 'mobile': 'client_mobile',
  'client email': 'client_email', 'email': 'client_email',
  'client address': 'client_address', 'address': 'client_address',
  'client city': 'client_city', 'city': 'client_city',
  'client state': 'client_state', 'state': 'client_state',
  'invoice date': 'invoice_date', 'date': 'invoice_date',
  'due date': 'due_date',
  'item description': 'item_particular', 'item / service': 'item_particular',
  'service': 'item_particular', 'particular': 'item_particular', 'particulars': 'item_particular',
  'item details': 'item_description', 'description': 'item_description', 'scope of work': 'item_description',
  'rate': 'rate', 'rate (₹)': 'rate', 'amount': 'rate', 'amount (₹)': 'rate',
  'discount': 'discount', 'discount (₹)': 'discount',
  'apply gst': 'apply_gst', 'apply gst?': 'apply_gst', 'gst': 'apply_gst',
};

// IMPORT INVOICES — CSV/XLSX upload → grouped by "Old Invoice No." so one
// invoice can carry several line-item rows, client auto-matched/created by
// name, then created through the same POST /api/invoices every other invoice
// goes through (so numbering, GST math, and validation all stay identical
// to a hand-created invoice — the import doesn't get a separate, divergent
// code path). GST is not recalculated by default: migrated invoices carry a
// final historical amount, and re-taxing it on top would double-count
// whatever was already charged, unless a row explicitly opts in.
// Injects the shared striped-progress-bar keyframes once per page — same
// animation the Client import uses, so all three importers move identically.
function ensureImportStyles() {
  if (document.getElementById('cx-import-styles')) return;
  const s = document.createElement('style');
  s.id = 'cx-import-styles';
  s.textContent = `@keyframes cx-stripe { from { background-position: 0 0; } to { background-position: 28px 0; } }`;
  document.head.appendChild(s);
}

function InvoiceImportModal({ token, clients, onClose, onDone, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  useEffect(() => { ensureImportStyles(); }, []);
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total, ok, skip, fail }
  const [result, setResult] = useState(null);
  const [numberingMode, setNumberingMode] = useState('new'); // 'new' | 'keep'
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Invoices');
    ws.columns = [
      { header: 'Old Invoice No.*', key: 'reference_number', width: 18 },
      { header: 'Client Name*', key: 'client_name', width: 26 },
      { header: 'Client GSTIN', key: 'client_gstin', width: 18 },
      { header: 'Client Mobile', key: 'client_mobile', width: 14 },
      { header: 'Client Email', key: 'client_email', width: 22 },
      { header: 'Client Address', key: 'client_address', width: 24 },
      { header: 'Client City', key: 'client_city', width: 16 },
      { header: 'Client State', key: 'client_state', width: 18 },
      { header: 'Invoice Date*', key: 'invoice_date', width: 14 },
      { header: 'Due Date', key: 'due_date', width: 14 },
      { header: 'Item / Service*', key: 'item_particular', width: 28 },
      { header: 'Item Details', key: 'item_description', width: 30 },
      { header: 'Rate (₹)*', key: 'rate', width: 12 },
      { header: 'Discount (₹)', key: 'discount', width: 12 },
      { header: 'Apply GST?', key: 'apply_gst', width: 12 },
    ];
    ws.getRow(1).eachCell(c => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1F45' } }; });
    ws.getColumn('invoice_date').numFmt = '@';
    ws.getColumn('due_date').numFmt = '@';
    ws.getCell('I1').note = 'Any common format works: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY-MM-DD.';
    ws.getCell('A1').note = 'Rows sharing the same Old Invoice No. become ONE invoice with multiple line items. Leave blank on a single-item invoice — each blank row becomes its own invoice.';
    ws.getCell('K1').note = 'If Practice put several task names in one cell separated by commas (e.g. "Auditor Appointment & ADT-1 Filing, ITR-6 Filing, MCA Annual Filing"), paste it in as-is — the importer splits it into one line item per name automatically. The row\'s Rate/Discount is applied to the first item only; the rest come in at ₹0, since Practice never gave a per-service price — only the invoice total, which this keeps exactly correct.';
    ws.getCell('O1').note = 'Yes = GST is calculated fresh on top of Rate/Discount. Leave blank/No for migrated invoices whose Rate already IS the final historical amount — this is the right choice for almost all imports.';
    // Two rows sharing one Old Invoice No. — shows how a multi-line invoice groups.
    ws.addRow({ reference_number: 'OLD-INV-101', client_name: 'ABC Private Limited', client_gstin: '09AABCA1234A1Z5', client_mobile: '9876543210', client_city: 'Lucknow', client_state: 'Uttar Pradesh', invoice_date: '24/05/2026', due_date: '07/06/2026', item_particular: 'Annual Filing', item_description: 'Scope of Work: MCA annual return filing', rate: 60000, discount: 0, apply_gst: 'No' });
    ws.addRow({ reference_number: 'OLD-INV-101', client_name: 'ABC Private Limited', item_particular: 'Trademark Hearing', rate: 24000, discount: 3000, apply_gst: 'No' });
    ws.addRow({ reference_number: '', client_name: 'XYZ Enterprises', invoice_date: '01/06/2026', item_particular: 'GST Registration', rate: 2500, discount: 0, apply_gst: 'No' });
    // Comma-joined cell (Practice's own export shape) — auto-splits into 3 line items on import: ₹18,000 on the first, ₹0 on the other two.
    ws.addRow({ reference_number: '', client_name: 'PQR Corporate Advisors', invoice_date: '10/06/2026', item_particular: 'Auditor Appointment & ADT-1 Filing, ITR-6 - Income Tax Return (Company ITR) 2025-26, MCA Annual Filing 2025-26', rate: 18000, discount: 0, apply_gst: 'No' });
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'Invoice_Import_Template.xlsx';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const normalized = data.map(row => {
        const out = {};
        Object.entries(row).forEach(([k, v]) => {
          const cleanLabel = k.replace(/\*/g, '').trim().toLowerCase();
          const field = INVOICE_IMPORT_HEADER_MAP[cleanLabel] || cleanLabel.replace(/\s+/g, '_');
          if (field === 'invoice_date' || field === 'due_date') out[field] = toISODateLoose(v);
          else out[field] = String(v).trim();
        });
        return out;
      });
      setRows(normalized);
      setResult(null);
    };
    reader.readAsBinaryString(file);
  };

  const handleSubmit = async () => {
    if (!rows.length) return;
    setImporting(true);
    setResult(null);
    const clientsArr = [...clients];
    const clientCache = new Map();
    let success = 0, skipped = 0;
    const errors = [];
    const warnings = [];

    // Group rows into invoices: rows sharing a non-blank reference_number
    // collapse into one invoice with multiple line items — BUT only when
    // they also agree on client name. An Old Invoice No. that turns out not
    // to be unique (e.g. a date got pasted into that column instead of a
    // real invoice number, so every invoice issued that day carries the
    // "same" reference) must NOT merge unrelated clients' invoices into one
    // — that silently attributes another client's line items and amount to
    // whoever's row happened to come first. Same reference + different
    // client name = treated as separate invoices, with a warning so the
    // mismatch is visible instead of silently wrong.
    const groups = [];
    const groupIndex = new Map();
    rows.forEach((row, i) => {
      const normName = (row.client_name || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const key = row.reference_number ? `ref:${row.reference_number}|${normName}` : `single:${i}`;
      if (!groupIndex.has(key)) { groupIndex.set(key, groups.length); groups.push({ key, rows: [], firstRowNum: i + 2 }); }
      groups[groupIndex.get(key)].rows.push({ ...row, __rowNum: i + 2 });
    });

    // Flag (don't block) Old Invoice Nos. that show up under more than one
    // client name — almost always means that column isn't actually a unique
    // invoice number in this file, and every such invoice imported as
    // separate single-line invoices rather than merged.
    const refToClients = new Map();
    rows.forEach(row => {
      if (!row.reference_number) return;
      const set = refToClients.get(row.reference_number) || new Set();
      set.add((row.client_name || '').replace(/\s+/g, ' ').trim().toLowerCase());
      refToClients.set(row.reference_number, set);
    });
    for (const [ref, clientSet] of refToClients.entries()) {
      if (clientSet.size > 1) {
        warnings.push(`Old Invoice No. "${ref}" appears against ${clientSet.size} different clients — each kept as its own separate invoice instead of being merged. If that column actually holds a date rather than a unique invoice number, leave it blank instead.`);
      }
    }

    // Invoices are created in Invoice Date order (earliest first), NOT the
    // order rows happen to sit in the spreadsheet — so when numbers are
    // freshly generated, the sequence still reads chronologically (lowest
    // number = earliest date) exactly like real invoicing, rather than
    // mirroring whatever order the file happened to be exported in. Rows
    // with no readable date sort last (they'll fail their own validation a
    // few lines below anyway) instead of colliding at the front.
    groups.sort((a, b) => {
      const da = a.rows[0]?.invoice_date || '9999-99-99';
      const db = b.rows[0]?.invoice_date || '9999-99-99';
      return da < db ? -1 : da > db ? 1 : 0;
    });

    const tick = (i) => setProgress({ current: i + 1, total: groups.length, ok: success, skip: skipped, fail: errors.length });
    setProgress({ current: 0, total: groups.length, ok: 0, skip: 0, fail: 0 });
    for (const [gi, group] of groups.entries()) {
      const head = group.rows[0];
      if (!head.client_name) { errors.push(`Row ${head.__rowNum}: Client Name is required`); tick(gi); continue; }
      if (!head.invoice_date) { errors.push(`Row ${head.__rowNum}: Invoice Date is required or unreadable`); tick(gi); continue; }
      const itemRows = group.rows.filter(r => r.item_particular);
      if (!itemRows.length) { errors.push(`Row ${head.__rowNum}: no line item (Item / Service is required)`); tick(gi); continue; }

      try {
        const client_id = await resolveOrCreateClient(head.client_name, token, clientCache, clientsArr);
        if (!client_id) { errors.push(`Row ${head.__rowNum}: could not resolve/create client "${head.client_name}"`); tick(gi); continue; }

        // Practice bundles several task names into ONE cell, comma-separated,
        // against a single total — there's no per-service price to recover.
        // Split into one line item per name; the first carries the row's
        // full Rate/Discount so the invoice's total stays exactly correct,
        // the rest come in at ₹0 rather than a fabricated even split.
        const items = itemRows.flatMap(r => {
          const names = String(r.item_particular || '').split(',').map(s => s.trim()).filter(Boolean);
          if (names.length <= 1) {
            return [{
              particular: r.item_particular, description: r.item_description || '', sac_code: '',
              quantity: 1, rate: parseFloat(r.rate) || 0,
              discount_type: parseFloat(r.discount) > 0 ? 'fixed' : 'none', discount_value: parseFloat(r.discount) || 0,
            }];
          }
          return names.map((particular, i) => ({
            particular, description: i === 0 ? (r.item_description || '') : '', sac_code: '',
            quantity: 1, rate: i === 0 ? (parseFloat(r.rate) || 0) : 0,
            discount_type: i === 0 && parseFloat(r.discount) > 0 ? 'fixed' : 'none',
            discount_value: i === 0 ? (parseFloat(r.discount) || 0) : 0,
          }));
        });
        const applyGst = /^y/i.test(head.apply_gst || '');

        // "Keep Old Invoice No." only actually applies when this row/group has
        // one — a blank Old Invoice No. always falls back to a fresh number
        // (flagged as a warning) regardless of the chosen mode.
        const keepOld = numberingMode === 'keep' && head.reference_number;
        if (numberingMode === 'keep' && !head.reference_number) {
          warnings.push(`Row ${head.__rowNum}: no Old Invoice No. given — a new invoice number was auto-generated for this row instead.`);
        }

        const res = await fetch(`${BASE}/api/invoices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            client_id, invoice_date: head.invoice_date, due_date: head.due_date || null,
            payment_terms: 'due_on_receipt', items,
            discount_type: 'none', discount_value: 0,
            gst_type: applyGst ? 'cgst_sgst' : 'none',
            custom_invoice_number: keepOld ? head.reference_number : undefined,
            remarks: keepOld ? 'Imported — original invoice number retained' : head.reference_number ? `Imported — original invoice no. ${head.reference_number}` : 'Imported',
          }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) { errors.push(`Row ${head.__rowNum}: ${d.message || 'invoice creation failed'}`); tick(gi); continue; }

        // Migrated invoices were already issued — mark it Sent, not Draft.
        await fetch(`${BASE}/api/invoices/${d.id}/status`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ status: 'sent' }),
        }).catch(() => {});

        success++;
      } catch (e) {
        errors.push(`Row ${head.__rowNum}: ${e.message}`);
      }
      tick(gi);
    }
    setResult({ success, skipped, errors, warnings, total: groups.length });
    setImporting(false);
    setProgress(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full shadow-2xl" style={{ maxWidth:'580px' }}>

        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold" style={{ color:'#0f1f45' }}>Import Invoices</h3>
            <p className="text-xs text-gray-400 mt-0.5">Bulk upload from Excel/CSV — Practice or any other source</p>
          </div>
          <button onClick={() => { if (importing) return; onClose(); }} disabled={importing}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400 transition disabled:opacity-30 disabled:cursor-not-allowed">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="p-7 space-y-5">
          {(!importing && !result) && (
          <>
          {/* Step 1 */}
          <div className="border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center text-green-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Download Template</p>
                  <p className="text-xs text-gray-400 mt-0.5">Rows sharing an Old Invoice No. become one invoice</p>
                </div>
              </div>
              <button onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-green-200 text-green-700 hover:bg-green-50 transition">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download
              </button>
            </div>
          </div>

          {/* Step 2 — Invoice numbering */}
          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-800 mb-1">Invoice Numbering</p>
            <p className="text-xs text-gray-400 mb-3">Choose what happens to the "Old Invoice No." column on import</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'new', title: 'Generate New Numbers', desc: 'Every imported invoice gets the next number in this firm\'s own sequence. Old Invoice No. is kept only as a note.' },
                { key: 'keep', title: 'Keep Old Invoice No.', desc: 'Uses the Old Invoice No. column as the real invoice number. Duplicates of an existing number are rejected; blank rows still get a new number.' },
              ].map(opt => (
                <button key={opt.key} type="button" onClick={() => setNumberingMode(opt.key)}
                  className={`text-left rounded-xl border p-3 transition ${numberingMode === opt.key ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${numberingMode === opt.key ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} style={numberingMode === opt.key ? { boxShadow: 'inset 0 0 0 2px #fff' } : undefined} />
                    <span className={`text-sm font-semibold ${numberingMode === opt.key ? 'text-blue-700' : 'text-gray-700'}`}>{opt.title}</span>
                  </div>
                  <p className="text-xs text-gray-400 leading-snug">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Step 3 */}
          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-800 mb-3">Upload Filled Template</p>
            <label className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition ${rows.length > 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
              {rows.length > 0 ? (
                <div className="text-center">
                  <svg className="w-8 h-8 text-blue-500 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <p className="text-sm font-semibold text-blue-700">{rows.length} row(s) ready</p>
                  <p className="text-xs text-blue-400 mt-0.5">Click to change file</p>
                </div>
              ) : (
                <div className="text-center">
                  <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <p className="text-sm font-medium text-gray-500">Click to upload Excel/CSV file</p>
                  <p className="text-xs text-gray-400 mt-0.5">.xlsx, .xls or .csv</p>
                </div>
              )}
            </label>
          </div>
          </>
          )}

          {/* Progress */}
          {importing && progress && (() => {
            const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
            return (
              <div className="rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"/>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Importing invoices</p>
                      <p className="text-xs text-gray-400">Please keep this window open</p>
                    </div>
                  </div>
                  <p className="text-lg font-extrabold" style={{ color:'#0f1f45', fontVariantNumeric:'tabular-nums' }}>
                    {progress.current}<span className="text-gray-300 font-semibold text-sm"> / {progress.total}</span>
                  </p>
                </div>
                <div style={{ height: 12, width: '100%', borderRadius: 999, background: '#eef1f6', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 999, width: `${Math.max(pct, 2)}%`,
                    background: 'linear-gradient(90deg,#0f1f45,#2a4d82)', transition: 'width 0.35s ease-out',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{
                      position: 'absolute', inset: 0,
                      backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.22) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.22) 75%, transparent 75%, transparent)',
                      backgroundSize: '28px 28px', animation: 'cx-stripe 0.7s linear infinite',
                    }} />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-4 text-xs font-semibold" style={{ fontVariantNumeric:'tabular-nums' }}>
                    <span className="text-green-600">✓ {progress.ok || 0} added</span>
                    <span className={progress.fail ? 'text-red-600' : 'text-gray-300'}>✕ {progress.fail || 0} failed</span>
                  </div>
                  <p className="text-xs font-bold text-gray-500" style={{ fontVariantNumeric:'tabular-nums' }}>{pct}%</p>
                </div>
              </div>
            );
          })()}

          {/* Report */}
          {result && (
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100" style={{ background:'#f8faff' }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: result.errors.length ? '#fef2f2' : '#f0fdf4' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={result.errors.length ? '#dc2626' : '#16a34a'} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <p className="text-sm font-bold" style={{ color:'#0f1f45' }}>Import complete</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                {[
                  { label:'Imported', value: result.success,        color:'#16a34a', bg:'#f0fdf4' },
                  { label:'Failed',   value: result.errors.length,  color:'#dc2626', bg:'#fef2f2' },
                ].map(s => (
                  <div key={s.label} className="p-4 text-center" style={{ background: s.value ? s.bg : '#fff' }}>
                    <p className="text-2xl font-extrabold" style={{ color: s.value ? s.color : '#94a3b8', fontVariantNumeric:'tabular-nums' }}>{s.value}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              {(result.errors.length > 0 || result.warnings.length > 0) && (
                <div className="border-t border-gray-100 p-4 space-y-1.5 max-h-40 overflow-y-auto bg-gray-50">
                  {result.errors.map((e, i) => (
                    <p key={'e' + i} className="text-xs text-red-600 flex gap-1.5"><span className="flex-shrink-0">✕</span><span>{e}</span></p>
                  ))}
                  {result.warnings.map((w, i) => (
                    <p key={'w' + i} className="text-xs text-gray-500 flex gap-1.5"><span className="flex-shrink-0 text-amber-500 font-bold">!</span><span>{w}</span></p>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-100 px-4 py-2.5 bg-white">
                <p className="text-xs text-gray-500">
                  {result.success > 0
                    ? `${result.success} of ${result.total} invoice${result.total === 1 ? '' : 's'} created.`
                    : 'No invoices were created.'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={() => { if (importing) return; result ? onDone() : onClose(); }} disabled={importing}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition disabled:opacity-40 disabled:cursor-not-allowed">
            {result ? 'Done' : 'Close'}
          </button>
          {rows.length > 0 && !result && (
            <button onClick={handleSubmit} disabled={importing}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
              style={{ background:'#0f1f45' }}>
              {importing && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
              {importing
                ? (progress ? `Importing… ${progress.current}/${progress.total}` : 'Importing…')
                : `Import ${rows.length} Row${rows.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// CREATE INVOICE
function CreateInvoice({ token, onClose, onSuccess, prefillData, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [firmSettings, setFirmSettings] = useState({});
  const [nextInvoiceNo, setNextInvoiceNo] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: '', invoice_date: new Date().toISOString().split('T')[0],
    due_date: '', payment_terms: 'due_on_receipt',
    discount_type: 'none', discount_value: 0,
    gst_type: 'cgst_sgst', remarks: ''
  });
  const [items, setItems] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [openServicePicker, setOpenServicePicker] = useState(null); // item index

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE}/api/invoices/settings/config`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE}/api/invoices/next-number`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${BASE}/api/services/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
      ]).then(([c, s, nextNo, svc]) => {
      setClients(Array.isArray(c) ? c : []);
      setClientsLoaded(true);
      setServices(Array.isArray(svc) ? svc : []);
      const settings = s || {};
      setFirmSettings(settings);
      setNextInvoiceNo(nextNo?.invoice_number || '');
      if (settings.is_gst_registered === false) {
        setForm(f => ({ ...f, gst_type: 'none' }));
      }
      // Pre-fill items from CRM quotation — task_id (if the quotation's
      // acceptance auto-created a matching Task) is carried over so the
      // invoice marks that real task "billed", not left task-less.
      if (prefillData?.items && prefillData.items.length > 0) {
        setItems(prefillData.items.map(i => ({
          task_id: i.task_id || null, service_id: null,
          particular: i.particular || '', description: i.description || '',
          sac_code: '', quantity: i.quantity || 1, rate: i.rate || 0,
          discount_type: i.discount_type || 'none', discount_value: i.discount_value || 0
        })));
      }
      if (Array.isArray(prefillData?.task_ids) && prefillData.task_ids.length > 0) {
        setSelectedTaskIds(prefillData.task_ids);
      }
      if (prefillData?.remarks) {
        setForm(f => ({ ...f, remarks: prefillData.remarks }));
      }
    }).catch(console.error);
  }, [token]);

  // ✅ Fix race condition: set client AFTER clients array is loaded
  useEffect(() => {
    if (!clientsLoaded || !prefillData?.client_id) return;
    handleClientChange(String(prefillData.client_id));
  }, [clientsLoaded]);

  const handleClientChange = async (clientId) => {
    setForm(f => ({ ...f, client_id: clientId }));
    // Only clear items that came from the PREVIOUS client's pending-tasks
    // picker (flagged _fromPendingTask) — manually-added or quotation-
    // prefilled items (which may carry their own task_id from an
    // auto-created task, but weren't picked via this client's pending-task
    // list) aren't tied to whichever client the dropdown currently shows,
    // and shouldn't vanish just because it got touched. This used to
    // silently wipe the whole invoice when correcting/auto-selecting the
    // client on a CRM quotation handoff.
    setItems(prev => {
      const kept = prev.filter(it => !it._fromPendingTask && !it._fromPendingExpense);
      setSelectedTaskIds(kept.filter(it => it.task_id).map(it => it.task_id));
      setSelectedExpenseIds(kept.filter(it => it.expense_id).map(it => it.expense_id));
      return kept;
    });
    if (!clientId) { setPendingTasks([]); setPendingExpenses([]); return; }
    try {
      const res = await fetch(`${BASE}/api/invoices/pending-tasks/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPendingTasks(Array.isArray(data) ? data : []);

      // Expenses logged as "Billing Required" but not yet added to any
      // invoice — surfaced here so they don't get forgotten between being
      // logged (Task/Client Expenses tab) and the client's next invoice.
      const expRes = await fetch(`${BASE}/api/invoices/pending-expenses/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const expData = await expRes.json();
      setPendingExpenses(Array.isArray(expData) ? expData : []);

      // Auto-select CGST/SGST vs IGST only when firm is GST registered
      if (firmSettings.is_gst_registered !== false) {
        const client = clients.find(c => String(c.id) === String(clientId));
        if (client && firmSettings.firm_state) {
          const gstType = client.state?.toLowerCase() === firmSettings.firm_state?.toLowerCase()
            ? 'cgst_sgst' : 'igst';
          setForm(f => ({ ...f, client_id: clientId, gst_type: gstType }));
        }
      }
    } catch (e) { console.error(e); }
  };

  const handleAddTask = (task) => {
    if (selectedTaskIds.includes(task.id)) return;
    setSelectedTaskIds(prev => [...prev, task.id]);
    setItems(prev => [...prev, {
      task_id: task.id,
      service_id: task.service_id,
      particular: task.service_name || task.title,
      description: task.invoice_description || task.service_invoice_description || '',
      sac_code: task.sac_code || '',
      quantity: 1,
      rate: task.default_amount || 0,
      discount_type: 'none',
      discount_value: 0,
      _fromPendingTask: true,
    }]);
  };

  const handleAddExpense = (exp) => {
    if (selectedExpenseIds.includes(exp.id)) return;
    setSelectedExpenseIds(prev => [...prev, exp.id]);
    setItems(prev => [...prev, {
      expense_id: exp.id,
      task_id: null, service_id: null,
      particular: exp.category || 'Expense Reimbursement',
      description: exp.notes || '',
      sac_code: '',
      quantity: 1,
      rate: Number(exp.amount || 0),
      discount_type: 'none',
      discount_value: 0,
      _fromPendingExpense: true,
    }]);
  };

  const handleRemoveItem = (index) => {
    const item = items[index];
    if (item.task_id) setSelectedTaskIds(prev => prev.filter(id => id !== item.task_id));
    if (item.expense_id) setSelectedExpenseIds(prev => prev.filter(id => id !== item.expense_id));
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddManualItem = () => {
    setItems(prev => [...prev, {
      task_id: null, service_id: null,
      particular: '', description: '', sac_code: '',
      quantity: 1, rate: 0, discount_type: 'none', discount_value: 0
    }]);
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  // Route-A — pick an existing service from the Service Master instead of
  // typing the particular by hand (Route-B, still available on every row via
  // the plain text field). Filling service_id here is what makes the
  // "Create Task for this Service" checkbox appear on the row.
  const handleSelectService = (index, sv) => {
    setItems(prev => prev.map((item, i) => i === index ? {
      ...item,
      service_id: sv.id,
      particular: sv.name,
      description: sv.invoice_description || sv.description || item.description,
      sac_code: sv.sac_code || sv.service_code || item.sac_code,
      rate: sv.default_amount || sv.base_price || sv.rate || item.rate,
    } : item));
    setOpenServicePicker(null);
    setServiceSearch('');
  };

  const filteredServices = services.filter(sv =>
    !serviceSearch || sv.name?.toLowerCase().includes(serviceSearch.toLowerCase()) || (sv.service_code||'').toLowerCase().includes(serviceSearch.toLowerCase())
  );

  // Calculations
  const calculateItemAmount = (item) => Number(item.quantity || 1) * Number(item.rate || 0);
  const calculateItemDiscount = (item) => {
    const amt = calculateItemAmount(item);
    if (item.discount_type === 'percentage') return amt * Number(item.discount_value || 0) / 100;
    if (item.discount_type === 'fixed') return Number(item.discount_value || 0);
    return 0;
  };
  const calculateItemNet = (item) => calculateItemAmount(item) - calculateItemDiscount(item);

  // Subtotal shown to the user is the GROSS line total (before any discount).
  // Discount is whichever is larger: the sum of what was actually knocked off
  // item-by-item, or the aggregate figure typed into the summary — an
  // aggregate discount can only top up the item-level total, never undercut
  // it (enforced in the Discount field's onChange below).
  const subtotal = items.reduce((sum, item) => sum + calculateItemAmount(item), 0);
  const itemDiscountTotal = items.reduce((sum, item) => sum + calculateItemDiscount(item), 0);
  const aggregateDiscountInput = form.discount_type === 'percentage'
    ? subtotal * Number(form.discount_value || 0) / 100
    : Number(form.discount_value || 0);
  const aggregateDiscount = Math.max(aggregateDiscountInput, itemDiscountTotal);
  const taxableAmount = subtotal - aggregateDiscount;

  // Two discount modes share one aggregate figure: entering item-level
  // discounts bumps the summary's Discount field up to at least their sum
  // (this never fires when no item discounts exist, so a purely aggregate
  // discount typed by hand is left untouched); typing a bigger number by
  // hand sticks, since that's a genuine extra discount on top of the items'.
  useEffect(() => {
    if (Number(form.discount_value || 0) < itemDiscountTotal) {
      setForm(f => ({ ...f, discount_type: 'fixed', discount_value: itemDiscountTotal }));
    }
  }, [itemDiscountTotal]);

  const cgstAmount = form.gst_type === 'cgst_sgst' ? taxableAmount * 9 / 100 : 0;
  const sgstAmount = form.gst_type === 'cgst_sgst' ? taxableAmount * 9 / 100 : 0;
  const igstAmount = form.gst_type === 'igst' ? taxableAmount * 18 / 100 : 0;
  const totalGst = cgstAmount + sgstAmount + igstAmount;
  const totalBeforeRound = taxableAmount + totalGst;
  const roundOff = Math.round(totalBeforeRound) - totalBeforeRound;
  const totalAmount = Math.round(totalBeforeRound);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { alert('Add at least one item!'); return; }
    if (!form.client_id) { alert('Select a client!'); return; }
    if (saving) return;
    setSaving(true);
    try {
      // Route-A items flagged "Create Task for this Service" don't have a
      // task yet — create it now (before the invoice itself) so the invoice
      // item can carry a real task_id and that task can be marked billed
      // in the same way a pending-task pick already does.
      let workingItems = items;
      const autoCreatedTaskIds = [];
      const toAutoCreate = items
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => item._createTask && item.service_id && !item.task_id);
      if (toAutoCreate.length > 0) {
        for (const { item, idx } of toAutoCreate) {
          const tRes = await fetch(`${BASE}/api/tasks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              client_id: form.client_id, service_id: item.service_id,
              title: item.particular, is_billable: true,
              notes: `Auto-created from Invoice ${nextInvoiceNo || ''}`.trim(),
            }),
          });
          const tData = await tRes.json().catch(() => ({}));
          if (!tRes.ok || !tData.id) {
            alert(`Could not create task for "${item.particular}": ${tData.message || 'Server error'}`);
            setSaving(false);
            return;
          }
          autoCreatedTaskIds.push(tData.id);
          workingItems = workingItems.map((it, i) => i === idx ? { ...it, task_id: tData.id } : it);
        }
      }

      const payload = {
        ...form,
        items: workingItems.map(item => ({
          ...item,
          amount: calculateItemAmount(item),
          discount_amount: calculateItemDiscount(item),
          net_amount: calculateItemNet(item)
        })),
        task_ids: [...selectedTaskIds, ...autoCreatedTaskIds],
        quotation_id: prefillData?.quotation_id || null,
      };
      const res = await fetch(`${BASE}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) onSuccess(data);
      else alert(data.message || 'Error creating invoice');
    } catch (e) { alert('Server error'); }
    finally { setSaving(false); }
  };

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;
  const readonlyIs = { ...is, background: darkMode?'rgba(255,255,255,0.04)':T.bg3, color:T.text3, cursor:'default' };

  // Searchable client picker — same look/behavior as the Task form's Client/
  // Service pickers (type-to-search popover) instead of a bare native <select>
  // that dumps every client alphabetically with no way to filter.
  const selectedClientLabel = (() => {
    const c = clients.find(c => String(c.id) === String(form.client_id));
    return c ? `${c.client_name} (${c.file_number})` : '';
  })();
  const filteredClients = clients.filter(c => !clientSearch ||
    c.client_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.file_number?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.pan?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.group_name?.toLowerCase().includes(clientSearch.toLowerCase()));
  // Two clients can share a name (different PAN/File No.) — subtitle carries
  // whatever's available (Group, File No., PAN) so the dropdown itself
  // disambiguates them instead of a human having to guess from the name alone.
  const clientDisambigLine = (c) => [
    c.group_name ? `Group: ${c.group_name}` : null,
    c.file_number ? `File No: ${c.file_number}` : (c.pan ? `PAN: ${c.pan}` : null),
  ].filter(Boolean).join(' · ');

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)', width:'100%', maxWidth:1200, height:'94vh' }}>

        {/* HEADER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 32px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </div>
            <div>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>New Invoice</h3>
              <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>
                Invoice No.: <strong style={{ color:'#fff' }}>{nextInvoiceNo || 'Auto-generated'}</strong>
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ width:36, height:36, borderRadius:10, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* BODY */}
        <div style={{ flex:1, overflowY:'auto', padding:28, background:T.bg3, scrollbarWidth:'none', display:'flex', flexDirection:'column', gap:20 }}>

          {/* Section 1 — Invoice Details */}
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:24 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>1</div>
              <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Invoice Details</h4>
              <div style={{ flex:1, height:1, background:T.border }}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
              <div>
                <SL>Billing Entity</SL>
                <select style={readonlyIs} value="primary" readOnly>
                  <option value="primary">Primary</option>
                </select>
              </div>
              <div>
                <SL req>Date</SL>
                <input type="date" value={form.invoice_date} onChange={e=>setForm(f=>({...f,invoice_date:e.target.value}))} style={is} />
              </div>
              <div style={{ position:'relative' }}>
                <SL req>Client</SL>
                <button type="button" onClick={() => { setClientSearch(''); setShowClientPicker(p => !p); }}
                  style={{ ...is, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
                  <span style={{ color: selectedClientLabel ? T.text : T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedClientLabel || 'Select client...'}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginLeft:6, transform: showClientPicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {showClientPicker && (
                  <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, width:'100%' }}
                    onMouseLeave={() => setShowClientPicker(false)}>
                    <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
                      <div style={{ position:'relative' }}>
                        <svg style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input type="text" autoFocus placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                          style={{ width:'100%', paddingLeft:28, paddingRight:10, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                      </div>
                    </div>
                    <div style={{ maxHeight:220, overflowY:'auto' }}>
                      {filteredClients.length === 0 ? (
                        <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No clients found</p>
                      ) : filteredClients.map(c => {
                        const sel = String(form.client_id) === String(c.id);
                        return (
                          <button key={c.id} type="button" onClick={() => { handleClientChange(c.id); setShowClientPicker(false); }}
                            style={{ width:'100%', textAlign:'left', padding:'8px 14px', border:'none', cursor:'pointer', background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent' }}
                            onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                            onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                            <div style={{ fontSize:13, fontWeight:500, color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text }}>{c.client_name}</div>
                            {clientDisambigLine(c) && <div style={{ fontSize:10.5, color:T.text3, marginTop:1 }}>{clientDisambigLine(c)}</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <SL>Invoice No.</SL>
                <input type="text" value={nextInvoiceNo||'Loading...'} readOnly style={readonlyIs} />
              </div>
              <div>
                {firmSettings.is_gst_registered !== false ? (
                  <>
                    <SL req>GST Type</SL>
                    <select value={form.gst_type} onChange={e=>setForm(f=>({...f,gst_type:e.target.value}))} style={is}>
                      <option value="cgst_sgst">CGST + SGST (Same State)</option>
                      <option value="igst">IGST (Different State)</option>
                    </select>
                    <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>{form.gst_type==='igst'?'IGST @ 18% will be charged.':'CGST @ 9% and SGST @ 9% will be charged.'}</p>
                  </>
                ) : (
                  <div style={{ background:T.bg3, border:`1px solid ${T.border}`, borderRadius:10, padding:'10px 14px' }}>
                    <p style={{ fontSize:12, fontWeight:600, color:T.text2, margin:0 }}>GST Disabled</p>
                    <p style={{ fontSize:11, color:T.text3, margin:'4px 0 0' }}>Normal invoice without GST.</p>
                  </div>
                )}
              </div>
              <div>
                <SL req>Payment Term</SL>
                <select value={form.payment_terms} onChange={e=>{
                  const term = e.target.value;
                  const invoiceDate = new Date(form.invoice_date||new Date());
                  let days = 0;
                  if (term==='net_15') days=15;
                  if (term==='net_30') days=30;
                  if (term==='net_45') days=45;
                  if (term==='net_60') days=60;
                  const due = new Date(invoiceDate);
                  due.setDate(due.getDate()+days);
                  setForm(f=>({...f,payment_terms:term,due_date:term==='due_on_receipt'?f.invoice_date:due.toISOString().split('T')[0]}));
                }} style={is}>
                  <option value="due_on_receipt">Due on Receipt</option>
                  <option value="net_15">Net 15 days</option>
                  <option value="net_30">Net 30 days</option>
                  <option value="net_45">Net 45 days</option>
                  <option value="net_60">Net 60 days</option>
                </select>
              </div>
              <div>
                <SL req>Due Date</SL>
                <input type="date" value={form.due_date||form.invoice_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))} style={readonlyIs} />
              </div>
              <div>
                <SL>Remarks</SL>
                <input type="text" placeholder="Optional remarks" value={form.remarks} onChange={e=>setForm(f=>({...f,remarks:e.target.value}))} style={is} />
              </div>
            </div>
          </div>

          {/* Pending Tasks */}
          {form.client_id && pendingTasks.length > 0 && (
            <div style={{ background: darkMode?'rgba(59,130,246,0.08)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, borderRadius:14, padding:16 }}>
              <p style={{ fontSize:13, fontWeight:600, color:'#2563eb', marginBottom:12 }}>
                {pendingTasks.length} unbilled task(s) — click to add to invoice:
              </p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {pendingTasks.map(task => {
                  const sel = selectedTaskIds.includes(task.id);
                  return (
                    <button type="button" key={task.id} onClick={()=>handleAddTask(task)} disabled={sel}
                      style={{ padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:500, cursor: sel?'not-allowed':'pointer', transition:'all 0.15s',
                        background: sel?'#2563eb':(darkMode?'rgba(255,255,255,0.06)':T.bg2),
                        color: sel?'#fff':'#2563eb',
                        border:`1px solid ${sel?'#2563eb':darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`,
                        opacity: sel?0.6:1 }}>
                      {sel?'✓ ':'+ '}{task.service_name||task.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pending Billable Expenses — logged with "Billing Required: Yes" but not
              yet on any invoice (Task/Client Expenses tab → Billing Required toggle) */}
          {form.client_id && pendingExpenses.length > 0 && (
            <div style={{ background: darkMode?'rgba(16,185,129,0.08)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(16,185,129,0.2)':'#bbf7d0'}`, borderRadius:14, padding:16 }}>
              <p style={{ fontSize:13, fontWeight:600, color:'#16a34a', marginBottom:12 }}>
                {pendingExpenses.length} expense(s) pending billing — click to add to invoice:
              </p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {pendingExpenses.map(exp => {
                  const sel = selectedExpenseIds.includes(exp.id);
                  return (
                    <button type="button" key={exp.id} onClick={()=>handleAddExpense(exp)} disabled={sel}
                      style={{ padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:500, cursor: sel?'not-allowed':'pointer', transition:'all 0.15s',
                        background: sel?'#16a34a':(darkMode?'rgba(255,255,255,0.06)':T.bg2),
                        color: sel?'#fff':'#16a34a',
                        border:`1px solid ${sel?'#16a34a':darkMode?'rgba(16,185,129,0.3)':'#bbf7d0'}`,
                        opacity: sel?0.6:1 }}>
                      {sel?'✓ ':'+ '}{exp.category || 'Expense'} — ₹{Number(exp.amount||0).toLocaleString('en-IN')}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section 2 — Items */}
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'visible' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'16px 20px', borderBottom:`1px solid ${T.border}` }}>
              <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>2</div>
              <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Invoice Items</h4>
              <div style={{ flex:1 }}/>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                {[{c:'#3b82f6',l:'Task'},{c:'#10b981',l:'Expense'},{c:'#06b6d4',l:'Retainer'}].map(x=>(
                  <span key={x.l} style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:T.text3 }}>
                    <span style={{ width:8, height:8, borderRadius:2, background:x.c }}/>
                    {x.l}
                  </span>
                ))}
              </div>
            </div>

            {/* Table */}
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  {['','Particulars / Description','','Amount (₹)','Discount (₹)','Net Amount',''].map((h,i)=>(
                    <th key={i} style={{ padding:'10px 14px', textAlign: i>=3&&i<=5?'right':'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length===0 ? (
                  <tr><td colSpan="7" style={{ padding:'40px 16px', textAlign:'center', color:T.text3, fontSize:13 }}>No invoice items yet. Click "+ Add Item" below.</td></tr>
                ) : items.map((item,index)=>(
                  <tr key={index} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode&&index%2!==0?T.bg3:'transparent', verticalAlign:'top', transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode&&index%2!==0?T.bg3:'transparent'}>
                    <td style={{ padding:'12px 14px', color:T.text3, fontSize:16, width:32, textAlign:'center' }}>≡</td>
                    <td style={{ padding:'12px 14px', position:'relative' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                        <input type="text" value={item.particular} onChange={e=>handleItemChange(index,'particular',e.target.value)}
                          placeholder="Particulars — type your own or pick a service"
                          style={{ flex:1, minWidth:0, background:'transparent', border:'none', borderBottom:`1px solid ${T.border2}`, outline:'none', fontSize:13, fontWeight:600, color:T.text, paddingBottom:5, boxSizing:'border-box' }} />
                        <button type="button"
                          onClick={()=>{ setServiceSearch(''); setOpenServicePicker(openServicePicker===index?null:index); }}
                          style={{
                            flexShrink:0, display:'flex', alignItems:'center', gap:5,
                            padding:'5px 10px', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer',
                            border: openServicePicker===index ? '1px solid #3b82f6' : `1px solid ${T.border2}`,
                            background: openServicePicker===index ? (darkMode?'rgba(59,130,246,0.15)':'#eff6ff') : T.bg2,
                            color: openServicePicker===index ? '#3b82f6' : T.text3,
                          }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                          Pick Service
                        </button>
                      </div>

                      {/* Route-A — Service Master picker */}
                      {openServicePicker === index && (
                        <div style={{
                          position:'absolute', left:0, top:'calc(100% - 4px)',
                          background:T.bg2, border:`1px solid ${T.border2}`,
                          borderRadius:16, boxShadow: darkMode?'0 16px 48px rgba(0,0,0,0.6)':'0 16px 48px rgba(15,31,69,0.18)',
                          zIndex:70, width:380,
                        }}
                          onMouseLeave={()=>setOpenServicePicker(null)}>
                          <div style={{ padding:12, borderBottom:`1px solid ${T.border}` }}>
                            <div style={{ position:'relative' }}>
                              <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                              <input type="text" autoFocus placeholder="Search services..."
                                value={serviceSearch} onChange={e=>setServiceSearch(e.target.value)}
                                style={{ width:'100%', paddingLeft:32, paddingRight:12, paddingTop:8, paddingBottom:8, border:`1px solid ${T.border}`, borderRadius:8, fontSize:13, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                            </div>
                          </div>
                          <div style={{ maxHeight:224, overflowY:'auto', scrollbarWidth:'none', padding:'6px 0' }}>
                            {filteredServices.length === 0 ? (
                              <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No services found</p>
                            ) : filteredServices.map(sv => (
                              <button key={sv.id} type="button" onClick={()=>handleSelectService(index, sv)}
                                style={{ width:'100%', textAlign:'left', padding:'10px 16px', border:'none', background:'transparent', cursor:'pointer', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}
                                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                <div style={{ minWidth:0 }}>
                                  <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sv.name}</p>
                                  {sv.service_code && <span style={{ fontFamily:'monospace', fontSize:11, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color:T.text3, padding:'1px 6px', borderRadius:4, marginTop:3, display:'inline-block' }}>{sv.service_code}</span>}
                                </div>
                                {(sv.default_amount||sv.base_price||sv.rate) && (
                                  <span style={{ fontSize:12, fontWeight:700, color:'#16a34a', flexShrink:0, marginTop:2 }}>₹{Number(sv.default_amount||sv.base_price||sv.rate).toLocaleString('en-IN')}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <textarea rows="2" value={item.description} onChange={e=>handleItemChange(index,'description',e.target.value)}
                        placeholder="Description"
                        style={{ width:'100%', fontSize:12, color:T.text3, border:`1px solid ${T.border}`, borderRadius:6, padding:'5px 8px', outline:'none', resize:'none', background:T.bg3, boxSizing:'border-box' }} />

                      {/* Only offered for a service-linked item with no task behind it yet
                          (an item picked from "Pending Tasks" already has a real task_id). */}
                      {item.service_id && !item.task_id && (
                        <label style={{ display:'flex', alignItems:'center', gap:6, marginTop:6, fontSize:11.5, color:T.text2, cursor:'pointer', userSelect:'none' }}>
                          <input type="checkbox" checked={!!item._createTask}
                            onChange={e=>handleItemChange(index,'_createTask',e.target.checked)}
                            style={{ accentColor:'#16a34a', cursor:'pointer' }} />
                          Create task for this service on save
                        </label>
                      )}
                    </td>
                    <td style={{ padding:'12px 14px', width:36 }}>
                      <input type="checkbox" checked readOnly style={{ accentColor:'#2563eb' }} />
                    </td>
                    <td style={{ padding:'12px 14px', width:120, textAlign:'right' }}>
                      <input type="number" min="0" value={item.rate} onChange={e=>handleItemChange(index,'rate',e.target.value)}
                        style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'6px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, textAlign:'right', boxSizing:'border-box' }} />
                    </td>
                    <td style={{ padding:'12px 14px', width:120, textAlign:'right' }}>
                      <input type="number" min="0" value={item.discount_value||0}
                        onChange={e=>{ handleItemChange(index,'discount_type','fixed'); handleItemChange(index,'discount_value',e.target.value); }}
                        style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'6px 10px', fontSize:13, outline:'none', background:T.inputBg, color:'#f97316', textAlign:'right', boxSizing:'border-box' }} />
                    </td>
                    <td style={{ padding:'12px 14px', width:140, textAlign:'right' }}>
                      <div style={{ ...readonlyIs, padding:'6px 10px', fontSize:13, fontWeight:700, color:T.text, textAlign:'right' }}>
                        ₹{calculateItemNet(item).toLocaleString('en-IN')}
                      </div>
                    </td>
                    <td style={{ padding:'12px 14px', width:36 }}>
                      <button type="button" onClick={()=>handleRemoveItem(index)}
                        style={{ width:28, height:28, borderRadius:6, border:'none', background:'none', color:T.text3, cursor:'pointer', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center' }}
                        onMouseEnter={e=>{e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2';e.currentTarget.style.color='#ef4444';}}
                        onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=T.text3;}}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display:'flex', justifyContent:'center', padding:'14px 0', borderTop:`1px solid ${T.border}` }}>
              <button type="button" onClick={handleAddManualItem} style={{
                display:'flex', alignItems:'center', gap:6, padding:'7px 18px', borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer',
                border:`1px solid ${darkMode?'rgba(16,185,129,0.3)':'#bbf7d0'}`,
                background: darkMode?'rgba(16,185,129,0.1)':'#f0fdf4', color:'#16a34a',
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Item
              </button>
            </div>

            {/* Totals */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', borderTop:`2px solid ${T.border}` }}>
              <div style={{ padding:'20px 24px', borderRight:`1px solid ${T.border}` }}/>
              <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:10 }}>
                {[
                  { label:'Subtotal', value:`₹ ${subtotal.toLocaleString('en-IN')}`, readOnly:true },
                  { label:'Discount', value: form.discount_value||0, readOnly:false,
                    onChange: e=>setForm(f=>({...f,discount_type:'fixed',discount_value:e.target.value})),
                    onBlur: () => setForm(f=>({...f,discount_value:Math.max(Number(f.discount_value)||0,itemDiscountTotal)})),
                    hint: itemDiscountTotal>0
                      ? (Number(form.discount_value||0)>itemDiscountTotal
                          ? `₹${itemDiscountTotal.toLocaleString('en-IN')} from item discounts + ₹${(aggregateDiscount-itemDiscountTotal).toLocaleString('en-IN')} extra`
                          : `Auto-summed from item-level discounts — won't go below ₹${itemDiscountTotal.toLocaleString('en-IN')}`)
                      : null,
                  },
                ].map(row=>(
                  <div key={row.label} style={{ display:'flex', flexDirection:'column', gap:3 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:13, color:T.text2 }}>{row.label}</span>
                      {row.readOnly
                        ? <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{row.value}</span>
                        : <input type="number" min="0" value={row.value} onChange={row.onChange} onBlur={row.onBlur}
                            style={{ width:120, border:`1px solid ${T.border}`, borderRadius:8, padding:'5px 10px', fontSize:13, outline:'none', background:T.inputBg, color:'#f97316', textAlign:'right', boxSizing:'border-box' }} />
                      }
                    </div>
                    {row.hint && <p style={{ fontSize:10.5, color:T.text3, margin:0, textAlign:'right' }}>{row.hint}</p>}
                  </div>
                ))}
                {firmSettings.is_gst_registered !== false && (<>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, color:T.text2 }}>Taxable Amount</span>
                    <span style={{ fontSize:13, fontWeight:600, color:T.text }}>₹ {taxableAmount.toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                  </div>
                  {form.gst_type==='cgst_sgst' ? (<>
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontSize:13, color:T.text2 }}>CGST @ 9%</span>
                      <span style={{ fontSize:13, color:T.text }}>₹ {cgstAmount.toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontSize:13, color:T.text2 }}>SGST @ 9%</span>
                      <span style={{ fontSize:13, color:T.text }}>₹ {sgstAmount.toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                    </div>
                  </>) : (
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontSize:13, color:T.text2 }}>IGST @ 18%</span>
                      <span style={{ fontSize:13, color:T.text }}>₹ {igstAmount.toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                    </div>
                  )}
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', borderRadius:8 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:'#2563eb' }}>Total GST</span>
                    <span style={{ fontSize:13, fontWeight:700, color:'#2563eb' }}>₹ {totalGst.toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                  </div>
                </>)}
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:T.text3 }}>Round Off</span>
                  <span style={{ fontSize:12, color:T.text3 }}>₹ {roundOff.toFixed(2)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', borderTop:`2px solid ${T.border}`, paddingTop:12 }}>
                  <span style={{ fontSize:15, fontWeight:800, color:T.text }}>Total Amount</span>
                  <span style={{ fontSize:16, fontWeight:800, color:'#0f1f45' }}>₹ {totalAmount.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 32px', borderTop:`1px solid ${T.border}`, background:T.bg2, flexShrink:0, borderRadius:'0 0 20px 20px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background: items.length>0?'#10b981':'#94a3b8' }}/>
            <span style={{ fontSize:13, color:T.text3 }}>{items.length} item{items.length!==1?'s':''}</span>
            <span style={{ fontSize:13, color:T.text3 }}>·</span>
            <span style={{ fontSize:16, fontWeight:800, color:T.text }}>₹ {totalAmount.toLocaleString('en-IN')}</span>
          </div>
          <div style={{ display:'flex', gap:12 }}>
            <button onClick={onClose} style={{
              padding:'10px 20px', borderRadius:10, border:`1px solid ${T.border2}`,
              background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
            }}
            onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
            onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={saving} style={{
              display:'flex', alignItems:'center', gap:8, padding:'10px 28px', borderRadius:10, border:'none',
              background: saving?'#94a3b8':'linear-gradient(135deg,#1d4ed8,#2563eb)',
              color:'#fff', fontSize:13, fontWeight:600, cursor: saving?'not-allowed':'pointer',
              boxShadow: saving?'none':'0 4px 12px rgba(37,99,235,0.35)',
            }}>
              {saving && <span style={{ width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving ? 'Saving...' : 'Save Invoice'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// INVOICE DETAIL
function InvoiceDetail({ invoiceId, token, onClose, onEdit, onSuccess, showConfirm, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9' };
  // Only an admin may reverse a cancelled invoice back to normal — cancelling
  // is easy to do by mistake, so undoing it needs a higher bar than any staff click.
  const isAdmin = (JSON.parse(localStorage.getItem('user') || '{}').role) === 'admin';
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };
  React.useEffect(() => {
    const s = document.createElement('style');
    s.textContent = `@keyframes slideUp { from { opacity:0; transform:translateX(-50%) translateY(20px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`;
    document.head.appendChild(s);
    return () => document.head.removeChild(s);
  }, []);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [showCreditNote, setShowCreditNote] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const modeLabel = (m) => paymentModes.find(pm => pm.value === m)?.name || m?.replace(/_/g, ' ') || '—';
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '', mode: 'bank_transfer', reference_number: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    fetch(`${BASE_URL}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active) : [])).catch(() => {});
  }, [token]);

  const fetchInvoiceData = () => {
    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    fetch(`${BASE_URL}/api/invoices/${invoiceId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(data => {
      setInvoice(data);
      setLoading(false);
      setPaymentForm(f => ({ ...f, amount: data.balance_due || '' }));
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchInvoiceData();
  }, [invoiceId]); // eslint-disable-line

  // Re-fetch when billing settings change so display toggles (SAC code, QR, etc.) take effect immediately
  useEffect(() => {
    const handler = () => fetchInvoiceData();
    window.addEventListener('settings-updated', handler);
    return () => window.removeEventListener('settings-updated', handler);
  }, [invoiceId]); // eslint-disable-line

  // Editing a draft is routine; editing anything already sent/paid/etc. rewrites
  // numbers the client may already have in hand, so make that an explicit,
  // confirmed step rather than a silent click.
  const handleEditClick = () => {
    if (invoice.status !== 'draft') {
      showConfirm(
        'Edit Invoice',
        `This invoice is marked as "${(invoice.status||'').replace('_',' ')}". Editing it will recalculate its line items and totals. Do you want to continue?`,
        () => onEdit && onEdit(invoiceId),
        { confirmLabel: 'Continue', confirmColor: '#f97316', iconBg: '#fff7ed', icon: CONFIRM_ICON_EDIT }
      );
    } else {
      onEdit && onEdit(invoiceId);
    }
  };

  const handleDeleteInvoice = async (force = false) => {
    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const doDelete = async () => {
      const res = await fetch(`${BASE_URL}/api/invoices/${invoiceId}${force ? '?force=true' : ''}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { onSuccess(); onClose(); return; }
      const d = await res.json().catch(() => ({}));
      // Admin hit the "has linked receipts" guard — offer the explicit force-delete step
      // rather than a dead-end alert (previously the UI had no way to act on this).
      if (d.requires_force && isAdmin) {
        showConfirm('Delete Linked Receipts Too?', d.message, () => handleDeleteInvoice(true));
      } else {
        alert(d.message || 'Delete failed');
      }
    };
    if (force) { doDelete(); return; }
    showConfirm('Delete Invoice', `Delete invoice ${invoice.invoice_number}? This cannot be undone.`, doDelete);
  };

  const handleStatusChange = async (status) => {
    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    try {
      const res = await fetch(`${BASE_URL}/api/invoices/${invoiceId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const updated = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(updated.message || 'Status update failed', 'error');
        return;
      }
      // The PUT already returns the updated row (RETURNING *) — status/
      // updated_at are the only fields it can have changed, so patch those
      // onto the invoice already in state instead of re-fetching the whole
      // thing (a GET here re-runs 3 queries — invoice+client+firm settings,
      // items, payments — none of which a status change touches; that
      // second round trip was the real cost behind "Mark as Sent feels slow").
      setInvoice(prev => prev ? { ...prev, status: updated.status ?? status, updated_at: updated.updated_at ?? prev.updated_at } : prev);
      if (typeof onSuccess === 'function') {
        // Parent list refresh — without closing detail view
        // We invoke a soft refresh marker that parent can listen on
        window.dispatchEvent(new CustomEvent('invoice-status-changed', { detail: { invoiceId, status } }));
      }
    } catch (e) { console.error(e); showToast('Server error while updating status', 'error'); }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/invoices/${invoiceId}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(paymentForm)
      });
      if (res.ok) {
        setShowPayment(false);
        const updated = await fetch(`${BASE}/api/invoices/${invoiceId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json());
        setInvoice(updated);
      } else {
        const d = await res.json().catch(() => ({}));
        showToast(d.message || 'Could not record payment', 'error');
      }
    } catch (e) { console.error(e); showToast('Server error while recording payment', 'error'); }
    finally { setSaving(false); }
  };

  // ── WhatsApp Send ─────────────────────────────────────────────────────────
  const handleSendWhatsApp = () => {
    if (!invoice) return;
    const phone = (invoice.client_mobile || '').replace(/[^0-9]/g, '');
    const bal = Number(invoice.balance_due);
    const statusLine = bal <= 0 ? 'Status: PAID %E2%9C%93' : 'Balance Due: Rs.' + bal.toLocaleString('en-IN');
    const msg = 'Dear ' + invoice.client_name + ',%0A%0A'
      + 'Invoice No: *' + invoice.invoice_number + '*%0A'
      + 'Date: ' + new Date(invoice.invoice_date).toLocaleDateString('en-IN') + '%0A'
      + 'Amount: *Rs.' + Number(invoice.total_amount).toLocaleString('en-IN') + '*%0A'
      + statusLine + '%0A%0A'
      + 'Kindly arrange payment at the earliest.%0A%0A'
      + 'Regards,%0A' + (invoice.firm_name || 'ChanakyaX');
    window.open('https://wa.me/91' + phone + '?text=' + msg, '_blank');
  };

  // ── Email Send ────────────────────────────────────────────────────────────
  const handleSendEmail = async () => {
    if (!invoice) return;
    const clientEmail = invoice.client_email;
    if (!clientEmail) {
      alert('No email address found for this client. Please update the client profile with their email.');
      return;
    }
    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    try {
      const res = await fetch(`${BASE_URL}/api/notification-settings/send-finance-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          type: 'invoice',
          to: clientEmail,
          invoice_id: invoice.id,
          invoice_number: invoice.invoice_number,
          client_name: invoice.client_name,
          total_amount: invoice.total_amount,
          balance_due: invoice.balance_due,
          invoice_date: invoice.invoice_date,
          due_date: invoice.due_date,
          firm_name: invoice.firm_name,
          status: invoice.status,
        })
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Email sent successfully to ${clientEmail}`, 'success');
      } else {
        showToast('Failed: ' + (data.reason || 'Unknown error'), 'error');
      }
    } catch (e) {
      showToast('Server error while sending email', 'error');
    }
  };

  // Receipt PDF — opens the browser's native print dialog on the same
  // ReceiptLetterhead component the Billing Settings live preview renders, so
  // "Save as PDF" gives real text, a small file, and a normal save-location
  // prompt instead of a rasterized image wrapped in a PDF.
  const handlePrintReceipt = async (payment) => {
    if (!invoice || !payment) return;
    const amountForQr = Number(invoice.balance_due) > 0 ? invoice.balance_due : invoice.total_amount;
    let qrDataUrl = '';
    if (invoice.upi_id && invoice.show_invoice_qr !== false && Number(invoice.balance_due) > 0) {
      const upiString = `upi://pay?${new URLSearchParams({ pa: invoice.upi_id, pn: invoice.firm_name || 'ChanakyaX', am: String(amountForQr || ''), cu: 'INR', tn: `Receipt ${payment.receipt_number || ''}` }).toString()}`;
      try { qrDataUrl = await QRCode.toDataURL(upiString, { width: 240, margin: 1 }); } catch (e) { console.error('QR generation failed:', e); }
    }
    try {
      await printComponent(
        <ReceiptLetterhead settings={invoice} receipt={{
          receipt_number: payment.receipt_number, payment_date: payment.payment_date,
          mode: payment.mode, mode_label: modeLabel(payment.mode), reference_number: payment.reference_number,
          client_name: invoice.client_name, client_address: invoice.client_address,
          client_city: invoice.client_city, client_state: invoice.client_state, client_pincode: invoice.client_pincode,
          settlements: invoice.show_receipt_settlement !== false ? [{ invoice_number: invoice.invoice_number, invoice_amount: invoice.total_amount, amount: payment.amount }] : [],
          received_amount: payment.amount, balance_after: invoice.balance_due,
          notes: invoice.show_receipt_remark !== false ? payment.notes : '',
        }} qrDataUrl={qrDataUrl} />,
        `Receipt ${payment.receipt_number || ''}`
      );
    } catch (e) {
      showToast('Could not open print dialog: ' + e.message, 'error');
    }
  };

  // ── Invoice PDF ───────────────────────────────────────────────────────────
  // Opens the browser's native print dialog on the SAME <InvoiceLetterhead>
  // component the Billing Settings live preview renders — "Save as PDF"
  // there gives real, selectable text at a small file size and lets the user
  // pick where to save it, instead of a rasterized image wrapped in a PDF.
  const handlePrint = async () => {
    if (!invoice) return;
    const amountForQr = Number(invoice.balance_due) > 0 ? invoice.balance_due : invoice.total_amount;

    let qrDataUrl = '';
    if (invoice.upi_id && invoice.show_invoice_qr !== false) {
      const upiString = `upi://pay?${new URLSearchParams({ pa: invoice.upi_id, pn: invoice.firm_name || 'ChanakyaX', am: String(amountForQr || ''), cu: 'INR', tn: `Invoice ${invoice.invoice_number}` }).toString()}`;
      try { qrDataUrl = await QRCode.toDataURL(upiString, { width: 240, margin: 1 }); } catch (e) { console.error('QR generation failed:', e); }
    }

    try {
      await printComponent(
        <InvoiceLetterhead settings={invoice} invoice={invoice} qrDataUrl={qrDataUrl} />,
        `Invoice ${invoice.invoice_number}`
      );
    } catch (e) {
      showToast('Could not open print dialog: ' + e.message, 'error');
    }
  };

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'64px 0', color:T.text3 }}>
      <span style={{ width:20, height:20, border:`2px solid ${T.border2}`, borderTopColor:'#3b82f6', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite', marginRight:10 }}/>
      Loading invoice...
    </div>
  );
  if (!invoice) return <div style={{ textAlign:'center', padding:'64px 0', color:'#ef4444', fontSize:14 }}>Invoice not found</div>;

  const STMETA = {
    draft:          { c:'#64748b', bg: darkMode?'rgba(100,116,139,0.12)':'#f8fafc' },
    sent:           { c:'#2563eb', bg: darkMode?'rgba(37,99,235,0.12)':'#eff6ff'  },
    partially_paid: { c:'#d97706', bg: darkMode?'rgba(217,119,6,0.12)':'#fffbeb'  },
    paid:           { c:'#16a34a', bg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'  },
    overdue:        { c:'#dc2626', bg: darkMode?'rgba(220,38,38,0.12)':'#fef2f2'  },
    cancelled:      { c:'#94a3b8', bg: darkMode?'rgba(148,163,184,0.12)':'#f8fafc' },
  };
  const stm = STMETA[invoice.status]||{c:T.text3,bg:T.bg3};

  const ActionBtn = ({ label, color, border, bg, onClick, icon }) => (
    <button onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:500,
      cursor:'pointer', border:`1px solid ${border}`, background:bg, color,
    }}
    onMouseEnter={e=>{e.currentTarget.style.opacity='0.8';}}
    onMouseLeave={e=>{e.currentTarget.style.opacity='1';}}>
      {icon && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{icon}</svg>}
      {label}
    </button>
  );

  return (
    <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:16, paddingRight:4, scrollbarWidth:'none' }}>

      {/* PAGE HEADER */}
      {/* Toast Notification */}
        {toast && (
          <div style={{
            position:'fixed', bottom:32, left:'50%', transform:'translateX(-50%)',
            zIndex:99999, display:'flex', alignItems:'center', gap:12,
            padding:'14px 22px', borderRadius:14,
            background: toast.type==='success'
              ? 'linear-gradient(135deg,#16a34a,#15803d)'
              : toast.type==='error'
              ? 'linear-gradient(135deg,#dc2626,#b91c1c)'
              : 'linear-gradient(135deg,#0f1f45,#1a3a6b)',
            boxShadow:'0 8px 32px rgba(0,0,0,0.25)',
            minWidth:280, maxWidth:480,
            animation:'slideUp 0.3s ease',
          }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {toast.type==='success'
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                : toast.type==='error'
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/></svg>
              }
            </div>
            <p style={{ fontSize:13, fontWeight:600, color:'#fff', margin:0, flex:1 }}>{toast.msg}</p>
            <button onClick={() => setToast(null)}
              style={{ width:24, height:24, borderRadius:6, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        )}

        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
            <button onClick={onClose} style={{ display:'flex', alignItems:'center', gap:6, border:'none', background:'none', cursor:'pointer', color:'#3b82f6', fontSize:13, fontWeight:500, padding:0 }}
              onMouseEnter={e=>e.currentTarget.style.textDecoration='underline'}
              onMouseLeave={e=>e.currentTarget.style.textDecoration='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M5 12l7 7M5 12l7-7"/></svg>
              Invoices
            </button>
            <span style={{ color:T.text3 }}>›</span>
            <span style={{ fontSize:13, fontWeight:500, color:T.text3 }}>Invoice Detail</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h2 style={{ fontSize:20, fontWeight:800, color:T.text, margin:0 }}>{invoice.invoice_number}</h2>
            <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20, color:stm.c, background:stm.bg, textTransform:'capitalize' }}>
              {invoice.status?.replace('_',' ')}
            </span>
          </div>
          <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>{invoice.client_name}</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {invoice.status!=='cancelled' && (<>
            <ActionBtn label="Edit" color="#f97316" border={darkMode?'rgba(249,115,22,0.3)':'#fed7aa'} bg={darkMode?'rgba(249,115,22,0.1)':'#fff7ed'} onClick={handleEditClick} icon={<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>} />
            <ActionBtn label="Delete" color="#ef4444" border={darkMode?'rgba(239,68,68,0.3)':'#fecaca'} bg={darkMode?'rgba(239,68,68,0.1)':'#fef2f2'} onClick={()=>handleDeleteInvoice()} icon={<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></>} />
          </>)}
          {invoice.status==='draft' && (
            <ActionBtn label="Mark as Sent" color="#2563eb" border={darkMode?'rgba(37,99,235,0.3)':'#bfdbfe'} bg={darkMode?'rgba(37,99,235,0.1)':'#eff6ff'} onClick={()=>handleStatusChange('sent')} icon={<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>} />
          )}
          {invoice.status!=='paid'&&invoice.status!=='cancelled'&&Number(invoice.amount_paid||0)<=0 && (
            <ActionBtn label="Cancel Invoice" color={T.text3} border={T.border2} bg={T.bg2} onClick={()=>showConfirm(
              'Cancel Invoice',
              `Cancel invoice ${invoice.invoice_number}? It will be frozen — no edits and no new receipts can be adjusted against it.`,
              ()=>handleStatusChange('cancelled'),
              { confirmLabel:'Cancel Invoice', confirmColor:'#64748b', iconBg:'#f8fafc', icon:CONFIRM_ICON_CANCEL }
            )} />
          )}
          {invoice.status==='cancelled'&&isAdmin && (
            <ActionBtn label="Reverse Cancellation" color="#2563eb" border={darkMode?'rgba(37,99,235,0.3)':'#bfdbfe'} bg={darkMode?'rgba(37,99,235,0.1)':'#eff6ff'} onClick={()=>showConfirm(
              'Reverse Cancellation',
              `Reverse the cancellation on invoice ${invoice.invoice_number}? It will go back to "Sent".`,
              ()=>handleStatusChange('sent'),
              { confirmLabel:'Reverse', confirmColor:'#2563eb', iconBg:'#eff6ff', icon:CONFIRM_ICON_EDIT }
            )} />
          )}
          {invoice.status!=='paid'&&invoice.status!=='cancelled' && (
            <ActionBtn label="Record Payment" color="#16a34a" border={darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'} bg={darkMode?'rgba(22,163,74,0.1)':'#f0fdf4'} onClick={()=>setShowPayment(!showPayment)} icon={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>} />
          )}
          {invoice.status!=='cancelled' && (
            <ActionBtn label="Credit Note" color="#f97316" border={darkMode?'rgba(249,115,22,0.3)':'#fed7aa'} bg={darkMode?'rgba(249,115,22,0.1)':'#fff7ed'} onClick={()=>setShowCreditNote(!showCreditNote)} />
          )}
          <ActionBtn label="WhatsApp" color="#16a34a" border={darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'} bg={darkMode?'rgba(22,163,74,0.1)':'#f0fdf4'} onClick={handleSendWhatsApp} icon={<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.65 3.18 2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>} />
          <ActionBtn label="Email" color="#2563eb" border={darkMode?'rgba(37,99,235,0.3)':'#bfdbfe'} bg={darkMode?'rgba(37,99,235,0.1)':'#eff6ff'} onClick={handleSendEmail} icon={<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>} />
          <button onClick={handlePrint} style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            PDF
          </button>
        </div>
      </div>

      {/* Record Payment Panel */}
      {showPayment && (
        <div style={{ background: darkMode?'rgba(16,185,129,0.08)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(16,185,129,0.2)':'#bbf7d0'}`, borderRadius:14, padding:20 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:'#16a34a', margin:'0 0 16px' }}>Record Payment</h3>
          <form onSubmit={handleRecordPayment}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:14 }}>
              {[
                { label:'Date', type:'date', value:paymentForm.payment_date, onChange:e=>setPaymentForm(f=>({...f,payment_date:e.target.value})), req:true },
                { label:'Amount (₹)', type:'number', value:paymentForm.amount, onChange:e=>setPaymentForm(f=>({...f,amount:e.target.value})), req:true, min:'0.01', step:'0.01' },
                { label:'Reference No.', type:'text', value:paymentForm.reference_number, onChange:e=>setPaymentForm(f=>({...f,reference_number:e.target.value})), placeholder:'UTR / Cheque No.' },
              ].map(f=>(
                <div key={f.label}>
                  <SL req={f.req}>{f.label}</SL>
                  <input type={f.type} required={f.req} value={f.value} onChange={f.onChange} placeholder={f.placeholder} min={f.min} step={f.step} style={is} />
                </div>
              ))}
              <div>
                <SL>Mode</SL>
                <select value={paymentForm.mode} onChange={e=>setPaymentForm(f=>({...f,mode:e.target.value}))} style={is}>
                  {paymentModes.map(m=><option key={m.value} value={m.value}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button type="button" onClick={()=>setShowPayment(false)} style={{ padding:'8px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button type="submit" disabled={saving} style={{ padding:'8px 20px', borderRadius:9, border:'none', background:'#16a34a', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
                {saving?'Saving...':'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Credit Note Form */}
      {showCreditNote && (
        <CreditNoteForm token={token} invoice={invoice}
          onClose={()=>setShowCreditNote(false)}
          onSuccess={()=>setShowCreditNote(false)} />
      )}

      {/* Main Content */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:20, alignItems:'start' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Bill To + Dates */}
          <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              <div>
                <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Bill To</p>
                <p style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>{invoice.client_name}</p>
                {invoice.client_address && <p style={{ fontSize:12, color:T.text3, marginTop:5 }}>{invoice.client_address}</p>}
                {invoice.client_gstin && <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>GSTIN: {invoice.client_gstin}</p>}
              </div>
              <div style={{ textAlign:'right' }}>
                <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>Invoice Date</p>
                <p style={{ fontSize:14, fontWeight:600, color:T.text }}>{new Date(invoice.invoice_date).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})}</p>
                {invoice.due_date && (<>
                  <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:10, marginBottom:5 }}>Due Date</p>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text }}>{new Date(invoice.due_date).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})}</p>
                </>)}
                {invoice.gst_type!=='none' && Number(invoice.total_gst)>0 && (<>
                  <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:10, marginBottom:5 }}>GST Type</p>
                  <p style={{ fontSize:13, fontWeight:500, color:T.text }}>{invoice.gst_type==='cgst_sgst'?'CGST + SGST':'IGST'}</p>
                </>)}
              </div>
            </div>
          </div>

          {/* Items */}
          <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  {['#','Particulars','SAC','Qty','Rate','Discount','Amount'].map(h=>(
                    <th key={h} style={{ padding:'10px 16px', textAlign: ['Rate','Discount','Amount'].includes(h)?'right':'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(invoice.items||[]).map((item,i)=>(
                  <tr key={item.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode&&i%2!==0?T.bg3:'transparent', transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode&&i%2!==0?T.bg3:'transparent'}>
                    <td style={{ padding:'10px 16px', fontSize:12, color:T.text3 }}>{i+1}</td>
                    <td style={{ padding:'10px 16px' }}>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{item.particular}</p>
                      {item.description && <p style={{ fontSize:11, color:T.text3, marginTop:3, whiteSpace:'pre-line' }}>{item.description}</p>}
                    </td>
                    <td style={{ padding:'10px 16px', fontSize:12, color:T.text3 }}>{item.sac_code||'—'}</td>
                    <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{item.quantity}</td>
                    <td style={{ padding:'10px 16px', fontSize:13, color:T.text2, textAlign:'right' }}>₹{Number(item.rate).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
                    <td style={{ padding:'10px 16px', fontSize:13, color:'#ef4444', textAlign:'right' }}>
                      {Number(item.discount_amount)>0?`-₹${Number(item.discount_amount).toLocaleString('en-IN',{minimumFractionDigits:2})}`:'—'}
                    </td>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:T.text, textAlign:'right' }}>₹{Number(item.net_amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, overflow:'hidden' }}>
              <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
                <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Payment History</h3>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                  <tr>
                    {['Date','Amount','Mode','Reference','Recorded By','Receipt'].map(h=>(
                      <th key={h} style={{ padding:'9px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map((p,i)=>(
                    <tr key={p.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode&&i%2!==0?T.bg3:'transparent' }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                      onMouseLeave={e=>e.currentTarget.style.background=darkMode&&i%2!==0?T.bg3:'transparent'}>
                      <td style={{ padding:'9px 16px', fontSize:12, color:T.text3 }}>{new Date(p.payment_date).toLocaleDateString('en-IN')}</td>
                      <td style={{ padding:'9px 16px', fontSize:13, fontWeight:600, color:'#16a34a' }}>₹{Number(p.amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
                      <td style={{ padding:'9px 16px', fontSize:13, color:T.text2 }}>{modeLabel(p.mode)}</td>
                      <td style={{ padding:'9px 16px', fontSize:13, color:T.text2 }}>{p.reference_number||'—'}</td>
                      <td style={{ padding:'9px 16px', fontSize:13, color:T.text2 }}>{p.recorded_by_name}</td>
                      <td style={{ padding:'9px 16px' }}>
                        <button onClick={()=>handlePrintReceipt(p)} style={{ fontSize:12, fontWeight:500, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}
                          onMouseEnter={e=>e.currentTarget.style.textDecoration='underline'}
                          onMouseLeave={e=>e.currentTarget.style.textDecoration='none'}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                          Receipt
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Sidebar */}
        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20, position:'sticky', top:16 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:'0 0 16px' }}>Invoice Summary</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {(()=>{
              const isGst = invoice.gst_type!=='none' && Number(invoice.total_gst)>0;
              // Subtotal = gross, before any discount — recomputed from the line
              // items rather than trusted from invoice.subtotal, which older
              // invoices stored net-of-item-discount (the bug this fixes).
              // Discount = whichever is larger: the item-level discounts actually
              // given away, or the invoice's own aggregate discount (the aggregate
              // can only top up the item total, never undercut it).
              const grossSubtotal = (invoice.items||[]).reduce((s,it)=>s+Number(it.amount||0),0);
              const itemDiscountTotal = (invoice.items||[]).reduce((s,it)=>s+Number(it.discount_amount||0),0);
              const combinedDiscount = Math.max(itemDiscountTotal, Number(invoice.discount_amount||0));
              const rows = [
                { label:'Subtotal', value:grossSubtotal },
                ...(combinedDiscount>0?[{label:'Discount',value:-combinedDiscount,color:'#ef4444'}]:[]),
                ...(isGst?[
                  {label:'Taxable Amount',value:invoice.taxable_amount},
                  ...(invoice.gst_type==='cgst_sgst'?[{label:'CGST @ 9%',value:invoice.cgst_amount},{label:'SGST @ 9%',value:invoice.sgst_amount}]:[{label:'IGST @ 18%',value:invoice.igst_amount}]),
                ]:[]),
                ...(Number(invoice.round_off)!==0?[{label:'Round Off',value:invoice.round_off,small:true}]:[]),
              ];
              return rows.map(row=>(
                <div key={row.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize: row.small?11:12, color: row.small?T.text3:T.text2 }}>{row.label}</span>
                  <span style={{ fontSize: row.small?11:13, fontWeight: row.small?400:500, color: row.color||(row.small?T.text3:T.text) }}>
                    {row.color?'-':''}₹{Math.abs(Number(row.value)).toLocaleString('en-IN',{minimumFractionDigits:2})}
                  </span>
                </div>
              ));
            })()}
            <div style={{ display:'flex', justifyContent:'space-between', borderTop:`2px solid ${T.border}`, paddingTop:12 }}>
              <span style={{ fontSize:15, fontWeight:800, color:T.text }}>Total Amount</span>
              <span style={{ fontSize:15, fontWeight:800, color:'#0f1f45' }}>₹{Number(invoice.total_amount).toLocaleString('en-IN')}</span>
            </div>
            {Number(invoice.amount_paid)>0 && (<>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:12, color:'#16a34a' }}>Amount Paid</span>
                <span style={{ fontSize:13, fontWeight:600, color:'#16a34a' }}>-₹{Number(invoice.amount_paid).toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', borderTop:`1px solid ${T.border}`, paddingTop:10 }}>
                <span style={{ fontSize:14, fontWeight:700, color:'#dc2626' }}>Balance Due</span>
                <span style={{ fontSize:14, fontWeight:800, color:'#dc2626' }}>₹{Number(invoice.balance_due).toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
              </div>
            </>)}
          </div>
        </div>
      </div>
    </div>
  );
}

const RECEIPT_IMPORT_HEADER_MAP = {
  'old receipt no': 'reference_number', 'old receipt no.': 'reference_number',
  'receipt no': 'reference_number', 'receipt no.': 'reference_number', 'receipt number': 'reference_number',
  'reference no': 'reference_number', 'reference no.': 'reference_number', 'reference number': 'reference_number',
  'utr': 'reference_number', 'cheque no': 'reference_number', 'cheque no.': 'reference_number',
  'client name': 'client_name', 'client': 'client_name', 'name': 'client_name',
  'payment date': 'payment_date', 'date': 'payment_date',
  'amount': 'amount', 'amount (₹)': 'amount', 'amount received': 'amount',
  'mode': 'mode', 'payment mode': 'mode',
  'bank': 'bank', 'bank account': 'bank', 'bank a/c': 'bank',
  'notes': 'notes', 'remarks': 'notes',
};

// IMPORT RECEIPTS — one row = one payment, client auto-matched/created by
// name, posted through the same "Create Receipt" endpoint every manual
// receipt uses. Imported deliberately UNLINKED to any invoice (no
// settlements) — matching a migrated receipt to the right migrated invoice
// automatically is exactly the kind of guess that's cheap to get wrong and
// expensive to notice; settling it against an invoice afterward through the
// normal receipt screen is one click and lets a human confirm the match.
function ReceiptImportModal({ token, clients, onClose, onDone, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  useEffect(() => { ensureImportStyles(); }, []);
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total, ok, fail }
  const [result, setResult] = useState(null);
  const [numberingMode, setNumberingMode] = useState('new'); // 'new' | 'keep'
  const [paymentModes, setPaymentModes] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active) : [])).catch(() => {});
  }, [token]);
  // Mode ("Cash"/"Bank Transfer"/"UPI"...) and Bank Account (which of the
  // firm's own bank_accounts actually received it) are two separate things
  // — importing only ever sent Mode, so every imported receipt landed on
  // Cash regardless of what was typed there, even "ICICI 4445". A Bank
  // column lets a row resolve straight to the real bank_account_id.
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/bank/accounts`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setBankAccounts(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);
  const bankAccountLabel = (a) => `${a.bank_name}${a.nickname ? ` — ${a.nickname}` : ''}${a.account_last4 ? ` (••${a.account_last4})` : ''}`;
  const matchBankAccount = (input) => {
    const bankInput = (input || '').toLowerCase().trim();
    if (!bankInput) return null;
    return bankAccounts.find(a => {
      const bn = (a.bank_name || '').toLowerCase();
      const nn = (a.nickname || '').toLowerCase();
      const last4 = a.account_last4 || '';
      if (bankInput === bn || (nn && bankInput === nn)) return true;
      const bnFirstWord = bn.split(' ')[0];
      const nameHit = (bnFirstWord && bankInput.includes(bnFirstWord)) || (nn && bankInput.includes(nn));
      const digitsHit = !last4 || bankInput.includes(last4);
      return nameHit && digitsHit;
    }) || null;
  };
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Receipts');
    ws.columns = [
      { header: 'Old Receipt No.', key: 'reference_number', width: 18 },
      { header: 'Client Name*', key: 'client_name', width: 26 },
      { header: 'Payment Date*', key: 'payment_date', width: 14 },
      { header: 'Amount (₹)*', key: 'amount', width: 14 },
      { header: 'Mode', key: 'mode', width: 16 },
      { header: 'Bank', key: 'bank', width: 22 },
      { header: 'Notes', key: 'notes', width: 26 },
    ];
    ws.getRow(1).eachCell(c => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1F45' } }; });
    ws.getColumn('payment_date').numFmt = '@';
    ws.getCell('C1').note = 'Any common format works: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY-MM-DD.';
    const modeNames = paymentModes.map(m => m.name).filter(Boolean);
    ws.getCell('E1').note = modeNames.length
      ? `Type the exact name of one of your firm's own Payment Modes (Settings → Payment Modes): ${modeNames.join(', ')}. Defaults to "${modeNames[0]}" if left blank or unrecognised.`
      : 'Type the exact name of one of your firm\'s own Payment Modes (Settings → Payment Modes). No active Payment Modes are configured yet — set those up first so imported rows have something to match against.';
    const bankLabels = bankAccounts.map(bankAccountLabel);
    ws.getCell('F1').note = bankLabels.length
      ? `Which of your firm's own Bank Accounts actually received this — the Bank name and/or last 4 digits are enough to match, e.g. "ICICI 4445": ${bankLabels.join(', ')}. Left blank or unmatched, the receipt posts to Cash instead (same as before).`
      : 'Which of your firm\'s own Bank Accounts received this. No Bank Accounts are set up yet (Finance → Receipts → Bank Statements) — left blank, every row posts to Cash.';
    ws.getCell('A1').note = 'Imported receipts come in unlinked to any invoice — settle each one against its invoice afterward from the Receipts screen, same as a manually recorded payment.';
    const sampleMode1 = modeNames[0] || 'Bank Transfer';
    const sampleMode2 = modeNames[1] || modeNames[0] || 'UPI';
    const sampleBank = bankLabels[0] || '';
    ws.addRow({ reference_number: 'OLD-RCT-55', client_name: 'ABC Private Limited', payment_date: '02/06/2026', amount: 30000, mode: sampleMode1, bank: sampleBank, notes: 'Part payment against Annual Filing' });
    ws.addRow({ reference_number: '', client_name: 'XYZ Enterprises', payment_date: '05/06/2026', amount: 2500, mode: sampleMode2, bank: '', notes: '' });
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'Receipt_Import_Template.xlsx';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const normalized = data.map(row => {
        const out = {};
        Object.entries(row).forEach(([k, v]) => {
          const cleanLabel = k.replace(/\*/g, '').trim().toLowerCase();
          const field = RECEIPT_IMPORT_HEADER_MAP[cleanLabel] || cleanLabel.replace(/\s+/g, '_');
          if (field === 'payment_date') out[field] = toISODateLoose(v);
          else out[field] = String(v).trim();
        });
        return out;
      });
      setRows(normalized);
      setResult(null);
    };
    reader.readAsBinaryString(file);
  };

  const handleSubmit = async () => {
    if (!rows.length) return;
    setImporting(true);
    setResult(null);
    const clientsArr = [...clients];
    const clientCache = new Map();
    let success = 0;
    const errors = [];
    const warnings = [];

    // Same as Invoice Import: receipts are created in Payment Date order
    // (earliest first), not the order rows happen to sit in the file, so
    // freshly-generated receipt numbers read chronologically (lowest number
    // = earliest date) instead of mirroring the file's own row order. Row
    // numbers for error messages still refer back to the original file row.
    const orderedRows = rows
      .map((row, i) => ({ row, rowNum: i + 2 }))
      .sort((a, b) => {
        const da = a.row.payment_date || '9999-99-99';
        const db = b.row.payment_date || '9999-99-99';
        return da < db ? -1 : da > db ? 1 : 0;
      });

    const tick = (i) => setProgress({ current: i + 1, total: rows.length, ok: success, fail: errors.length });
    setProgress({ current: 0, total: rows.length, ok: 0, fail: 0 });
    for (const [i, { row, rowNum }] of orderedRows.entries()) {
      if (!row.client_name) { errors.push(`Row ${rowNum}: Client Name is required`); tick(i); continue; }
      if (!row.payment_date) { errors.push(`Row ${rowNum}: Payment Date is required or unreadable`); tick(i); continue; }
      const amount = parseFloat(row.amount);
      if (!amount || amount <= 0) { errors.push(`Row ${rowNum}: Amount must be a positive number`); tick(i); continue; }

      // Match against THIS firm's own configured Payment Modes (Settings →
      // Payment Modes) instead of a fixed hardcoded list — a firm that
      // renamed/added its own modes couldn't otherwise ever match them from
      // an import file, and every unmatched row silently got hardcoded to
      // "bank_transfer" regardless of what was actually typed.
      const modeInput = (row.mode || '').trim().toLowerCase();
      const matchedMode = paymentModes.find(m =>
        m.name?.trim().toLowerCase() === modeInput || m.value?.trim().toLowerCase() === modeInput.replace(/\s+/g, '_')
      );
      const mode = matchedMode?.value || paymentModes[0]?.value || 'other';
      if (row.mode && !matchedMode) {
        warnings.push(`Row ${rowNum}: mode "${row.mode}" doesn't match any configured Payment Mode, used "${paymentModes[0]?.name || 'Other'}"`);
      }

      // Bank is separate from Mode — which of the firm's own bank_accounts
      // this actually landed in, not just the payment category. Left blank
      // or unmatched, it stays NULL and posts to Cash exactly as before.
      const matchedBank = matchBankAccount(row.bank);
      const bank_account_id = matchedBank?.id || null;
      if (row.bank && !matchedBank) {
        warnings.push(`Row ${rowNum}: bank "${row.bank}" doesn't match any of your Bank Accounts — posted to Cash instead`);
      }

      // "Keep Old Receipt No." only applies when this row actually has one —
      // a blank Old Receipt No. always falls back to a fresh number.
      const keepOld = numberingMode === 'keep' && row.reference_number;
      if (numberingMode === 'keep' && !row.reference_number) {
        warnings.push(`Row ${rowNum}: no Old Receipt No. given — a new receipt number was auto-generated for this row instead.`);
      }

      try {
        const client_id = await resolveOrCreateClient(row.client_name, token, clientCache, clientsArr);
        if (!client_id) { errors.push(`Row ${rowNum}: could not resolve/create client "${row.client_name}"`); tick(i); continue; }

        const notes = keepOld ? [row.notes, 'Imported'].filter(Boolean).join(' — ') : [row.notes, row.reference_number ? `Imported — original receipt no. ${row.reference_number}` : 'Imported'].filter(Boolean).join(' — ');
        const res = await fetch(`${BASE}/api/invoices/receipts/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            client_id, payment_date: row.payment_date, received_amount: amount,
            tds_amount: 0, discount_amount: 0, mode, bank_account_id,
            custom_receipt_number: keepOld ? row.reference_number : undefined,
            reference_number: row.reference_number || '', notes, settlements: [],
          }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) { errors.push(`Row ${rowNum}: ${d.message || 'receipt creation failed'}`); tick(i); continue; }
        success++;
      } catch (e) {
        errors.push(`Row ${rowNum}: ${e.message}`);
      }
      tick(i);
    }
    setResult({ success, errors, warnings, total: rows.length });
    setImporting(false);
    setProgress(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full shadow-2xl" style={{ maxWidth:'580px' }}>

        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold" style={{ color:'#0f1f45' }}>Import Receipts</h3>
            <p className="text-xs text-gray-400 mt-0.5">Bulk upload from Excel/CSV — Practice or any other source</p>
          </div>
          <button onClick={() => { if (importing) return; onClose(); }} disabled={importing}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400 transition disabled:opacity-30 disabled:cursor-not-allowed">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="p-7 space-y-5">
          {(!importing && !result) && (
          <>
          {/* Step 1 */}
          <div className="border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center text-green-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Download Template</p>
                  <p className="text-xs text-gray-400 mt-0.5">Imported receipts come in unlinked to any invoice</p>
                </div>
              </div>
              <button onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-green-200 text-green-700 hover:bg-green-50 transition">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download
              </button>
            </div>
          </div>

          {/* Step 2 — Receipt numbering */}
          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-800 mb-1">Receipt Numbering</p>
            <p className="text-xs text-gray-400 mb-3">Choose what happens to the "Old Receipt No." column on import</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'new', title: 'Generate New Numbers', desc: 'Every imported receipt gets the next number in this firm\'s own sequence. Old Receipt No. is kept only as a note.' },
                { key: 'keep', title: 'Keep Old Receipt No.', desc: 'Uses the Old Receipt No. column as the real receipt number. Duplicates of an existing number are rejected; blank rows still get a new number.' },
              ].map(opt => (
                <button key={opt.key} type="button" onClick={() => setNumberingMode(opt.key)}
                  className={`text-left rounded-xl border p-3 transition ${numberingMode === opt.key ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${numberingMode === opt.key ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} style={numberingMode === opt.key ? { boxShadow: 'inset 0 0 0 2px #fff' } : undefined} />
                    <span className={`text-sm font-semibold ${numberingMode === opt.key ? 'text-blue-700' : 'text-gray-700'}`}>{opt.title}</span>
                  </div>
                  <p className="text-xs text-gray-400 leading-snug">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Step 3 */}
          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-800 mb-3">Upload Filled Template</p>
            <label className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition ${rows.length > 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
              {rows.length > 0 ? (
                <div className="text-center">
                  <svg className="w-8 h-8 text-blue-500 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <p className="text-sm font-semibold text-blue-700">{rows.length} row(s) ready</p>
                  <p className="text-xs text-blue-400 mt-0.5">Click to change file</p>
                </div>
              ) : (
                <div className="text-center">
                  <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <p className="text-sm font-medium text-gray-500">Click to upload Excel/CSV file</p>
                  <p className="text-xs text-gray-400 mt-0.5">.xlsx, .xls or .csv</p>
                </div>
              )}
            </label>
          </div>
          </>
          )}

          {/* Progress */}
          {importing && progress && (() => {
            const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
            return (
              <div className="rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"/>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Importing receipts</p>
                      <p className="text-xs text-gray-400">Please keep this window open</p>
                    </div>
                  </div>
                  <p className="text-lg font-extrabold" style={{ color:'#0f1f45', fontVariantNumeric:'tabular-nums' }}>
                    {progress.current}<span className="text-gray-300 font-semibold text-sm"> / {progress.total}</span>
                  </p>
                </div>
                <div style={{ height: 12, width: '100%', borderRadius: 999, background: '#eef1f6', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 999, width: `${Math.max(pct, 2)}%`,
                    background: 'linear-gradient(90deg,#0f1f45,#2a4d82)', transition: 'width 0.35s ease-out',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{
                      position: 'absolute', inset: 0,
                      backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.22) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.22) 75%, transparent 75%, transparent)',
                      backgroundSize: '28px 28px', animation: 'cx-stripe 0.7s linear infinite',
                    }} />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-4 text-xs font-semibold" style={{ fontVariantNumeric:'tabular-nums' }}>
                    <span className="text-green-600">✓ {progress.ok || 0} added</span>
                    <span className={progress.fail ? 'text-red-600' : 'text-gray-300'}>✕ {progress.fail || 0} failed</span>
                  </div>
                  <p className="text-xs font-bold text-gray-500" style={{ fontVariantNumeric:'tabular-nums' }}>{pct}%</p>
                </div>
              </div>
            );
          })()}

          {/* Report */}
          {result && (
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100" style={{ background:'#f8faff' }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: result.errors.length ? '#fef2f2' : '#f0fdf4' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={result.errors.length ? '#dc2626' : '#16a34a'} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <p className="text-sm font-bold" style={{ color:'#0f1f45' }}>Import complete</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                {[
                  { label:'Imported', value: result.success,        color:'#16a34a', bg:'#f0fdf4' },
                  { label:'Failed',   value: result.errors.length,  color:'#dc2626', bg:'#fef2f2' },
                ].map(s => (
                  <div key={s.label} className="p-4 text-center" style={{ background: s.value ? s.bg : '#fff' }}>
                    <p className="text-2xl font-extrabold" style={{ color: s.value ? s.color : '#94a3b8', fontVariantNumeric:'tabular-nums' }}>{s.value}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              {(result.errors.length > 0 || result.warnings.length > 0) && (
                <div className="border-t border-gray-100 p-4 space-y-1.5 max-h-40 overflow-y-auto bg-gray-50">
                  {result.errors.map((e, i) => (
                    <p key={'e' + i} className="text-xs text-red-600 flex gap-1.5"><span className="flex-shrink-0">✕</span><span>{e}</span></p>
                  ))}
                  {result.warnings.map((w, i) => (
                    <p key={'w' + i} className="text-xs text-gray-500 flex gap-1.5"><span className="flex-shrink-0 text-amber-500 font-bold">!</span><span>{w}</span></p>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-100 px-4 py-2.5 bg-white">
                <p className="text-xs text-gray-500">
                  {result.success > 0
                    ? `${result.success} of ${result.total} receipt${result.total === 1 ? '' : 's'} created.`
                    : 'No receipts were created.'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={() => { if (importing) return; result ? onDone() : onClose(); }} disabled={importing}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition disabled:opacity-40 disabled:cursor-not-allowed">
            {result ? 'Done' : 'Close'}
          </button>
          {rows.length > 0 && !result && (
            <button onClick={handleSubmit} disabled={importing}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
              style={{ background:'#0f1f45' }}>
              {importing && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
              {importing
                ? (progress ? `Importing… ${progress.current}/${progress.total}` : 'Importing…')
                : `Import ${rows.length} Row${rows.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReceiptsTab({ token, showConfirm, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [paymentModes, setPaymentModes] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active) : [])).catch(() => {});
  }, [token]);
  const [receipts, setReceipts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editReceiptId, setEditReceiptId] = useState(null);
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState('');
  const [filterAllocation, setFilterAllocation] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [prefillReceiptData, setPrefillReceiptData] = useState(null);

  const [openKebabId, setOpenKebabId] = useState(null);
  const [sortKey, setSortKey] = useState('payment_date');
  const [sortDir, setSortDir] = useState('desc');
  const onSort = makeSortToggle(sortKey, setSortKey, setSortDir);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const cc = useColumnCustomizer(RECEIPT_COLUMNS, { visKey: 'receiptVisibleColumns', orderKey: 'receiptColumnOrder', T, darkMode });
  const { visibleColumns, orderedVisibleKeys } = cc;

  const fetchReceipts = () => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE}/api/invoices/receipts/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => []),
    ]).then(([data, clientData]) => {
      setReceipts(Array.isArray(data) ? data : []);
      setClients(Array.isArray(clientData) ? clientData : []);
      setLoading(false);
    });
  };

    useEffect(() => {
    fetchReceipts();

    const openId = localStorage.getItem('openReceiptId');
    if (openId) {
      localStorage.removeItem('openReceiptId');
      setSelectedReceiptId(Number(openId));
      return;
    }

    const pendingReceipt = localStorage.getItem('pendingReceiptFromInvoice');
    if (pendingReceipt) {
      try {
        setPrefillReceiptData(JSON.parse(pendingReceipt));
        setShowNew(true);
      } catch (_) {}
      localStorage.removeItem('pendingReceiptFromInvoice');
    }
  }, []);



  if (showNew) {
    return <NewReceiptForm token={token} prefillData={prefillReceiptData}
      onClose={() => { setShowNew(false); setPrefillReceiptData(null); }}
      onSuccess={() => { fetchReceipts(); setShowNew(false); setPrefillReceiptData(null); }}
      darkMode={darkMode} T={T} />;
  }

  if (editReceiptId) {
    return <NewReceiptForm token={token} editReceiptId={editReceiptId}
      onClose={() => setEditReceiptId(null)}
      onSuccess={() => { fetchReceipts(); setEditReceiptId(null); }}
      darkMode={darkMode} T={T} />;
  }
    if (selectedReceiptId) {
    return <ReceiptDetail
      token={token}
      receiptId={selectedReceiptId}
      onClose={() => setSelectedReceiptId(null)}
      onEdit={() => {
        setEditReceiptId(selectedReceiptId);
        setSelectedReceiptId(null);
      }}
      onSuccess={() => {
        fetchReceipts();
        setSelectedReceiptId(null);
      }}
      showConfirm={showConfirm}
    />;
  }

  const creatorOptions = Array.from(new Set(receipts.map(r => r.recorded_by_name).filter(Boolean))).sort();
  const clientGroupById = new Map(clients.map(c => [String(c.id), c.group_id ? String(c.group_id) : '']));
  const groupOptions = Array.from(new Map(clients.filter(c => c.group_id).map(c => [String(c.group_id), c.group_name])).entries());

  const filtered = applySort(receipts.filter(r => {
    const matchSearch = !searchTerm ||
      r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchMode = !filterMode || r.mode === filterMode;
    const matchClient = !filterClient || String(r.client_id) === String(filterClient);
    const matchGroup = !filterGroup || clientGroupById.get(String(r.client_id)) === String(filterGroup);
    const matchCreatedBy = !filterCreatedBy || r.recorded_by_name === filterCreatedBy;
    const unallocated = Math.max(0, Number(r.available_amount ?? r.received_amount ?? 0) - Number(r.settled_amount ?? 0));
    const matchAllocation = !filterAllocation || (filterAllocation === 'unallocated' && unallocated > 0.01);
    const matchDateFrom = !filterDateFrom || new Date(r.payment_date) >= new Date(filterDateFrom);
    const matchDateTo = !filterDateTo || new Date(r.payment_date) <= new Date(filterDateTo + 'T23:59:59');
    return matchSearch && matchMode && matchClient && matchGroup && matchCreatedBy && matchAllocation && matchDateFrom && matchDateTo;
  }), RECEIPT_COLUMNS, sortKey, sortDir);

  const totalCollected = filtered.reduce((sum, r) => sum + Number(r.received_amount ?? r.amount ?? 0), 0);
  const totalUnallocated = filtered.reduce((sum, r) => sum + Math.max(0, Number(r.available_amount ?? r.received_amount ?? 0) - Number(r.settled_amount ?? 0)), 0);
  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const handleDeleteReceipt = async (r) => {
    showConfirm('Delete Receipt', `Delete receipt ${r.receipt_number}? This will reverse all linked invoice payments.`, async () => {
      const res = await fetch(`${BASE}/api/invoices/receipts/${r.id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchReceipts();
        // Same stale-Invoices-tab problem as saving a settlement — this
        // reverses whatever invoice_payments the receipt held, so ping the
        // Invoices tab (if mounted) to re-fetch instead of keep showing the
        // now-wrong Paid/Balance/Status. The listener ignores detail and
        // just refetches, so no need to know which invoice(s) were touched.
        window.dispatchEvent(new CustomEvent('invoice-status-changed'));
      }
      else { const d = await res.json(); alert(d.message || 'Delete failed'); }
    });
  };

  const exportReceiptsToCSV = () => {
    const headers = ['Receipt No.', 'Date', 'Client', 'Invoice No.', 'Amount (₹)', 'Allocation Status', 'Unallocated (₹)', 'Mode', 'Bank', 'Reference', 'Recorded By'];
    const rows = filtered.map(r => {
      const available = Number(r.available_amount ?? r.received_amount ?? 0);
      const settled = Number(r.settled_amount ?? 0);
      const unallocated = Math.max(0, available - settled);
      const allocationStatus = unallocated < 0.01 ? 'Settled' : (settled > 0.01 ? 'Partially Settled' : 'Unallocated');
      return [
      r.receipt_number || '',
      r.payment_date ? new Date(r.payment_date).toLocaleDateString('en-IN') : '',
      r.client_name || '',
      r.invoice_numbers || r.invoice_number || '',
      Number(r.received_amount ?? r.amount ?? 0).toFixed(2),
      allocationStatus,
      unallocated.toFixed(2),
      paymentModes.find(m => m.value === r.mode)?.name || r.mode?.replace(/_/g, ' ') || '',
      r.bank_name ? `${r.bank_name}${r.bank_nickname ? ` — ${r.bank_nickname}` : ''}` : 'Cash',
      r.reference_number || '',
      r.recorded_by_name || ''
      ];
    });
    const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ChanakyaX_Receipts_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const renderReceiptCell = (r, key) => {
    if (key === 'receipt_number') return <button onClick={() => setSelectedReceiptId(r.id)} className="text-purple-600 hover:text-purple-800 hover:underline font-semibold">{r.receipt_number || '-'}</button>;
    if (key === 'payment_date') return new Date(r.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    if (key === 'client_name') return <span className="font-medium text-gray-800">{r.client_name}</span>;
    if (key === 'invoice_numbers') return <span className="text-blue-600 text-sm">{r.invoice_numbers || r.invoice_number || '—'}</span>;
    if (key === 'received_amount') return <span className="font-bold text-green-600">{fmt(r.received_amount ?? r.amount)}</span>;
    if (key === 'allocation_status') {
      // available_amount/settled_amount come straight from the backend (see
      // GET /receipts/all) — no re-derivation here, so this always agrees
      // with the "Unallocated Fees" stat card and the Allocation filter.
      const available = Number(r.available_amount ?? r.received_amount ?? 0);
      const settled = Number(r.settled_amount ?? 0);
      const unallocated = Math.max(0, available - settled);
      if (unallocated < 0.01) {
        return <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 9px', borderRadius:999, fontSize:11, fontWeight:700, background:'#dcfce7', color:'#16a34a' }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
          Settled
        </span>;
      }
      if (settled > 0.01) {
        return <span style={{ display:'inline-flex', flexDirection:'column', gap:1 }}>
          <span style={{ display:'inline-flex', alignItems:'center', width:'fit-content', padding:'3px 9px', borderRadius:999, fontSize:11, fontWeight:700, background:'#fef3c7', color:'#b45309' }}>Partially Settled</span>
          <span style={{ fontSize:10.5, color:'#b45309' }}>{fmt(unallocated)} left</span>
        </span>;
      }
      return <span style={{ display:'inline-flex', flexDirection:'column', gap:1 }}>
        <span style={{ display:'inline-flex', alignItems:'center', width:'fit-content', padding:'3px 9px', borderRadius:999, fontSize:11, fontWeight:700, background:'#fee2e2', color:'#dc2626' }}>Unallocated</span>
        <span style={{ fontSize:10.5, color:'#dc2626' }}>{fmt(unallocated)}</span>
      </span>;
    }
    if (key === 'mode') {
      // Show the firm's own configured Payment Mode name (e.g. "ICICI 4445"),
      // not a generic humanised version of its internal key — the key is
      // just the stable value stored on the receipt row, not meant for
      // display (see Settings → Payment Modes' own Mode/Key columns).
      const modeLabel = paymentModes.find(m => m.value === r.mode)?.name || r.mode?.replace(/_/g, ' ') || '—';
      return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
          r.mode==='upi'?'bg-purple-100 text-purple-700':r.mode==='cash'?'bg-green-100 text-green-700':
          r.mode==='cheque'?'bg-yellow-100 text-yellow-700':r.mode==='card'?'bg-blue-100 text-blue-700':'bg-gray-100 text-gray-600'
        }`}>{modeLabel}</span>
      );
    }
    if (key === 'bank_name') return r.bank_name
      ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{r.bank_name}{r.bank_nickname ? ` — ${r.bank_nickname}` : ''}</span>
      : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Cash</span>;
    if (key === 'reference_number') return <span className="text-gray-600">{r.reference_number || '-'}</span>;
    if (key === 'recorded_by_name') return <span className="text-gray-500">{r.recorded_by_name || '-'}</span>;
    return r[key] || '-';
  };

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };

  return (
    <div style={{ flex:1, minHeight:0, display:"flex", flexDirection:"column" }}>
      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
        {[
          { label:'Total Collected',  value: fmt(totalCollected),    color:'#10b981', glow:'rgba(16,185,129,0.12)' },
          { label:'Unallocated Fees', value: fmt(totalUnallocated),  color:'#f59e0b', glow:'rgba(245,158,11,0.12)' },
          { label:'Total Receipts',   value: filtered.length,        color:'#6366f1', glow:'rgba(99,102,241,0.12)' },
          { label:'Average Receipt',  value: filtered.length?fmt(totalCollected/filtered.length):'₹0', color:'#3b82f6', glow:'rgba(59,130,246,0.12)' },
        ].map(s => (
          <div key={s.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:'16px 20px', boxShadow:`0 4px 16px ${s.glow}`, position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:-16, right:-16, width:56, height:56, borderRadius:'50%', background:s.glow, filter:'blur(12px)' }}/>
            <p style={{ fontSize:20, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
            <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <input type="text" placeholder="Search client, invoice, receipt no..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ ...is, width:280 }} />
          <FilterButton activeCount={[filterMode,filterClient,filterGroup,filterCreatedBy,filterAllocation,filterDateFrom,filterDateTo].filter(Boolean).length} onClick={() => setShowFilters(s => !s)} darkMode={darkMode} T={T} />
          {(searchTerm||filterMode||filterClient||filterGroup||filterCreatedBy||filterAllocation||filterDateFrom||filterDateTo) && (
            <button onClick={() => { setSearchTerm(''); setFilterMode(''); setFilterClient(''); setFilterGroup(''); setFilterCreatedBy(''); setFilterAllocation(''); setFilterDateFrom(''); setFilterDateTo(''); }}
              style={{ fontSize:12, color:'#ef4444', padding:'6px 10px', borderRadius:8, border:'1px solid #fecaca', background:'none', cursor:'pointer' }}>Clear</button>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={exportReceiptsToCSV} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
            borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2,
            color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#f97316'; e.currentTarget.style.color='#f97316'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button onClick={() => setShowImport(true)} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
            borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2,
            color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#0f1f45'; e.currentTarget.style.color='#0f1f45'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import
          </button>
          <button onClick={() => setShowNew(true)} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 18px',
            borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)',
            color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer',
            boxShadow:'0 4px 12px rgba(15,31,69,0.3)',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Receipt
          </button>
        </div>
      </div>
      {showImport && (
        <ReceiptImportModal token={token} clients={clients} onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); fetchReceipts(); }} darkMode={darkMode} T={T} />
      )}

      {/* Filter panel */}
      {showFilters && (
        <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, padding:16, marginBottom:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Payment Mode</label>
              <select value={filterMode} onChange={e => setFilterMode(e.target.value)} style={is}>
                <option value="">All Modes</option>
                {paymentModes.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Client</label>
              <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={is}>
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Group</label>
              <FilterSelect value={filterGroup} onChange={setFilterGroup} placeholder="All Groups" darkMode={darkMode} T={T}
                options={[['','All Groups'],...groupOptions]} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Created By</label>
              <select value={filterCreatedBy} onChange={e => setFilterCreatedBy(e.target.value)} style={is}>
                <option value="">All Users</option>
                {creatorOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Allocation</label>
              <select value={filterAllocation} onChange={e => setFilterAllocation(e.target.value)} style={is}>
                <option value="">All Receipts</option>
                <option value="unallocated">Unallocated Only</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>From Date</label>
              <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} style={is} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>To Date</label>
              <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} style={is} />
            </div>
          </div>
          <p style={{ fontSize:11, color:T.text3, marginTop:10 }}>{filtered.length} receipt(s) match the filters</p>
        </div>
      )}

      {/* Bulk actions bar */}
      {selectedIds.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, padding:'8px 14px', borderRadius:10, background: darkMode?'rgba(239,68,68,0.1)':'#fef2f2', border:`1px solid ${darkMode?'rgba(239,68,68,0.25)':'#fecaca'}` }}>
          <span style={{ fontSize:13, fontWeight:600, color:'#dc2626' }}>{selectedIds.length} receipt{selectedIds.length===1?'':'s'} selected</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setSelectedIds([])} style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:'transparent', color:T.text2, fontSize:12, fontWeight:600, cursor:'pointer' }}>
              Clear selection
            </button>
            <button onClick={() => setShowBulkDelete(true)} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'#dc2626', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              Delete Selected
            </button>
          </div>
        </div>
      )}
      {showBulkDelete && (
        <BulkDeleteModal token={token} entityName="Receipt"
          items={filtered.filter(r => selectedIds.includes(r.id))}
          getLabel={r => r.receipt_number || `Receipt #${r.id}`}
          buildDeleteUrl={r => `${BASE}/api/invoices/receipts/${r.id}`}
          warningText="Deleting a receipt reverses any invoice payments it settled."
          onClose={() => setShowBulkDelete(false)}
          onDone={() => { setShowBulkDelete(false); setSelectedIds([]); fetchReceipts(); }}
          darkMode={darkMode} T={T} />
      )}

      {/* Table */}
      <div style={{ flex:1, minHeight:0, background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
        <div style={{ flex:1, minHeight:0, overflowX:'auto', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
            <tr>
              <th style={{ padding:'10px 12px', width:36 }}>
                <input type="checkbox"
                  checked={filtered.length > 0 && selectedIds.length === filtered.length}
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < filtered.length; }}
                  onChange={e => setSelectedIds(e.target.checked ? filtered.map(r => r.id) : [])}
                  style={{ cursor:'pointer' }} />
              </th>
              {orderedVisibleKeys.map(key => {
                const col = RECEIPT_COLUMNS.find(c => c.key === key);
                return <SortableTh key={key} label={col?.label} sortKey={key} activeKey={sortKey} dir={sortDir} onSort={onSort} align={key==='received_amount'?'right':'left'} />;
              })}
              <th style={{ padding:'10px 16px', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase' }}>Action</th>
              <th style={{ padding:'10px 8px', width:40, background:'transparent' }}>
                {cc.menu}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleColumns.length+3} style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={visibleColumns.length+3} style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>No receipts yet. Click "+ New Receipt" to record one.</td></tr>
            ) : filtered.map((r, idx) => (
              <tr key={r.id} style={{ borderBottom:`1px solid ${T.border}`, background: selectedIds.includes(r.id) ? (darkMode?'rgba(239,68,68,0.08)':'#fef2f2') : darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                onMouseEnter={e => { if (!selectedIds.includes(r.id)) e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = selectedIds.includes(r.id) ? (darkMode?'rgba(239,68,68,0.08)':'#fef2f2') : darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'); }}>
                <td style={{ padding:'10px 12px' }}>
                  <input type="checkbox" checked={selectedIds.includes(r.id)}
                    onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, r.id] : prev.filter(id => id !== r.id))}
                    style={{ cursor:'pointer' }} />
                </td>
                {orderedVisibleKeys.map(key => (
                  <td key={key} style={{ padding:'10px 16px', fontSize:13 }}>{renderReceiptCell(r, key)}</td>
                ))}
                <td style={{ padding:'10px 16px', position:'relative' }}>
                  <button onClick={() => setOpenKebabId(openKebabId===r.id?null:r.id)}
                    style={{ width:32, height:32, borderRadius:8, border:'none', background:'none', color:T.text3, fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                    onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                    onMouseLeave={e => e.currentTarget.style.background='none'}>⋮</button>
                  {openKebabId===r.id && (
                    <div onMouseLeave={() => setOpenKebabId(null)}
                      style={{ position:'absolute', right:0, top:40, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:999, width:176, padding:6 }}>
                      <button onClick={() => { setOpenKebabId(null); setEditReceiptId(r.id); }}
                        style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#f97316', fontSize:13, fontWeight:500, cursor:'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(249,115,22,0.1)':'#fff7ed'}
                        onMouseLeave={e => e.currentTarget.style.background='none'}>
                        Edit
                      </button>
                      <div style={{ height:1, background:T.border, margin:'4px 0' }}/>
                      <button onClick={() => { setOpenKebabId(null); handleDeleteReceipt(r); }}
                        style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#ef4444', fontSize:13, fontWeight:500, cursor:'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.1)':'#fef2f2'}
                        onMouseLeave={e => e.currentTarget.style.background='none'}>
                        Delete
                      </button>
                    </div>
                  )}
                </td>
                <td style={{ padding:'10px 8px' }}/>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {filtered.length > 0 && (
          <div style={{ padding:'12px 16px', background:T.bg3, borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between' }}>
            <p style={{ fontSize:12, color:T.text3, margin:0 }}>{filtered.length} receipt(s)</p>
            <p style={{ fontSize:13, fontWeight:700, color:'#16a34a', margin:0 }}>Total: {fmt(totalCollected)}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// NEW RECEIPT FORM
function ReceiptDetail({ token, receiptId, onClose, onEdit, onSuccess, showConfirm, darkMode = false, T: theme }) {
  const T = theme || (darkMode ? {
    bg:'#0f172a', bg2:'#1e293b', bg3:'#334155', border:'#334155', border2:'#475569',
    text:'#f1f5f9', text2:'#94a3b8', text3:'#64748b', cardBg:'#1e293b', cardBorder:'#334155',
  } : {
    bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0',
    text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9',
  });

  const [receipt, setReceipt]     = useState(null);
  const [settings, setSettings]   = useState({});
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  useEffect(() => {
    Promise.all([
      fetch(`${BASE_URL}/api/invoices/receipts/${receiptId}`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE_URL}/api/invoices/settings/config`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
      fetch(`${BASE_URL}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
    ]).then(([rd, sd, pm]) => { setReceipt(rd); setSettings(sd || {}); setPaymentModes(Array.isArray(pm) ? pm : []); setLoading(false); });
  }, [receiptId, token]);

  const fc  = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 })}`;
  // Show the firm's own configured Payment Mode name (e.g. "ICICI 4445"), not
  // a generic humanised version of its internal key.
  const fmtMode = (m) => paymentModes.find(pm => pm.value === m)?.name || (m ? m.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()) : '-');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' }) : '-';

  const handleSendEmail = async () => {
    const email = receipt?.client_email;
    if (!email) { showToast('No email address for this client. Update client profile.', 'error'); return; }
    setEmailSending(true);
    try {
      const res = await fetch(`${BASE_URL}/api/notification-settings/send-finance-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type:'receipt', to:email, invoice_number:receipt.receipt_number, client_name:receipt.client_name, total_amount:receipt.received_amount, balance_due:0, invoice_date:receipt.payment_date, due_date:null, firm_name:settings.firm_name, status:'paid' })
      });
      const d = await res.json();
      if (d.success) showToast(`Email sent to ${email}`, 'success');
      else showToast('Failed: ' + (d.reason || 'Unknown error'), 'error');
    } catch { showToast('Server error while sending email', 'error'); }
    setEmailSending(false);
  };

  const handleSendWhatsApp = () => {
    const mobile = receipt?.client_mobile || receipt?.client_phone || '';
    const num = mobile.replace(/\D/g, '');
    if (!num) { showToast('No mobile number for this client.', 'error'); return; }
    const settlements = receipt?.settlements || [];
    const invText = settlements.length ? settlements.map(s => s.invoice_number).join(', ') : '';
    const msg = encodeURIComponent(`Dear ${receipt.client_name},\n\nWe acknowledge receipt of *${fc(receipt.received_amount)}* via ${fmtMode(receipt.mode)}.\nReceipt No.: *${receipt.receipt_number}*\nDate: ${fmtDate(receipt.payment_date)}${invText ? `\nAgainst Invoice: ${invText}` : ''}\n\nThank you.\n\nRegards,\n${settings.firm_name || 'Legal Chanakya Corporate Advisors'}`);
    window.open(`https://wa.me/91${num}?text=${msg}`, '_blank');
    showToast('Opening WhatsApp...', 'info');
  };

  const handleDelete = async () => {
    showConfirm('Delete Receipt', `Delete receipt ${receipt.receipt_number}? This will reverse all linked invoice payments.`, async () => {
      const res = await fetch(`${BASE_URL}/api/invoices/receipts/${receipt.id}`, { method:'DELETE', headers:{ 'Authorization':`Bearer ${token}` } });
      if (res.ok) { showToast('Receipt deleted', 'success'); setTimeout(() => { onSuccess && onSuccess(); onClose && onClose(); }, 1200); }
      else { const d = await res.json(); showToast(d.message || 'Delete failed', 'error'); }
    });
  };

  // Opens the browser's native print dialog on the same ReceiptLetterhead
  // component the Billing Settings live preview renders — real text, small
  // file, user picks where "Save as PDF" goes.
  const handlePrint = async () => {
    if (!receipt) return;
    let qrDataUrl = '';
    if (settings.upi_id && settings.show_invoice_qr !== false && Number(receipt.balance_after) > 0) {
      const upiString = `upi://pay?${new URLSearchParams({ pa: settings.upi_id, pn: settings.firm_name || 'ChanakyaX', am: String(receipt.balance_after || ''), cu: 'INR', tn: `Receipt ${receipt.receipt_number || ''}` }).toString()}`;
      try { qrDataUrl = await QRCode.toDataURL(upiString, { width: 240, margin: 1 }); } catch (e) { console.error('QR generation failed:', e); }
    }
    try {
      await printComponent(
        <ReceiptLetterhead settings={settings} receipt={{ ...receipt, mode_label: fmtMode(receipt.mode) }} qrDataUrl={qrDataUrl} />,
        `Receipt ${receipt.receipt_number || ''}`
      );
    } catch (e) {
      showToast('Could not open print dialog: ' + e.message, 'error');
    }
  };

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, color:'#94a3b8', fontSize:13 }}>Loading receipt...</div>;
  if (!receipt) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, color:'#ef4444', fontSize:13 }}>Receipt not found</div>;

  const settlements = receipt.settlements || [];

  return (
    <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:16, paddingRight:4, scrollbarWidth:'none' }}>
      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', bottom:32, left:'50%', transform:'translateX(-50%)', zIndex:99999, display:'flex', alignItems:'center', gap:12, padding:'14px 22px', borderRadius:14, background:toast.type==='success'?'linear-gradient(135deg,#16a34a,#15803d)':toast.type==='error'?'linear-gradient(135deg,#dc2626,#b91c1c)':'linear-gradient(135deg,#0f1f45,#1a3a6b)', boxShadow:'0 8px 32px rgba(0,0,0,0.25)', minWidth:280, maxWidth:480 }}>
          <div style={{ width:30, height:30, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {toast.type==='success' ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> : toast.type==='error' ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}
          </div>
          <p style={{ fontSize:13, fontWeight:600, color:'#fff', margin:0, flex:1 }}>{toast.msg}</p>
          <button onClick={() => setToast(null)} style={{ width:22, height:22, borderRadius:6, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* Breadcrumb + Actions — matching InvoiceDetail */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
            <button onClick={onClose} style={{ display:'flex', alignItems:'center', gap:6, border:'none', background:'none', cursor:'pointer', color:'#3b82f6', fontSize:13, fontWeight:500, padding:0 }}
              onMouseEnter={e=>e.currentTarget.style.textDecoration='underline'}
              onMouseLeave={e=>e.currentTarget.style.textDecoration='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M5 12l7 7M5 12l7-7"/></svg>
              Receipts
            </button>
            <span style={{ color:T.text3 }}>›</span>
            <span style={{ fontSize:13, fontWeight:500, color:T.text3 }}>Receipt Detail</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h2 style={{ fontSize:20, fontWeight:800, color:T.text, margin:0 }}>{receipt.receipt_number}</h2>
            <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20, color:'#16a34a', background:darkMode?'rgba(22,163,74,0.12)':'#f0fdf4' }}>Paid</span>
          </div>
          <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>{receipt.client_name}</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {/* WhatsApp */}
          <button onClick={handleSendWhatsApp} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'}`, background:darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', color:'#16a34a' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
            WhatsApp
          </button>
          {/* Email */}
          <button onClick={handleSendEmail} disabled={emailSending} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:emailSending?'not-allowed':'pointer', border:`1px solid ${darkMode?'rgba(37,99,235,0.3)':'#bfdbfe'}`, background:darkMode?'rgba(37,99,235,0.1)':'#eff6ff', color:'#2563eb', opacity:emailSending?0.6:1 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            {emailSending ? 'Sending...' : 'Email'}
          </button>
          {/* Edit */}
          <button onClick={onEdit} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Edit
          </button>
          {/* Delete */}
          <button onClick={handleDelete} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${darkMode?'rgba(239,68,68,0.3)':'#fecaca'}`, background:darkMode?'rgba(239,68,68,0.08)':'#fef2f2', color:'#ef4444' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
            Delete
          </button>
          {/* Print */}
          <button onClick={handlePrint} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print / PDF
          </button>
        </div>
      </div>

      {/* Main content — two column like InvoiceDetail */}
      <div style={{ display:'flex', gap:20, alignItems:'flex-start' }}>
        {/* Left — receipt detail card */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', gap:16 }}>

          {/* Received From + Date — matches InvoiceDetail's Bill To card exactly */}
          <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              <div>
                <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Received From</p>
                <p style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>{receipt.client_name || '-'}</p>
                {settlements.length > 0 && <p style={{ fontSize:12, color:'#2563eb', marginTop:5, fontWeight:600 }}>{settlements.map(s => s.invoice_number).join(', ')}</p>}
              </div>
              <div style={{ textAlign:'right' }}>
                <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>Receipt Date</p>
                <p style={{ fontSize:14, fontWeight:600, color:T.text }}>{fmtDate(receipt.payment_date)}</p>
                <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:10, marginBottom:5 }}>Payment Mode</p>
                <p style={{ fontSize:13, fontWeight:500, color:T.text }}>{fmtMode(receipt.mode)}</p>
                {receipt.reference_number && (<>
                  <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:10, marginBottom:5 }}>Reference No.</p>
                  <p style={{ fontSize:13, fontWeight:500, color:T.text }}>{receipt.reference_number}</p>
                </>)}
              </div>
            </div>
          </div>

          {/* Settlements or description */}
          {settlements.length > 0 ? (
            <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, overflow:'hidden', marginBottom:14 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  {['Invoice No.','Invoice Date','Amount Settled'].map((h,i) => (
                    <th key={h} style={{ padding:'9px 16px', textAlign:i===2?'right':'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {settlements.map((s,i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode&&i%2!==0?T.bg3:'transparent' }}
                    onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode&&i%2!==0?T.bg3:'transparent'}>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color:'#2563eb' }}>{s.invoice_number}</td>
                    <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{fmtDate(s.payment_date || receipt.payment_date)}</td>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:'#16a34a', textAlign:'right' }}>{fc(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, overflow:'hidden', marginBottom:14 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  <th style={{ padding:'9px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>Description</th>
                  <th style={{ padding:'9px 16px', textAlign:'right', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{receipt.notes || `Payment received via ${fmtMode(receipt.mode)}`}</td>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:T.text, textAlign:'right' }}>{fc(receipt.received_amount)}</td>
                </tr>
              </tbody>
            </table>
            </div>
          )}

          {/* Total */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px', background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, marginBottom:28 }}>
            <span style={{ fontSize:14, fontWeight:700, color:T.text }}>Total Received</span>
            <span style={{ fontSize:17, fontWeight:900, color:settings.primary_color||'#0f1f45' }}>{fc(receipt.received_amount)}</span>
          </div>

          {/* Footer — signature only; the "For more services..." marketing
              footer belongs on the printed/PDF Receipt (invoiceDocument.js),
              not this in-app detail view. */}
          <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'flex-end', borderTop:`1px solid ${T.border}`, paddingTop:18 }}>
            <div style={{ textAlign:'center' }}>
              {settings.signature_url && <img src={settings.signature_url} alt="Signature" style={{ maxHeight:60, maxWidth:130, display:'block', margin:'0 auto 6px' }} />}
              <p style={{ fontSize:11, color:T.text3, margin:0 }}>Authorised Signatory</p>
              <p style={{ fontSize:11, fontWeight:700, color:T.text, margin:'2px 0 0' }}>{settings.firm_name || 'ChanakyaX'}</p>
            </div>
          </div>
        </div>

        {/* Right — summary card like InvoiceDetail */}
        <div style={{ width:260, flexShrink:0, display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:20, boxShadow:darkMode?'0 2px 12px rgba(0,0,0,0.3)':'0 2px 8px rgba(15,31,69,0.06)' }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:'0 0 16px' }}>Receipt Summary</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:13, color:T.text3 }}>Received Amount</span>
                <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{fc(receipt.received_amount)}</span>
              </div>
              {Number(receipt.tds_amount||0) > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, color:T.text3 }}>TDS Deducted</span>
                  <span style={{ fontSize:13, fontWeight:600, color:'#f97316' }}>-{fc(receipt.tds_amount)}</span>
                </div>
              )}
              {Number(receipt.discount_amount||0) > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, color:T.text3 }}>Discount</span>
                  <span style={{ fontSize:13, fontWeight:600, color:'#f97316' }}>-{fc(receipt.discount_amount)}</span>
                </div>
              )}
              <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10, borderTop:`1px solid ${T.border}` }}>
                <span style={{ fontSize:14, fontWeight:700, color:T.text }}>Net Received</span>
                <span style={{ fontSize:16, fontWeight:800, color:'#16a34a' }}>{fc(receipt.received_amount)}</span>
              </div>
            </div>
          </div>
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:20, boxShadow:darkMode?'0 2px 12px rgba(0,0,0,0.3)':'0 2px 8px rgba(15,31,69,0.06)' }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:'0 0 14px' }}>Payment Info</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:12, color:T.text3 }}>Mode</span>
                <span style={{ fontSize:12, fontWeight:600, color:T.text }}>{fmtMode(receipt.mode)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:12, color:T.text3 }}>Date</span>
                <span style={{ fontSize:12, fontWeight:600, color:T.text }}>{fmtDate(receipt.payment_date)}</span>
              </div>
              {receipt.reference_number && (
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:T.text3 }}>Reference</span>
                  <span style={{ fontSize:12, fontWeight:600, color:T.text }}>{receipt.reference_number}</span>
                </div>
              )}
              {receipt.notes && (
                <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                  <p style={{ fontSize:11, color:T.text3, margin:'0 0 4px', fontWeight:600, textTransform:'uppercase' }}>Remark</p>
                  <p style={{ fontSize:12, color:T.text2, margin:0 }}>{receipt.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewReceiptForm({ token, onClose, onSuccess, editReceiptId, prefillData, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [formToast, setFormToast] = useState(null);
  const showFormToast = (msg, type='success') => { setFormToast({msg,type}); setTimeout(()=>setFormToast(null), type==='success'?2000:4000); };
  const [paymentModes, setPaymentModes] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active) : [])).catch(() => {});
  }, [token]);
  const handleCreatePaymentMode = async (name) => {
    const res = await fetch(`${BASE}/api/payment-modes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name, value: name }),
    });
    const created = await res.json().catch(() => null);
    if (res.ok && created) setPaymentModes(prev => [...prev, created]);
    return created;
  };
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/bank/accounts`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setBankAccounts(Array.isArray(d) ? d.filter(a => a.is_active !== false) : [])).catch(() => {});
  }, [token]);
  const isEdit = !!editReceiptId;
  const [clients, setClients] = useState([]);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const clientPickerRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (clientPickerRef.current && !clientPickerRef.current.contains(e.target)) setShowClientPicker(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const [nextReceiptNo, setNextReceiptNo] = useState('');
  const [existingReceiptNo, setExistingReceiptNo] = useState('');
  const [form, setForm] = useState({
    client_id: '', payment_date: new Date().toISOString().split('T')[0],
    mode: '', bank_account_id: '', reference_number: '', notes: '',
    received_amount: '', tds_amount: '', discount_amount: ''
  });
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [clientBalance, setClientBalance] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settleScope, setSettleScope] = useState('client'); // 'client' | 'group' | 'any'
  const [groupSearch, setGroupSearch] = useState('');
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;

        // Refresh receipt number if receipt settings change
  useEffect(() => {
    const handler = () => {
      if (isEdit) return;

      fetch(`${BASE}/api/invoices/receipts/next-number`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.receipt_number) setNextReceiptNo(data.receipt_number);
        })
        .catch(() => {});
    };

    window.addEventListener('settings-updated', handler);
    return () => window.removeEventListener('settings-updated', handler);
  }, [token, isEdit]);


  useEffect(() => {
    // Compute financial year string e.g. "26-27"
    const getFY = () => {
      const now = new Date();
      const y = now.getFullYear();
      return now.getMonth() >= 3
        ? `${String(y).slice(-2)}-${String(y + 1).slice(-2)}`
        : `${String(y - 1).slice(-2)}-${String(y).slice(-2)}`;
    };

    // EDIT MODE: the clients list (200+ rows) isn't needed to kick off the
    // outstanding-invoices/ledger fetch — it's only used afterward to label
    // rows in a "group"/"any" scope table. Fetching it separately instead of
    // via Promise.all means a slow clients fetch no longer blocks starting
    // the (client-specific) outstanding/ledger fetch the moment the much
    // smaller single-receipt fetch resolves, which was adding a full extra
    // network round-trip's worth of wait to every Edit Receipt open.
    if (isEdit) {
      fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => setClients([]));

      fetch(`${BASE}/api/invoices/receipts/${editReceiptId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null).catch(() => null).then(async (receipt) => {
        if (!receipt) return;
        setExistingReceiptNo(receipt.receipt_number || '');
        setForm({
          client_id: String(receipt.client_id || ''),
          payment_date: receipt.payment_date?.split('T')[0] || new Date().toISOString().split('T')[0],
          mode: receipt.mode || '',
          bank_account_id: receipt.bank_account_id ? String(receipt.bank_account_id) : '',
          reference_number: receipt.reference_number || '',
          notes: receipt.notes || '',
          received_amount: receipt.received_amount ?? receipt.amount ?? '',
          tds_amount: receipt.tds_amount || '',
          discount_amount: receipt.discount_amount || '',
        });
        // After client is set, load outstanding invoices and pre-fill existing settlements
        if (receipt.client_id) {
          const initialScope = receipt.is_group_receipt ? 'any' : 'client';
          setSettleScope(initialScope);
          await loadOutstandingForClient(String(receipt.client_id), receipt.settlements || [], initialScope);
        }
      });
      return;
    }

    // CREATE MODE: fetch clients + exact next receipt number from backend
    Promise.all([
      fetch(`${BASE}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => []),

      fetch(`${BASE}/api/invoices/receipts/next-number`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([clientData, nextNumData]) => {
      setClients(Array.isArray(clientData) ? clientData : []);
      setNextReceiptNo(nextNumData?.receipt_number || '');

      if (prefillData?.client_id && prefillData?.invoice_id) {
        // Handed off from a specific invoice ("Record Payment") — pre-fill
        // the amount/notes and the settlement row for that invoice.
        setForm(f => ({
          ...f,
          client_id: String(prefillData.client_id),
          payment_date: prefillData.payment_date || new Date().toISOString().split('T')[0],
          received_amount: prefillData.balance_due || '',
          notes: `Receipt against Invoice ${prefillData.invoice_number || ''}`
        }));

        loadOutstandingForClient(String(prefillData.client_id), [{
          invoice_id: prefillData.invoice_id,
          invoice_number: prefillData.invoice_number,
          total_amount: prefillData.total_amount,
          balance_due: prefillData.balance_due,
          amount: prefillData.balance_due
        }]);
      } else if (prefillData?.client_id) {
        // Handed off from Client Dashboard → Ledger → "+ Create Receipt" —
        // just pre-select the client, no invoice to settle yet; let the form's
        // own outstanding-invoices lookup populate normally once the user
        // picks a mode/amount.
        setForm(f => ({ ...f, client_id: String(prefillData.client_id) }));
        loadOutstandingForClient(String(prefillData.client_id), []);
      }
    });
  }, [token, editReceiptId]);

  // Shared loader so both handleClientChange (create) and useEffect (edit) can reuse.
  // scope: 'client' = only this client's invoices (original behaviour)
  //        'group'  = all clients sharing the same client_group as clientId
  //        'any'    = firm-wide, filtered by optional `search` text
  const loadOutstandingForClient = async (clientId, existingSettlements = [], scope = 'client', search = '') => {
    setLoadingInvoices(true);
    try {
      const isScoped = scope === 'group' || scope === 'any';
      const outstUrl = isScoped
        ? `${BASE}/api/retainers/outstanding/invoices?scope=${scope}${clientId ? `&client_id=${clientId}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`
        : `${BASE}/api/invoices/outstanding/${clientId}`;

      const [outstRes, ledgerRes] = await Promise.all([
        fetch(outstUrl, { headers: { 'Authorization': `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : []).catch(() => []),
        // Ledger balance only makes sense for a single client
        (!isScoped && clientId)
          ? fetch(`${BASE}/api/invoices/ledger/${clientId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }).then(r => r.json()).catch(() => ({}))
          : Promise.resolve(null)
      ]);

      let invList = Array.isArray(outstRes) ? outstRes : [];
      // Tag single-client fetches with the selected client's id/name for consistent rendering
      if (!isScoped) {
        const selClient = clients.find(c => String(c.id) === String(clientId));
        invList = invList.map(inv => ({ ...inv, client_id: clientId, client_name: selClient?.client_name || '' }));
      }

      existingSettlements.forEach(s => {
        const exists = invList.some(inv => String(inv.id) === String(s.invoice_id));
        if (!exists) {
          invList.push({
            id: s.invoice_id,
            invoice_number: s.invoice_number,
            invoice_date: s.payment_date || new Date().toISOString(),
            due_date: null,
            total_amount: s.total_amount || 0,
            amount_paid: Number(s.total_amount || 0) - Number(s.balance_due || 0),
            balance_due: Number(s.balance_due || 0) + Number(s.amount || 0),
            status: 'partially_paid',
            client_id: s.client_id || clientId,
            client_name: s.client_name || ''
          });
        }
      });

      setOutstandingInvoices(invList);

      setSettlements(invList.map(inv => {
        const prev = existingSettlements.find(s => String(s.invoice_id) === String(inv.id));
        return {
          invoice_id: inv.id,
          invoice_number: inv.invoice_number,
          invoice_date: inv.invoice_date,
          due_date: inv.due_date,
          total_amount: inv.total_amount,
          amount_paid: inv.amount_paid,
          balance_due: prev ? Number(inv.balance_due || 0) : inv.balance_due,
          status: inv.status,
          client_id: inv.client_id,
          client_name: inv.client_name,
          amount: prev ? String(prev.amount) : ''
        };
      }));

      setClientBalance(isScoped ? null : (ledgerRes?.closing_balance ?? null));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Whatever's currently allocated (typed into a Payment box, or carried in
  // from the receipt's own already-saved settlements) has to survive a scope
  // switch — loadOutstandingForClient's re-fetch only naturally re-finds an
  // invoice if it still has a balance outstanding to SOME receipt; one this
  // receipt already fully settled (balance_due=0 on the invoice itself)
  // only stays visible/editable via the existingSettlements merge-back, so
  // losing that list on every scope change silently dropped it — the row
  // vanished, and its amount stopped counting against "Available to Settle"
  // at all, making an already-committed ₹6,000 look like ₹11,000 was still
  // free to allocate. `balance_due` here has to be de-ceilinged back to the
  // TRUE remaining balance (excluding this row's own current contribution)
  // before handing it back, since loadOutstandingForClient's own merge-back
  // re-adds that contribution — passing the already-ceilinged value through
  // unchanged would double-count it a little more on every switch.
  const getCurrentAllocated = () => settlements
    .filter(s => Number(s.amount) > 0)
    .map(s => ({
      invoice_id: s.invoice_id, invoice_number: s.invoice_number, payment_date: s.invoice_date,
      total_amount: s.total_amount, balance_due: Number(s.balance_due || 0) - Number(s.amount || 0),
      amount: s.amount, client_id: s.client_id, client_name: s.client_name,
    }));

  const handleClientChange = async (clientId) => {
    setForm(f => ({ ...f, client_id: clientId }));
    setOutstandingInvoices([]);
    setSettlements([]);
    setClientBalance(null);
    if (!clientId) return;
    await loadOutstandingForClient(clientId, [], settleScope, groupSearch);
  };

  const handleScopeChange = async (newScope) => {
    const carryOver = getCurrentAllocated();
    setSettleScope(newScope);
    setOutstandingInvoices([]);
    setSettlements([]);
    if (newScope === 'any' || form.client_id) {
      await loadOutstandingForClient(form.client_id, carryOver, newScope, groupSearch);
    }
  };

  const handleGroupSearch = async (term) => {
    setGroupSearch(term);
    if (settleScope === 'any') {
      await loadOutstandingForClient(form.client_id, getCurrentAllocated(), 'any', term);
    }
  };

  const availableAmount = Number(form.received_amount || 0) - Number(form.tds_amount || 0) - Number(form.discount_amount || 0);
  const totalSettled   = settlements.reduce((s, inv) => s + Number(inv.amount || 0), 0);
  const unallocated    = availableAmount - totalSettled;

  const handleAutoApply = () => {
  const netAvailable = Math.max(0, availableAmount);

  if (netAvailable <= 0) {
    alert('Enter received amount first.');
    return;
  }

  if (settlements.length === 0) {
    alert('No pending invoice found. You can still save this receipt as advance/unallocated.');
    return;
  }

  let rem = netAvailable;

  const updated = settlements.map(inv => {
    const bal = Number(inv.balance_due || 0);
    if (rem <= 0 || bal <= 0) return { ...inv, amount: '' };

    const apply = Math.min(rem, bal);
    rem -= apply;

    return { ...inv, amount: apply.toFixed(2) };
  });

  setSettlements(updated);
};

  const getOverdueDays = (dueDate) => {
    if (!dueDate) return 0;
    const diff = Math.floor((new Date() - new Date(dueDate)) / 86400000);
    return diff > 0 ? diff : 0;
  };

  const handleSave = async () => {
    const validSettlementsPreview = settlements.filter(s => Number(s.amount) > 0);
    // If no primary client was picked (possible in "Any clients" scope), anchor the receipt
    // to the first settled invoice's client instead of blocking the save.
    let anchorClientId = form.client_id;
    if (!anchorClientId && validSettlementsPreview.length) {
      anchorClientId = validSettlementsPreview[0].client_id;
      setForm(f => ({ ...f, client_id: anchorClientId }));
    }
    if (!anchorClientId)                                            { alert('Select a client (or settle at least one invoice)'); return; }
    if (!form.received_amount || Number(form.received_amount) <= 0) { alert('Enter received amount'); return; }
    const validSettlements = settlements
  .map(s => ({
    ...s,
    amount: Math.min(Number(s.amount || 0), Number(s.balance_due || 0))
  }))
  .filter(s => Number(s.amount) > 0);

    const distinctSettledClients = new Set(validSettlements.map(s => String(s.client_id || anchorClientId)));
    const isGroupReceipt = settleScope !== 'client' && distinctSettledClients.size > 1;

    // RELAXED: allow saving an advance receipt even when invoices exist
    // (User explicitly asked for this — receipt can be recorded first, settled later)
    // Only nag the user if they typed amounts in some invoice rows but didn't reach the full amount
    if (saving) return;
    setSaving(true);
    try {
      const url = isEdit
        ? `${BASE}/api/invoices/receipts/${editReceiptId}`
        : `${BASE}/api/invoices/receipts/create`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          client_id: anchorClientId,
          payment_date: form.payment_date,
          received_amount: form.received_amount,
          tds_amount: form.tds_amount || 0,
          discount_amount: form.discount_amount || 0,
          mode: form.mode,
          bank_account_id: form.bank_account_id || null,
          reference_number: form.reference_number,
          notes: form.notes,
          settlements: validSettlements,
          is_group_receipt: isGroupReceipt
        })
      });
      const data = await res.json();
      if (res.ok) {
        showFormToast(isEdit ? 'Receipt updated successfully!' : `Receipt ${data.receipt_number||''} created successfully!`, 'success');
        // Settling a receipt updates invoices.amount_paid/balance_due/status
        // on the backend immediately, but the Invoices tab (kept mounted,
        // not refetched on every tab switch — see `visitedTabs`) has no way
        // to know that happened from over here. Same soft-refresh signal
        // EditInvoice's own status-change action already fires, so if that
        // tab is open it just quietly re-fetches instead of showing stale
        // Paid/Balance/Status for whatever this receipt just settled.
        validSettlements.forEach(s => {
          window.dispatchEvent(new CustomEvent('invoice-status-changed', { detail: { invoiceId: s.invoice_id } }));
        });
        setTimeout(() => onSuccess(data), 1500);
      } else {
        showFormToast(data.message || 'Error saving receipt', 'error');
      }
    } catch (e) { showFormToast('Server error', 'error'); }
    finally { setSaving(false); }
  };

  const fc = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const selectedClientName = clients.find(c => String(c.id) === String(form.client_id))?.client_name || '';
  const filteredClients = clients.filter(c => !clientSearch ||
    c.client_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.file_number?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.pan?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.group_name?.toLowerCase().includes(clientSearch.toLowerCase()));
  const clientDisambigLine = (c) => [
    c.group_name ? `Group: ${c.group_name}` : null,
    c.file_number ? `File No: ${c.file_number}` : (c.pan ? `PAN: ${c.pan}` : null),
  ].filter(Boolean).join(' · ');

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)', width:'100%', maxWidth:1100, height:'92vh' }}>

        {/* Toast */}
        {formToast && (
          <div style={{ position:'fixed', bottom:32, left:'50%', transform:'translateX(-50%)', zIndex:99999, display:'flex', alignItems:'center', gap:12, padding:'14px 22px', borderRadius:14, background:formToast.type==='success'?'linear-gradient(135deg,#16a34a,#15803d)':'linear-gradient(135deg,#dc2626,#b91c1c)', boxShadow:'0 8px 32px rgba(0,0,0,0.25)', minWidth:280, maxWidth:480 }}>
            <div style={{ width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {formToast.type==='success' ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
            </div>
            <p style={{ fontSize:13, fontWeight:600, color:'#fff', margin:0, flex:1 }}>{formToast.msg}</p>
          </div>
        )}

        {/* HEADER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 32px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                <path d="M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <div>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>{isEdit ? 'Edit Receipt' : 'New Receipt'}</h3>
              <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>
                Receipt No.: <strong style={{ color:'#fff' }}>{isEdit ? existingReceiptNo : (nextReceiptNo || 'Auto-generated')}</strong>
              </p>
            </div>
          </div>
          <button onClick={onClose}
            style={{ width:36, height:36, borderRadius:10, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* BODY */}
        <div style={{ flex:1, overflowY:'auto', padding:28, background:T.bg3, scrollbarWidth:'none', display:'flex', flexDirection:'column', gap:20 }}>

      {/* Section 1 — Receipt Details */}
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:24, boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>1</div>
              <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Receipt Details</h4>
              <div style={{ flex:1, height:1, background:T.border }}/>
            </div>

            {/* Row 1 */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:16 }}>
              <div>
                <SL req>Date</SL>
                <input type="date" value={form.payment_date}
                  onChange={e=>setForm(f=>({...f,payment_date:e.target.value}))} style={is} />
              </div>
              <div>
                <SL>Receipt No.</SL>
                <input type="text" value={isEdit?existingReceiptNo:nextReceiptNo} readOnly
                  style={{ ...is, background: darkMode?'rgba(255,255,255,0.04)':T.bg3, color:T.text3, cursor:'default' }} />
              </div>
              <div>
                <SL req>Bank Account</SL>
                <BankAccountSelect accounts={bankAccounts} value={form.bank_account_id}
                  onChange={v=>setForm(f=>({...f,bank_account_id:v}))} darkMode={darkMode} T={T} />
              </div>
            </div>

            {/* Row 1b */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:16, marginBottom:16 }}>
              <div>
                <SL>Payment Mode</SL>
                <PaymentModeDropdown modes={paymentModes} value={form.mode}
                  onChange={v=>setForm(f=>({...f,mode:v}))} onCreate={handleCreatePaymentMode}
                  darkMode={darkMode} T={T} />
              </div>
              <div>
                <SL>Reference No.</SL>
                <input type="text" placeholder="UTR / Cheque No."
                  value={form.reference_number} onChange={e=>setForm(f=>({...f,reference_number:e.target.value}))} style={is} />
              </div>
            </div>

            {/* Row 2 */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:16 }}>
              <div ref={clientPickerRef} style={{ position:'relative' }}>
                <SL req>Client</SL>
                <button type="button" onClick={() => { setClientSearch(''); setShowClientPicker(p => !p); }}
                  style={{ ...is, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
                  <span style={{ color: selectedClientName ? T.text : T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedClientName || '— Select Client —'}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginLeft:6, transform: showClientPicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {showClientPicker && (
                  <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, width:'100%' }}>
                    <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
                      <div style={{ position:'relative' }}>
                        <svg style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input type="text" autoFocus placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                          style={{ width:'100%', paddingLeft:28, paddingRight:10, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                      </div>
                    </div>
                    <div style={{ maxHeight:220, overflowY:'auto' }}>
                      {filteredClients.length === 0 ? (
                        <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No clients found</p>
                      ) : filteredClients.map(c => {
                        const sel = String(form.client_id) === String(c.id);
                        return (
                          <button key={c.id} type="button" onClick={() => { handleClientChange(c.id); setShowClientPicker(false); }}
                            style={{ width:'100%', textAlign:'left', padding:'8px 14px', border:'none', cursor:'pointer', background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent' }}
                            onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                            onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                            <div style={{ fontSize:13, fontWeight:500, color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text }}>{c.client_name}</div>
                            {clientDisambigLine(c) && <div style={{ fontSize:10.5, color:T.text3, marginTop:1 }}>{clientDisambigLine(c)}</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {clientBalance !== null && (
                  <p style={{ fontSize:11, fontWeight:600, marginTop:5, display:'flex', alignItems:'center', gap:5, color: Number(clientBalance)>0?'#ef4444':'#16a34a' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{Number(clientBalance)>0?<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>:<polyline points="20 6 9 17 4 12"/>}</svg>
                    Ledger: {fc(Math.abs(clientBalance))} {Number(clientBalance)>0?' (Due)':' (NIL)'}
                  </p>
                )}
              </div>
              <div>
                <SL req>Received Amount (₹)</SL>
                <div style={{ position:'relative' }}>
                  <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:13, color:T.text3, pointerEvents:'none' }}>₹</span>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    value={form.received_amount} onChange={e=>setForm(f=>({...f,received_amount:e.target.value}))}
                    style={{ ...is, paddingLeft:28 }} />
                </div>
              </div>
              <div>
                <SL>TDS Amount (₹)</SL>
                <div style={{ position:'relative' }}>
                  <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:13, color:T.text3, pointerEvents:'none' }}>₹</span>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    value={form.tds_amount} onChange={e=>setForm(f=>({...f,tds_amount:e.target.value}))}
                    style={{ ...is, paddingLeft:28 }} />
                </div>
              </div>
              <div>
                <SL>Discount (₹)</SL>
                <div style={{ position:'relative' }}>
                  <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:13, color:T.text3, pointerEvents:'none' }}>₹</span>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    value={form.discount_amount} onChange={e=>setForm(f=>({...f,discount_amount:e.target.value}))}
                    style={{ ...is, paddingLeft:28 }} />
                </div>
              </div>
            </div>

            {/* Settlement Scope Toggle */}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, flexWrap:'wrap' }}>
              <span style={{ fontSize:11, fontWeight:600, color:T.text3 }}>Settle invoices from:</span>
              <div style={{ display:'flex', borderRadius:9, border:`1px solid ${T.inputBorder}`, overflow:'hidden' }}>
                {[
                  { key:'client', label:'This client only' },
                  { key:'group',  label:"Client's group" },
                  { key:'any',    label:'Any clients' },
                ].map(opt => (
                  <button key={opt.key} type="button"
                    onClick={() => handleScopeChange(opt.key)}
                    disabled={opt.key !== 'any' && !form.client_id}
                    style={{
                      padding:'7px 14px', fontSize:12, fontWeight:600, border:'none', cursor: (opt.key !== 'any' && !form.client_id) ? 'not-allowed' : 'pointer',
                      background: settleScope===opt.key ? '#0f1f45' : 'transparent',
                      color: settleScope===opt.key ? '#fff' : T.text3,
                      opacity: (opt.key !== 'any' && !form.client_id) ? 0.5 : 1
                    }}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {settleScope === 'any' && (
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <input type="text" placeholder="Search client or invoice no. — press Enter"
                    value={groupSearch}
                    onChange={e => setGroupSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleGroupSearch(groupSearch); }}
                    style={{ ...is, width:220 }} />
                  <button type="button" onClick={() => handleGroupSearch(groupSearch)}
                    style={{ padding:'8px 12px', borderRadius:9, border:`1px solid ${T.inputBorder}`, background:T.bg3, color:T.text2, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    Search
                  </button>
                </div>
              )}
              {settleScope !== 'client' && (
                <span style={{ fontSize:11, color:'#f97316', fontWeight:600 }}>
                  Group Receipt — settling invoices across multiple clients
                </span>
              )}
            </div>

            {/* Row 3 */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div>
                <SL>Remark</SL>
                <input type="text" placeholder="Optional note"
                  value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={is} />
              </div>
              <div style={{ background: darkMode?'rgba(59,130,246,0.08)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, borderRadius:10, padding:'12px 18px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontSize:13, fontWeight:500, color:T.text2 }}>Available to Settle</span>
                <span style={{ fontSize:18, fontWeight:800, color: availableAmount<0?'#ef4444':'#2563eb' }}>
                  {fc(Math.max(0, availableAmount))}
                </span>
              </div>
            </div>
          </div>

          {/* Section 2 — Settle Invoices */}
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)', flex:1, display:'flex', flexDirection:'column', minHeight:280 }}>
            {/* Section header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', background:T.bg3, borderBottom:`1px solid ${T.border}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>2</div>
                <h4 style={{ fontSize:13, fontWeight:700, color:T.text, margin:0 }}>Settle Invoices</h4>
                {outstandingInvoices.length > 0 && (
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background: darkMode?'rgba(249,115,22,0.15)':'#fff7ed', color:'#f97316', border:`1px solid ${darkMode?'rgba(249,115,22,0.3)':'#fed7aa'}` }}>
                    {outstandingInvoices.length} pending
                  </span>
                )}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                <span style={{ fontSize:12, color:T.text3 }}>
                  Available: <strong style={{ color: availableAmount>0?'#2563eb':T.text3 }}>{fc(Math.max(0,availableAmount))}</strong>
                </span>
                {unallocated>0 && totalSettled>0 && (
                  <span style={{ fontSize:12, color:'#f97316', fontWeight:600 }}>Unallocated: {fc(unallocated)}</span>
                )}
                {outstandingInvoices.length > 0 && (
                  <button onClick={handleAutoApply} disabled={availableAmount<=0}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor: availableAmount<=0?'not-allowed':'pointer', border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`, background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', color:'#2563eb', opacity: availableAmount<=0?0.4:1 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    Auto Apply
                  </button>
                )}
              </div>
            </div>

            {/* Loading */}
            {loadingInvoices && (
              <div style={{ padding:20, display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#2563eb' }}>
                  <span style={{ width:16, height:16, border:'2px solid #2563eb', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>
                  Fetching pending invoices...
                </div>
                {[1,2,3].map(n=>(
                  <div key={n} style={{ height:36, background:T.bg3, borderRadius:8, opacity: 1-n*0.25 }}/>
                ))}
              </div>
            )}

            {/* No client */}
            {!form.client_id && settleScope !== 'any' && !loadingInvoices && (
              <div style={{ padding:'48px 0', textAlign:'center', color:T.text3 }}>
                <svg style={{ margin:'0 auto 12px', display:'block' }} width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                <p style={{ fontSize:13, fontWeight:600, color:T.text2, margin:0 }}>Select a client above</p>
                <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Pending invoices will auto-appear here</p>
              </div>
            )}

            {/* No invoices */}
            {(form.client_id || settleScope === 'any') && !loadingInvoices && outstandingInvoices.length===0 && (
              <div style={{ padding:'48px 0', textAlign:'center' }}>
                <svg style={{ margin:'0 auto 12px', display:'block' }} width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5"><polyline points="20 6 9 17 4 12"/></svg>
                <p style={{ fontSize:13, fontWeight:600, color:'#16a34a', margin:0 }}>No pending invoices</p>
                <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>All bills settled — receipt will be recorded as advance</p>
              </div>
            )}

            {/* Settlement table — scrolls independently with a sticky header so
                every matched invoice stays reachable no matter how many there
                are, instead of relying on the whole modal happening to be
                tall enough (a "9 pending" badge with only 3 rows visible
                wasn't missing data, just easy to mistake for it without an
                obvious scrollbar). */}
            {!loadingInvoices && settlements.length>0 && (
              <div style={{ maxHeight:320, overflowY:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:1 }}>
                  <tr>
                    {(settleScope !== 'client'
                      ? ['Client','Invoice Date','Invoice No.','Status','Total Amount','Amount Paid','Due Amount','Payment (₹)']
                      : ['Invoice Date','Invoice No.','Status','Total Amount','Amount Paid','Due Amount','Payment (₹)']
                    ).map(h=>(
                      <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s,i)=>{
                    const inv = outstandingInvoices[i];
                    const overdueDays = getOverdueDays(s.due_date);
                    const isPartial = s.status==='partially_paid';
                    const inputFilled = Number(s.amount)>0;
                    return (
                      <tr key={s.invoice_id} style={{ borderBottom:`1px solid ${T.border}`, background: inputFilled?(darkMode?'rgba(16,185,129,0.08)':'#f0fdf4'):(darkMode&&i%2!==0?T.bg3:'transparent'), transition:'background 0.15s' }}
                        onMouseEnter={e=>{ if(!inputFilled) e.currentTarget.style.background=T.bg3; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background=inputFilled?(darkMode?'rgba(16,185,129,0.08)':'#f0fdf4'):(darkMode&&i%2!==0?T.bg3:'transparent'); }}>
                        {settleScope !== 'client' && (
                          <td style={{ padding:'10px 16px', fontSize:12, fontWeight:600, color:T.text2 }}>{s.client_name || '—'}</td>
                        )}
                        <td style={{ padding:'10px 16px', fontSize:12, color:T.text3 }}>
                          {new Date(s.invoice_date).toLocaleDateString('en-IN')}
                          {overdueDays>0 && (
                            <span style={{ marginLeft:6, fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:6, background: darkMode?'rgba(239,68,68,0.15)':'#fef2f2', color:'#ef4444' }}>
                              {overdueDays}d overdue
                            </span>
                          )}
                        </td>
                        <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color:'#3b82f6' }}>{s.invoice_number}</td>
                        <td style={{ padding:'10px 16px' }}>
                          <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
                            color: isPartial?'#d97706':'#dc2626',
                            background: isPartial?(darkMode?'rgba(217,119,6,0.12)':'#fffbeb'):(darkMode?'rgba(220,38,38,0.12)':'#fef2f2') }}>
                            {isPartial?'Partial':'Due'}
                          </span>
                        </td>
                        <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{fc(inv?.total_amount)}</td>
                        <td style={{ padding:'10px 16px', fontSize:13, fontWeight:500, color:'#16a34a' }}>{fc(s.amount_paid)}</td>
                        <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:'#dc2626' }}>{fc(s.balance_due)}</td>
                        <td style={{ padding:'10px 16px' }}>
                          <div style={{ position:'relative' }}>
                            <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:12, color:T.text3, pointerEvents:'none' }}>₹</span>
                            <input type="number" min="0" step="0.01" max={s.balance_due} placeholder="0.00"
                              value={s.amount}
                              onChange={e=>setSettlements(prev=>prev.map((x,idx)=>idx===i?{...x,amount:e.target.value}:x))}
                              style={{ width:120, paddingLeft:26, paddingRight:8, paddingTop:7, paddingBottom:7, borderRadius:8, fontSize:13, outline:'none', border:`1px solid ${inputFilled?'#10b981':T.border}`, background: inputFilled?(darkMode?'rgba(16,185,129,0.1)':'#f0fdf4'):T.inputBg, color:T.text, boxSizing:'border-box' }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ background:T.bg3, borderTop:`2px solid ${T.border}` }}>
                    <td colSpan={settleScope !== 'client' ? 6 : 5} style={{ padding:'12px 16px', fontSize:13, fontWeight:600, color:T.text2, textAlign:'right' }}>Total Amount Settled</td>
                    <td style={{ padding:'12px 16px', fontSize:13, fontWeight:700, color:'#dc2626' }}>{fc(settlements.reduce((s,x)=>s+Number(x.balance_due||0),0))}</td>
                    <td style={{ padding:'12px 16px', fontSize:14, fontWeight:800, color:'#16a34a' }}>{fc(totalSettled)}</td>
                  </tr>
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 28px', borderTop:`1px solid ${T.border}`, background:T.bg2, flexShrink:0, borderRadius:'0 0 20px 20px', flexWrap:'wrap', gap:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
            <div style={{ fontSize:13, color:T.text3 }}>Received: <span style={{ fontWeight:700, color:T.text }}>{fc(form.received_amount||0)}</span></div>
            {Number(form.tds_amount)>0 && <div style={{ fontSize:13, color:'#f97316' }}>TDS: <span style={{ fontWeight:700 }}>-{fc(form.tds_amount)}</span></div>}
            {Number(form.discount_amount)>0 && <div style={{ fontSize:13, color:'#ef4444' }}>Discount: <span style={{ fontWeight:700 }}>-{fc(form.discount_amount)}</span></div>}
            <div style={{ fontSize:13, color:'#2563eb', fontWeight:600 }}>Net Available: <span style={{ fontWeight:800 }}>{fc(Math.max(0,availableAmount))}</span></div>
            {totalSettled>0 && <div style={{ fontSize:13, color:'#16a34a', fontWeight:600 }}>Settling: <span style={{ fontWeight:800 }}>{fc(totalSettled)}</span></div>}
            {unallocated>0.01 && totalSettled>0 && (
              <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#f97316', fontWeight:600 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                {fc(unallocated)} unallocated
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:12 }}>
            <button onClick={onClose} style={{
              padding:'10px 20px', borderRadius:10, border:`1px solid ${T.border2}`,
              background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
            }}
            onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
            onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving||!form.client_id} style={{
              display:'flex', alignItems:'center', gap:8, padding:'10px 28px', borderRadius:10, border:'none',
              background: saving||!form.client_id?'#94a3b8':'linear-gradient(135deg,#0f1f45,#1a3a6b)',
              color:'#fff', fontSize:13, fontWeight:600, cursor: saving||!form.client_id?'not-allowed':'pointer',
              boxShadow: saving||!form.client_id?'none':'0 4px 12px rgba(15,31,69,0.3)',
            }}>
              {saving && <span style={{ width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving ? 'Saving...' : isEdit ? 'Update Receipt' : 'Save Receipt'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// CREDIT NOTE FORM
function CreditNoteForm({ token, invoice, onClose, onSuccess }) {
  const [form, setForm] = useState({ reason: '', credit_date: new Date().toISOString().split('T')[0] });
  const [items, setItems] = useState(invoice.items?.map(item => ({
    particular: item.particular, description: '',
    amount: item.net_amount, selected: false
  })) || []);
  const [saving, setSaving] = useState(false);

  const selectedItems = items.filter(i => i.selected);
  const total = selectedItems.reduce((sum, i) => sum + Number(i.amount), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) { alert('Select at least one item for credit note'); return; }
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/invoices/credit-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          original_invoice_id: invoice.id,
          client_id: invoice.client_id,
          credit_date: form.credit_date,
          reason: form.reason,
          items: selectedItems
        })
      });
      if (res.ok) { alert('Credit note created!'); onSuccess(); }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  return (
    <div className="bg-orange-50 rounded-xl p-4 mb-4 border border-orange-200">
      <h3 className="font-semibold text-orange-700 mb-3">Issue Credit Note — {invoice.invoice_number}</h3>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Credit Date</label>
            <input type="date" value={form.credit_date}
              onChange={e => setForm(f => ({ ...f, credit_date: e.target.value }))} className={ic} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reason *</label>
            <input type="text" required placeholder="Reason for credit note"
              value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} className={ic} />
          </div>
        </div>
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-600 mb-2">Select items to credit:</p>
          <div className="space-y-2">
            {items.map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-2.5 border border-gray-200">
                <input type="checkbox" checked={item.selected}
                  onChange={e => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, selected: e.target.checked } : it))}
                  className="rounded" />
                <span className="text-sm flex-1 text-gray-700">{item.particular}</span>
                <input type="number" step="0.01" min="0" max={item.amount}
                  value={item.amount}
                  onChange={e => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, amount: e.target.value } : it))}
                  className="border border-gray-200 rounded px-2 py-1 text-sm w-28" />
              </div>
            ))}
          </div>
        </div>
        {total > 0 && (
          <p className="text-sm font-bold text-orange-700 mb-3">
            Credit Note Total: ₹{Number(total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </p>
        )}
        <div className="flex gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm">Cancel</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-lg text-white text-sm disabled:opacity-50" style={{ background: '#f97316' }}>
            {saving ? 'Creating...' : 'Issue Credit Note'}
          </button>
        </div>
      </form>
    </div>
  );
}

// CREDIT NOTE DETAIL
function CreditNoteDetail({ creditId, token, onClose, onEdit, onSuccess, showConfirm, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9' };
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [cn, setCn] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE_URL}/api/invoices/credit-notes/${creditId}`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
      fetch(`${BASE_URL}/api/invoices/settings/config`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
    ]).then(([cnData, sd]) => { setCn(cnData); setSettings(sd || {}); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, [creditId]); // eslint-disable-line

  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 })}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' }) : '-';

  const handleDelete = async () => {
    showConfirm('Delete Credit Note', `Delete credit note ${cn.credit_note_number}? This cannot be undone.`, async () => {
      const res = await fetch(`${BASE_URL}/api/invoices/credit-notes/${creditId}`, { method:'DELETE', headers:{ 'Authorization':`Bearer ${token}` } });
      if (res.ok) { showToast('Credit note deleted', 'success'); setTimeout(() => { onSuccess && onSuccess(); onClose && onClose(); }, 1200); }
      else { const d = await res.json(); showToast(d.message || 'Delete failed', 'error'); }
    });
  };

  // ── WhatsApp Send ─────────────────────────────────────────────────────────
  const handleSendWhatsApp = () => {
    if (!cn) return;
    const phone = (cn.client_mobile || cn.client_phone || '').replace(/[^0-9]/g, '');
    if (!phone) { showToast('No mobile number found for this client.', 'error'); return; }
    const msg = 'Dear ' + cn.client_name + ',%0A%0A'
      + 'Credit Note No: *' + cn.credit_note_number + '*%0A'
      + 'Date: ' + new Date(cn.credit_date).toLocaleDateString('en-IN') + '%0A'
      + (cn.original_invoice_number ? 'Against Invoice: *' + cn.original_invoice_number + '*%0A' : '')
      + 'Credit Amount: *Rs.' + Number(cn.total_amount).toLocaleString('en-IN') + '*%0A%0A'
      + (cn.reason ? 'Reason: ' + cn.reason + '%0A%0A' : '')
      + 'Regards,%0A' + (settings.firm_name || 'ChanakyaX');
    window.open('https://wa.me/91' + phone + '?text=' + msg, '_blank');
    showToast('Opening WhatsApp...', 'info');
  };

  // ── Email Send ────────────────────────────────────────────────────────────
  const handleSendEmail = async () => {
    if (!cn) return;
    const clientEmail = cn.client_email;
    if (!clientEmail) {
      showToast('No email address found for this client. Please update the client profile with their email.', 'error');
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}/api/notification-settings/send-finance-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          type: 'credit_note',
          to: clientEmail,
          invoice_number: cn.credit_note_number,
          client_name: cn.client_name,
          total_amount: cn.total_amount,
          balance_due: 0,
          invoice_date: cn.credit_date,
          due_date: null,
          firm_name: settings.firm_name,
          status: 'issued',
        })
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Email sent successfully to ${clientEmail}`, 'success');
      } else {
        showToast('Failed: ' + (data.reason || 'Unknown error'), 'error');
      }
    } catch (e) {
      showToast('Server error while sending email', 'error');
    }
  };

  const handlePrint = () => {
    if (!cn) return;
    const pColor     = settings.primary_color   || '#0f1f45';
    const font       = settings.invoice_font    || 'Plus Jakarta Sans';
    const isCorp     = (settings.template_style || 'classic') === 'corporate';
    const isBold     = (settings.template_style || 'classic') === 'bold';
    const headerBg   = isCorp ? '#ffffff' : pColor;
    const headerText = isCorp ? pColor    : '#ffffff';
    const cnHeading  = settings.credit_note_heading || 'Credit Note';
    const gFonts = ['Plus Jakarta Sans','Poppins','Montserrat','Inter'].some(f => font.includes(f))
      ? `<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&display=swap" rel="stylesheet">`
      : '';
    const separator = isCorp
      ? `<div style="height:2px;background:${pColor}"></div>`
      : isBold
      ? `<div style="height:4px;background:#f97316"></div>`
      : `<div style="display:flex;height:4px"><div style="flex:1;background:${pColor}"></div><div style="flex:1;background:#c8a951"></div><div style="flex:1;background:#e8d9a0"></div></div>`;
    const firmAddr = [settings.firm_address, settings.firm_city, settings.firm_pincode].filter(Boolean).join(', ');
    const items = cn.items || [];
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Credit Note - ${cn.credit_note_number}</title>
    ${gFonts}
    <style>
      @page { size:A4; margin:14mm; }
      *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
      body { font-family:"${font}",Arial,sans-serif; margin:0; padding:0; color:#1a1a1a; font-size:12px; }
      .header { background:${headerBg}; color:${headerText}; padding:20px 24px; display:flex; justify-content:space-between; align-items:flex-start; ${isCorp?'border-bottom:2.5px solid '+pColor+';border-left:5px solid '+pColor+';':''} }
      .firm-col { display:flex; flex-direction:column; gap:10px; max-width:55%; }
      .firm-logo { max-height:52px; max-width:200px; width:auto; height:auto; object-fit:contain; object-position:left center; display:block; }
      .firm-name { font-size:15px; font-weight:bold; color:${headerText}; }
      .firm-sub  { font-size:9px; color:${headerText}; opacity:${isCorp?0.7:0.85}; margin-top:3px; line-height:1.7; }
      .doc-title { text-align:right; flex-shrink:0; }
      .doc-title h1 { font-size:20px; font-weight:bold; margin:0; letter-spacing:1px; color:${headerText}; }
      .doc-badge { display:inline-block; border:1px solid ${isCorp?pColor:'rgba(255,255,255,0.5)'}; color:${headerText}; font-size:9px; padding:2px 8px; border-radius:2px; margin-top:4px; letter-spacing:0.4px; }
      .meta { display:grid; grid-template-columns:1fr 1fr; gap:16px; padding:16px 24px; border-bottom:1px solid #eee; }
      .meta-label { font-size:10px; color:#888; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:4px; }
      .meta-value { font-size:13px; font-weight:600; color:#1a1a1a; }
      .meta-sub { font-size:11px; color:#555; margin-top:2px; }
      .ref-box { background:#fff5f5; border:1px solid #fecaca; border-radius:8px; padding:10px 14px; margin:12px 24px; display:flex; align-items:center; gap:8px; }
      .ref-box span { font-size:11px; color:#991b1b; }
      .ref-box strong { font-size:12px; color:#7f1d1d; }
      table { width:100%; border-collapse:collapse; margin:0 24px; width:calc(100% - 48px); }
      th { background:${pColor}; color:#fff; padding:8px 12px; text-align:left; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.4px; }
      th.r { text-align:right; }
      td { padding:8px 12px; border-bottom:1px solid #f3f4f6; font-size:11px; }
      td.r { text-align:right; }
      .totals { margin:0 24px; }
      .total-row { display:flex; justify-content:space-between; padding:6px 12px; font-size:12px; }
      .total-row.final { background:${pColor}; color:#fff; border-radius:8px; margin-top:4px; padding:10px 12px; font-size:14px; font-weight:bold; }
      .footer { margin:16px 24px 0; padding-top:12px; border-top:1px solid #eee; display:flex; justify-content:space-between; }
      .note-box { background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:10px 14px; margin:12px 24px; font-size:11px; color:#555; }
    </style></head><body>

    <div class="header">
      <div class="firm-col">
        ${settings.logo_url ? `<img src="${settings.logo_url}" class="firm-logo" alt="Logo"/>` : ''}
        <div>
          <div class="firm-name">${settings.firm_name || ''}</div>
          <div class="firm-sub">${firmAddr}</div>
          ${settings.firm_mobile ? `<div class="firm-sub">Mobile: ${settings.firm_mobile}</div>` : ''}
          ${settings.firm_gstin  ? `<div class="firm-sub">GSTIN: ${settings.firm_gstin}</div>`   : ''}
        </div>
      </div>
      <div class="doc-title">
        <h1>${cnHeading}</h1>
        <div class="doc-badge">ORIGINAL</div>
        <div style="margin-top:8px;font-size:9px;color:${headerText};opacity:${isCorp?1:0.9};line-height:1.9;text-align:right">
          <div><span style="opacity:0.7">CN No. </span><strong>${cn.credit_note_number||''}</strong></div>
          <div><span style="opacity:0.7">Date </span><strong>${cn.credit_date ? new Date(cn.credit_date).toLocaleDateString('en-IN') : ''}</strong></div>
        </div>
      </div>
    </div>
    ${separator}

    <div class="meta">
      <div class="meta-box">
        <div class="meta-label">Credit Note To</div>
        <div class="meta-value">${cn.client_name || '—'}</div>
        ${cn.client_address ? `<div class="meta-sub">${cn.client_address}</div>` : ''}
        ${cn.client_gstin ? `<div class="meta-sub">GSTIN: ${cn.client_gstin}</div>` : ''}
      </div>
      <div class="meta-box" style="text-align:right">
        <div class="meta-label">Credit Note No.</div>
        <div class="meta-value" style="font-size:15px">${cn.credit_note_number || '—'}</div>
        <div class="meta-sub">Date: ${cn.credit_date ? new Date(cn.credit_date).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : '—'}</div>
      </div>
    </div>

    <div class="ref-box">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#991b1b" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <span>Against Invoice: <strong>${cn.original_invoice_number || '—'}</strong></span>
      ${cn.reason ? `<span style="margin-left:16px">Reason: <strong>${cn.reason}</strong></span>` : ''}
    </div>

    <table style="margin-top:12px">
      <thead>
        <tr>
          <th style="width:35px">#</th>
          <th>Description</th>
          <th class="r" style="width:90px">Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${items.length > 0
          ? items.map((item, i) => `
            <tr>
              <td>${i+1}</td>
              <td>${item.particular || item.description || '—'}</td>
              <td class="r">${Number(item.amount || 0).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
            </tr>`).join('')
          : `<tr><td colspan="3" style="text-align:center;color:#999;padding:16px">No items</td></tr>`
        }
      </tbody>
    </table>

    <div class="totals" style="margin-top:8px">
      <div class="total-row final"><span>Total Credit Amount</span><span>${fc(cn.total_amount)}</span></div>
    </div>

    ${cn.notes ? `<div class="note-box"><strong>Notes:</strong> ${cn.notes}</div>` : ''}
    ${settings.credit_note_footer_html ? `<div class="note-box">${settings.credit_note_footer_html}</div>` : ''}

    <div class="footer">
      <div style="font-size:9px;color:#9ca3af">This is a computer-generated credit note.</div>
      <div style="text-align:center">
        ${settings.signature_url ? `<img src="${settings.signature_url}" style="max-height:36px;max-width:100px;display:block;margin:0 auto 4px"/>` : `<div style="width:100px;height:30px;border-bottom:1px solid #999;margin:0 auto 4px"></div>`}
        <div style="font-size:10px;font-weight:600">${settings.signature_name || 'Authorised Signatory'}</div>
        ${settings.signature_designation ? `<div style="font-size:9px;color:#888">${settings.signature_designation}</div>` : ''}
        <div style="font-size:9px;color:#555;margin-top:1px">For ${settings.firm_name || ''}</div>
      </div>
    </div>

    <script>window.onload=function(){window.print()}<\/script>
    </body></html>`);
    w.document.close();
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'64px 0', color:T.text3 }}>
      <span style={{ width:20, height:20, border:`2px solid ${T.border2}`, borderTopColor:'#f97316', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite', marginRight:10 }}/>
      Loading credit note...
    </div>
  );
  if (!cn) return <div style={{ textAlign:'center', padding:'64px 0', color:'#ef4444', fontSize:14 }}>Credit note not found</div>;

  const items = cn.items || [];

  return (
    <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:16, paddingRight:4, scrollbarWidth:'none' }}>
      {toast && (
        <div style={{ position:'fixed', bottom:32, left:'50%', transform:'translateX(-50%)', zIndex:99999, display:'flex', alignItems:'center', gap:12, padding:'14px 22px', borderRadius:14, background:toast.type==='success'?'linear-gradient(135deg,#16a34a,#15803d)':toast.type==='error'?'linear-gradient(135deg,#dc2626,#b91c1c)':'linear-gradient(135deg,#0f1f45,#1a3a6b)', boxShadow:'0 8px 32px rgba(0,0,0,0.25)', minWidth:280, maxWidth:480 }}>
          <p style={{ fontSize:13, fontWeight:600, color:'#fff', margin:0, flex:1 }}>{toast.msg}</p>
          <button onClick={() => setToast(null)} style={{ width:24, height:24, borderRadius:6, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', cursor:'pointer' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* Header bar */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:T.text3, marginBottom:8 }}>
            <button onClick={onClose} style={{ color:'#3b82f6', background:'none', border:'none', cursor:'pointer', fontWeight:500, padding:0 }}>Credit Notes</button>
            <span>›</span><span>Credit Note Detail</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h2 style={{ fontSize:20, fontWeight:800, color:T.text, margin:0 }}>{cn.credit_note_number}</h2>
            <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20, color:'#f97316', background:darkMode?'rgba(249,115,22,0.12)':'#fff7ed' }}>Issued</span>
          </div>
          <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>{cn.client_name}</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <button onClick={handleSendWhatsApp} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'}`, background:darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', color:'#16a34a' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
            WhatsApp
          </button>
          <button onClick={handleSendEmail} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${darkMode?'rgba(37,99,235,0.3)':'#bfdbfe'}`, background:darkMode?'rgba(37,99,235,0.1)':'#eff6ff', color:'#2563eb' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            Email
          </button>
          <button onClick={() => onEdit(creditId)} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Edit
          </button>
          <button onClick={handleDelete} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${darkMode?'rgba(239,68,68,0.3)':'#fecaca'}`, background:darkMode?'rgba(239,68,68,0.08)':'#fef2f2', color:'#ef4444' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
            Delete
          </button>
          <button onClick={handlePrint} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print / PDF
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:20, alignItems:'start' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Credit Note To + Date — matches InvoiceDetail's Bill To card */}
          <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              <div>
                <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Credit Note To</p>
                <p style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>{cn.client_name}</p>
                {cn.client_address && <p style={{ fontSize:12, color:T.text3, marginTop:5 }}>{cn.client_address}</p>}
                {cn.client_gstin && <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>GSTIN: {cn.client_gstin}</p>}
              </div>
              <div style={{ textAlign:'right' }}>
                <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>Credit Note Date</p>
                <p style={{ fontSize:14, fontWeight:600, color:T.text }}>{fmtDate(cn.credit_date)}</p>
                <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:10, marginBottom:5 }}>Against Invoice</p>
                <p style={{ fontSize:13, fontWeight:500, color:'#3b82f6' }}>{cn.original_invoice_number || '—'}</p>
                {cn.reason && (<>
                  <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:10, marginBottom:5 }}>Reason</p>
                  <p style={{ fontSize:13, fontWeight:500, color:T.text }}>{cn.reason}</p>
                </>)}
              </div>
            </div>
          </div>

          {/* Items */}
          <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  {['#','Description','Amount'].map((h,i) => (
                    <th key={h} style={{ padding:'9px 16px', textAlign:i===2?'right':'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan="3" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>No items</td></tr>
                ) : items.map((item, i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode&&i%2!==0?T.bg3:'transparent' }}
                    onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode&&i%2!==0?T.bg3:'transparent'}>
                    <td style={{ padding:'10px 16px', fontSize:12, color:T.text3 }}>{i+1}</td>
                    <td style={{ padding:'10px 16px' }}>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{item.particular || item.description}</p>
                    </td>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:T.text, textAlign:'right' }}>{fc(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {cn.notes && (
            <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:16 }}>
              <p style={{ fontSize:11, color:T.text3, margin:'0 0 4px', fontWeight:600, textTransform:'uppercase' }}>Notes</p>
              <p style={{ fontSize:13, color:T.text2, margin:0 }}>{cn.notes}</p>
            </div>
          )}
        </div>

        {/* Summary Sidebar */}
        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20, position:'sticky', top:16 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:'0 0 16px' }}>Credit Note Summary</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:13, color:T.text2 }}>Total Items</span>
              <span style={{ fontSize:13, color:T.text }}>{items.length}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', borderTop:`2px solid ${T.border}`, paddingTop:12 }}>
              <span style={{ fontSize:15, fontWeight:800, color:T.text }}>Total Credit</span>
              <span style={{ fontSize:16, fontWeight:800, color:'#f97316' }}>{fc(cn.total_amount)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// CREDIT NOTES TAB (Practive-style)
function CreditNotesTab({ token, showConfirm, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [paymentModes, setPaymentModes] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active) : [])).catch(() => {});
  }, [token]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editCreditId, setEditCreditId] = useState(null);
  const [selectedCreditId, setSelectedCreditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortKey, setSortKey] = useState('credit_date');
  const [sortDir, setSortDir] = useState('desc');
  const onSort = makeSortToggle(sortKey, setSortKey, setSortDir);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE}/api/invoices/credit-notes/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => []),
    ]).then(([cnData, cData]) => {
      setCreditNotes(Array.isArray(cnData) ? cnData : []);
      setClients(Array.isArray(cData) ? cData : []);
      setLoading(false);
    });
  };

  useEffect(() => { fetchAll(); }, []);

  if (showNew) {
    return <NewCreditNoteForm token={token}
      onClose={() => setShowNew(false)}
      onSuccess={() => { fetchAll(); setShowNew(false); }}
      darkMode={darkMode} T={T} />;
  }
  if (editCreditId) {
    return <NewCreditNoteForm token={token} editCreditId={editCreditId}
      onClose={() => setEditCreditId(null)}
      onSuccess={() => { fetchAll(); setEditCreditId(null); }}
      darkMode={darkMode} T={T} />;
  }
  if (selectedCreditId) {
    return <CreditNoteDetail creditId={selectedCreditId} token={token}
      onClose={() => setSelectedCreditId(null)}
      onEdit={(id) => { setSelectedCreditId(null); setEditCreditId(id); }}
      onSuccess={() => { fetchAll(); setSelectedCreditId(null); }}
      darkMode={darkMode} T={T} 
      showConfirm={showConfirm}
    />;
  }

  const creatorOptions = Array.from(new Set(creditNotes.map(cn => cn.created_by_name).filter(Boolean))).sort();
  const clientGroupById = new Map(clients.map(c => [String(c.id), c.group_id ? String(c.group_id) : '']));
  const groupOptions = Array.from(new Map(clients.filter(c => c.group_id).map(c => [String(c.group_id), c.group_name])).entries());

  const filtered = applySort(creditNotes.filter(cn => {
    const matchSearch = !searchTerm ||
      cn.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cn.credit_note_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cn.original_invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchClient = !filterClient || String(cn.client_id) === String(filterClient);
    const matchGroup = !filterGroup || clientGroupById.get(String(cn.client_id)) === String(filterGroup);
    const matchCreatedBy = !filterCreatedBy || cn.created_by_name === filterCreatedBy;
    const matchDateFrom = !filterDateFrom || new Date(cn.credit_date) >= new Date(filterDateFrom);
    const matchDateTo = !filterDateTo || new Date(cn.credit_date) <= new Date(filterDateTo + 'T23:59:59');
    return matchSearch && matchClient && matchGroup && matchCreatedBy && matchDateFrom && matchDateTo;
  }), CREDIT_NOTE_SORT_COLUMNS, sortKey, sortDir);

  const totalCredited = filtered.reduce((s, cn) => s + Number(cn.total_amount || 0), 0);
  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  // Opens the browser's native print dialog on the same CreditNoteLetterhead
  // component the Billing Settings live preview renders — real text, small
  // file, user picks where "Save as PDF" goes.
  const handlePrintCreditNote = async (cn) => {
    let items = [];
    let s = {};
    try {
      const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const [cnRes, stRes] = await Promise.all([
        fetch(`${BASE_URL}/api/invoices/credit-notes/${cn.id}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${BASE_URL}/api/invoices/settings/config`,       { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      const cnData = await cnRes.json();
      items = cnData.items || [];
      s = (await stRes.json()) || {};
    } catch(e) { items = []; s = {}; }

    try {
      await printComponent(
        <CreditNoteLetterhead settings={s} creditNote={{ ...cn, items }} />,
        `Credit Note ${cn.credit_note_number || ''}`
      );
    } catch (e) {
      alert('Could not open print dialog: ' + e.message);
    }
  };

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };

  return (
    <div style={{ flex:1, minHeight:0, display:"flex", flexDirection:"column" }}>
      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:20 }}>
        {[
          { label:'Total Credit Notes', value: filtered.length,    color:'#f97316', glow:'rgba(249,115,22,0.12)' },
          { label:'Total Credited',     value: fmt(totalCredited), color:'#ef4444', glow:'rgba(239,68,68,0.12)'  },
          { label:'Avg. Credit Note',   value: filtered.length?fmt(totalCredited/filtered.length):'₹0', color:'#6366f1', glow:'rgba(99,102,241,0.12)' },
        ].map(s => (
          <div key={s.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:'16px 20px', boxShadow:`0 4px 16px ${s.glow}`, position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:-16, right:-16, width:56, height:56, borderRadius:'50%', background:s.glow, filter:'blur(12px)' }}/>
            <p style={{ fontSize:20, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
            <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <input type="text" placeholder="Search client, credit note no., invoice no..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ ...is, width:320 }} />
          <FilterButton activeCount={[filterClient,filterGroup,filterCreatedBy,filterDateFrom,filterDateTo].filter(Boolean).length} onClick={() => setShowFilters(s => !s)} darkMode={darkMode} T={T} />
          {(searchTerm||filterClient||filterGroup||filterCreatedBy||filterDateFrom||filterDateTo) && (
            <button onClick={() => { setSearchTerm(''); setFilterClient(''); setFilterGroup(''); setFilterCreatedBy(''); setFilterDateFrom(''); setFilterDateTo(''); }}
              style={{ fontSize:12, color:'#ef4444', padding:'6px 10px', borderRadius:8, border:'1px solid #fecaca', background:'none', cursor:'pointer' }}>Clear</button>
          )}
        </div>
        <button onClick={() => setShowNew(true)} style={{
          display:'flex', alignItems:'center', gap:6, padding:'8px 18px',
          borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)',
          color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer',
          boxShadow:'0 4px 12px rgba(15,31,69,0.3)',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Credit Note
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, padding:16, marginBottom:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Client</label>
              <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={is}>
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Group</label>
              <FilterSelect value={filterGroup} onChange={setFilterGroup} placeholder="All Groups" darkMode={darkMode} T={T}
                options={[['','All Groups'],...groupOptions]} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Created By</label>
              <select value={filterCreatedBy} onChange={e => setFilterCreatedBy(e.target.value)} style={is}>
                <option value="">All Users</option>
                {creatorOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>From Date</label>
              <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} style={is} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>To Date</label>
              <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} style={is} />
            </div>
          </div>
          <p style={{ fontSize:11, color:T.text3, marginTop:10 }}>{filtered.length} credit note(s) match the filters</p>
        </div>
      )}

      {/* Bulk actions bar */}
      {selectedIds.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, padding:'8px 14px', borderRadius:10, background: darkMode?'rgba(239,68,68,0.1)':'#fef2f2', border:`1px solid ${darkMode?'rgba(239,68,68,0.25)':'#fecaca'}` }}>
          <span style={{ fontSize:13, fontWeight:600, color:'#dc2626' }}>{selectedIds.length} credit note{selectedIds.length===1?'':'s'} selected</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setSelectedIds([])} style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:'transparent', color:T.text2, fontSize:12, fontWeight:600, cursor:'pointer' }}>
              Clear selection
            </button>
            <button onClick={() => setShowBulkDelete(true)} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'#dc2626', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              Delete Selected
            </button>
          </div>
        </div>
      )}
      {showBulkDelete && (
        <BulkDeleteModal token={token} entityName="Credit Note"
          items={filtered.filter(cn => selectedIds.includes(cn.id))}
          getLabel={cn => cn.credit_note_number || `Credit Note #${cn.id}`}
          buildDeleteUrl={cn => `${BASE}/api/invoices/credit-notes/${cn.id}`}
          warningText="This permanently removes the credit note. It does not restore any amount on the original invoice."
          onClose={() => setShowBulkDelete(false)}
          onDone={() => { setShowBulkDelete(false); setSelectedIds([]); fetchAll(); }}
          darkMode={darkMode} T={T} />
      )}

      {/* Table */}
      <div style={{ flex:1, minHeight:0, background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
        <div style={{ flex:1, minHeight:0, overflowX:'auto', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
            <tr>
              <th style={{ padding:'10px 12px', width:36 }}>
                <input type="checkbox"
                  checked={filtered.length > 0 && selectedIds.length === filtered.length}
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < filtered.length; }}
                  onChange={e => setSelectedIds(e.target.checked ? filtered.map(cn => cn.id) : [])}
                  style={{ cursor:'pointer' }} />
              </th>
              {CREDIT_NOTE_SORT_COLUMNS.map(col => (
                <SortableTh key={col.key} label={col.label} sortKey={col.key} activeKey={sortKey} dir={sortDir} onSort={onSort} align={col.key==='total_amount'?'right':'left'} />
              ))}
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="8" style={{ padding:'48px 16px', textAlign:'center', color:T.text3 }}>
                <p style={{ fontSize:13, fontWeight:600, color:T.text2, margin:'0 0 4px' }}>No Credit Notes</p>
                <p style={{ fontSize:11, color:T.text3, margin:0 }}>Click "+ New Credit Note" to create one</p>
              </td></tr>
            ) : filtered.map((cn, idx) => (
              <tr key={cn.id} style={{ borderBottom:`1px solid ${T.border}`, background: selectedIds.includes(cn.id) ? (darkMode?'rgba(239,68,68,0.08)':'#fef2f2') : darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                onMouseEnter={e => { if (!selectedIds.includes(cn.id)) e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = selectedIds.includes(cn.id) ? (darkMode?'rgba(239,68,68,0.08)':'#fef2f2') : darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'); }}>
                <td style={{ padding:'10px 12px' }}>
                  <input type="checkbox" checked={selectedIds.includes(cn.id)}
                    onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, cn.id] : prev.filter(id => id !== cn.id))}
                    style={{ cursor:'pointer' }} />
                </td>
                <td style={{ padding:'10px 16px', fontSize:13, color:T.text3, whiteSpace:'nowrap' }}>
                  {new Date(cn.credit_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                </td>
                <td style={{ padding:'10px 16px' }}>
                  <button onClick={() => setSelectedCreditId(cn.id)}
                    style={{ fontSize:13, fontWeight:600, color:'#f97316', background:'none', border:'none', cursor:'pointer', padding:0 }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                    onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                    {cn.credit_note_number}
                  </button>
                </td>
                <td style={{ padding:'10px 16px', fontSize:13, fontWeight:500, color:T.text }}>{cn.client_name}</td>
                <td style={{ padding:'10px 16px', fontSize:13, color:'#3b82f6' }}>{cn.original_invoice_number||'—'}</td>
                <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{cn.reason||'—'}</td>
                <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:'#dc2626' }}>{fmt(cn.total_amount)}</td>
                <td style={{ padding:'10px 16px' }}>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => handlePrintCreditNote(cn)}
                      style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, fontWeight:500, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', padding:0 }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                      onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                      Print
                    </button>
                    <button onClick={() => setEditCreditId(cn.id)}
                      style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, fontWeight:500, color:'#f97316', background:'none', border:'none', cursor:'pointer', padding:0 }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                      onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {filtered.length > 0 && (
          <div style={{ padding:'12px 16px', background:T.bg3, borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between' }}>
            <p style={{ fontSize:12, color:T.text3, margin:0 }}>{filtered.length} credit note(s)</p>
            <p style={{ fontSize:13, fontWeight:700, color:'#dc2626', margin:0 }}>Total: {fmt(totalCredited)}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// NEW CREDIT NOTE FORM (Quotation-style modal — also handles edit mode)
function NewCreditNoteForm({ token, onClose, onSuccess, editCreditId, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const isEdit = !!editCreditId;
  const [clients, setClients] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [firmSettings, setFirmSettings] = useState({});
  const [nextCnNo, setNextCnNo] = useState('');
  const [existingCnNo, setExistingCnNo] = useState('');
  const [form, setForm] = useState({
    client_id: '', original_invoice_id: '',
    credit_date: new Date().toISOString().split('T')[0],
    reason: '', billing_entity: 'Primary'
  });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;

  useEffect(() => {
    const getFY = () => {
      const now = new Date();
      const y = now.getFullYear();
      return now.getMonth() >= 3
        ? `${String(y).slice(-2)}-${String(y + 1).slice(-2)}`
        : `${String(y - 1).slice(-2)}-${String(y).slice(-2)}`;
    };

    if (isEdit) {
      Promise.all([
        fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
        fetch(`${BASE}/api/invoices/credit-notes/${editCreditId}`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]).then(([cData, cn]) => {
        setClients(Array.isArray(cData) ? cData : []);
        if (!cn) return;
        setExistingCnNo(cn.credit_note_number || '');
        setForm({
          client_id: String(cn.client_id || ''),
          original_invoice_id: cn.original_invoice_id ? String(cn.original_invoice_id) : '',
          credit_date: cn.credit_date?.split('T')[0] || new Date().toISOString().split('T')[0],
          reason: cn.reason || '',
          billing_entity: cn.billing_entity || 'Primary',
        });
        setItems((cn.items || []).map(i => ({
          particular: i.particular || '', description: i.description || '',
          amount: i.amount || 0
        })));
      });
      return;
    }

    // CREATE mode
    Promise.all([
      fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/invoices/settings/config`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
      fetch(`${BASE}/api/invoices/credit-notes/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
    ]).then(([cData, settings, allCn]) => {
      setClients(Array.isArray(cData) ? cData : []);
      setFirmSettings(settings || {});
      const prefix = settings?.credit_note_prefix || 'CN';
      const fy     = getFY();
      const list   = Array.isArray(allCn) ? allCn : [];
      let maxSerial = 0;
      list.forEach(cn => {
        const match = (cn.credit_note_number || '').match(/\/(\d+)$/);
        if (match) {
          const n = parseInt(match[1], 10);
          if (n > maxSerial) maxSerial = n;
        }
      });
      const next = String(maxSerial + 1).padStart(3, '0');
      setNextCnNo(`${prefix}/${fy}/${next}`);
    });
  }, [token, editCreditId]);

  const handleClientChange = async (clientId) => {
    setForm(f => ({ ...f, client_id: clientId, original_invoice_id: '' }));
    setClientInvoices([]);
    setItems([]);
    if (!clientId) return;
    try {
      const res = await fetch(`${BASE}/api/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const all = await res.json();
      const list = Array.isArray(all) ? all.filter(inv => String(inv.client_id) === String(clientId)) : [];
      setClientInvoices(list);
    } catch (e) { console.error(e); }
  };

  const handleInvoiceChange = (invoiceId) => {
    setForm(f => ({ ...f, original_invoice_id: invoiceId }));
    if (!invoiceId) { setItems([]); return; }
    const inv = clientInvoices.find(i => String(i.id) === String(invoiceId));
    if (!inv) return;
    // Preload items from the chosen invoice; user can edit/remove
    fetch(`${BASE}/api/invoices/${invoiceId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(full => {
      const its = (full.items || []).map(it => ({
        particular: it.particular,
        description: it.description || '',
        amount: it.net_amount
      }));
      setItems(its);
    }).catch(() => {});
  };

  const handleAddItem = () => setItems(prev => [...prev, { particular: '', description: '', amount: 0 }]);
  const handleItemChange = (idx, field, val) => setItems(prev => prev.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  const handleRemoveItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalRounded = Math.round(subtotal);
  const roundOff = totalRounded - subtotal;

  const handleSave = async () => {
    if (!form.client_id) { alert('Select a client'); return; }
    if (items.length === 0) { alert('Add at least one item'); return; }
    if (saving) return;
    setSaving(true);
    try {
      const url = isEdit
        ? `${BASE}/api/invoices/credit-notes/${editCreditId}`
        : `${BASE}/api/invoices/credit-notes`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          client_id: form.client_id,
          original_invoice_id: form.original_invoice_id || null,
          credit_date: form.credit_date,
          reason: form.reason,
          billing_entity: form.billing_entity,
          items,
          total_amount: totalRounded
        })
      });
      if (res.ok) {
        const data = await res.json();
        alert(isEdit ? 'Credit note updated' : `Credit Note ${data.credit_note_number || ''} created!`);
        onSuccess();
      } else {
        const d = await res.json();
        alert(d.message || 'Failed to save credit note');
      }
    } catch (e) { alert('Server error'); }
    finally { setSaving(false); }
  };

  const fc = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)', width:'100%', maxWidth:1000, height:'90vh' }}>

        {/* HEADER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 32px', borderBottom:`1px solid rgba(255,255,255,0.1)`, flexShrink:0, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
            </div>
            <div>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>{isEdit ? 'Edit Credit Note' : 'New Credit Note'}</h3>
              <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>
                CN No.: <strong style={{ color:'#fff' }}>{isEdit ? existingCnNo : (nextCnNo || 'Auto-generated')}</strong>
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ width:36, height:36, borderRadius:10, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* BODY */}
        <div style={{ flex:1, overflowY:'auto', padding:32, background:T.bg3, scrollbarWidth:'none' }}>

          {/* Section 1 — Credit Note Details */}
          <div style={{ marginBottom:28 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>1</div>
              <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Credit Note Details</h4>
              <div style={{ flex:1, height:1, background:T.border }}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
              <div>
                <SL>Billing Entity</SL>
                <select value={form.billing_entity} onChange={e=>setForm(f=>({...f,billing_entity:e.target.value}))} style={is}>
                  <option value="Primary">Primary</option>
                </select>
              </div>
              <div>
                <SL req>Client</SL>
                <select value={form.client_id} onChange={e=>handleClientChange(e.target.value)} style={is}>
                  <option value="">— Select Client —</option>
                  {clients.map(c=><option key={c.id} value={c.id}>{c.client_name}</option>)}
                </select>
              </div>
              <div>
                <SL>Original Invoice</SL>
                <select value={form.original_invoice_id} onChange={e=>handleInvoiceChange(e.target.value)} style={{ ...is, opacity: form.client_id?1:0.5 }} disabled={!form.client_id}>
                  <option value="">Select invoice...</option>
                  {clientInvoices.map(inv=>(
                    <option key={inv.id} value={inv.id}>{inv.invoice_number} (₹{Number(inv.total_amount).toLocaleString('en-IN')})</option>
                  ))}
                </select>
                {!form.client_id && <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Select a client first</p>}
              </div>
              <div>
                <SL req>Credit Note Date</SL>
                <input type="date" value={form.credit_date} onChange={e=>setForm(f=>({...f,credit_date:e.target.value}))} style={is} />
              </div>
              <div>
                <SL>Credit Note No.</SL>
                <input type="text" value={isEdit?existingCnNo:nextCnNo} readOnly
                  style={{ ...is, background: darkMode?'rgba(255,255,255,0.04)':T.bg3, color:T.text3, cursor:'default' }} />
                <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Auto-generated</p>
              </div>
              <div>
                <SL>Reason</SL>
                <input type="text" placeholder="Reason for credit note" value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} style={is} />
              </div>
            </div>
          </div>

          {/* Section 2 — Items */}
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>2</div>
              <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Items</h4>
              <div style={{ flex:1, height:1, background:T.border }}/>
              <button onClick={handleAddItem} style={{
                display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer',
                border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`,
                background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', color:'#3b82f6',
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Item
              </button>
            </div>

            <div style={{ border:`1px solid ${T.border}`, borderRadius:16, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.3)':'0 2px 8px rgba(15,31,69,0.06)' }}>
              {/* Table header */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 0.4fr 32px', padding:'10px 16px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                <div>Particulars / Description</div>
                <div style={{ textAlign:'right' }}>Amount (₹)</div>
                <div/>
              </div>

              {items.length === 0 ? (
                <div style={{ padding:'40px 0', textAlign:'center', background:T.bg2 }}>
                  <p style={{ fontSize:13, color:T.text3, margin:'0 0 12px' }}>No items added yet</p>
                  <button onClick={handleAddItem} style={{
                    padding:'8px 18px', borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer',
                    border:`1px solid ${darkMode?'rgba(16,185,129,0.3)':'#bbf7d0'}`,
                    background: darkMode?'rgba(16,185,129,0.1)':'#f0fdf4', color:'#16a34a',
                  }}>+ Add First Item</button>
                </div>
              ) : items.map((it,idx) => (
                <div key={idx} style={{ display:'grid', gridTemplateColumns:'1fr 0.4fr 32px', padding:'12px 16px', borderBottom: idx<items.length-1?`1px solid ${T.border}`:'none', alignItems:'start', background: darkMode&&idx%2!==0?T.bg3:'transparent', transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                  onMouseLeave={e => e.currentTarget.style.background=darkMode&&idx%2!==0?T.bg3:'transparent'}>
                  <div style={{ paddingRight:16 }}>
                    <input type="text" placeholder="Particular / Service name"
                      value={it.particular} onChange={e=>handleItemChange(idx,'particular',e.target.value)}
                      style={{ width:'100%', background:'transparent', border:'none', borderBottom:`1px solid ${T.border2}`, outline:'none', fontSize:13, fontWeight:500, color:T.text, paddingBottom:5, marginBottom:6, boxSizing:'border-box' }} />
                    <textarea rows="1" placeholder="Description (optional)"
                      value={it.description} onChange={e=>handleItemChange(idx,'description',e.target.value)}
                      style={{ width:'100%', fontSize:12, color:T.text3, border:`1px solid ${T.border}`, borderRadius:6, padding:'4px 8px', outline:'none', resize:'none', background:T.bg3, boxSizing:'border-box' }} />
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <input type="number" min="0" step="0.01" placeholder="0.00"
                      value={it.amount} onChange={e=>handleItemChange(idx,'amount',e.target.value)}
                      style={{ width:'100%', background:'transparent', border:'none', borderBottom:`1px solid ${T.border2}`, outline:'none', fontSize:14, fontWeight:600, color:T.text, textAlign:'right', paddingBottom:3, boxSizing:'border-box' }} />
                  </div>
                  <div style={{ display:'flex', justifyContent:'center', paddingTop:4 }}>
                    <button onClick={()=>handleRemoveItem(idx)}
                      style={{ width:26, height:26, borderRadius:6, border:'none', background:'none', cursor:'pointer', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center' }}
                      onMouseEnter={e => { e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'; e.currentTarget.style.color='#ef4444'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='none'; e.currentTarget.style.color=T.text3; }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                </div>
              ))}

              {/* Totals */}
              {items.length > 0 && (
                <div style={{ background:T.bg3, borderTop:`2px solid ${T.border}`, padding:'14px 16px' }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, maxWidth:280, marginLeft:'auto' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', width:'100%', fontSize:13, color:T.text2 }}>
                      <span>Subtotal</span>
                      <span>{fc(subtotal)}</span>
                    </div>
                    {Math.abs(roundOff) > 0.001 && (
                      <div style={{ display:'flex', justifyContent:'space-between', width:'100%', fontSize:12, color:T.text3 }}>
                        <span>Round Off</span>
                        <span>{roundOff>0?'+':''}{fc(roundOff)}</span>
                      </div>
                    )}
                    <div style={{ display:'flex', justifyContent:'space-between', width:'100%', fontSize:15, fontWeight:800, color:T.text, borderTop:`1px solid ${T.border}`, paddingTop:10 }}>
                      <span>Total Amount</span>
                      <span style={{ color:'#dc2626' }}>{fc(totalRounded)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 32px', borderTop:`1px solid ${T.border}`, background:T.bg2, flexShrink:0, borderRadius:'0 0 20px 20px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'#ef4444' }}/>
            <span style={{ fontSize:13, color:T.text3 }}>Total Credit:</span>
            <span style={{ fontSize:17, fontWeight:800, color:'#dc2626' }}>{fc(totalRounded)}</span>
          </div>
          <div style={{ display:'flex', gap:12 }}>
            <button onClick={onClose} style={{
              padding:'10px 20px', borderRadius:10, border:`1px solid ${T.border2}`,
              background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.background=T.bg3}
            onMouseLeave={e => e.currentTarget.style.background=T.bg2}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving||items.length===0||!form.client_id} style={{
              padding:'10px 28px', borderRadius:10, border:'none',
              background: saving||items.length===0||!form.client_id ? '#94a3b8' : 'linear-gradient(135deg,#0f1f45,#1a3a6b)',
              color:'#fff', fontSize:13, fontWeight:600, cursor: saving||items.length===0||!form.client_id?'not-allowed':'pointer',
              display:'flex', alignItems:'center', gap:8,
              boxShadow: saving||items.length===0||!form.client_id?'none':'0 4px 12px rgba(15,31,69,0.3)',
            }}>
              {saving && <span style={{ width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving ? 'Saving...' : isEdit ? 'Update Credit Note' : 'Save Credit Note'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// EDIT INVOICE (Draft only — loads existing, calls PUT /:id)
function EditInvoice({ token, invoiceId, onClose, onSuccess, darkMode = false, T: theme }) {
  const T = theme || { bg:'#f4f7ff', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [invoice, setInvoice] = useState(null);
  const [clients, setClients] = useState([]);
  const [firmSettings, setFirmSettings] = useState({});
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/invoices/${invoiceId}`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE}/api/invoices/settings/config`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([inv, c, s]) => {
      setInvoice(inv);
      setClients(Array.isArray(c) ? c : []);
      setFirmSettings(s || {});
      setForm({
        client_id: inv.client_id ? String(inv.client_id) : '',
        invoice_date: inv.invoice_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        due_date: inv.due_date?.split('T')[0] || '',
        payment_terms: inv.payment_terms || 'due_on_receipt',
        discount_type: inv.discount_type || 'none',
        discount_value: inv.discount_value || 0,
        gst_type: inv.gst_type || 'cgst_sgst',
        remarks: inv.remarks || '',
      });
      setItems((inv.items || []).map(i => ({
        task_id: i.task_id, service_id: i.service_id, expense_id: i.expense_id || null,
        particular: i.particular, description: i.description || '',
        sac_code: i.sac_code || '', quantity: i.quantity,
        rate: i.rate, discount_type: i.discount_type || 'none',
        discount_value: i.discount_value || 0
      })));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [invoiceId, token]);

  // Calc helpers — identical to CreateInvoice
  const calculateItemAmount = (item) => Number(item.quantity || 1) * Number(item.rate || 0);
  const calculateItemDiscount = (item) => {
    const amt = calculateItemAmount(item);
    if (item.discount_type === 'percentage') return amt * Number(item.discount_value || 0) / 100;
    if (item.discount_type === 'fixed') return Number(item.discount_value || 0);
    return 0;
  };
  const calculateItemNet = (item) => calculateItemAmount(item) - calculateItemDiscount(item);

  // Same gross-subtotal / floor-enforced-discount presentation as CreateInvoice.
  const subtotal = items.reduce((sum, item) => sum + calculateItemAmount(item), 0);
  const itemDiscountTotal = items.reduce((sum, item) => sum + calculateItemDiscount(item), 0);
  const aggregateDiscountInput = form.discount_type === 'percentage'
    ? subtotal * Number(form.discount_value || 0) / 100
    : Number(form.discount_value || 0);
  const aggregateDiscount = Math.max(aggregateDiscountInput, itemDiscountTotal);
  const taxableAmount = subtotal - aggregateDiscount;

  useEffect(() => {
    if (Number(form.discount_value || 0) < itemDiscountTotal) {
      setForm(f => ({ ...f, discount_type: 'fixed', discount_value: itemDiscountTotal }));
    }
  }, [itemDiscountTotal]);

  const isGst = firmSettings.is_gst_registered !== false && form.gst_type !== 'none';
  const cgstAmount = (isGst && form.gst_type === 'cgst_sgst') ? taxableAmount * 9 / 100 : 0;
  const sgstAmount = (isGst && form.gst_type === 'cgst_sgst') ? taxableAmount * 9 / 100 : 0;
  const igstAmount = (isGst && form.gst_type === 'igst') ? taxableAmount * 18 / 100 : 0;
  const totalGst = cgstAmount + sgstAmount + igstAmount;
  const totalBeforeRound = taxableAmount + totalGst;
  const roundOff = Math.round(totalBeforeRound) - totalBeforeRound;
  const totalAmount = Math.round(totalBeforeRound);

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };
  const handleAddManualItem = () => {
    setItems(prev => [...prev, {
      task_id: null, service_id: null,
      particular: '', description: '', sac_code: '',
      quantity: 1, rate: 0, discount_type: 'none', discount_value: 0
    }]);
  };
  const handleRemoveItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (saving || items.length === 0) { alert('Add at least one item'); return; }
    setSaving(true);
    try {
      const task_ids = items.filter(i => i.task_id).map(i => i.task_id);
      const payload = {
        ...form,
        items: items.map(item => ({
          ...item,
          amount: calculateItemAmount(item),
          discount_amount: calculateItemDiscount(item),
          net_amount: calculateItemNet(item)
        })),
        task_ids
      };
      const res = await fetch(`${BASE}/api/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) onSuccess();
      else { const d = await res.json(); alert(d.message || 'Save failed'); }
    } catch (e) { alert('Save failed'); }
    finally { setSaving(false); }
  };

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  if (loading) return <div style={{ textAlign:'center', padding:'48px 0', color:T.text3 }}>Loading invoice...</div>;
  if (!invoice) return <div style={{ textAlign:'center', padding:'48px 0', color:'#ef4444' }}>Invoice not found</div>;

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;

  // Changing the client is safe up until a real Receipt has actually been
  // settled against this invoice — after that, the Receipt's own client_id
  // and the Sundry Debtors ledger it posted to are already locked in, and
  // silently re-pointing the invoice would desync them (the backend also
  // enforces this, this is just the same rule surfaced in the UI).
  const clientLocked = Number(invoice.amount_paid || 0) > 0;
  const selectedClient = clients.find(c => String(c.id) === String(form.client_id));
  const filteredClients = clients.filter(c => !clientSearch ||
    c.client_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.file_number?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.pan?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.group_name?.toLowerCase().includes(clientSearch.toLowerCase()));
  const clientDisambigLine = (c) => [
    c.group_name ? `Group: ${c.group_name}` : null,
    c.file_number ? `File No: ${c.file_number}` : (c.pan ? `PAN: ${c.pan}` : null),
  ].filter(Boolean).join(' · ');

  return (
    <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:16, paddingRight:4, scrollbarWidth:'none' }}>
      {/* Breadcrumb */}
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <button onClick={onClose} style={{ display:'flex', alignItems:'center', gap:6, border:'none', background:'none', cursor:'pointer', color:'#3b82f6', fontSize:13, fontWeight:500, padding:0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M5 12l7 7M5 12l7-7"/></svg>
          Invoices
        </button>
        <span style={{ color:T.text3 }}>›</span>
        <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Edit Invoice — {invoice.invoice_number}</span>
      </div>

      {/* Title + Save row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#0f1f45' }}>Edit Invoice</h2>
          <p className="text-xs text-gray-400 mt-0.5">Client: {selectedClient?.client_name || invoice.client_name}</p>
        </div>
        <button onClick={handleSave} disabled={saving || items.length === 0}
          className="px-6 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
          style={{ background: '#0f1f45' }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left — Form */}
        <div className="col-span-2 space-y-4">

          {/* Invoice Details */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-gray-700 mb-4">Invoice Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2" style={{ position:'relative' }}>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
                {clientLocked ? (
                  <>
                    <input type="text" value={invoice.client_name} readOnly
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500" />
                    <p className="text-xs text-gray-400 mt-1">Client can't be changed — this invoice already has payment(s) recorded against it. Cancel it and issue a new invoice to the correct client instead.</p>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => { setClientSearch(''); setShowClientPicker(p => !p); }}
                      style={{ ...is, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
                      <span style={{ color: selectedClient ? T.text : T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedClient?.client_name || 'Select client...'}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginLeft:6, transform: showClientPicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {selectedClient && clientDisambigLine(selectedClient) && (
                      <p className="text-xs text-gray-400 mt-1">{clientDisambigLine(selectedClient)}</p>
                    )}
                    {showClientPicker && (
                      <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, width:'100%' }}
                        onMouseLeave={() => setShowClientPicker(false)}>
                        <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
                          <input type="text" autoFocus placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                            style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                        </div>
                        <div style={{ maxHeight:220, overflowY:'auto' }}>
                          {filteredClients.length === 0 ? (
                            <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No clients found</p>
                          ) : filteredClients.map(c => {
                            const sel = String(form.client_id) === String(c.id);
                            return (
                              <button key={c.id} type="button" onClick={() => { setForm(f => ({ ...f, client_id: String(c.id) })); setShowClientPicker(false); }}
                                style={{ width:'100%', textAlign:'left', padding:'8px 14px', border:'none', cursor:'pointer', background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent' }}
                                onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                                onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                                <div style={{ fontSize:13, fontWeight:500, color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text }}>{c.client_name}</div>
                                {clientDisambigLine(c) && <div style={{ fontSize:10.5, color:T.text3, marginTop:1 }}>{clientDisambigLine(c)}</div>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Date</label>
                <input type="date" value={form.invoice_date || ''}
                  onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
                <input type="date" value={form.due_date || ''}
                  onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Terms</label>
                <select value={form.payment_terms}
                  onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} className={ic}>
                  <option value="due_on_receipt">Due on Receipt</option>
                  <option value="net_15">Net 15 days</option>
                  <option value="net_30">Net 30 days</option>
                  <option value="net_60">Net 60 days</option>
                </select>
              </div>
              {firmSettings.is_gst_registered !== false && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">GST Type</label>
                  <select value={form.gst_type}
                    onChange={e => setForm(f => ({ ...f, gst_type: e.target.value }))} className={ic}>
                    <option value="cgst_sgst">CGST + SGST (Same State)</option>
                    <option value="igst">IGST (Different State)</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Items */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">Invoice Items</h3>
              <button onClick={handleAddManualItem}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs hover:bg-gray-50">
                + Add Item
              </button>
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <p className="text-sm">Click "+ Add Item" to add invoice line</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                    <tr>
                      <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', width:32 }}>#</th>
                      <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff' }}>Particular & Description</th>
                      <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', width:64 }}>SAC</th>
                      <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', width:64 }}>Qty</th>
                      <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', width:96 }}>Rate (₹)</th>
                      <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', width:80 }}>Amt (₹)</th>
                      <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', width:96 }}>Discount</th>
                      <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', width:96 }}>Net (₹)</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs text-gray-400">{index + 1}</td>
                        <td className="px-3 py-2">
                          <input type="text" placeholder="Service name"
                            value={item.particular}
                            onChange={e => handleItemChange(index, 'particular', e.target.value)}
                            className="w-full border-0 border-b border-gray-200 text-sm font-medium text-gray-800 focus:outline-none focus:border-blue-500 pb-0.5 mb-1" />
                          <textarea rows="2" placeholder="Description"
                            value={item.description}
                            onChange={e => handleItemChange(index, 'description', e.target.value)}
                            className="w-full text-xs text-gray-500 border border-gray-200 rounded p-1 focus:outline-none focus:border-blue-400 resize-none mt-1" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" placeholder="SAC"
                            value={item.sac_code}
                            onChange={e => handleItemChange(index, 'sac_code', e.target.value)}
                            className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" min="1" step="0.5"
                            value={item.quantity}
                            onChange={e => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none text-center" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" min="0"
                            value={item.rate}
                            onChange={e => handleItemChange(index, 'rate', e.target.value)}
                            className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none" />
                        </td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-800">
                          {Number(calculateItemAmount(item)).toLocaleString('en-IN')}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <select value={item.discount_type}
                              onChange={e => handleItemChange(index, 'discount_type', e.target.value)}
                              className="border border-gray-200 rounded px-1 py-1 text-xs focus:outline-none w-12">
                              <option value="none">-</option>
                              <option value="percentage">%</option>
                              <option value="fixed">₹</option>
                            </select>
                            {item.discount_type !== 'none' && (
                              <input type="number" min="0"
                                value={item.discount_value}
                                onChange={e => handleItemChange(index, 'discount_value', e.target.value)}
                                className="border border-gray-200 rounded px-1 py-1 text-xs focus:outline-none w-14" />
                            )}
                          </div>
                          {calculateItemDiscount(item) > 0 && (
                            <p className="text-xs text-red-500 mt-0.5">-₹{calculateItemDiscount(item).toLocaleString('en-IN')}</p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm font-bold text-gray-800">
                          {calculateItemNet(item).toLocaleString('en-IN')}
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => handleRemoveItem(index)}
                            className="text-red-400 hover:text-red-600 text-xs">✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Remarks */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <label className="block text-xs font-medium text-gray-600 mb-1">Remarks / Notes</label>
            <textarea rows="2" placeholder="Any notes..."
              value={form.remarks}
              onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>
        </div>

        {/* Right — Totals */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-5 shadow-sm sticky top-4">
            <h3 className="font-semibold text-gray-700 mb-4">Invoice Summary</h3>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-2">Aggregate Discount</label>
              <div className="flex gap-2">
                <select value={form.discount_type}
                  onChange={e => setForm(f => ({ ...f, discount_type: e.target.value, discount_value: 0 }))}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none flex-1">
                  <option value="none">No Discount</option>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                </select>
                {form.discount_type !== 'none' && (
                  <input type="number" min="0"
                    value={form.discount_value}
                    onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                    onBlur={() => setForm(f => ({ ...f, discount_value: Math.max(Number(f.discount_value)||0, itemDiscountTotal) }))}
                    placeholder={form.discount_type === 'percentage' ? '%' : '₹'}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none w-24" />
                )}
              </div>
              {itemDiscountTotal > 0 && (
                <p className="text-[10.5px] text-gray-400 mt-1">
                  {Number(form.discount_value||0) > itemDiscountTotal
                    ? `₹${itemDiscountTotal.toLocaleString('en-IN')} from item discounts + ₹${(aggregateDiscount-itemDiscountTotal).toLocaleString('en-IN')} extra`
                    : `Auto-summed from item-level discounts — won't go below ₹${itemDiscountTotal.toLocaleString('en-IN')}`}
                </p>
              )}
            </div>

            <div className="space-y-2 text-sm border-t pt-3">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              {aggregateDiscount > 0 && (
                <div className="flex justify-between text-red-500">
                  <span>Discount {form.discount_type === 'percentage' ? `(${form.discount_value}%)` : ''}</span>
                  <span>-₹{aggregateDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {isGst ? (
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>Taxable Amount</span>
                    <span>₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {form.gst_type === 'cgst_sgst' ? (
                    <>
                      <div className="flex justify-between text-gray-600">
                        <span>CGST @ 9%</span>
                        <span>₹{cgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>SGST @ 9%</span>
                        <span>₹{sgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-gray-600">
                      <span>IGST @ 18%</span>
                      <span>₹{igstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </>
              ) : (
                aggregateDiscount > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Net Amount</span>
                    <span>₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )
              )}
              {roundOff !== 0 && (
                <div className="flex justify-between text-gray-500 text-xs">
                  <span>Round Off</span>
                  <span>{roundOff > 0 ? '+' : ''}₹{roundOff.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-gray-800 text-base border-t pt-2">
                <span>Total Amount</span>
                <span style={{ color: '#0f1f45' }}>₹{totalAmount.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <button onClick={handleSave} disabled={saving || items.length === 0}
              className="w-full mt-4 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-50"
              style={{ background: '#0f1f45' }}>
              {saving ? 'Saving...' : '✓ Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// CLIENT LEDGER TAB
function ClientLedgerTab({ token, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const clientPickerRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (clientPickerRef.current && !clientPickerRef.current.contains(e.target)) setShowClientPicker(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selectedClientName = clients.find(c => String(c.id) === String(selectedClient))?.client_name || '';
  const filteredClients = clients.filter(c => !clientSearch ||
    c.client_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.file_number?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.pan?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.group_name?.toLowerCase().includes(clientSearch.toLowerCase()));
  const clientDisambigLine = (c) => [
    c.group_name ? `Group: ${c.group_name}` : null,
    c.file_number ? `File No: ${c.file_number}` : (c.pan ? `PAN: ${c.pan}` : null),
  ].filter(Boolean).join(' · ');

  useEffect(() => {
    fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const loadLedger = async (clientId) => {
    if (!clientId) { setLedger(null); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/invoices/ledger/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLedger(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fc = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20 }}>
        <div ref={clientPickerRef} style={{ position:'relative', flex:1, maxWidth:320 }}>
          <button type="button" onClick={() => { setClientSearch(''); setShowClientPicker(p => !p); }}
            style={{ width:'100%', border:`1px solid ${showClientPicker ? '#3b82f6' : T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', boxSizing:'border-box' }}>
            <span style={{ color: selectedClientName ? T.text : T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedClientName || '— Select Client —'}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginLeft:6, transform: showClientPicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {showClientPicker && (
            <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, width:'100%' }}>
              <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
                <div style={{ position:'relative' }}>
                  <svg style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input type="text" autoFocus placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                    style={{ width:'100%', paddingLeft:28, paddingRight:10, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                </div>
              </div>
              <div style={{ maxHeight:220, overflowY:'auto' }}>
                {filteredClients.length === 0 ? (
                  <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No clients found</p>
                ) : filteredClients.map(c => {
                  const sel = String(selectedClient) === String(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => { setSelectedClient(c.id); loadLedger(c.id); setShowClientPicker(false); }}
                      style={{ width:'100%', textAlign:'left', padding:'8px 14px', border:'none', cursor:'pointer', background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent' }}
                      onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                      onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                      <div style={{ fontSize:13, fontWeight:500, color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text }}>{c.client_name}</div>
                      {clientDisambigLine(c) && <div style={{ fontSize:10.5, color:T.text3, marginTop:1 }}>{clientDisambigLine(c)}</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {ledger && (
          <div style={{
            padding:'8px 18px', borderRadius:10, fontSize:13, fontWeight:700,
            color: ledger.closing_balance>0?'#dc2626':'#16a34a',
            background: ledger.closing_balance>0?(darkMode?'rgba(220,38,38,0.1)':'#fef2f2'):(darkMode?'rgba(22,163,74,0.1)':'#f0fdf4'),
            border:`1px solid ${ledger.closing_balance>0?(darkMode?'rgba(220,38,38,0.25)':'#fecaca'):(darkMode?'rgba(22,163,74,0.25)':'#bbf7d0')}`,
          }}>
            Closing: {fc(Math.abs(ledger.closing_balance))} {ledger.closing_balance>0?'(Outstanding)':'(NIL)'}
          </div>
        )}
      </div>

      {loading && <div style={{ textAlign:'center', padding:'48px 0', color:T.text3 }}>Loading ledger...</div>}

      {!loading && !ledger && (
        <div style={{ textAlign:'center', padding:'64px 0', color:T.text3 }}>
          <p style={{ fontSize:32, margin:'0 0 10px' }}>📒</p>
          <p style={{ fontSize:13 }}>Select a client to view their ledger</p>
        </div>
      )}

      {!loading && ledger && (
        <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
          <div style={{ padding:'12px 16px', background:T.bg3, borderBottom:`1px solid ${T.border}` }}>
            <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>{ledger.client?.client_name}</p>
            <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{ledger.client?.email||ledger.client?.mobile||''}</p>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Date','Type','Reference','Debit (₹)','Credit (₹)','Balance (₹)'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ledger.entries.length===0 ? (
                <tr><td colSpan="6" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>No transactions yet</td></tr>
              ) : ledger.entries.map((entry, idx) => {
                const typeMeta = {
                  Invoice: { c:'#2563eb', bg: darkMode?'rgba(37,99,235,0.1)':'#eff6ff' },
                  Receipt: { c:'#16a34a', bg: darkMode?'rgba(22,163,74,0.1)':'#f0fdf4' },
                };
                const tm = typeMeta[entry.type]||{ c:T.text3, bg:T.bg3 };
                return (
                  <tr key={idx} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background=darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff')}>
                    <td style={{ padding:'10px 16px', fontSize:13, color:T.text3 }}>{new Date(entry.date).toLocaleDateString('en-IN')}</td>
                    <td style={{ padding:'10px 16px' }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, color:tm.c, background:tm.bg }}>{entry.type}</span>
                    </td>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:500, color:T.text }}>{entry.reference}</td>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color: entry.debit>0?'#dc2626':T.text3 }}>{entry.debit>0?fc(entry.debit):'—'}</td>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color: entry.credit>0?'#16a34a':T.text3 }}>{entry.credit>0?fc(entry.credit):'—'}</td>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color: entry.balance>0?'#dc2626':'#16a34a' }}>{fc(Math.abs(entry.balance))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── SMART DATE PICKER ─────────────────────────────────────────────────────────
function SmartDatePicker({ label, value, onChange, placeholder = 'dd/mm/yyyy', darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value ? new Date(value).getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? new Date(value).getMonth() : new Date().getMonth());
  const [mode, setMode] = useState('day');
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  const today = new Date(); today.setHours(0,0,0,0);
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const getDays = () => {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prev = new Date(viewYear, viewMonth, 0).getDate();
    const cells = [];
    for (let i = first - 1; i >= 0; i--) cells.push({ d: prev - i, curr: false });
    for (let d = 1; d <= total; d++) cells.push({ d, curr: true });
    while (cells.length % 7 !== 0) cells.push({ d: cells.length - first - total + 1, curr: false });
    return cells;
  };
  const selectDay = (d) => {
    const m = String(viewMonth + 1).padStart(2,'0'), dd = String(d).padStart(2,'0');
    onChange(`${viewYear}-${m}-${dd}`); setOpen(false);
  };
  const yearRange = Array.from({ length: 12 }, (_, i) => viewYear - 5 + i);
  const displayVal = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';
  return (
    <div style={{ position:'relative' }} ref={ref}>
      {label && <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</label>}
      <button type="button" onClick={() => { setOpen(o => !o); setMode('day'); }}
        style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', background:T.inputBg, cursor:'pointer', outline:'none', boxSizing:'border-box' }}>
        <span style={{ color: displayVal ? T.text : T.text3 }}>{displayVal || placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.12)', zIndex:9999, width:288, overflow:'hidden' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:`1px solid ${T.border}` }}>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===0){setViewYear(y=>y-1);return 11;} return m-1; }); else if(mode==='year') setViewYear(y=>y-12); }}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text2 }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg3} onMouseLeave={e => e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div style={{ display:'flex', gap:4 }}>
              <button type="button" onClick={() => setMode(m => m==='month'?'day':'month')}
                style={{ padding:'4px 8px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:700, color:T.text, cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3} onMouseLeave={e => e.currentTarget.style.background='none'}>
                {MONTHS[viewMonth]}
              </button>
              <button type="button" onClick={() => setMode(m => m==='year'?'day':'year')}
                style={{ padding:'4px 8px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:700, color:T.text, cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3} onMouseLeave={e => e.currentTarget.style.background='none'}>
                {viewYear}
              </button>
            </div>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===11){setViewYear(y=>y+1);return 0;} return m+1; }); else if(mode==='year') setViewYear(y=>y+12); }}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text2 }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg3} onMouseLeave={e => e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
          {mode==='day' && (
            <div style={{ padding:12 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:4 }}>
                {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, color:T.text3, padding:'4px 0' }}>{d}</div>)}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
                {getDays().map((cell,i) => {
                  const date = cell.curr ? new Date(viewYear, viewMonth, cell.d) : null;
                  const isToday = date && date.getTime()===today.getTime();
                  const isSel  = date && selected && date.getTime()===selected.getTime();
                  return (
                    <button key={i} type="button" onClick={() => cell.curr && selectDay(cell.d)}
                      style={{ height:32, width:'100%', borderRadius:8, border: isToday&&!isSel?`1px solid #3b82f6`:'none',
                        background: isSel?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none',
                        color: !cell.curr?T.text3:isSel?'#fff':isToday?'#3b82f6':T.text2,
                        fontSize:12, fontWeight: isSel||isToday?700:400, cursor: cell.curr?'pointer':'default' }}
                      onMouseEnter={e => { if(cell.curr&&!isSel) e.currentTarget.style.background=T.bg3; }}
                      onMouseLeave={e => { if(!isSel) e.currentTarget.style.background='none'; }}>
                      {cell.d}
                    </button>
                  );
                })}
              </div>
              <div style={{ display:'flex', justifyContent:'center', marginTop:8, paddingTop:8, borderTop:`1px solid ${T.border}`, gap:8 }}>
                <button type="button" onClick={() => { const t=new Date(); setViewYear(t.getFullYear()); setViewMonth(t.getMonth()); onChange(t.toISOString().split('T')[0]); setOpen(false); }}
                  style={{ fontSize:12, fontWeight:500, padding:'4px 12px', borderRadius:8, border:'none', background:'none', cursor:'pointer', color:'#3b82f6' }}
                  onMouseEnter={e => e.currentTarget.style.background=T.bg3} onMouseLeave={e => e.currentTarget.style.background='none'}>
                  Today
                </button>
                {value && <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                  style={{ fontSize:12, fontWeight:500, padding:'4px 12px', borderRadius:8, border:'none', background:'none', cursor:'pointer', color:'#ef4444' }}
                  onMouseEnter={e => e.currentTarget.style.background=T.bg3} onMouseLeave={e => e.currentTarget.style.background='none'}>
                  Clear
                </button>}
              </div>
            </div>
          )}
          {mode==='month' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, padding:12 }}>
              {MONTHS.map((m,i) => (
                <button key={m} type="button" onClick={() => { setViewMonth(i); setMode('day'); }}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer',
                    background: viewMonth===i?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none',
                    color: viewMonth===i?'#fff':T.text2 }}
                  onMouseEnter={e => { if(viewMonth!==i) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e => { if(viewMonth!==i) e.currentTarget.style.background='none'; }}>
                  {m}
                </button>
              ))}
            </div>
          )}
          {mode==='year' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, padding:12 }}>
              {yearRange.map(y => (
                <button key={y} type="button" onClick={() => { setViewYear(y); setMode('day'); }}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer',
                    background: viewYear===y?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none',
                    color: viewYear===y?'#fff':T.text2 }}
                  onMouseEnter={e => { if(viewYear!==y) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e => { if(viewYear!==y) e.currentTarget.style.background='none'; }}>
                  {y}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── BUSINESS EXPENSE CATEGORY DROPDOWN — same shell as PaymentModeDropdown
// (rounded panel, checkmark on the selected row, inline "+ Add New Category")
// so creating an overhead category never requires leaving the expense form. ──
// Lightweight select + "add new" for name-keyed category lists (expense_categories
// — the Client/Task Expense category system, matched/created by plain name rather
// than the {name,value} slug shape BizCategoryDropdown expects for business
// categories). Used wherever a Client Expense needs a category picked inline.
function NamedCategorySelect({ categories, value, onChange, onCreate, darkMode = false, T: theme }) {
  const T = theme || { bg3:'#f8faff', border:'#e8edf5', text:'#0f1f45', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.name) onChange(created.name);
      setNewName(''); setAdding(false);
    } finally { setBusy(false); }
  };

  if (adding) {
    return (
      <div style={{ display:'flex', gap:6 }}>
        <input autoFocus type="text" placeholder="New category name" value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
          style={{ ...is, flex:1 }} />
        <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
          style={{ padding:'0 14px', borderRadius:9, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1 }}>
          {busy ? '…' : 'Add'}
        </button>
        <button type="button" onClick={() => { setAdding(false); setNewName(''); }}
          style={{ padding:'0 12px', borderRadius:9, border:`1px solid ${T.inputBorder}`, background:'transparent', color:T.text3, fontSize:12, cursor:'pointer' }}>✕</button>
      </div>
    );
  }

  return (
    <select value={value || ''} onChange={e => { if (e.target.value === '__add__') setAdding(true); else onChange(e.target.value); }} style={is}>
      <option value="">Select category...</option>
      {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
      <option value="__add__">+ Add New Category</option>
    </select>
  );
}

function BizCategoryDropdown({ categories, value, onChange, onCreate, onRename, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); setEditingId(null); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = categories.find(c => c.value === value);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.value) onChange(created.value);
      setNewName(''); setAdding(false); setOpen(false);
    } finally { setBusy(false); }
  };

  const submitRename = async (cat) => {
    const name = editValue.trim();
    if (!name || renaming || !onRename) { setEditingId(null); return; }
    setRenaming(true);
    try {
      await onRename(cat.id, name, cat.value);
      setEditingId(null);
    } finally { setRenaming(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38,
          transition:'border-color 0.15s', boxSizing:'border-box',
        }}>
        <span style={{ color: selected ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {selected?.name || 'Select category...'}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:60, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {categories.map(c => {
              const sel = c.value === value;
              const isEditing = editingId === (c.id || c.value);
              if (isEditing) {
                return (
                  <div key={c.id||c.value} style={{ display:'flex', gap:6, alignItems:'center', padding:'4px 6px' }}>
                    <input autoFocus type="text" value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitRename(c); } if (e.key === 'Escape') setEditingId(null); }}
                      style={{ flex:1, border:`1px solid #3b82f6`, borderRadius:8, padding:'6px 9px', fontSize:12.5, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                    <button type="button" onClick={() => submitRename(c)} disabled={renaming || !editValue.trim()} title="Save"
                      style={{ width:28, height:28, borderRadius:7, border:'none', background: renaming?'#94a3b8':'#16a34a', color:'#fff', cursor: renaming?'default':'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    </button>
                    <button type="button" onClick={() => setEditingId(null)} title="Cancel"
                      style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'transparent', color:T.text3, cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                );
              }
              return (
                <div key={c.id||c.value} style={{ display:'flex', alignItems:'center', gap:2, borderRadius:8, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <button type="button" onClick={() => { onChange(c.value); setOpen(false); }}
                    style={{ flex:1, minWidth:0, display:'flex', alignItems:'center', gap:8, padding:'8px 4px 8px 10px', border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background:'transparent' }}>
                    <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.name}</span>
                    {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg>}
                  </button>
                  {onRename && c.id && (
                    <button type="button" title="Rename category"
                      onClick={e => { e.stopPropagation(); setEditingId(c.id); setEditValue(c.name); }}
                      style={{ width:26, height:26, borderRadius:6, border:'none', background:'transparent', color:T.text3, cursor:'pointer', flexShrink:0, marginRight:4, display:'flex', alignItems:'center', justifyContent:'center' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#3b82f6'; e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.15)':'#dbeafe'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = T.text3; e.currentTarget.style.background = 'transparent'; }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="M15 5l4 4"/></svg>
                    </button>
                  )}
                </div>
              );
            })}
            {categories.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>No categories yet</p>}
          </div>

          <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            {!onCreate ? (
              <p style={{ fontSize:11, color:T.text3, padding:'8px 10px', margin:0, lineHeight:1.5 }}>
                Business Expense categories are Ledgers now — create, rename or remove them from <strong>Accounting → Chart of Accounts → Indirect Expenses</strong>.
              </p>
            ) : adding ? (
              <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                <input autoFocus type="text" value={newName} placeholder="e.g. Internet Bill"
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                  style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add New Category
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ADD BUSINESS EXPENSE MODAL ──────────────────────────────────────────────
function AddBusinessExpenseModal({ token, categories, onCreateCategory, onRenameCategory, onClose, onSuccess, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [paymentModes, setPaymentModes] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active) : [])).catch(() => {});
  }, [token]);
  const handleCreatePaymentMode = async (name) => {
    const res = await fetch(`${BASE}/api/payment-modes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name, value: name }),
    });
    const created = await res.json().catch(() => null);
    if (res.ok && created) setPaymentModes(prev => [...prev, created]);
    return created;
  };
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/bank/accounts`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setBankAccounts(Array.isArray(d) ? d.filter(a => a.is_active !== false) : [])).catch(() => {});
  }, [token]);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);
  const [clientExpenseCategories, setClientExpenseCategories] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/expense-categories`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClientExpenseCategories(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);
  const handleCreateClientExpenseCategory = async (name) => {
    const res = await fetch(`${BASE}/api/expense-categories`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name }),
    });
    const created = await res.json().catch(() => null);
    if (res.ok && created) setClientExpenseCategories(prev => [...prev, created]);
    return created;
  };

  const [form, setForm] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    category: '', client_id: '', amount: '', payee: '', mode: '', bank_account_id: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isClientExpense = !!form.client_id;

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;

  const handleSave = async () => {
    setError('');
    if (!form.expense_date || !form.category || !Number(form.amount)) {
      setError('Date, Category and Amount are required.'); return;
    }
    setSaving(true);
    try {
      const url = isClientExpense ? `${BASE}/api/clients/${form.client_id}/expenses` : `${BASE}/api/business-expenses`;
      const body = isClientExpense
        ? { expense_date: form.expense_date, category: form.category, mode: form.mode, bank_account_id: form.bank_account_id || null, notes: form.notes, amount: form.amount, billing_status: 'unbilled' }
        : form;
      const res = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.message || 'Could not save expense'); setSaving(false); return; }
      onSuccess();
    } catch (e) { setError('Server error'); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,480px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'18px 18px 0 0' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Add {isClientExpense ? 'Client/Task' : 'Business'} Expense</h3>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:14 }}>
          {error && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600 }}>{error}</div>}
          <div>
            <SL>Client</SL>
            <select value={form.client_id} onChange={e=>setForm(f=>({...f,client_id:e.target.value,category:''}))} style={is}>
              <option value="">None — firm overhead expense</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
            </select>
            <p style={{ fontSize:10.5, color:T.text3, marginTop:5 }}>Pick a client to bill/track this against them (shows on their Expenses tab) instead of as firm overhead.</p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <SL req>Date</SL>
              <input type="date" value={form.expense_date} onChange={e=>setForm(f=>({...f,expense_date:e.target.value}))} style={is} />
            </div>
            <div>
              <SL req>Amount (₹)</SL>
              <input type="number" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} style={is} />
            </div>
          </div>
          <div>
            <SL req>Category</SL>
            {isClientExpense ? (
              <NamedCategorySelect categories={clientExpenseCategories} value={form.category}
                onChange={v=>setForm(f=>({...f,category:v}))} onCreate={handleCreateClientExpenseCategory}
                darkMode={darkMode} T={T} />
            ) : (
              <BizCategoryDropdown categories={categories} value={form.category}
                onChange={v=>setForm(f=>({...f,category:v}))}
                darkMode={darkMode} T={T} />
            )}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <SL req>Bank Account</SL>
              <BankAccountSelect accounts={bankAccounts} value={form.bank_account_id}
                onChange={v=>setForm(f=>({...f,bank_account_id:v}))} darkMode={darkMode} T={T} />
            </div>
            <div>
              <SL>Payment Mode</SL>
              <PaymentModeDropdown modes={paymentModes} value={form.mode}
                onChange={v=>setForm(f=>({...f,mode:v}))} onCreate={handleCreatePaymentMode}
                darkMode={darkMode} T={T} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <SL>Payee</SL>
              <input type="text" placeholder="Who was paid" value={form.payee} onChange={e=>setForm(f=>({...f,payee:e.target.value}))} style={is} />
            </div>
            <div>
              <SL>Notes</SL>
              <input type="text" placeholder="Optional note" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={is} />
            </div>
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: saving?'#94a3b8':'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor: saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving...' : 'Save Expense'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ADD OTHER INCOME MODAL ──────────────────────────────────────────────────
function AddOtherIncomeModal({ token, onClose, onSuccess, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [form, setForm] = useState({
    income_date: new Date().toISOString().split('T')[0],
    source: '', amount: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;

  const handleSave = async () => {
    setError('');
    if (!form.income_date || !form.source.trim() || !Number(form.amount)) {
      setError('Date, Source and Amount are required.'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/other-income`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.message || 'Could not save income'); setSaving(false); return; }
      onSuccess();
    } catch (e) { setError('Server error'); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,440px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'18px 18px 0 0' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Add Other Income</h3>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:14 }}>
          {error && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600 }}>{error}</div>}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <SL req>Date</SL>
              <input type="date" value={form.income_date} onChange={e=>setForm(f=>({...f,income_date:e.target.value}))} style={is} />
            </div>
            <div>
              <SL req>Amount (₹)</SL>
              <input type="number" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} style={is} />
            </div>
          </div>
          <div>
            <SL req>Source</SL>
            <input type="text" placeholder="e.g. Interest, Rebate, Misc income" value={form.source} onChange={e=>setForm(f=>({...f,source:e.target.value}))} style={is} />
          </div>
          <div>
            <SL>Notes</SL>
            <input type="text" placeholder="Optional note" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={is} />
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: saving?'#94a3b8':'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor: saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving...' : 'Save Income'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Resolves a period key + optional custom range into { from, to } ISO dates.
// Quarters follow the Indian financial year (Apr–Jun, Jul–Sep, Oct–Dec, Jan–Mar),
// matching the FY convention already used for invoice/receipt numbering.
function resolveIncomeStatementRange(period, customFrom, customTo) {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth();
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  if (period === 'month') return { from: iso(new Date(y, m, 1)), to: iso(today) };
  if (period === 'quarter') {
    const qStart = m >= 3 && m <= 5 ? 3 : m >= 6 && m <= 8 ? 6 : m >= 9 && m <= 11 ? 9 : 0;
    return { from: iso(new Date(qStart === 0 ? y : y, qStart, 1)), to: iso(today) };
  }
  if (period === 'fy') {
    const fyStartYear = m >= 3 ? y : y - 1;
    return { from: iso(new Date(fyStartYear, 3, 1)), to: iso(today) };
  }
  const fyStartYear = m >= 3 ? y : y - 1;
  return { from: customFrom || iso(new Date(fyStartYear, 3, 1)), to: customTo || iso(today) };
}

// ── IMPORT BUSINESS EXPENSES + RECEIPTS (one workbook, two sheets) ──────────
// Same shell/progress/report pattern as InvoiceImportModal / ReceiptImportModal
// (Step 1 download template → Step 2 upload → progress → report), but reads
// BOTH an "Expenses" and a "Receipts" sheet from one file and posts each row
// to its own endpoint, so a firm migrating its books from Excel doesn't have
// to run two separate imports.
//
// Each sheet has an optional Client column: when given, the row is mapped to
// that client instead of staying an internal firm-only record — an Expense
// row becomes a Client/Task Expense (shows on that client's dashboard,
// category resolved against expense_categories, the client-level list) and
// a Receipts row becomes a real (unlinked) Receipt against that client
// instead of a generic Other Income entry — the sheet is titled "Receipts",
// not "Income", because that's genuinely what a bank statement or a firm's
// books capture: money received, from a specific source or a specific
// client, not an abstract "income" line.
const EXPENSE_IMPORT_HEADER_MAP = {
  'date': 'expense_date', 'expense date': 'expense_date',
  'category': 'category', 'category*': 'category',
  'client': 'client',
  'amount': 'amount', 'amount (₹)': 'amount', 'amount (rs)': 'amount',
  'payee': 'payee', 'mode': 'mode', 'payment mode': 'mode', 'notes': 'notes',
};
const RECEIPT_IMPORT2_HEADER_MAP = {
  'date': 'income_date', 'receipt date': 'income_date', 'income date': 'income_date',
  'client': 'client',
  'source': 'source', 'source*': 'source',
  'amount': 'amount', 'amount (₹)': 'amount', 'amount (rs)': 'amount',
  'notes': 'notes',
};

function IncomeExpenseImportModal({ token, categories, onCreateCategory, onClose, onDone, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  useEffect(() => { ensureImportStyles(); }, []);
  const [expenseRows, setExpenseRows] = useState([]);
  const [incomeRows, setIncomeRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const [localCategories, setLocalCategories] = useState(categories);
  useEffect(() => { setLocalCategories(categories); }, [categories]);
  const [clientExpenseCategories, setClientExpenseCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  useEffect(() => {
    fetch(`${BASE}/api/payment-modes`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPaymentModes(Array.isArray(d) ? d.filter(m => m.is_active) : [])).catch(() => {});
    fetch(`${BASE}/api/expense-categories`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClientExpenseCategories(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);
  const fileRef = useRef(null);
  const totalRows = expenseRows.length + incomeRows.length;

  // Client-level expense categories live in a separate table (expense_categories,
  // matched/created by plain name) from Business Expense categories
  // (business_expense_categories, matched by a value slug) — an Expense row
  // with a Client picks from/creates in this list instead.
  const handleCreateClientExpenseCategory = async (name) => {
    const res = await fetch(`${BASE}/api/expense-categories`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name }),
    });
    const created = await res.json().catch(() => null);
    if (res.ok && created) setClientExpenseCategories(prev => [...prev, created]);
    return created;
  };

  const downloadTemplate = async () => {
    // Fetched fresh right here (not read from this modal's own state) so the
    // template always reflects whatever exists at the moment of download —
    // including a category someone added seconds ago in another tab, or
    // before this modal's own on-mount fetches had a chance to resolve.
    setDownloadingTemplate(true);
    const h = { 'Authorization': `Bearer ${token}` };
    const [freshCats, freshClientCats, freshModes, freshClients] = await Promise.all([
      fetch(`${BASE}/api/business-expenses/categories`, { headers: h }).then(r => r.json()).catch(() => localCategories),
      fetch(`${BASE}/api/expense-categories`, { headers: h }).then(r => r.json()).catch(() => clientExpenseCategories),
      fetch(`${BASE}/api/payment-modes`, { headers: h }).then(r => r.json()).catch(() => paymentModes),
      fetch(`${BASE}/api/clients`, { headers: h }).then(r => r.json()).catch(() => clients),
    ]);

    const wb = new ExcelJS.Workbook();
    const catNames = (Array.isArray(freshCats) ? freshCats : localCategories).map(c => c.name).filter(Boolean);
    const clientCatNames = (Array.isArray(freshClientCats) ? freshClientCats : clientExpenseCategories).map(c => c.name).filter(Boolean);
    const modeNames = (Array.isArray(freshModes) ? freshModes : paymentModes).map(m => m.name).filter(Boolean);
    const clientNames = (Array.isArray(freshClients) ? freshClients : clients).map(c => c.client_name).filter(Boolean);

    const wsExp = wb.addWorksheet('Expenses');
    wsExp.columns = [
      { header: 'Date*', key: 'expense_date', width: 14 },
      { header: 'Category*', key: 'category', width: 24 },
      { header: 'Client', key: 'client', width: 24 },
      { header: 'Amount (₹)*', key: 'amount', width: 14 },
      { header: 'Payee', key: 'payee', width: 22 },
      { header: 'Mode', key: 'mode', width: 16 },
      { header: 'Notes', key: 'notes', width: 26 },
    ];
    wsExp.getRow(1).eachCell(c => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDC2626' } }; });
    wsExp.getColumn('expense_date').numFmt = '@';
    wsExp.getCell('A1').note = 'Any common format works: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY-MM-DD.';
    wsExp.getCell('B1').note = 'Firm overhead category — used only when Client is left blank.\n' + (catNames.length
      ? `Existing: ${catNames.join(', ')}. Any other name is created automatically.`
      : 'Type any category name — it is created automatically on import.');
    wsExp.getCell('C1').note = 'Optional. Type an existing client\'s name to bill/track this expense against them — it then appears on that client\'s own Expenses tab (using their Client/Task expense categories instead) rather than as firm overhead.'
      + (clientNames.length ? `\nExisting clients: ${clientNames.slice(0, 15).join(', ')}${clientNames.length > 15 ? ', …' : ''}` : '');
    wsExp.getCell('F1').note = modeNames.length
      ? `Type the exact name of one of your firm's own Payment Modes: ${modeNames.join(', ')}. Left blank or unrecognised falls back to "${modeNames[0]}".`
      : 'Type a Payment Mode name (Settings → Payment Modes). Leave blank if not applicable.';
    wsExp.addRow({ expense_date: '02/06/2026', category: catNames[0] || 'Office Rent', client: '', amount: 25000, payee: 'Landlord', mode: modeNames[0] || 'Bank Transfer', notes: 'June office rent' });
    wsExp.addRow({ expense_date: '05/06/2026', category: clientCatNames[0] || 'Stamp Duty', client: clientNames[0] || 'ABC Private Limited', amount: 1499, payee: '', mode: '', notes: 'Client-billed expense' });

    const wsInc = wb.addWorksheet('Receipts');
    wsInc.columns = [
      { header: 'Date*', key: 'income_date', width: 14 },
      { header: 'Client', key: 'client', width: 24 },
      { header: 'Source*', key: 'source', width: 26 },
      { header: 'Amount (₹)*', key: 'amount', width: 14 },
      { header: 'Notes', key: 'notes', width: 30 },
    ];
    wsInc.getRow(1).eachCell(c => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF16A34A' } }; });
    wsInc.getColumn('income_date').numFmt = '@';
    wsInc.getCell('A1').note = 'Any common format works: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY-MM-DD.';
    wsInc.getCell('B1').note = 'Optional. Type an existing client\'s name if this money was received FROM them — it creates a real (unlinked) Receipt against that client instead of a generic Other Income entry, and shows on their ledger.'
      + (clientNames.length ? `\nExisting clients: ${clientNames.slice(0, 15).join(', ')}${clientNames.length > 15 ? ', …' : ''}` : '');
    wsInc.getCell('C1').note = 'Only used when Client is left blank — describes non-client income (bank interest, refund, asset sale, etc.).';
    wsInc.addRow({ income_date: '10/06/2026', client: '', source: 'Bank Interest', amount: 850, notes: '' });
    wsInc.addRow({ income_date: '18/06/2026', client: clientNames[0] || 'ABC Private Limited', source: '', amount: 5000, notes: 'Advance received, not yet settled against an invoice' });

    // Real Excel dropdowns (data validation), not just cell-comment hints —
    // built from whatever Categories/Clients/Payment Modes exist AT THE
    // MOMENT this template is downloaded. There's no live connection once
    // the file leaves the browser, so re-download the template any time a
    // category is added or renamed under Income Statement / Settings to
    // pick up the change here.
    const wsList = wb.addWorksheet('Lists');
    wsList.state = 'veryHidden';
    // Category column offers BOTH category systems together (Business
    // Expense categories ∪ Client/Task Expense categories) since which one
    // actually applies to a given row depends on whether that row's own
    // Client cell is filled — Excel can't branch a dropdown's source on
    // another cell's value without a much more fragile INDIRECT() setup.
    const catUnion = [...new Set([...catNames, ...clientCatNames])];
    catUnion.forEach((name, i) => { wsList.getCell(`A${i + 2}`).value = name; });
    clientNames.forEach((name, i) => { wsList.getCell(`B${i + 2}`).value = name; });
    modeNames.forEach((name, i) => { wsList.getCell(`C${i + 2}`).value = name; });

    const applyListValidation = (ws, colLetter, listColLetter, count, rowEnd = 500) => {
      const end = Math.max(2, count + 1);
      for (let r = 2; r <= rowEnd; r++) {
        ws.getCell(`${colLetter}${r}`).dataValidation = {
          type: 'list', allowBlank: true, formulae: [`Lists!$${listColLetter}$2:$${listColLetter}$${end}`],
          showErrorMessage: true, errorStyle: 'warning',
          errorTitle: 'Not in the existing list',
          error: 'That\'s fine — typing a new name here still works and creates it automatically on import.',
        };
      }
    };
    applyListValidation(wsExp, 'B', 'A', catUnion.length);   // Expenses: Category
    applyListValidation(wsExp, 'C', 'B', clientNames.length); // Expenses: Client
    applyListValidation(wsExp, 'F', 'C', modeNames.length);   // Expenses: Mode
    applyListValidation(wsInc, 'B', 'B', clientNames.length); // Receipts: Client

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'Expense_Receipt_Import_Template.xlsx';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloadingTemplate(false);
  };

  const parseSheet = (wb, sheetNameMatch, headerMap, dateField) => {
    const sheetName = wb.SheetNames.find(n => n.trim().toLowerCase() === sheetNameMatch);
    if (!sheetName) return [];
    const ws = wb.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
    return data
      .filter(row => Object.values(row).some(v => String(v).trim() !== ''))
      .map(row => {
        const out = {};
        Object.entries(row).forEach(([k, v]) => {
          const cleanLabel = k.replace(/\*/g, '').trim().toLowerCase();
          const field = headerMap[cleanLabel] || cleanLabel.replace(/\s+/g, '_');
          if (field === dateField) out[field] = toISODateLoose(v);
          else out[field] = String(v).trim();
        });
        return out;
      });
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary', cellDates: true });
      setExpenseRows(parseSheet(wb, 'expenses', EXPENSE_IMPORT_HEADER_MAP, 'expense_date'));
      // Accept a file built against the old "Income" sheet name too, so a
      // template downloaded before this rename still imports.
      const receiptsSheet = wb.SheetNames.find(n => n.trim().toLowerCase() === 'receipts') ? 'receipts' : 'income';
      setIncomeRows(parseSheet(wb, receiptsSheet, RECEIPT_IMPORT2_HEADER_MAP, 'income_date'));
      setResult(null);
    };
    reader.readAsBinaryString(file);
  };

  const handleSubmit = async () => {
    if (!totalRows) return;
    setImporting(true);
    setResult(null);
    let success = 0;
    const errors = [];
    const warnings = [];
    const catCache = new Map(localCategories.map(c => [c.name.trim().toLowerCase(), c]));
    const clientCatCache = new Map(clientExpenseCategories.map(c => [c.name.trim().toLowerCase(), c]));
    const clientCache = new Map();
    const clientsArr = [...clients];

    const tick = (i) => setProgress({ current: i + 1, total: totalRows, ok: success, fail: errors.length });
    setProgress({ current: 0, total: totalRows, ok: 0, fail: 0 });

    for (const [i, row] of expenseRows.entries()) {
      const rowNum = i + 2;
      if (!row.expense_date) { errors.push(`Expenses row ${rowNum}: Date is required or unreadable`); tick(i); continue; }
      if (!row.category) { errors.push(`Expenses row ${rowNum}: Category is required`); tick(i); continue; }
      const amount = parseFloat(row.amount);
      if (!amount || amount <= 0) { errors.push(`Expenses row ${rowNum}: Amount must be a positive number`); tick(i); continue; }

      try {
        const modeInput = (row.mode || '').trim().toLowerCase();
        const matchedMode = paymentModes.find(m =>
          m.name?.trim().toLowerCase() === modeInput || m.value?.trim().toLowerCase() === modeInput.replace(/\s+/g, '_')
        );
        if (row.mode && !matchedMode) warnings.push(`Expenses row ${rowNum}: mode "${row.mode}" doesn't match any configured Payment Mode, left blank`);

        if (row.client) {
          // Client given → a Client/Task Expense, so it shows on that
          // client's own Expenses tab — category resolved against
          // expense_categories (the client-level list), not
          // business_expense_categories.
          const client_id = await resolveOrCreateClient(row.client, token, clientCache, clientsArr);
          if (!client_id) { errors.push(`Expenses row ${rowNum}: could not resolve/create client "${row.client}"`); tick(i); continue; }

          const catKey = row.category.trim().toLowerCase();
          let cat = clientCatCache.get(catKey);
          if (!cat) {
            cat = await handleCreateClientExpenseCategory(row.category.trim());
            if (!cat) { errors.push(`Expenses row ${rowNum}: could not create category "${row.category}"`); tick(i); continue; }
            clientCatCache.set(catKey, cat);
            warnings.push(`Expenses row ${rowNum}: category "${row.category}" didn't exist for clients — created automatically`);
          }

          const res = await fetch(`${BASE}/api/clients/${client_id}/expenses`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ expense_date: row.expense_date, category: cat.name, mode: matchedMode?.value || '', notes: row.notes || '', amount, billing_status: 'unbilled' }),
          });
          const d = await res.json().catch(() => ({}));
          if (!res.ok) { errors.push(`Expenses row ${rowNum}: ${d.message || 'save failed'}`); tick(i); continue; }
        } else {
          // No client → firm overhead, a Business Expense.
          const catKey = row.category.trim().toLowerCase();
          let cat = catCache.get(catKey);
          if (!cat) {
            cat = await onCreateCategory(row.category.trim());
            if (!cat) { errors.push(`Expenses row ${rowNum}: could not create category "${row.category}"`); tick(i); continue; }
            catCache.set(catKey, cat);
            warnings.push(`Expenses row ${rowNum}: category "${row.category}" didn't exist — created automatically`);
          }

          const res = await fetch(`${BASE}/api/business-expenses`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ expense_date: row.expense_date, category: cat.value, amount, payee: row.payee || '', mode: matchedMode?.value || '', notes: row.notes || '' }),
          });
          const d = await res.json().catch(() => ({}));
          if (!res.ok) { errors.push(`Expenses row ${rowNum}: ${d.message || 'save failed'}`); tick(i); continue; }
        }
        success++;
      } catch (e) { errors.push(`Expenses row ${rowNum}: ${e.message}`); }
      tick(i);
    }

    for (const [i, row] of incomeRows.entries()) {
      const idx = expenseRows.length + i;
      const rowNum = i + 2;
      if (!row.income_date) { errors.push(`Receipts row ${rowNum}: Date is required or unreadable`); tick(idx); continue; }
      const amount = parseFloat(row.amount);
      if (!amount || amount <= 0) { errors.push(`Receipts row ${rowNum}: Amount must be a positive number`); tick(idx); continue; }

      try {
        if (row.client) {
          // Client given → a real (unlinked) Receipt against them, same as
          // Import Receipts — settle it against an invoice afterward if needed.
          const client_id = await resolveOrCreateClient(row.client, token, clientCache, clientsArr);
          if (!client_id) { errors.push(`Receipts row ${rowNum}: could not resolve/create client "${row.client}"`); tick(idx); continue; }

          const res = await fetch(`${BASE}/api/invoices/receipts/create`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              client_id, payment_date: row.income_date, received_amount: amount,
              tds_amount: 0, discount_amount: 0, mode: '', reference_number: '',
              notes: row.notes || row.source || 'Imported', settlements: [],
            }),
          });
          const d = await res.json().catch(() => ({}));
          if (!res.ok) { errors.push(`Receipts row ${rowNum}: ${d.message || 'save failed'}`); tick(idx); continue; }
        } else {
          // No client → genuine Other Income (bank interest, refund, etc.), Source is required.
          if (!row.source) { errors.push(`Receipts row ${rowNum}: Source is required when no Client is given`); tick(idx); continue; }
          const res = await fetch(`${BASE}/api/other-income`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ income_date: row.income_date, source: row.source, amount, notes: row.notes || '' }),
          });
          const d = await res.json().catch(() => ({}));
          if (!res.ok) { errors.push(`Receipts row ${rowNum}: ${d.message || 'save failed'}`); tick(idx); continue; }
        }
        success++;
      } catch (e) { errors.push(`Receipts row ${rowNum}: ${e.message}`); }
      tick(idx);
    }

    setResult({ success, errors, warnings, total: totalRows });
    setImporting(false);
    setProgress(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full shadow-2xl" style={{ maxWidth:'580px' }}>

        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold" style={{ color:'#0f1f45' }}>Import Expenses & Receipts</h3>
            <p className="text-xs text-gray-400 mt-0.5">One Excel file, two sheets — Expenses and Receipts, imported together</p>
          </div>
          <button onClick={() => { if (importing) return; onClose(); }} disabled={importing}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400 transition disabled:opacity-30 disabled:cursor-not-allowed">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="p-7 space-y-5">
          {(!importing && !result) && (
          <>
          <div className="border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center text-green-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Download Template</p>
                  <p className="text-xs text-gray-400 mt-0.5">"Expenses" and "Receipts" sheets — fill either or both, add a Client to map to their dashboard</p>
                </div>
              </div>
              <button onClick={downloadTemplate} disabled={downloadingTemplate}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-green-200 text-green-700 hover:bg-green-50 transition disabled:opacity-60">
                {downloadingTemplate
                  ? <span className="w-3.5 h-3.5 border-2 border-green-600 border-t-transparent rounded-full animate-spin"/>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}
                {downloadingTemplate ? 'Preparing…' : 'Download'}
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-800 mb-3">Upload Filled Template</p>
            <label className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition ${totalRows > 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
              {totalRows > 0 ? (
                <div className="text-center">
                  <svg className="w-8 h-8 text-blue-500 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <p className="text-sm font-semibold text-blue-700">{expenseRows.length} expense + {incomeRows.length} receipt row(s) ready</p>
                  <p className="text-xs text-blue-400 mt-0.5">{fileName} — click to change file</p>
                </div>
              ) : (
                <div className="text-center">
                  <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <p className="text-sm font-medium text-gray-500">Click to upload Excel/CSV file</p>
                  <p className="text-xs text-gray-400 mt-0.5">.xlsx, .xls or .csv</p>
                </div>
              )}
            </label>
          </div>
          </>
          )}

          {importing && progress && (() => {
            const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
            return (
              <div className="rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"/>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Importing entries</p>
                      <p className="text-xs text-gray-400">Please keep this window open</p>
                    </div>
                  </div>
                  <p className="text-lg font-extrabold" style={{ color:'#0f1f45', fontVariantNumeric:'tabular-nums' }}>
                    {progress.current}<span className="text-gray-300 font-semibold text-sm"> / {progress.total}</span>
                  </p>
                </div>
                <div style={{ height: 12, width: '100%', borderRadius: 999, background: '#eef1f6', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 999, width: `${Math.max(pct, 2)}%`,
                    background: 'linear-gradient(90deg,#0f1f45,#2a4d82)', transition: 'width 0.35s ease-out',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{
                      position: 'absolute', inset: 0,
                      backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.22) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.22) 75%, transparent 75%, transparent)',
                      backgroundSize: '28px 28px', animation: 'cx-stripe 0.7s linear infinite',
                    }} />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-4 text-xs font-semibold" style={{ fontVariantNumeric:'tabular-nums' }}>
                    <span className="text-green-600">✓ {progress.ok || 0} added</span>
                    <span className={progress.fail ? 'text-red-600' : 'text-gray-300'}>✕ {progress.fail || 0} failed</span>
                  </div>
                  <p className="text-xs font-bold text-gray-500" style={{ fontVariantNumeric:'tabular-nums' }}>{pct}%</p>
                </div>
              </div>
            );
          })()}

          {result && (
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100" style={{ background:'#f8faff' }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: result.errors.length ? '#fef2f2' : '#f0fdf4' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={result.errors.length ? '#dc2626' : '#16a34a'} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <p className="text-sm font-bold" style={{ color:'#0f1f45' }}>Import complete</p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                {[
                  { label:'Imported', value: result.success,        color:'#16a34a', bg:'#f0fdf4' },
                  { label:'Failed',   value: result.errors.length,  color:'#dc2626', bg:'#fef2f2' },
                ].map(s => (
                  <div key={s.label} className="p-4 text-center" style={{ background: s.value ? s.bg : '#fff' }}>
                    <p className="text-2xl font-extrabold" style={{ color: s.value ? s.color : '#94a3b8', fontVariantNumeric:'tabular-nums' }}>{s.value}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              {(result.errors.length > 0 || result.warnings.length > 0) && (
                <div className="border-t border-gray-100 p-4 space-y-1.5 max-h-40 overflow-y-auto bg-gray-50">
                  {result.errors.map((e, i) => (
                    <p key={'e' + i} className="text-xs text-red-600 flex gap-1.5"><span className="flex-shrink-0">✕</span><span>{e}</span></p>
                  ))}
                  {result.warnings.map((w, i) => (
                    <p key={'w' + i} className="text-xs text-gray-500 flex gap-1.5"><span className="flex-shrink-0 text-amber-500 font-bold">!</span><span>{w}</span></p>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-100 px-4 py-2.5 bg-white">
                <p className="text-xs text-gray-500">
                  {result.success > 0
                    ? `${result.success} of ${result.total} entr${result.total === 1 ? 'y' : 'ies'} imported.`
                    : 'No entries were imported.'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={() => { if (importing) return; result ? onDone() : onClose(); }} disabled={importing}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition disabled:opacity-40 disabled:cursor-not-allowed">
            {result ? 'Done' : 'Close'}
          </button>
          {totalRows > 0 && !result && (
            <button onClick={handleSubmit} disabled={importing}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
              style={{ background:'#0f1f45' }}>
              {importing && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
              {importing
                ? (progress ? `Importing… ${progress.current}/${progress.total}` : 'Importing…')
                : `Import ${totalRows} Row${totalRows === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── INCOME STATEMENT TAB ────────────────────────────────────────────────────
function IncomeStatementTab({ token, darkMode = false, T: theme }) {
  const T = theme || { bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [period, setPeriod] = useState('quarter'); // 'month' | 'quarter' | 'fy' | 'custom'
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [expenseEntries, setExpenseEntries] = useState([]);
  const [incomeEntries, setIncomeEntries] = useState([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [trendMonths, setTrendMonths] = useState(6); // 3 | 6 | 12

  const { from, to } = resolveIncomeStatementRange(period, customFrom, customTo);

  const fetchAll = (months = trendMonths) => {
    setLoading(true);
    const h = { 'Authorization': `Bearer ${token}` };
    Promise.all([
      fetch(`${BASE}/api/business-expenses/income-statement?from=${from}&to=${to}&months=${months}`, { headers: h }).then(r => r.json()).catch(() => null),
      fetch(`${BASE}/api/business-expenses/categories`, { headers: h }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/business-expenses?from=${from}&to=${to}`, { headers: h }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/other-income?from=${from}&to=${to}`, { headers: h }).then(r => r.json()).catch(() => []),
    ]).then(([stmt, cats, exps, incs]) => {
      setStatement(stmt);
      setCategories(Array.isArray(cats) ? cats : []);
      setExpenseEntries(Array.isArray(exps) ? exps : []);
      setIncomeEntries(Array.isArray(incs) ? incs : []);
      setLoading(false);
    });
  };

  useEffect(() => { fetchAll(); }, [period, customFrom, customTo]); // eslint-disable-line

  // Only the trend window changed — refetch just the statement (which carries
  // the trend), not the categories/expense/income lists that don't depend on it.
  const fetchTrend = (months) => {
    const h = { 'Authorization': `Bearer ${token}` };
    fetch(`${BASE}/api/business-expenses/income-statement?from=${from}&to=${to}&months=${months}`, { headers: h })
      .then(r => r.json()).then(stmt => setStatement(stmt)).catch(() => {});
  };
  const handleTrendMonthsChange = (m) => { setTrendMonths(m); fetchTrend(m); };

  // Same window.open()+createRoot()+window.print() pipeline as every other
  // document in this module (see utils/printComponent.js) — real, crisp,
  // small-file text via the browser's own print engine, not a rasterized
  // screenshot. Firm branding comes from the same Billing Settings every
  // Invoice/Receipt/Quotation/Credit Note PDF already uses, so the P&L looks
  // like it belongs to the same document family.
  const handleExportPDF = async () => {
    if (!statement || exporting) return;
    setExporting(true);
    try {
      const settingsRes = await fetch(`${BASE}/api/invoices/settings/config`, { headers: { 'Authorization': `Bearer ${token}` } });
      const settings = await settingsRes.json().catch(() => ({}));
      await printComponent(
        <IncomeStatementLetterhead settings={settings} statement={statement} period={{ from, to }} />,
        `Statement of Profit & Loss ${from} to ${to}`
      );
    } finally { setExporting(false); }
  };

  const handleCreateCategory = async (name) => {
    const res = await fetch(`${BASE}/api/business-expenses/categories`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name }),
    });
    const created = await res.json().catch(() => null);
    if (res.ok && created) setCategories(prev => [...prev, created]);
    return created;
  };

  const handleRenameCategory = async (id, name, value) => {
    const res = await fetch(`${BASE}/api/business-expenses/categories/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name, value, is_active: true }),
    });
    const updated = await res.json().catch(() => null);
    if (res.ok && updated) setCategories(prev => prev.map(c => c.id === updated.id ? updated : c));
    return updated;
  };

  const handleDeleteExpense = async (id) => {
    await fetch(`${BASE}/api/business-expenses/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    fetchAll();
  };
  const handleDeleteIncome = async (id) => {
    await fetch(`${BASE}/api/other-income/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    fetchAll();
  };

  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const fd = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  const catNameByValue = (v) => categories.find(c => c.value === v)?.name || v;

  if (loading && !statement) {
    return <div style={{ padding:'80px 0', textAlign:'center', color:T.text3, fontSize:13 }}>Loading income statement...</div>;
  }
  if (!statement) {
    return <div style={{ padding:'80px 0', textAlign:'center', color:'#ef4444', fontSize:13 }}>Could not load the income statement.</div>;
  }

  const maxCatAmount = Math.max(1, ...statement.expenses.by_category.map(c => c.amount));
  const incomeRows = [
    { name: 'Invoiced Revenue', amount: statement.income.invoiced_revenue },
    { name: 'Other Income', amount: statement.income.other_income },
  ];
  const maxIncomeAmount = Math.max(1, ...incomeRows.map(r => r.amount));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16, paddingBottom:24 }}>

      {showAddExpense && (
        <AddBusinessExpenseModal token={token} categories={categories} onCreateCategory={handleCreateCategory} onRenameCategory={handleRenameCategory}
          onClose={() => setShowAddExpense(false)}
          onSuccess={() => { setShowAddExpense(false); fetchAll(); }}
          darkMode={darkMode} T={T} />
      )}
      {showAddIncome && (
        <AddOtherIncomeModal token={token}
          onClose={() => setShowAddIncome(false)}
          onSuccess={() => { setShowAddIncome(false); fetchAll(); }}
          darkMode={darkMode} T={T} />
      )}
      {showImport && (
        <IncomeExpenseImportModal token={token} categories={categories} onCreateCategory={handleCreateCategory}
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); fetchAll(); }}
          darkMode={darkMode} T={T} />
      )}

      {/* Header: period + actions */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div style={{ display:'flex', borderRadius:9, border:`1px solid ${T.border2}`, overflow:'hidden' }}>
            {[['month','This Month'],['quarter','This Quarter'],['fy','This FY'],['custom','Custom']].map(([k,l]) => (
              <button key={k} onClick={() => setPeriod(k)} style={{
                padding:'8px 14px', fontSize:12.5, fontWeight:600, cursor:'pointer', border:'none', borderRight:`1px solid ${T.border2}`,
                background: period===k ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg2, color: period===k ? '#fff' : T.text2,
              }}>{l}</button>
            ))}
          </div>
          {period === 'custom' ? (
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <input type="date" value={customFrom} onChange={e=>setCustomFrom(e.target.value)} style={{ border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.inputBg, color:T.text }} />
              <span style={{ color:T.text3, fontSize:12 }}>to</span>
              <input type="date" value={customTo} onChange={e=>setCustomTo(e.target.value)} style={{ border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.inputBg, color:T.text }} />
            </div>
          ) : (
            <span style={{ fontSize:12, color:T.text3, display:'flex', alignItems:'center', gap:6 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {fd(from)} – {fd(to)}
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button onClick={() => setShowImport(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import
          </button>
          <button onClick={handleExportPDF} disabled={exporting} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor: exporting?'default':'pointer', opacity: exporting?0.6:1 }}>
            {exporting
              ? <span style={{ width:13, height:13, border:'2px solid #94a3b8', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block' }} className="animate-spin" />
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}
            {exporting ? 'Preparing…' : 'Export Statement'}
          </button>
          <button onClick={() => setShowAddIncome(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:'#16a34a', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Income
          </button>
          <button onClick={() => setShowAddExpense(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Expense
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
        {[
          { label:'Total Income', value: fc(statement.income.total), color:'#16a34a', glow:'rgba(16,185,129,0.12)', sub:`${fc(statement.income.invoiced_revenue)} invoiced + ${fc(statement.income.other_income)} other` },
          { label:'Total Expenses', value: fc(statement.expenses.total), color:'#dc2626', glow:'rgba(239,68,68,0.12)', sub:`${fc(statement.expenses.client_expenses)} client costs + ${fc(statement.expenses.business_expenses)} overhead` },
          { label:'Net Profit', value: fc(statement.net_profit), color: statement.net_profit>=0?'#4338ca':'#dc2626', glow:'rgba(99,102,241,0.12)', sub: statement.net_profit>=0 ? 'Profitable this period' : 'Loss this period' },
          { label:'Profit Margin', value: `${statement.margin.toFixed(1)}%`, color:'#f97316', glow:'rgba(249,115,22,0.12)', sub: statement.income.total>0 ? `Of every ₹100 earned, ₹${statement.margin.toFixed(0)} kept` : 'No income recorded yet' },
        ].map(s => (
          <div key={s.label} style={{ position:'relative', overflow:'hidden', background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, padding:'16px 20px', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
            <div style={{ position:'absolute', top:-18, right:-18, width:60, height:60, borderRadius:'50%', background:s.glow, filter:'blur(14px)' }}/>
            <p style={{ fontSize:11, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', color:T.text3, margin:'0 0 8px' }}>{s.label}</p>
            <p style={{ fontSize:22, fontWeight:800, margin:0, color:s.color, fontVariantNumeric:'tabular-nums' }}>{s.value}</p>
            <p style={{ fontSize:11, color:T.text3, marginTop:6 }}>{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Trend chart — Bar (Income/Expenses) + Line (Net Profit) together in
          one ComposedChart, as it was originally. */}
      <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, padding:20, boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:10 }}>
          <h3 style={{ fontSize:13, fontWeight:700, color:T.text, margin:0 }}>Trend</h3>
          <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
            <div style={{ display:'flex', borderRadius:8, border:`1px solid ${T.border2}`, overflow:'hidden' }}>
              {[[3,'3M'],[6,'6M'],[12,'12M']].map(([m,l]) => (
                <button key={m} onClick={() => handleTrendMonthsChange(m)} style={{
                  padding:'5px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer', border:'none', borderRight: m!==12?`1px solid ${T.border2}`:'none',
                  background: trendMonths===m ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg2, color: trendMonths===m ? '#fff' : T.text2,
                }}>{l}</button>
              ))}
            </div>
            <div style={{ display:'flex', gap:16, fontSize:12, color:T.text2 }}>
              <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:9, height:9, borderRadius:3, background:'#16a34a' }}/>Income</span>
              <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:9, height:9, borderRadius:3, background:'#dc2626' }}/>Expenses</span>
              <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:9, height:9, borderRadius:3, background:'#4338ca' }}/>Net Profit</span>
            </div>
          </div>
        </div>
        <div style={{ height:260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={statement.trend} margin={{ top:8, right:8, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize:11, fill:T.text3 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:11, fill:T.text3 }} axisLine={false} tickLine={false} tickFormatter={(v)=>`₹${Math.round(v/1000)}k`} width={54} />
              <Tooltip formatter={(v)=>fc(v)} contentStyle={{ background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:10, fontSize:12 }} labelStyle={{ color:T.text, fontWeight:700 }} />
              <Bar dataKey="income" name="Income" fill="#16a34a" radius={[6,6,0,0]} barSize={20} />
              <Bar dataKey="expenses" name="Expenses" fill="#dc2626" radius={[6,6,0,0]} barSize={20} />
              <Line type="monotone" dataKey="net" name="Net Profit" stroke="#4338ca" strokeWidth={2.5} dot={{ r:4, fill:'#4338ca' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Income vs Expense breakdown — moved below the trend chart */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
            <h3 style={{ fontSize:13, fontWeight:700, color:T.text, margin:0, display:'flex', alignItems:'center', gap:8 }}><span style={{ width:8, height:8, borderRadius:'50%', background:'#16a34a' }}/>Income</h3>
            <span style={{ fontSize:14, fontWeight:800, color:'#16a34a' }}>{fc(statement.income.total)}</span>
          </div>
          <div style={{ padding:'6px 20px 16px' }}>
            {incomeRows.map(r => (
              <div key={r.name} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:`1px dashed ${T.border}` }}>
                <span style={{ flex:'0 0 130px', fontSize:12.5, fontWeight:600, color:T.text2 }}>{r.name}</span>
                <span style={{ flex:1, height:8, background:T.bg3, borderRadius:6, overflow:'hidden' }}>
                  <span style={{ display:'block', height:'100%', borderRadius:6, width:`${Math.max(2,(r.amount/maxIncomeAmount)*100)}%`, background:'linear-gradient(90deg,#16a34a,#22c55e)' }}/>
                </span>
                <span style={{ flex:'0 0 auto', fontSize:12.5, fontWeight:700, color:T.text, minWidth:90, textAlign:'right' }}>{fc(r.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
            <h3 style={{ fontSize:13, fontWeight:700, color:T.text, margin:0, display:'flex', alignItems:'center', gap:8 }}><span style={{ width:8, height:8, borderRadius:'50%', background:'#dc2626' }}/>Expenses</h3>
            <span style={{ fontSize:14, fontWeight:800, color:'#dc2626' }}>{fc(statement.expenses.total)}</span>
          </div>
          <div style={{ padding:'6px 20px 16px', maxHeight:240, overflowY:'auto' }}>
            {statement.expenses.by_category.length === 0 ? (
              <p style={{ fontSize:12, color:T.text3, textAlign:'center', padding:'16px 0' }}>No expenses in this period</p>
            ) : statement.expenses.by_category.map(c => (
              <div key={c.value} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:`1px dashed ${T.border}` }}>
                <span style={{ flex:'0 0 130px', fontSize:12.5, fontWeight:600, color:T.text2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.category}</span>
                <span style={{ flex:1, height:8, background:T.bg3, borderRadius:6, overflow:'hidden' }}>
                  <span style={{ display:'block', height:'100%', borderRadius:6, width:`${Math.max(2,(c.amount/maxCatAmount)*100)}%`, background:'linear-gradient(90deg,#dc2626,#f87171)' }}/>
                </span>
                <span style={{ flex:'0 0 auto', fontSize:12.5, fontWeight:700, color:T.text, minWidth:90, textAlign:'right' }}>{fc(c.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Category table */}
      <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
            <tr>
              {['Category','Type','Amount (₹)','Share'].map((h,i) => (
                <th key={h} style={{ padding:'11px 18px', textAlign: i>=2?'right':'left', fontSize:10.5, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statement.expenses.by_category.map(c => (
              <tr key={c.value} style={{ borderBottom:`1px solid ${T.border}` }}>
                <td style={{ padding:'11px 18px', fontSize:12.5, fontWeight:600, color:T.text }}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}>
                    <span style={{ width:9, height:9, borderRadius:3, background: c.type==='direct'?'#2563eb':'#c2410c' }}/>
                    {c.category}
                  </span>
                </td>
                <td style={{ padding:'11px 18px' }}>
                  <span style={{ fontSize:10.5, fontWeight:700, padding:'2px 9px', borderRadius:20, background: c.type==='direct'?(darkMode?'rgba(37,99,235,0.18)':'#eff6ff'):(darkMode?'rgba(194,65,12,0.2)':'#fff7ed'), color: c.type==='direct'?'#2563eb':'#c2410c' }}>
                    {c.type==='direct' ? 'Direct' : 'Overhead'}
                  </span>
                </td>
                <td style={{ padding:'11px 18px', textAlign:'right', fontSize:12.5, color:T.text2 }}>{fc(c.amount)}</td>
                <td style={{ padding:'11px 18px', textAlign:'right', fontSize:12.5, color:T.text3 }}>{statement.expenses.total>0 ? `${((c.amount/statement.expenses.total)*100).toFixed(1)}%` : '—'}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={4} style={{ padding:'10px 18px 16px' }}>
                <button onClick={() => setShowAddExpense(true)} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:700, color:'#f97316', background:'none', border:'1px dashed #fdba74', borderRadius:9, padding:'7px 12px', cursor:'pointer' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add New Cost Category
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 18px', background:T.bg3, borderTop:`1px solid ${T.border}` }}>
          <span style={{ fontSize:12.5, fontWeight:700, color:T.text2 }}>Total Expenses</span>
          <span style={{ fontSize:15, fontWeight:800, color:'#dc2626' }}>{fc(statement.expenses.total)}</span>
        </div>
      </div>

      {/* Recent entries — Business Expenses & Other Income, editable */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
          <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
            <h3 style={{ fontSize:13, fontWeight:700, color:T.text, margin:0 }}>Recent Business Expenses</h3>
          </div>
          <div style={{ maxHeight:260, overflowY:'auto' }}>
            {expenseEntries.length === 0 ? (
              <p style={{ fontSize:12, color:T.text3, textAlign:'center', padding:'24px 0' }}>No entries in this period yet</p>
            ) : expenseEntries.map(e => (
              <div key={e.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 20px', borderBottom:`1px solid ${T.border}` }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:12.5, fontWeight:600, color:T.text, margin:0 }}>{catNameByValue(e.category)}{e.payee ? ` — ${e.payee}` : ''}</p>
                  <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{fd(e.expense_date)}</p>
                </div>
                <span style={{ fontSize:12.5, fontWeight:700, color:'#dc2626', flexShrink:0 }}>{fc(e.amount)}</span>
                <button onClick={() => handleDeleteExpense(e.id)} title="Delete" style={{ width:26, height:26, borderRadius:7, border:'none', background:'none', color:T.text3, cursor:'pointer', flexShrink:0 }}
                  onMouseEnter={ev=>{ev.currentTarget.style.background=darkMode?'rgba(239,68,68,0.15)':'#fef2f2'; ev.currentTarget.style.color='#ef4444';}}
                  onMouseLeave={ev=>{ev.currentTarget.style.background='none'; ev.currentTarget.style.color=T.text3;}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin:'0 auto' }}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
          <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
            <h3 style={{ fontSize:13, fontWeight:700, color:T.text, margin:0 }}>Recent Other Income</h3>
          </div>
          <div style={{ maxHeight:260, overflowY:'auto' }}>
            {incomeEntries.length === 0 ? (
              <p style={{ fontSize:12, color:T.text3, textAlign:'center', padding:'24px 0' }}>No entries in this period yet</p>
            ) : incomeEntries.map(e => (
              <div key={e.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 20px', borderBottom:`1px solid ${T.border}` }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:12.5, fontWeight:600, color:T.text, margin:0 }}>{e.source}</p>
                  <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{fd(e.income_date)}</p>
                </div>
                <span style={{ fontSize:12.5, fontWeight:700, color:'#16a34a', flexShrink:0 }}>{fc(e.amount)}</span>
                <button onClick={() => handleDeleteIncome(e.id)} title="Delete" style={{ width:26, height:26, borderRadius:7, border:'none', background:'none', color:T.text3, cursor:'pointer', flexShrink:0 }}
                  onMouseEnter={ev=>{ev.currentTarget.style.background=darkMode?'rgba(239,68,68,0.15)':'#fef2f2'; ev.currentTarget.style.color='#ef4444';}}
                  onMouseLeave={ev=>{ev.currentTarget.style.background='none'; ev.currentTarget.style.color=T.text3;}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin:'0 auto' }}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p style={{ fontSize:11, color:T.text3, textAlign:'center' }}>Income counted on Invoice Date (accrual basis) · Expenses counted on Expense Date</p>
    </div>
  );
}

// ── BANK STATEMENTS — import + categorize into the Income Statement ────────
// A firm can hold more than one bank account. Each account gets its own
// imported statement rows ("bank_transactions"); every row starts unmatched
// and gets "categorized" into one of: Invoice Payment (creates a Receipt),
// Other Income, a Business Expense (with category), or Transfer/Ignore
// (no record created — internal transfers, card-settlement sweeps etc.).
// Categorizing does not duplicate any income/expense logic — it creates the
// real record through the SAME endpoints the rest of the app already uses
// (POST /api/invoices/receipts/create, /api/other-income, /api/business-
// expenses), then just remembers the link on the bank row. So a categorized
// row flows into the Income Statement automatically — no separate sync step.
const BANK_IMPORT_HEADER_MAP = {
  'value date': 'txn_date', 'date': 'txn_date', 'transaction date': 'txn_date', 'txn date': 'txn_date',
  'transaction remarks': 'description', 'narration': 'description', 'particulars': 'description', 'remarks': 'description', 'description': 'description',
  'withdrawal amt (inr)': 'withdrawal_amount', 'withdrawal amt (₹)': 'withdrawal_amount', 'withdrawal amt.': 'withdrawal_amount', 'withdrawal amount': 'withdrawal_amount', 'debit': 'withdrawal_amount', 'withdrawal': 'withdrawal_amount',
  'deposit amt (inr)': 'deposit_amount', 'deposit amt (₹)': 'deposit_amount', 'deposit amt.': 'deposit_amount', 'deposit amount': 'deposit_amount', 'credit': 'deposit_amount', 'deposit': 'deposit_amount',
  'balance (inr)': 'balance_after', 'balance (₹)': 'balance_after', 'balance': 'balance_after', 'closing balance': 'balance_after',
  'tran. id': 'external_ref', 'tran id': 'external_ref', 'transaction id': 'external_ref', 'chq./ref.no.': 'external_ref', 'cheque no.': 'external_ref', 'reference no.': 'external_ref',
  'group': 'group_name', 'ledger': 'ledger_name',
};
const BANK_HEADER_KEYWORDS = ['withdrawal', 'deposit', 'debit', 'credit', 'balance', 'narration', 'remarks', 'particulars'];

// Bank Statements tab's own account switcher — a professional searchable-
// style dropdown replacing the old native <select> + separate "+ Add Bank"
// button, with "+ Add New Bank Account" folded in as the list's own footer
// action (same shell as PaymentModeDropdown above), matching how every
// other "pick one, or add one" list in this app already works. Distinct
// from BankAccountSelect above — that one is the "Cash or a Bank Account"
// field on Receipts/Expenses and keeps its own plain <select>, untouched.
function BankAccountDropdown({ accounts, value, onChange, onAddNew, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const label = (a) => `${a.bank_name}${a.nickname ? ` — ${a.nickname}` : ''}${a.account_last4 ? ` (••${a.account_last4})` : ''}`;
  const selected = accounts.find(a => String(a.id) === String(value));

  return (
    <div ref={ref} style={{ position:'relative', minWidth:240 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'8px 12px', fontSize:13, fontWeight:600, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', boxSizing:'border-box',
          transition:'border-color 0.15s',
        }}>
        <span style={{ color: selected ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {selected ? label(selected) : (accounts.length ? 'Select bank account...' : 'No bank accounts yet')}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, minWidth:'100%', width:'max-content', maxWidth:360, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:60, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ maxHeight:240, overflowY:'auto', padding:5 }}>
            {accounts.map(a => {
              const sel = String(a.id) === String(value);
              return (
                <button key={a.id} type="button" onClick={() => { onChange(String(a.id)); setOpen(false); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{label(a)}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {accounts.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>No bank accounts yet</p>}
          </div>
          <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            <button type="button" onClick={() => { setOpen(false); onAddNew(); }}
              style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
              onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add New Bank Account
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddBankAccountModal({ token, onClose, onSuccess, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [form, setForm] = useState({ bank_name:'', nickname:'', account_name:'', account_number:'', account_type:'', ifsc_code:'', branch_area:'', upi_id:'', opening_balance:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;

  const handleSave = async () => {
    setError('');
    if (!form.bank_name.trim()) { setError('Bank name is required.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/bank/accounts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.message || 'Could not save bank account'); setSaving(false); return; }
      onSuccess(d);
    } catch (e) { setError('Server error'); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,520px)', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'18px 18px 0 0', flexShrink:0 }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Add Bank Account</h3>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:14, overflowY:'auto' }}>
          {error && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600 }}>{error}</div>}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <SL req>Bank Name</SL>
              <input type="text" placeholder="e.g. ICICI Bank" value={form.bank_name} onChange={e=>setForm(f=>({...f,bank_name:e.target.value}))} style={is} />
            </div>
            <div>
              <SL>Nickname</SL>
              <input type="text" placeholder="e.g. Current Account" value={form.nickname} onChange={e=>setForm(f=>({...f,nickname:e.target.value}))} style={is} />
            </div>
          </div>
          <div>
            <SL>Account Name</SL>
            <input type="text" placeholder="Name on the account" value={form.account_name} onChange={e=>setForm(f=>({...f,account_name:e.target.value}))} style={is} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <SL>Account No.</SL>
              <input type="text" placeholder="Full account number" value={form.account_number} onChange={e=>setForm(f=>({...f,account_number:e.target.value.replace(/\s+/g,'')}))} style={is} />
            </div>
            <div>
              <SL>Type of Account</SL>
              <select value={form.account_type} onChange={e=>setForm(f=>({...f,account_type:e.target.value}))} style={is}>
                <option value="">Select...</option>
                <option value="savings">Savings</option>
                <option value="current">Current</option>
                <option value="cc">Cash Credit (CC)</option>
                <option value="od">Overdraft (OD)</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <SL>IFSC Code</SL>
              <input type="text" placeholder="e.g. ICIC0001234" value={form.ifsc_code} onChange={e=>setForm(f=>({...f,ifsc_code:e.target.value.toUpperCase()}))} style={is} />
            </div>
            <div>
              <SL>Branch / Area</SL>
              <input type="text" placeholder="e.g. Connaught Place" value={form.branch_area} onChange={e=>setForm(f=>({...f,branch_area:e.target.value}))} style={is} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <SL>UPI ID</SL>
              <input type="text" placeholder="e.g. firm@icici" value={form.upi_id} onChange={e=>setForm(f=>({...f,upi_id:e.target.value}))} style={is} />
            </div>
            <div>
              <SL>Opening Balance (₹)</SL>
              <input type="number" step="0.01" placeholder="0.00" value={form.opening_balance} onChange={e=>setForm(f=>({...f,opening_balance:e.target.value}))} style={is} />
            </div>
          </div>
          <p style={{ fontSize:11, color:T.text3, lineHeight:1.5 }}>Once added, this bank appears as a "Bank Account" option on Receipts and Expenses — anything recorded against it shows up on this statement automatically. Opening Balance is what this account held before any transaction tracked here.</p>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}`, flexShrink:0 }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: saving?'#94a3b8':'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor: saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving...' : 'Save Bank Account'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BankImportModal({ token, accountId, onClose, onDone, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  useEffect(() => { ensureImportStyles(); }, []);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // Only offered as a fallback — most banks' own export already works as-is
  // (see BANK_IMPORT_HEADER_MAP's aliases), so this exists for a firm typing
  // up a statement by hand or reformatting a source the auto-detection can't
  // read, using the same column names/order the parser recognizes natively.
  // Group/Ledger are fetched fresh at download time (not from any state this
  // component already holds) so the dropdown always reflects the Chart of
  // Accounts as it stands right now — same "fetch fresh on click" fix used
  // elsewhere for this exact reason.
  const downloadTemplate = async () => {
    const h = { Authorization: `Bearer ${token}` };
    const [groupsRes, ledgersRes] = await Promise.all([
      fetch(`${BASE}/api/accounting/groups`, { headers: h }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/accounting/ledgers`, { headers: h }).then(r => r.json()).catch(() => []),
    ]);
    const groupNames = (Array.isArray(groupsRes) ? groupsRes : []).map(g => g.name);
    const ledgerNames = (Array.isArray(ledgersRes) ? ledgersRes : []).map(l => l.name);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Statement');
    ws.columns = [
      { header: 'Date*', key: 'txn_date', width: 14 },
      { header: 'Transaction Remarks*', key: 'description', width: 40 },
      { header: 'Withdrawal Amt (₹)', key: 'withdrawal_amount', width: 18 },
      { header: 'Deposit Amt (₹)', key: 'deposit_amount', width: 18 },
      { header: 'Balance (₹)', key: 'balance_after', width: 16 },
      { header: 'Tran. Id', key: 'external_ref', width: 20 },
      { header: 'Group', key: 'group_name', width: 20 },
      { header: 'Ledger', key: 'ledger_name', width: 24 },
    ];
    ws.getRow(1).eachCell(c => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1F45' } }; });
    ws.getColumn('txn_date').numFmt = '@';
    ws.getCell('A1').note = 'Any common format works: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY-MM-DD.';
    ws.getCell('C1').note = 'Leave blank for a row that\'s a deposit, not a withdrawal.';
    ws.getCell('D1').note = 'Leave blank for a row that\'s a withdrawal, not a deposit.';
    ws.getCell('E1').note = 'Optional — the bank-stated balance after this transaction, if you have it. Rows without it get a running balance computed from the account\'s Opening Balance instead.';
    ws.getCell('F1').note = 'Optional but recommended — a unique transaction/reference ID. Without it, re-importing an overlapping date range can\'t be told apart from a genuine duplicate transaction.';
    ws.getCell('G1').note = 'Pick from the dropdown. Optional if Ledger already exists — REQUIRED if Ledger names something new, so the new Ledger has somewhere real to be created under. Without it, a new Ledger name is ignored and the row imports unmapped.';
    ws.getCell('H1').note = 'Optional but recommended — map this row straight to a Ledger under Accounting → Chart of Accounts. Pick an existing one from the dropdown, or type a brand-new name (with Group filled in) to have it created automatically on import — reusing that same name later never creates a duplicate. A mapped row is posted as a Voucher and marked categorized automatically. Leave both blank to categorize it later from the Bank Statement tab instead.';
    ws.addRow({ txn_date: '01/06/2026', description: 'UPI/109500378323/CLIENT PAYMENT/client@upi', withdrawal_amount: '', deposit_amount: 6000, balance_after: '', external_ref: 'S1234567', group_name: 'Direct Income', ledger_name: ledgerNames.find(n => /sales|professional fees/i.test(n)) || '' });
    ws.addRow({ txn_date: '02/06/2026', description: 'NEFT-Office Rent-Landlord', withdrawal_amount: 25000, deposit_amount: '', balance_after: '', external_ref: 'S1234568', group_name: 'Indirect Expenses', ledger_name: ledgerNames.find(n => /office rent/i.test(n)) || '' });

    // Real Excel dropdowns (data validation) for Group/Ledger, built from
    // whatever exists under Chart of Accounts AT THE MOMENT this template is
    // downloaded — same veryHidden-list-sheet technique used for the
    // Category dropdown on the Income/Expense import template. Re-download
    // any time a Group/Ledger is added under Accounting to pick up the change.
    const wsList = wb.addWorksheet('Lists');
    wsList.state = 'veryHidden';
    groupNames.forEach((name, i) => { wsList.getCell(`A${i + 2}`).value = name; });
    ledgerNames.forEach((name, i) => { wsList.getCell(`B${i + 2}`).value = name; });
    const applyListValidation = (colLetter, listColLetter, count) => {
      const end = Math.max(2, count + 1);
      for (let r = 2; r <= 500; r++) {
        ws.getCell(`${colLetter}${r}`).dataValidation = {
          type: 'list', allowBlank: true, formulae: [`Lists!$${listColLetter}$2:$${listColLetter}$${end}`],
          showErrorMessage: true, errorStyle: 'warning',
          errorTitle: 'Not in the existing list',
          error: 'That\'s fine to leave, but a Ledger name that doesn\'t exactly match one in Chart of Accounts won\'t be mapped on import.',
        };
      }
    };
    applyListValidation('G', 'A', groupNames.length);
    applyListValidation('H', 'B', ledgerNames.length);

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'Bank_Statement_Import_Template.xlsx';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Reads the file as an ArrayBuffer (not the deprecated binary-string path,
  // which silently mis-reads or blanks out some real-world .xlsx exports in
  // some browsers) and wraps every step in error handling, so a file that
  // fails to parse — or parses to zero recognizable transaction rows —
  // always surfaces a visible reason instead of the dropzone just quietly
  // sitting there looking like nothing happened.
  const parseFile = (file) => {
    setFileName(file.name);
    setParseError('');
    setParsing(true);
    setRows([]);
    setResult(null);
    const reader = new FileReader();
    reader.onerror = () => { setParsing(false); setParseError('Could not read this file — it may be corrupted or in an unsupported format.'); };
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        if (!ws) throw new Error('No sheet found in this file.');
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        // Bank exports often prepend a title/opening-balance line before the
        // real header row — scan for the first row that actually looks like a
        // statement header instead of assuming row 0 is it.
        let headerRowIdx = raw.findIndex(r => r.some(cell => BANK_HEADER_KEYWORDS.some(k => String(cell).toLowerCase().includes(k))));
        if (headerRowIdx === -1) headerRowIdx = 0;
        const headers = (raw[headerRowIdx] || []).map(h => String(h).replace(/\*/g, '').trim().toLowerCase());
        const normalized = raw.slice(headerRowIdx + 1)
          .filter(r => r.some(c => String(c).trim() !== ''))
          .map(r => {
            const out = {};
            headers.forEach((h, i) => {
              const field = BANK_IMPORT_HEADER_MAP[h] || h.replace(/\s+/g, '_');
              const v = r[i];
              if (field === 'txn_date') out[field] = toISODateLoose(v);
              else if (field === 'withdrawal_amount' || field === 'deposit_amount' || field === 'balance_after') out[field] = parseFloat(v) || 0;
              else out[field] = String(v != null ? v : '').trim();
            });
            return out;
          })
          .filter(r => r.txn_date && (r.withdrawal_amount > 0 || r.deposit_amount > 0));
        setParsing(false);
        if (!normalized.length) {
          setParseError('No transaction rows recognized in this file. Check that it has Date and Withdrawal/Deposit Amount columns (see the header keywords in the note above), or download the template and match its column names.');
          return;
        }
        setRows(normalized);
      } catch (err) {
        setParsing(false);
        setParseError(err.message || 'Could not parse this file — try re-exporting it from your bank, or use the Download Template format.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    // Reset so selecting the exact same file again (e.g. after fixing it and
    // re-saving under the same name) still fires this handler — otherwise
    // the input's own value not changing means onChange never re-fires.
    e.target.value = '';
    if (!file) return;
    parseFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) parseFile(file);
  };

  const handleSubmit = async () => {
    if (!rows.length) return;
    setImporting(true);
    setResult(null);
    // Sent in batches (not one row per request, and not all rows in one
    // request) so this gets the same real, incrementing progress bar as the
    // Invoice/Receipt importers even though a bank statement can run into
    // the hundreds of rows — one giant request would leave the bar frozen
    // at 0% until the whole thing finished.
    const BATCH_SIZE = 25;
    const total = rows.length;
    let imported = 0, skipped = 0, reconciled = 0, reconciledExisting = 0, ledgerMapped = 0, ledgerCreated = 0, ledgerNeedsGroup = 0;
    const errors = [];
    setProgress({ current: 0, total, ok: 0, fail: 0 });
    try {
      for (let i = 0; i < total; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const res = await fetch(`${BASE}/api/bank/accounts/${accountId}/import`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ rows: batch }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) { errors.push(d.message || `Rows ${i + 1}-${i + batch.length}: batch failed`); }
        else { imported += d.imported || 0; skipped += d.skipped || 0; reconciled += d.reconciled || 0; reconciledExisting += d.reconciledExisting || 0; ledgerMapped += d.ledgerMapped || 0; ledgerCreated += d.ledgerCreated || 0; ledgerNeedsGroup += d.ledgerNeedsGroup || 0; }
        const current = Math.min(i + batch.length, total);
        setProgress({ current, total, ok: imported + reconciled, fail: errors.length });
      }
      setResult({ imported, skipped, reconciled, reconciledExisting, ledgerMapped, ledgerCreated, ledgerNeedsGroup, errors, total });
    } catch (e) { setResult({ error: e.message }); }
    setImporting(false);
    setProgress(null);
  };

  // Three separate pop-up screens, one at a time — file picker, then a
  // dedicated "Importing…" popup, then a dedicated "Import Complete" popup —
  // instead of all three states sharing one scrolling panel (which made the
  // progress bar look like it was buried under leftover upload UI).
  if (importing) {
    const pct = progress && progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full shadow-2xl" style={{ maxWidth:'460px' }}>
          <div className="px-7 pt-7 pb-2 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <span className="w-5 h-5 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin"/>
            </span>
            <div>
              <h3 className="text-base font-bold" style={{ color:'#0f1f45' }}>Importing transactions…</h3>
              <p className="text-xs text-gray-400 mt-0.5">Please keep this window open</p>
            </div>
          </div>
          <div className="px-7 py-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Progress</p>
              <p className="text-lg font-extrabold" style={{ color:'#0f1f45', fontVariantNumeric:'tabular-nums' }}>
                {progress?.current ?? 0}<span className="text-gray-300 font-semibold text-sm"> / {progress?.total ?? rows.length}</span>
              </p>
            </div>
            <div style={{ height: 14, width: '100%', borderRadius: 999, background: '#eef1f6', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 999, width: `${Math.max(pct, 2)}%`,
                background: 'linear-gradient(90deg,#0f1f45,#2a4d82)', transition: 'width 0.35s ease-out',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', inset: 0,
                  backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.22) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.22) 75%, transparent 75%, transparent)',
                  backgroundSize: '28px 28px', animation: 'cx-stripe 0.7s linear infinite',
                }} />
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-4 text-xs font-semibold" style={{ fontVariantNumeric:'tabular-nums' }}>
                <span className="text-green-600">✓ {progress?.ok || 0} processed</span>
                <span className={progress?.fail ? 'text-red-600' : 'text-gray-300'}>✕ {progress?.fail || 0} failed</span>
              </div>
              <p className="text-xs font-bold text-gray-500" style={{ fontVariantNumeric:'tabular-nums' }}>{pct}%</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full shadow-2xl flex flex-col" style={{ maxWidth:'540px', maxHeight:'90vh' }}>
          <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: result.error || result.errors?.length ? '#fef2f2' : '#f0fdf4' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={result.error || result.errors?.length ? '#dc2626' : '#16a34a'} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              <h3 className="text-lg font-bold" style={{ color:'#0f1f45' }}>Import Complete</h3>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400 transition flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div className="p-7 space-y-4" style={{ overflowY:'auto' }}>
            {!result.error && (
              <div className="rounded-2xl border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-3 divide-x divide-gray-100">
                  <div className="p-4 text-center" style={{ background: result.imported ? '#f0fdf4' : '#fff' }}>
                    <p className="text-2xl font-extrabold" style={{ color: result.imported ? '#16a34a' : '#94a3b8', fontVariantNumeric:'tabular-nums' }}>{result.imported}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">Imported</p>
                  </div>
                  <div className="p-4 text-center" style={{ background: result.reconciled ? '#eff6ff' : '#fff' }}>
                    <p className="text-2xl font-extrabold" style={{ color: result.reconciled ? '#2563eb' : '#94a3b8', fontVariantNumeric:'tabular-nums' }}>{result.reconciled}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">Bank-confirmed</p>
                  </div>
                  <div className="p-4 text-center">
                    <p className="text-2xl font-extrabold" style={{ color:'#94a3b8', fontVariantNumeric:'tabular-nums' }}>{result.skipped}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">Skipped (duplicate)</p>
                  </div>
                </div>
                {result.reconciledExisting > 0 && (
                  <p className="text-xs px-5 py-3 border-t border-gray-100" style={{ color:'#16a34a', background:'#f0fdf4' }}>
                    {result.reconciledExisting} row{result.reconciledExisting === 1 ? '' : 's'} matched an existing Receipt/Expense already recorded in ChanakyaX (same date &amp; amount) — reconciled against it instead of importing as a new, duplicate transaction. Made a wrong match? Unlink it from the row's Status column to undo.
                  </p>
                )}
                {result.ledgerMapped > 0 && (
                  <p className="text-xs px-5 py-3 border-t border-gray-100" style={{ color:'#2563eb', background:'#eff6ff' }}>
                    {result.ledgerMapped} row{result.ledgerMapped === 1 ? '' : 's'} mapped straight to a Ledger and posted as a Voucher — no need to Categorize those from the Bank Statement tab.
                    {result.ledgerCreated > 0 && <> {result.ledgerCreated} of those named a brand-new Ledger, auto-created under the Group given.</>}
                  </p>
                )}
                {result.ledgerNeedsGroup > 0 && (
                  <p className="text-xs px-5 py-3 border-t border-gray-100" style={{ color:'#c2410c', background:'#fff7ed' }}>
                    {result.ledgerNeedsGroup} row{result.ledgerNeedsGroup === 1 ? '' : 's'} named a Ledger that doesn't exist yet, but the Group column was blank or didn't match a real Group — a new Ledger can only be auto-created when Group is filled in too, so these imported without a mapping. Fill in Group and re-import to create it.
                  </p>
                )}
                {result.errors?.length > 0 && (
                  <div className="border-t border-gray-100 p-4 space-y-1.5 max-h-40 overflow-y-auto bg-gray-50">
                    {result.errors.map((e, i) => (
                      <p key={'e' + i} className="text-xs text-red-600 flex gap-1.5"><span className="flex-shrink-0">✕</span><span>{e}</span></p>
                    ))}
                  </div>
                )}
              </div>
            )}
            {result.error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600 font-medium">{result.error}</div>
            )}
          </div>

          <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl flex-shrink-0">
            {result.error && (
              <button onClick={() => { setResult(null); }}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition">
                Back
              </button>
            )}
            <button onClick={() => (result.error ? onClose() : onDone())}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold" style={{ background:'#0f1f45' }}>
              {result.error ? 'Close' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full shadow-2xl flex flex-col" style={{ maxWidth:'540px', maxHeight:'90vh' }}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold" style={{ color:'#0f1f45' }}>Import Bank Statement</h3>
            <p className="text-xs text-gray-400 mt-0.5">Upload the Excel/CSV export from your bank — any common format works</p>
          </div>
          <button onClick={onClose}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400 transition flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="p-7 space-y-5" style={{ overflowY:'auto' }}>
          <div className="border border-gray-200 rounded-2xl p-4 text-xs text-gray-500 leading-relaxed">
            Works with the statement exactly as your bank exports it (Date, Transaction Remarks, Withdrawal Amt, Deposit Amt, Balance, Tran./Ref. ID columns) — no need to reformat it first. Add two more columns yourself — <strong>Group</strong> and <strong>Ledger</strong> — to map any row straight to a Chart of Accounts Ledger; a mapped row posts as a Voucher and skips the manual Categorize step. Name a Ledger that doesn't exist yet and it's created automatically — but only when <strong>Group is also filled in</strong>, so it lands somewhere real instead of a guessed bucket; the same new name reused across rows only ever creates it once. This never touches Finance's own Receipts/Expenses — to point a row at one, use Categorize → Invoice Payment → Existing Receipt by hand. Rows already imported before (matched by transaction ID) are skipped automatically, so re-uploading an overlapping date range is safe.
          </div>
          <div className="border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center text-green-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Download Template</p>
                  <p className="text-xs text-gray-400 mt-0.5">Only needed if you're typing up a statement by hand — your bank's own export usually works directly</p>
                </div>
              </div>
              <button onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-green-200 text-green-700 hover:bg-green-50 transition">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download
              </button>
            </div>
          </div>
          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-800 mb-3">Upload Statement File</p>
            <label
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition ${
                parseError ? 'border-red-300 bg-red-50'
                : dragOver ? 'border-blue-400 bg-blue-50'
                : rows.length > 0 ? 'border-blue-300 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} disabled={parsing} />
              {parsing ? (
                <div className="text-center">
                  <span className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" style={{ display:'block' }}/>
                  <p className="text-sm font-semibold text-blue-700">Reading {fileName}…</p>
                </div>
              ) : parseError ? (
                <div className="text-center px-4">
                  <svg className="w-8 h-8 text-red-400 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <p className="text-sm font-semibold text-red-600">Could not read {fileName}</p>
                  <p className="text-xs text-red-400 mt-0.5">{parseError} Click to try another file.</p>
                </div>
              ) : rows.length > 0 ? (
                <div className="text-center">
                  <svg className="w-8 h-8 text-blue-500 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <p className="text-sm font-semibold text-blue-700">{rows.length} transaction row(s) found</p>
                  <p className="text-xs text-blue-400 mt-0.5">{fileName} — click to change file</p>
                </div>
              ) : (
                <div className="text-center">
                  <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <p className="text-sm font-medium text-gray-500">Click to upload, or drag a file here</p>
                  <p className="text-xs text-gray-400 mt-0.5">.xlsx, .xls or .csv</p>
                </div>
              )}
            </label>
          </div>
        </div>

        <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl flex-shrink-0">
          <button onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition">
            Close
          </button>
          {rows.length > 0 && (
            <button onClick={handleSubmit}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center gap-2"
              style={{ background:'#0f1f45' }}>
              {`Import ${rows.length} Row${rows.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const LEDGER_NATURE_COLORS = { asset: '#2563eb', liability: '#dc2626', income: '#16a34a', expense: '#c2410c', equity: '#7c3aed' };

// Professional searchable Ledger picker — same pattern as Accounting.js's
// LedgerSelect (that component lives in a different page file, so this is
// a same-shaped copy rather than a shared import). Used by Categorize
// Transaction so picking a Ledger directly — "Bank Charges" for an
// overhead expense, a Client's own name for money paid on their behalf —
// replaces choosing a bucket and then a separate category/client field.
function LedgerSelect({ ledgers, value, onChange, darkMode = false, T: theme, placeholder = 'Select ledger...' }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sorted = [...ledgers].sort((a, b) => a.name.localeCompare(b.name));
  const selected = sorted.find(l => String(l.id) === String(value));
  const filtered = search.trim()
    ? sorted.filter(l => l.name.toLowerCase().includes(search.trim().toLowerCase()) || l.group_name?.toLowerCase().includes(search.trim().toLowerCase()))
    : sorted;

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
          border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13,
          background:T.inputBg, color: selected ? T.text : T.text3, cursor:'pointer', boxSizing:'border-box',
          textAlign:'left', transition:'border-color 0.15s',
        }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected ? selected.name : placeholder}</span>
        <span style={{ flexShrink:0, display:'flex', alignItems:'center', gap:6 }}>
          {selected && <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color: LEDGER_NATURE_COLORS[selected.group_nature] }}>{selected.group_name}</span>}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
        </span>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ padding:8, borderBottom:`1px solid ${T.border}` }}>
            <input autoFocus type="text" value={search} placeholder="Search ledgers..."
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
          </div>
          <div style={{ maxHeight:260, overflowY:'auto', padding:5 }}>
            {filtered.map(l => {
              const sel = String(l.id) === String(value);
              return (
                <button key={l.id} type="button" onClick={() => { onChange(String(l.id)); setOpen(false); setSearch(''); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, textAlign:'left', fontWeight: sel ? 700 : 500, color: sel ? '#2563eb' : T.text, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{l.name}</span>
                  <span style={{ flexShrink:0, fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: sel ? 'rgba(37,99,235,0.12)' : (darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'), color: sel ? '#2563eb' : LEDGER_NATURE_COLORS[l.group_nature] }}>{l.group_name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="3" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>{ledgers.length === 0 ? 'No ledgers available' : `No match for "${search}"`}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Same searchable-dropdown pattern as LedgerSelect, for picking a Client on
// the "Invoice Payment" bucket — keeps both buckets in Categorize
// Transaction visually consistent instead of one using a floating panel and
// the other a native <select>.
function ClientSelect({ clients, value, onChange, darkMode = false, T: theme, placeholder = 'Select client...' }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sorted = [...clients].sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
  const selected = sorted.find(c => String(c.id) === String(value));
  const filtered = search.trim()
    ? sorted.filter(c => c.client_name?.toLowerCase().includes(search.trim().toLowerCase()) || c.file_number?.toLowerCase().includes(search.trim().toLowerCase()))
    : sorted;

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
          border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13,
          background:T.inputBg, color: selected ? T.text : T.text3, cursor:'pointer', boxSizing:'border-box',
          textAlign:'left', transition:'border-color 0.15s',
        }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected ? selected.client_name : placeholder}</span>
        <span style={{ flexShrink:0, display:'flex', alignItems:'center', gap:6 }}>
          {selected && selected.file_number && <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color:'#2563eb' }}>{selected.file_number}</span>}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
        </span>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ padding:8, borderBottom:`1px solid ${T.border}` }}>
            <input autoFocus type="text" value={search} placeholder="Search clients..."
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
          </div>
          <div style={{ maxHeight:260, overflowY:'auto', padding:5 }}>
            {filtered.map(c => {
              const sel = String(c.id) === String(value);
              return (
                <button key={c.id} type="button" onClick={() => { onChange(String(c.id)); setOpen(false); setSearch(''); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, textAlign:'left', fontWeight: sel ? 700 : 500, color: sel ? '#2563eb' : T.text, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.client_name}</span>
                  {c.file_number && <span style={{ flexShrink:0, fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: sel ? 'rgba(37,99,235,0.12)' : (darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'), color:'#2563eb' }}>{c.file_number}</span>}
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="3" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>{clients.length === 0 ? 'No clients available' : `No match for "${search}"`}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Invoice Payment on this screen only ever MAPS this bank row onto a Receipt
// that already exists in Finance — it never creates, edits or deletes a
// Receipt. Accounting and Finance share data by reference only; a new
// Receipt is always recorded from the Finance module itself (Finance →
// Receipts → New Receipt), never from here. This is a hard rule, not a
// missing feature — see the comment on utils/bankSync.js for why.
function CategorizeTransactionModal({ token, txn, clients, ledgers, categories, clientExpenseCategories, onCreateCategory, onCreateClientExpenseCategory, onClose, onDone, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const isDeposit = Number(txn.deposit_amount) > 0;
  const amount = isDeposit ? Number(txn.deposit_amount) : Number(txn.withdrawal_amount);
  const [bucket, setBucket] = useState(isDeposit ? 'invoice_payment' : 'expense');
  const [ledgerId, setLedgerId] = useState('');
  const [notes, setNotes] = useState(txn.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [excScope, setExcScope] = useState('any'); // 'client' | 'group' | 'any'
  const [excClientId, setExcClientId] = useState('');
  const [excSearch, setExcSearch] = useState('');
  const [excReceipts, setExcReceipts] = useState([]);
  const [excLoading, setExcLoading] = useState(false);
  // Keyed by receipt id, not a single value — one bank row can settle
  // SEVERAL Receipts at once (e.g. one ₹12,000 deposit that's really three
  // ₹4,000 client payments). Holds the full receipt object for each pick,
  // not just its id, so the running total below stays correct even after
  // switching scope/search away from whatever list a given pick came from.
  const [excSelected, setExcSelected] = useState({});
  const toggleExcReceipt = (r) => setExcSelected(prev => {
    const next = { ...prev };
    if (next[r.id]) delete next[r.id]; else next[r.id] = r;
    return next;
  });

  // ── Expense bucket: optional "tag a Client" — only offered when the
  // picked Ledger is a Direct Expense (inherently client-specific, unlike
  // Indirect/business overhead). Tagging a Client either maps this row onto
  // an existing Client Expense already recorded in Finance, or creates a
  // new one — a deliberate, explicit exception to "Accounting never writes
  // into Finance" made only for this one action, only when a Client is
  // picked (see the conversation this was agreed in). Either way it's
  // linked back exactly like the Receipt mapping, so it can never be
  // double-claimed by two bank rows.
  const [tagClientId, setTagClientId] = useState('');
  const [expenseMode, setExpenseMode] = useState('new'); // 'new' | 'existing'
  const [candidateExpenses, setCandidateExpenses] = useState([]);
  const [candidateExpLoading, setCandidateExpLoading] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState('');

  const fetchCandidateExpenses = async (clientId) => {
    if (!clientId) { setCandidateExpenses([]); return; }
    setCandidateExpLoading(true);
    try {
      const res = await fetch(`${BASE}/api/bank/transactions/${txn.id}/candidate-expenses?client_id=${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await res.json().catch(() => []);
      setCandidateExpenses(Array.isArray(d) ? d : []);
    } catch (e) { setCandidateExpenses([]); }
    finally { setCandidateExpLoading(false); }
  };
  const handleTagClient = (cid) => {
    setTagClientId(cid); setSelectedExpenseId('');
    if (cid && expenseMode === 'existing') fetchCandidateExpenses(cid);
  };
  const handleExpenseMode = (m) => {
    setExpenseMode(m); setSelectedExpenseId('');
    if (m === 'existing' && tagClientId) fetchCandidateExpenses(tagClientId);
  };

  const fetchCandidates = async (scope, cid, search, seedSelection) => {
    setExcLoading(true);
    try {
      const params = new URLSearchParams({ scope });
      if (cid) params.set('client_id', cid);
      if (search) params.set('search', search);
      const res = await fetch(`${BASE}/api/bank/transactions/${txn.id}/candidate-receipts?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await res.json().catch(() => []);
      const list = Array.isArray(d) ? d : [];
      setExcReceipts(list);
      // Only the very first load seeds the selection from already_linked —
      // this is what makes reopening Categorize on an already multi-settled
      // row show its existing picks, without later scope/search fetches
      // stomping over picks the user has already made this session.
      if (seedSelection) {
        const linked = list.filter(r => r.already_linked);
        if (linked.length) setExcSelected(Object.fromEntries(linked.map(r => [r.id, r])));
      }
    } catch (e) { setExcReceipts([]); }
    finally { setExcLoading(false); }
  };

  useEffect(() => {
    if (isDeposit) fetchCandidates('any', '', '', true);
  }, []); // eslint-disable-line

  const handleExcScope = (s) => {
    setExcScope(s); setExcClientId('');
    fetchCandidates(s, '', s === 'any' ? excSearch : '');
  };
  const handleExcClient = (cid) => {
    setExcClientId(cid);
    fetchCandidates(excScope, cid, '');
  };
  const handleExcSearch = (term) => {
    setExcSearch(term);
    if (excScope === 'any') fetchCandidates('any', '', term);
  };

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const fd = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  // Every Ledger already sits under its own Group, so picking one already
  // says what kind of money this is — but for a Withdrawal, "any Ledger in
  // one dropdown" made it too easy to grab the wrong kind by mistake.
  // Split into two explicit buckets instead, each scoped to the right list:
  // Expense → only expense-nature Ledgers (Indirect/Direct Expenses —
  // Bank Charges, Rent, ...); Client's Ledger → only a Client's own Sundry
  // Debtors Ledger (money paid on their behalf, a receivable, not a P&L
  // entry); Other Ledger → everything else (Loans, Fixed Assets,
  // Investments, Capital Account, Current Liabilities, Bank/Cash — e.g. a
  // loan EMI payment isn't an Expense or a Client, but it's still a real
  // Withdrawal that needs a Ledger). All three still post through the same
  // categorize-ledger endpoint — the split is purely about not offering the
  // wrong list by default. Invoice Payment stays separate on the Deposit
  // side since it creates a real Receipt that can settle specific invoices,
  // which a plain Ledger post can't do.
  const isLedgerBucket = bucket === 'expense' || bucket === 'client_ledger' || bucket === 'other_ledger';
  const buckets = isDeposit
    ? [['invoice_payment','Invoice Payment'], ['ledger','Select Ledger'], ['transfer','Transfer / Ignore']]
    : [['expense','Expense'], ['client_ledger',"Client's Ledger"], ['other_ledger','Other Ledger'], ['transfer','Transfer / Ignore']];
  const ledgerChoices = bucket === 'expense' ? ledgers.filter(l => l.group_nature === 'expense')
    : bucket === 'client_ledger' ? ledgers.filter(l => l.source_type === 'client')
    : bucket === 'other_ledger' ? ledgers.filter(l => l.group_nature !== 'expense' && l.source_type !== 'client')
    : ledgers;
  const selectedLedger = ledgers.find(l => String(l.id) === String(ledgerId));
  const showClientTag = bucket === 'expense' && selectedLedger?.group_name === 'Direct Expenses';

  const excSelectedIds = Object.keys(excSelected);
  const excSelectedTotal = Object.values(excSelected).reduce((s, r) => s + Number(r.received_amount || 0), 0);

  const handleSave = async () => {
    setError('');
    if (bucket === 'invoice_payment' && !excSelectedIds.length) { setError('Select at least one receipt to map this transaction to.'); return; }
    if ((bucket === 'ledger' || isLedgerBucket) && !ledgerId) { setError('Select a Ledger.'); return; }
    if (showClientTag && tagClientId && expenseMode === 'existing' && !selectedExpenseId) { setError('Select an expense to map this transaction to, or switch to New Expense.'); return; }
    setSaving(true);
    try {
      // Direct Expense tagged to a Client — maps onto (or creates) a real
      // Client Expense in Finance instead of a plain Ledger post, so it
      // shows on that Client's own Dashboard → Expenses tab. An explicit,
      // deliberate exception made only here, only when a Client is picked.
      if (showClientTag && tagClientId) {
        let expenseId = selectedExpenseId;
        if (expenseMode === 'new') {
          const createRes = await fetch(`${BASE}/api/clients/${tagClientId}/expenses`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              expense_date: txn.txn_date, category: selectedLedger.name, mode: 'bank_transfer',
              notes: notes || 'From bank statement import', amount, billing_status: 'unbilled',
            }),
          });
          const created = await createRes.json().catch(() => ({}));
          if (!createRes.ok) throw new Error(created.message || 'Could not create Client Expense');
          expenseId = created.id;
        }
        const linkRes = await fetch(`${BASE}/api/bank/transactions/${txn.id}/link`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ category_type: 'client_expense', category_value: selectedLedger.name, client_id: tagClientId, linked_type: 'client_expense', linked_id: expenseId, notes }),
        });
        if (!linkRes.ok) { const d = await linkRes.json().catch(() => ({})); throw new Error(d.message || 'Could not link transaction'); }
        onDone();
        return;
      }

      if (bucket === 'ledger' || isLedgerBucket) {
        const res = await fetch(`${BASE}/api/bank/transactions/${txn.id}/categorize-ledger`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ledger_id: ledgerId, notes }),
        });
        if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || 'Could not categorize transaction'); }
        onDone();
        return;
      }

      // Invoice Payment only ever MAPS this row onto Receipt(s) that already
      // exist in Finance — writes to bank_transactions (and the
      // bank_transaction_receipts join table) alone, never to receipts
      // itself. One or several receipts can be picked, for a deposit that
      // settles more than one of them at once.
      if (bucket === 'invoice_payment') {
        const linkRes = await fetch(`${BASE}/api/bank/transactions/${txn.id}/link-receipts`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ receipt_ids: excSelectedIds, notes }),
        });
        if (!linkRes.ok) { const d = await linkRes.json().catch(() => ({})); throw new Error(d.message || 'Could not link transaction'); }
        onDone();
        return;
      }

      const linkRes = await fetch(`${BASE}/api/bank/transactions/${txn.id}/link`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ category_type: bucket, category_value: null, client_id: null, linked_type: null, linked_id: null, notes }),
      });
      if (!linkRes.ok) { const d = await linkRes.json().catch(() => ({})); throw new Error(d.message || 'Could not link transaction'); }
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,540px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'18px 18px 0 0' }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Categorize Transaction</h3>
            <p style={{ fontSize:11.5, color:'rgba(255,255,255,0.7)', margin:'3px 0 0' }}>{fd(txn.txn_date)} · {isDeposit ? 'Deposit' : 'Withdrawal'} of {fc(amount)}</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer', flexShrink:0 }}>✕</button>
        </div>
        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:14 }}>
          {error && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600 }}>{error}</div>}

          <div style={{ padding:'8px 12px', borderRadius:8, background:T.bg3, fontSize:11.5, color:T.text2, lineHeight:1.5 }}>{txn.description}</div>

          <div>
            <p style={{ fontSize:11, fontWeight:600, color:T.text3, marginBottom:8 }}>Group as</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {buckets.map(([k,l]) => (
                <button key={k} type="button" onClick={() => { setBucket(k); setLedgerId(''); setTagClientId(''); setSelectedExpenseId(''); }} style={{
                  padding:'7px 14px', borderRadius:9, fontSize:12.5, fontWeight:700, cursor:'pointer',
                  border: bucket===k ? 'none' : `1.5px solid ${T.border2}`,
                  background: bucket===k ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg2,
                  color: bucket===k ? '#fff' : T.text2,
                }}>{l}</button>
              ))}
            </div>
          </div>

          {bucket === 'invoice_payment' && (
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Map to Receipt<span style={{ color:'#ef4444', marginLeft:3 }}>*</span></label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:8 }}>
                <div style={{ display:'flex', borderRadius:9, border:`1px solid ${T.inputBorder}`, overflow:'hidden' }}>
                  {[['any','Any Client'], ['client','This Client'], ['group',"Client's Group"]].map(opt => (
                    <button key={opt[0]} type="button" onClick={() => handleExcScope(opt[0])} style={{
                      padding:'7px 12px', fontSize:11.5, fontWeight:600, border:'none', cursor:'pointer',
                      background: excScope===opt[0] ? '#0f1f45' : 'transparent',
                      color: excScope===opt[0] ? '#fff' : T.text3,
                    }}>{opt[1]}</button>
                  ))}
                </div>
              </div>
              {(excScope === 'client' || excScope === 'group') && (
                <div style={{ marginBottom:8 }}>
                  <ClientSelect clients={clients} value={excClientId} onChange={handleExcClient} darkMode={darkMode} T={T} placeholder="Select client to filter..." />
                </div>
              )}
              {excScope === 'any' && (
                <input type="text" placeholder="Search client or receipt no..."
                  value={excSearch} onChange={e => handleExcSearch(e.target.value)}
                  style={{ ...is, marginBottom:8 }} />
              )}

              <div style={{ border:`1px solid ${T.border}`, borderRadius:10, maxHeight:220, overflowY:'auto' }}>
                {excLoading && <p style={{ fontSize:12, color:T.text3, padding:14, textAlign:'center' }}>Loading receipts…</p>}
                {!excLoading && excReceipts.length === 0 && (
                  <p style={{ fontSize:12, color:T.text3, padding:14, textAlign:'center' }}>No unlinked receipts found here. Record it in Finance → Receipts first, then come back and map it here.</p>
                )}
                {!excLoading && excReceipts.map(r => {
                  const sel = !!excSelected[r.id];
                  return (
                    <button key={r.id} type="button" onClick={() => toggleExcReceipt(r)}
                      style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 12px', border:'none', borderBottom:`1px solid ${T.border}`, cursor:'pointer', textAlign:'left', background: sel ? (darkMode?'rgba(37,99,235,0.12)':'#eff6ff') : 'transparent' }}>
                      <span style={{ width:16, height:16, flexShrink:0, borderRadius:5, border:`1.5px solid ${sel?'#2563eb':T.border2}`, background: sel?'#2563eb':'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {sel && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                      </span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ fontSize:12.5, fontWeight:700, color: sel?'#2563eb':T.text }}>{r.receipt_number}</span>
                          {r.exact_match && <span style={{ fontSize:9.5, fontWeight:700, padding:'1px 6px', borderRadius:8, background: darkMode?'rgba(217,119,6,0.15)':'#fffbeb', color:'#d97706' }}>Same date &amp; amount</span>}
                        </div>
                        <div style={{ fontSize:11, color:T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.client_name || '—'} · {fd(r.payment_date)}</div>
                      </div>
                      <span style={{ fontSize:12.5, fontWeight:700, color:T.text2, flexShrink:0 }}>{fc(r.received_amount)}</span>
                    </button>
                  );
                })}
              </div>

              {excSelectedIds.length > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderRadius:10, marginTop:8, background: Math.abs(excSelectedTotal - amount) < 0.01 ? (darkMode?'rgba(22,163,74,0.12)':'#f0fdf4') : (darkMode?'rgba(249,115,22,0.12)':'#fff7ed') }}>
                  <span style={{ fontSize:12, color:T.text2 }}>{excSelectedIds.length} receipt{excSelectedIds.length===1?'':'s'} selected</span>
                  <span style={{ fontSize:12.5, fontWeight:800, color: Math.abs(excSelectedTotal - amount) < 0.01 ? '#16a34a' : '#f97316' }}>
                    {fc(excSelectedTotal)} {Math.abs(excSelectedTotal - amount) >= 0.01 && `of ${fc(amount)}`}
                  </span>
                </div>
              )}
              <p style={{ fontSize:10.5, color:T.text3, marginTop:5 }}>Tick one or more Receipts to map this bank row onto — this never creates, edits or deletes anything in Finance. To record a payment that isn't in Finance yet, go to Finance → Receipts → New Receipt first.</p>
            </div>
          )}
          {bucket === 'ledger' && (
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Ledger<span style={{ color:'#ef4444', marginLeft:3 }}>*</span></label>
              <LedgerSelect ledgers={ledgers} value={ledgerId} onChange={setLedgerId} darkMode={darkMode} T={T} />
              <p style={{ fontSize:10.5, color:T.text3, marginTop:5 }}>
                Posts straight to this Ledger — pick an Income Ledger for money that isn't settling an invoice, or a Client's own Ledger to reduce what they owe.
              </p>
            </div>
          )}
          {bucket === 'expense' && (
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Expense Ledger<span style={{ color:'#ef4444', marginLeft:3 }}>*</span></label>
              <LedgerSelect ledgers={ledgerChoices} value={ledgerId} onChange={v => { setLedgerId(v); setTagClientId(''); setSelectedExpenseId(''); }} placeholder="Select expense ledger..." darkMode={darkMode} T={T} />
              <p style={{ fontSize:10.5, color:T.text3, marginTop:5 }}>
                Only Expense Ledgers are shown (e.g. Bank Charges, Rent, Office Supplies) — posts as overhead, not against any Client.
              </p>

              {showClientTag && (
                <div style={{ marginTop:14, padding:14, borderRadius:10, border:`1px dashed ${T.border2}` }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Tag to Client <span style={{ fontWeight:400, color:T.text3 }}>(optional)</span></label>
                  <ClientSelect clients={clients} value={tagClientId} onChange={handleTagClient} darkMode={darkMode} T={T} placeholder="Select client this was paid for..." />

                  {tagClientId && (
                    <>
                      <div style={{ display:'flex', gap:8, marginTop:10, marginBottom:10 }}>
                        {[['new','New Expense'], ['existing','Existing Expense']].map(([k,l]) => (
                          <button key={k} type="button" onClick={() => handleExpenseMode(k)} style={{
                            flex:1, padding:'7px 10px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
                            border: expenseMode===k ? '1.5px solid #2563eb' : `1.5px solid ${T.border2}`,
                            background: expenseMode===k ? (darkMode?'rgba(37,99,235,0.12)':'#eff6ff') : T.bg2,
                            color: expenseMode===k ? '#2563eb' : T.text2,
                          }}>{l}</button>
                        ))}
                      </div>

                      {expenseMode === 'new' && (
                        <p style={{ fontSize:10.5, color:T.text3 }}>Creates a real Client Expense under {selectedLedger?.name} for this client — it will show on their Client Dashboard → Expenses tab, linked back to this bank row.</p>
                      )}

                      {expenseMode === 'existing' && (
                        <div>
                          <div style={{ border:`1px solid ${T.border}`, borderRadius:10, maxHeight:180, overflowY:'auto' }}>
                            {candidateExpLoading && <p style={{ fontSize:12, color:T.text3, padding:12, textAlign:'center' }}>Loading expenses…</p>}
                            {!candidateExpLoading && candidateExpenses.length === 0 && (
                              <p style={{ fontSize:12, color:T.text3, padding:12, textAlign:'center' }}>No unlinked expenses found for this client — switch to "New Expense" to create one.</p>
                            )}
                            {!candidateExpLoading && candidateExpenses.map(e => {
                              const sel = String(selectedExpenseId) === String(e.id);
                              return (
                                <button key={e.id} type="button" onClick={() => setSelectedExpenseId(e.id)}
                                  style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 12px', border:'none', borderBottom:`1px solid ${T.border}`, cursor:'pointer', textAlign:'left', background: sel ? (darkMode?'rgba(37,99,235,0.12)':'#eff6ff') : 'transparent' }}>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                      <span style={{ fontSize:12.5, fontWeight:700, color: sel?'#2563eb':T.text }}>{e.category || 'Expense'}</span>
                                      {e.exact_match && <span style={{ fontSize:9.5, fontWeight:700, padding:'1px 6px', borderRadius:8, background: darkMode?'rgba(217,119,6,0.15)':'#fffbeb', color:'#d97706' }}>Same date &amp; amount</span>}
                                    </div>
                                    <div style={{ fontSize:11, color:T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fd(e.expense_date)} {e.notes ? `· ${e.notes}` : ''}</div>
                                  </div>
                                  <span style={{ fontSize:12.5, fontWeight:700, color:T.text2, flexShrink:0 }}>{fc(e.amount)}</span>
                                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="3" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {bucket === 'client_ledger' && (
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Client<span style={{ color:'#ef4444', marginLeft:3 }}>*</span></label>
              <LedgerSelect ledgers={ledgerChoices} value={ledgerId} onChange={setLedgerId} placeholder="Select client..." darkMode={darkMode} T={T} />
              <p style={{ fontSize:10.5, color:T.text3, marginTop:5 }}>
                Only Clients' own Ledgers are shown — for money paid on their behalf (a receivable added to what they owe), not an expense of the firm.
              </p>
            </div>
          )}
          {bucket === 'other_ledger' && (
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Ledger<span style={{ color:'#ef4444', marginLeft:3 }}>*</span></label>
              <LedgerSelect ledgers={ledgerChoices} value={ledgerId} onChange={setLedgerId} placeholder="Select ledger..." darkMode={darkMode} T={T} />
              <p style={{ fontSize:10.5, color:T.text3, marginTop:5 }}>
                Everything that isn't an Expense or a Client's own Ledger — e.g. a Loan EMI, an Investment, a Fixed Asset purchase, or Capital drawn out.
              </p>
            </div>
          )}
          {bucket === 'transfer' && (
            <p style={{ fontSize:12, color:T.text3 }}>No income or expense entry is created — this row is just marked as an internal transfer / not relevant to the Income Statement.</p>
          )}

          {bucket !== 'transfer' && (
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Notes</label>
              <input type="text" value={notes} onChange={e=>setNotes(e.target.value)} style={is} />
            </div>
          )}
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: saving?'#94a3b8':'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor: saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving...' : 'Save & Categorize'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Splits one imported statement row into several rows that add up to the
// same amount — for a bank line that actually has to settle against
// multiple separate Receipts (or Ledgers/Expenses) recorded individually,
// e.g. one ₹12,000 UPI credit that's really three ₹4,000 client payments.
// Each resulting row is completely ordinary afterward — Categorize it
// against a different Receipt/Ledger one at a time from the same table, no
// special handling needed once split.
function SplitTransactionModal({ token, txn, onClose, onDone, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const isDeposit = Number(txn.deposit_amount) > 0;
  const total = isDeposit ? Number(txn.deposit_amount) : Number(txn.withdrawal_amount);
  const [amounts, setAmounts] = useState(['', '']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  const sum = amounts.reduce((s, a) => s + (Number(a) || 0), 0);
  const remaining = Math.round((total - sum) * 100) / 100;

  const setAmountAt = (i, v) => setAmounts(prev => prev.map((a, idx) => idx === i ? v : a));
  const addRow = () => setAmounts(prev => [...prev, remaining > 0 ? String(remaining) : '']);
  const removeRow = (i) => setAmounts(prev => prev.length > 2 ? prev.filter((_, idx) => idx !== i) : prev);

  const handleSave = async () => {
    setError('');
    const nums = amounts.map(Number);
    if (nums.some(n => !(n > 0))) { setError('Every split must be a positive amount.'); return; }
    if (Math.abs(remaining) > 0.01) { setError(`Splits must add up to ${fc(total)} — currently ${remaining > 0 ? `${fc(remaining)} short` : `${fc(-remaining)} over`}.`); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/bank/transactions/${txn.id}/split`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ amounts: nums }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || 'Could not split this transaction'); }
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,460px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'18px 18px 0 0' }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Split Transaction</h3>
            <p style={{ fontSize:11.5, color:'rgba(255,255,255,0.7)', margin:'3px 0 0' }}>{isDeposit ? 'Deposit' : 'Withdrawal'} of {fc(total)} — split into separate rows to Categorize individually</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer', flexShrink:0 }}>✕</button>
        </div>
        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:12 }}>
          {error && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600 }}>{error}</div>}
          <div style={{ padding:'8px 12px', borderRadius:8, background:T.bg3, fontSize:11.5, color:T.text2, lineHeight:1.5 }}>{txn.description}</div>

          {amounts.map((a, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ position:'relative', flex:1 }}>
                <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:13, color:T.text3, pointerEvents:'none' }}>₹</span>
                <input type="number" min="0" step="0.01" placeholder="0.00" value={a}
                  onChange={e => setAmountAt(i, e.target.value)}
                  style={{ ...is, paddingLeft:28 }} />
              </div>
              <button type="button" onClick={() => removeRow(i)} disabled={amounts.length <= 2}
                title="Remove split" style={{ width:32, height:32, flexShrink:0, borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color: amounts.length <= 2 ? T.text3 : '#ef4444', cursor: amounts.length <= 2 ? 'not-allowed' : 'pointer', opacity: amounts.length <= 2 ? 0.4 : 1 }}>✕</button>
            </div>
          ))}
          <button type="button" onClick={addRow} style={{ alignSelf:'flex-start', display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, border:`1px dashed ${T.border2}`, background:'none', color:'#2563eb', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Split
          </button>

          <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 14px', borderRadius:10, background: Math.abs(remaining) < 0.01 ? (darkMode?'rgba(22,163,74,0.12)':'#f0fdf4') : (darkMode?'rgba(249,115,22,0.12)':'#fff7ed') }}>
            <span style={{ fontSize:12.5, fontWeight:600, color:T.text2 }}>{Math.abs(remaining) < 0.01 ? 'Fully allocated' : remaining > 0 ? 'Remaining to allocate' : 'Over by'}</span>
            <span style={{ fontSize:13, fontWeight:800, color: Math.abs(remaining) < 0.01 ? '#16a34a' : '#f97316' }}>{fc(Math.abs(remaining))}</span>
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || Math.abs(remaining) > 0.01} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: saving || Math.abs(remaining) > 0.01 ?'#94a3b8':'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor: saving || Math.abs(remaining) > 0.01 ?'not-allowed':'pointer' }}>
            {saving ? 'Splitting...' : `Split into ${amounts.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Shared confirm/alert popups for the Bank Statements tab — replace
// window.confirm/window.alert with something styled like the rest of the
// app instead of the browser's own unstyled dialog.
function ConfirmPopup({ title = 'Are you sure?', message, confirmLabel = 'Delete', danger = true, onConfirm, onCancel, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568' };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,440px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ padding:'24px 24px 18px' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:T.text, margin:'0 0 8px' }}>{title}</h3>
          <p style={{ fontSize:13, color:T.text2, margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onCancel} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: danger ? '#dc2626' : 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function AlertPopup({ title = 'Notice', message, onClose, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', border:'#e8edf5', text:'#0f1f45', text2:'#4a5568' };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,440px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ padding:'24px 24px 18px' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:T.text, margin:'0 0 8px' }}>{title}</h3>
          <p style={{ fontSize:13, color:T.text2, margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>OK</button>
        </div>
      </div>
    </div>
  );
}

function BankStatementsTab({ token, darkMode = false, T: theme }) {
  const T = theme || { bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('unmatched'); // 'all' | 'unmatched' | 'matched'
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientExpenseCategories, setClientExpenseCategories] = useState([]);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [categorizingTxn, setCategorizingTxn] = useState(null);
  const [splittingTxn, setSplittingTxn] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(null); // { done, total } while a bulk clear is running

  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const fd = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  const fetchAccounts = () => {
    fetch(`${BASE}/api/bank/accounts`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => {
        const list = Array.isArray(d) ? d : [];
        setAccounts(list);
        setSelectedAccountId(prev => prev || (list[0] ? String(list[0].id) : ''));
      }).catch(() => {});
  };

  useEffect(() => {
    fetchAccounts();
    fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/accounting/ledgers`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setLedgers(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/business-expenses/categories`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setCategories(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/expense-categories`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClientExpenseCategories(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]); // eslint-disable-line

  const fetchTransactions = () => {
    if (!selectedAccountId) { setTransactions([]); setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams({ bank_account_id: selectedAccountId });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    fetch(`${BASE}/api/bank/transactions?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setTransactions(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchTransactions(); setSelectedIds(new Set()); }, [selectedAccountId, statusFilter]); // eslint-disable-line

  const handleCreateCategory = async (name) => {
    const res = await fetch(`${BASE}/api/business-expenses/categories`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name }),
    });
    const created = await res.json().catch(() => null);
    if (res.ok && created) setCategories(prev => [...prev, created]);
    return created;
  };

  const handleCreateClientExpenseCategory = async (name) => {
    const res = await fetch(`${BASE}/api/expense-categories`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name }),
    });
    const created = await res.json().catch(() => null);
    if (res.ok && created) setClientExpenseCategories(prev => [...prev, created]);
    return created;
  };

  const handleUnlink = async (id) => {
    await fetch(`${BASE}/api/bank/transactions/${id}/unlink`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
    fetchTransactions();
  };

  // Deleting/clearing a row here ONLY ever removes that bank_transactions
  // row. It never touches, edits or deletes anything in Finance
  // (Receipts/Business Expenses/Client Expenses) — including a row marked
  // "Auto-synced", which is just a leftover local mirror row now (the
  // auto-sync that used to create these has been removed). The real
  // Finance record, if any, is completely untouched either way.
  const clearableTxns = transactions;
  const allSelected = clearableTxns.length > 0 && clearableTxns.every(t => selectedIds.has(t.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(clearableTxns.map(t => t.id)));
  const toggleSelect = (id) => setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  const [confirmState, setConfirmState] = useState(null);
  const [alertMsg, setAlertMsg] = useState('');

  const handleDeleteOne = (t) => {
    setConfirmState({
      title: 'Clear transaction',
      message: `Clear this transaction — "${t.description || 'no description'}" (${fd(t.txn_date)})? This only removes this Bank Statement row — it never touches any Receipt or Expense in Finance. This can't be undone.`,
      onConfirm: async () => {
        setConfirmState(null);
        const res = await fetch(`${BASE}/api/bank/transactions/${t.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        if (!res.ok) { const d = await res.json().catch(() => ({})); setAlertMsg(d.message || 'Could not clear this transaction'); return; }
        fetchTransactions();
      },
    });
  };

  // Clears in small chunks (rather than one request for every id) purely so
  // there's real, honest progress to show — "X of Y cleared" and a filling
  // bar, not a spinner pretending to know how long an opaque single call
  // will take.
  const DELETE_CHUNK_SIZE = 25;
  const handleDeleteSelected = () => {
    if (!selectedIds.size) return;
    const ids = Array.from(selectedIds);
    setConfirmState({
      title: 'Clear selected transactions',
      message: `Clear ${ids.length} selected transaction${ids.length === 1 ? '' : 's'}? This only removes these Bank Statement rows — it never touches any Receipt or Expense in Finance. This can't be undone.`,
      onConfirm: async () => {
        setConfirmState(null);
        setDeleting(true);
        setDeleteProgress({ done: 0, total: ids.length });
        let totalDeleted = 0, totalSkipped = 0, failed = false;
        try {
          for (let i = 0; i < ids.length; i += DELETE_CHUNK_SIZE) {
            const chunk = ids.slice(i, i + DELETE_CHUNK_SIZE);
            const res = await fetch(`${BASE}/api/bank/transactions/delete-batch`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ ids: chunk }),
            });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) { setAlertMsg(d.message || 'Could not clear selected transactions'); failed = true; break; }
            totalDeleted += d.deleted || 0;
            totalSkipped += d.skipped || 0;
            setDeleteProgress({ done: Math.min(i + chunk.length, ids.length), total: ids.length });
          }
          setSelectedIds(new Set());
          if (!failed && totalSkipped > 0) setAlertMsg(`${totalDeleted} deleted. ${totalSkipped} skipped — likely a Client Expense already billed on an invoice; remove it from that invoice first.`);
          fetchTransactions();
        } catch (e) { setAlertMsg('Server error'); }
        setDeleting(false);
        setDeleteProgress(null);
      },
    });
  };

  const catNameByValue = (v) => categories.find(c => c.value === v)?.name || v;
  const CATEGORY_LABELS = { invoice_payment: 'Invoice Payment', other_income: 'Other Income', business_expense: 'Business Expense', client_expense: 'Client/Task Expense', transfer: 'Transfer / Ignore', ignore: 'Transfer / Ignore', ledger_mapped: 'Ledger' };
  const CATEGORY_COLORS = { invoice_payment: '#16a34a', other_income: '#16a34a', business_expense: '#dc2626', client_expense: '#dc2626', transfer: '#94a3b8', ignore: '#94a3b8', ledger_mapped: '#2563eb' };

  const selectedAccount = accounts.find(a => String(a.id) === String(selectedAccountId));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16, paddingBottom:24 }}>
      {showAddAccount && (
        <AddBankAccountModal token={token} onClose={() => setShowAddAccount(false)}
          onSuccess={(acct) => { setShowAddAccount(false); setAccounts(prev => [...prev, acct]); setSelectedAccountId(String(acct.id)); }}
          darkMode={darkMode} T={T} />
      )}
      {showImport && selectedAccountId && (
        <BankImportModal token={token} accountId={selectedAccountId}
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); fetchTransactions(); }}
          darkMode={darkMode} T={T} />
      )}
      {confirmState && (
        <ConfirmPopup title={confirmState.title} message={confirmState.message}
          onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)}
          darkMode={darkMode} T={T} />
      )}
      {alertMsg && <AlertPopup message={alertMsg} onClose={() => setAlertMsg('')} darkMode={darkMode} T={T} />}
      {categorizingTxn && (
        <CategorizeTransactionModal token={token} txn={categorizingTxn} clients={clients} ledgers={ledgers} categories={categories}
          clientExpenseCategories={clientExpenseCategories}
          onCreateCategory={handleCreateCategory}
          onCreateClientExpenseCategory={handleCreateClientExpenseCategory}
          onClose={() => setCategorizingTxn(null)}
          onDone={() => { setCategorizingTxn(null); fetchTransactions(); }}
          darkMode={darkMode} T={T} />
      )}
      {splittingTxn && (
        <SplitTransactionModal token={token} txn={splittingTxn}
          onClose={() => setSplittingTxn(null)}
          onDone={() => { setSplittingTxn(null); fetchTransactions(); }}
          darkMode={darkMode} T={T} />
      )}

      {/* Header: account selector + actions */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <BankAccountDropdown accounts={accounts} value={selectedAccountId} onChange={setSelectedAccountId}
            onAddNew={() => setShowAddAccount(true)} darkMode={darkMode} T={T} />

          <div style={{ display:'flex', borderRadius:9, border:`1px solid ${T.border2}`, overflow:'hidden' }}>
            {[['unmatched','Unmatched'],['matched','Categorized'],['all','All']].map(([k,l]) => (
              <button key={k} onClick={() => setStatusFilter(k)} style={{
                padding:'8px 14px', fontSize:12.5, fontWeight:600, cursor:'pointer', border:'none', borderRight:`1px solid ${T.border2}`,
                background: statusFilter===k ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg2, color: statusFilter===k ? '#fff' : T.text2,
              }}>{l}</button>
            ))}
          </div>

          {selectedIds.size > 0 && (
            <button onClick={handleDeleteSelected} disabled={deleting}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:9, border:'1px solid #fecaca', background: darkMode?'rgba(220,38,38,0.12)':'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:700, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1 }}>
              {deleting
                ? <span style={{ width:12, height:12, border:'2px solid #dc2626', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }} />
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>}
              {deleting ? `Clearing ${deleteProgress?.done ?? 0} / ${deleteProgress?.total ?? selectedIds.size}…` : `Clear Selected (${selectedIds.size})`}
            </button>
          )}
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => setShowImport(true)} disabled={!selectedAccountId}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:9, border:'none', background: selectedAccountId ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : '#cbd5e1', color:'#fff', fontSize:13, fontWeight:600, cursor: selectedAccountId ? 'pointer' : 'not-allowed', boxShadow: selectedAccountId ? '0 4px 12px rgba(15,31,69,0.3)' : 'none' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import Statement
          </button>
        </div>
      </div>

      {deleteProgress && (
        <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, padding:'12px 16px', display:'flex', alignItems:'center', gap:14 }}>
          <span style={{ width:16, height:16, border:'2.5px solid #dc2626', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite', flexShrink:0 }} />
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
              <span style={{ fontSize:12.5, fontWeight:600, color:T.text2 }}>Clearing bank statement rows…</span>
              <span style={{ fontSize:12.5, fontWeight:700, color:T.text, fontVariantNumeric:'tabular-nums' }}>{deleteProgress.done} / {deleteProgress.total}</span>
            </div>
            <div style={{ height:7, borderRadius:4, background:T.bg3, overflow:'hidden' }}>
              <div style={{
                height:'100%', borderRadius:4, background:'linear-gradient(90deg,#dc2626,#f87171)',
                width: `${deleteProgress.total ? (deleteProgress.done / deleteProgress.total) * 100 : 0}%`,
                transition:'width 0.25s ease',
              }} />
            </div>
          </div>
        </div>
      )}

      {accounts.length === 0 && !loading && (
        <div style={{ background:T.bg2, border:`1px dashed ${T.border2}`, borderRadius:16, padding:'40px 20px', textAlign:'center' }}>
          <p style={{ fontSize:13, fontWeight:600, color:T.text2, marginBottom:4 }}>No bank accounts yet</p>
          <p style={{ fontSize:12, color:T.text3, marginBottom:14 }}>Add a bank account to start importing and categorizing its statement.</p>
          <button onClick={() => setShowAddAccount(true)} style={{ padding:'9px 20px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>Add Bank Account</button>
        </div>
      )}

      {selectedAccountId && (
        <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                <th style={{ padding:'11px 12px', width:36 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} disabled={clearableTxns.length === 0}
                    style={{ width:14, height:14, cursor: clearableTxns.length === 0 ? 'default' : 'pointer' }} />
                </th>
                {['Date','Description','Withdrawal (₹)','Deposit (₹)','Balance (₹)','Status','Actions'].map((h,i) => (
                  <th key={h} style={{ padding:'11px 16px', textAlign: i>=2&&i<=4?'right':(i===6?'right':'left'), fontSize:10.5, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && transactions.length === 0 ? (
                // Only shows this collapsed placeholder on the very first
                // load (nothing to display yet). A refresh after Unlink/
                // Categorize/Delete/Split keeps rendering the existing rows
                // while the new data loads in the background instead —
                // blanking the table out on every refetch was what made the
                // page height collapse for a moment and reset your scroll
                // position back to the top on every single action.
                <tr><td colSpan={8} style={{ padding:'40px', textAlign:'center', color:T.text3, fontSize:13 }}>Loading transactions...</td></tr>
              ) : transactions.length === 0 ? (
                <tr><td colSpan={8} style={{ padding:'40px', textAlign:'center', color:T.text3, fontSize:13 }}>
                  {statusFilter === 'unmatched' ? 'No unmatched transactions — everything imported for this account is categorized.' : 'No transactions found.'}
                </td></tr>
              ) : transactions.map(t => {
                return (
                <tr key={t.id} style={{ borderBottom:`1px solid ${T.border}`, background: selectedIds.has(t.id) ? (darkMode?'rgba(59,130,246,0.08)':'#eff6ff') : 'transparent' }}>
                  <td style={{ padding:'10px 12px' }}>
                    <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} style={{ width:14, height:14, cursor:'pointer' }} />
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:12, color:T.text2, whiteSpace:'nowrap' }}>{fd(t.txn_date)}</td>
                  <td style={{ padding:'10px 16px', fontSize:12, color:T.text, maxWidth:320, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.description}>{t.description}</td>
                  <td style={{ padding:'10px 16px', fontSize:12, color:'#dc2626', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{Number(t.withdrawal_amount) > 0 ? fc(t.withdrawal_amount) : '—'}</td>
                  <td style={{ padding:'10px 16px', fontSize:12, color:'#16a34a', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{Number(t.deposit_amount) > 0 ? fc(t.deposit_amount) : '—'}</td>
                  <td style={{ padding:'10px 16px', fontSize:12, color:T.text3, textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fc(t.computed_balance)}</td>
                  <td style={{ padding:'10px 16px' }}>
                    {t.category_type ? (
                      <span style={{ fontSize:10.5, fontWeight:700, padding:'3px 10px', borderRadius:20, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color: CATEGORY_COLORS[t.category_type] }}>
                        {CATEGORY_LABELS[t.category_type]}{t.category_type==='business_expense' && t.category_value ? ` · ${catNameByValue(t.category_value)}` : ''}{t.category_type==='client_expense' && t.category_value ? ` · ${t.category_value}` : ''}{t.category_type==='ledger_mapped' && t.category_value ? ` · ${t.category_value}` : ''}{(t.category_type==='invoice_payment'||t.category_type==='client_expense') && t.client_name ? ` · ${t.client_name}` : ''}
                      </span>
                    ) : (
                      <span style={{ fontSize:10.5, fontWeight:700, padding:'3px 10px', borderRadius:20, background: darkMode?'rgba(249,115,22,0.15)':'#fff7ed', color:'#f97316' }}>Unmatched</span>
                    )}
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:12 }}>
                      {t.category_type ? (
                        <button onClick={() => handleUnlink(t.id)} style={{ fontSize:11.5, fontWeight:600, color:T.text3, background:'none', border:'none', cursor:'pointer' }}>Unlink</button>
                      ) : (
                        <>
                          <button onClick={() => setSplittingTxn(t)} title="Split this row into several — e.g. one deposit that settles against multiple Receipts"
                            style={{ fontSize:11.5, fontWeight:600, color:T.text2, background:'none', border:`1px solid ${T.border2}`, borderRadius:7, padding:'5px 10px', cursor:'pointer' }}>Split</button>
                          <button onClick={() => setCategorizingTxn(t)} style={{ fontSize:11.5, fontWeight:700, color:'#fff', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', border:'none', borderRadius:7, padding:'6px 12px', cursor:'pointer' }}>Categorize</button>
                        </>
                      )}
                      <button onClick={() => handleDeleteOne(t)} title="Clear this transaction — only removes this Bank Statement row" style={{ width:24, height:24, flexShrink:0, borderRadius:6, border:'none', background:'none', color:'#ef4444', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── QUOTE STATUS ──────────────────────────────────────────────────────────────
const QUOTE_STATUS = {
  draft:    { label: 'Draft',    bg: 'bg-gray-100',   text: 'text-gray-600'  },
  sent:     { label: 'Sent',     bg: 'bg-blue-50',    text: 'text-blue-700'  },
  accepted: { label: 'Accepted', bg: 'bg-green-50',   text: 'text-green-700' },
  rejected: { label: 'Rejected', bg: 'bg-red-50',     text: 'text-red-700'   },
};

function QuotationsTab({ token, showConfirm, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { 'Content-Type': 'application/json', ...headers };

  const [quotes,    setQuotes]    = useState([]);
  const [clients,   setClients]   = useState([]);
  const [services,  setServices]  = useState([]);
  const [settings,  setSettings]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editQuote, setEditQuote] = useState(null);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterClient, setFilterClient] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [nextNumber, setNextNumber] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [openServicePicker, setOpenServicePicker] = useState(null); // item index
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteToast, setQuoteToast] = useState(null);
  const [convertConfirm, setConvertConfirm] = useState(null); // holds quote object when confirm open
  const [converting, setConverting] = useState(false);
  const [emailSendingQ, setEmailSendingQ] = useState(false);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const onSort = makeSortToggle(sortKey, setSortKey, setSortDir);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const showQuoteToast = (msg, type='success') => { setQuoteToast({msg,type}); setTimeout(()=>setQuoteToast(null),4000); };

  const [form, setForm] = useState({
    client_id:'', title:'', notes:'',
    valid_till:'', status:'draft',
    items:[{ description:'', sac_code:'', amount:'', discount:'' }],
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, cRes, sRes, svRes] = await Promise.all([
        fetch(`${BASE}/api/invoices/quotations/all`, { headers }),
        fetch(`${BASE}/api/clients`, { headers }),
        fetch(`${BASE}/api/invoices/settings/config`, { headers }),
        fetch(`${BASE}/api/services/all`, { headers }),
      ]);
      const [qData, cData, sData, svData] = await Promise.all([qRes.json(), cRes.json(), sRes.json(), svRes.json()]);
      setQuotes(Array.isArray(qData) ? qData : []);
      setClients(Array.isArray(cData) ? cData : []);
      setSettings(sData || {});
      setServices(Array.isArray(svData) ? svData : []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchNextNumber = async () => {
    const res = await fetch(`${BASE}/api/invoices/quotations/next-number`, { headers });
    const d = await res.json();
    setNextNumber(d.quote_number || '');
  };

  const openNew = () => {
    setEditQuote(null);
    setForm({ client_id:'', title:'', notes:'', valid_till:'', status:'draft',
      items:[{ description:'', amount:'', discount:'' }] });
    fetchNextNumber();
    setShowForm(true);
  };

  const openEdit = (q) => {
    setEditQuote(q);
    const items = Array.isArray(q.items) ? q.items : (typeof q.items === 'string' ? JSON.parse(q.items || '[]') : []);
    setForm({
      client_id: q.client_id || '',
      title: q.title || '',
      notes: q.notes || '',
      valid_till: q.valid_till ? q.valid_till.split('T')[0] : '',
      status: q.status || 'draft',
      items: items.length ? items : [{ description: q.title || '', amount: q.amount || '', discount: '' }],
    });
    setShowForm(true);
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description:'', amount:'', discount:'' }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_,idx)=>idx!==i) }));
  const updateItem = (i, field, val) => setForm(f => ({
    ...f,
    items: f.items.map((item, idx) => idx===i ? { ...item, [field]: val } : item)
  }));

  const calcTotal = () => form.items.reduce((sum, item) => {
    const amt = Number(item.amount || 0);
    const disc = Number(item.discount || 0);
    return sum + amt - disc;
  }, 0);

  const handleSave = async () => {
    if (!form.client_id && !editQuote?.lead_id) return alert('Please select a client');
    setSaving(true);
    try {
      const total = calcTotal();
      const discount = form.items.reduce((s,i) => s + Number(i.discount||0), 0);
      const body = {
        client_id: form.client_id || null,
        title: form.title || form.items[0]?.description || 'Quotation',
        items: form.items,
        discount_amount: discount,
        total_amount: total,
        valid_till: form.valid_till || null,
        notes: form.notes,
        status: form.status,
      };
      const url = editQuote
        ? `${BASE}/api/invoices/quotations/${editQuote.id}`
        : `${BASE}/api/invoices/quotations`;
      const method = editQuote ? 'PUT' : 'POST';
      await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(body) });
      setShowForm(false);
      fetchAll();
    } catch(e) { alert('Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    showConfirm('Delete Quotation', 'Delete this quotation? This cannot be undone.', async () => {
      await fetch(`${BASE}/api/invoices/quotations/${id}`, { method:'DELETE', headers });
      fetchAll();
    });
  };

  const handleStatusChange = async (q, status) => {
    try {
      const res = await fetch(`${BASE}/api/invoices/quotations/${q.id}`, {
        method:'PUT', headers: jsonHeaders,
        body: JSON.stringify({ ...q, status, items: typeof q.items === 'string' ? JSON.parse(q.items||'[]') : (q.items||[]) })
      });
      if (!res.ok) { const d = await res.json(); alert(d.message||'Status update failed'); return; }
      await fetchAll();
    } catch(e) { alert('Error updating status'); }
  };

  // Opens the browser's native print dialog on the same QuotationLetterhead
  // component the Billing Settings live preview renders — real text, small
  // file, user picks where "Save as PDF" goes.
  const handlePrint = async (q) => {
    const s = settings;
    const items = Array.isArray(q.items) ? q.items : (typeof q.items === 'string' ? JSON.parse(q.items || '[]') : []);
    const total = Number(q.total_amount || q.amount || 0);

    try {
      await printComponent(
        <QuotationLetterhead settings={s} quote={{ ...q, items, total_amount: total }} />,
        `Quotation ${q.quote_number || ''}`
      );
    } catch (e) {
      alert('Could not open print dialog: ' + e.message);
    }
  };

  // Filter
  const creatorOptions = Array.from(new Set(quotes.map(q => q.created_by_name).filter(Boolean))).sort();
  const clientGroupById = new Map(clients.map(c => [String(c.id), c.group_id ? String(c.group_id) : '']));
  const groupOptions = Array.from(new Map(clients.filter(c => c.group_id).map(c => [String(c.group_id), c.group_name])).entries());
  const filtered = applySort(quotes.filter(q => {
    const matchSearch = !search || (q.quote_number||'').toLowerCase().includes(search.toLowerCase()) || (q.client_name||'').toLowerCase().includes(search.toLowerCase()) || (q.title||'').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || q.status === filterStatus;
    const matchSource = filterSource === 'all' || (q.source||'crm') === filterSource;
    const matchClient = !filterClient || String(q.client_id) === String(filterClient);
    const matchGroup = !filterGroup || clientGroupById.get(String(q.client_id)) === String(filterGroup);
    const matchCreatedBy = !filterCreatedBy || q.created_by_name === filterCreatedBy;
    const matchDateFrom = !filterDateFrom || new Date(q.created_at) >= new Date(filterDateFrom);
    const matchDateTo = !filterDateTo || new Date(q.created_at) <= new Date(filterDateTo + 'T23:59:59');
    return matchSearch && matchStatus && matchSource && matchClient && matchGroup && matchCreatedBy && matchDateFrom && matchDateTo;
  }), QUOTE_SORT_COLUMNS, sortKey, sortDir);

  const ic = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const icCls = ""; // legacy compat

  const filteredServices = services.filter(s =>
    !serviceSearch || s.name.toLowerCase().includes(serviceSearch.toLowerCase()) || (s.service_code||'').toLowerCase().includes(serviceSearch.toLowerCase())
  );

  // ── Full page quote detail ──────────────────────────────────────────────────
  if (selectedQuote && !showForm) {
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : '-';
    const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;
    const qItems = typeof selectedQuote.items==='string' ? JSON.parse(selectedQuote.items||'[]') : (selectedQuote.items||[]);
    // Quotations created from CRM's service-picker store quantity/rate/discount_type/
    // discount_value instead of a flat amount/discount — fall back to that math so
    // those items don't render as ₹0 here.
    const qItemAmount = (it) => it.amount != null ? Number(it.amount) : Number(it.quantity||1) * Number(it.rate||0);
    const qItemDiscount = (it) => {
      if (it.discount != null) return Number(it.discount);
      const amt = qItemAmount(it);
      if (it.discount_type === 'percentage') return amt * Number(it.discount_value||0) / 100;
      if (it.discount_type === 'fixed') return Number(it.discount_value||0);
      return 0;
    };
    const QSM = { draft:{c:'#64748b',bg:darkMode?'rgba(100,116,139,0.12)':'#f8fafc'}, sent:{c:'#2563eb',bg:darkMode?'rgba(37,99,235,0.12)':'#eff6ff'}, accepted:{c:'#16a34a',bg:darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'}, rejected:{c:'#dc2626',bg:darkMode?'rgba(220,38,38,0.12)':'#fef2f2'} };
    const qst = QSM[selectedQuote.status||'draft'];
    return (
      <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:16, paddingRight:4, scrollbarWidth:'none' }}>

        {/* Toast */}
        {quoteToast && (
          <div style={{ position:'fixed', bottom:32, left:'50%', transform:'translateX(-50%)', zIndex:99999, display:'flex', alignItems:'center', gap:12, padding:'14px 22px', borderRadius:14, background:quoteToast.type==='success'?'linear-gradient(135deg,#16a34a,#15803d)':quoteToast.type==='error'?'linear-gradient(135deg,#dc2626,#b91c1c)':'linear-gradient(135deg,#0f1f45,#1a3a6b)', boxShadow:'0 8px 32px rgba(0,0,0,0.25)', minWidth:280, maxWidth:480 }}>
            <div style={{ width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {quoteToast.type==='success'?<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
            </div>
            <p style={{ fontSize:13, fontWeight:600, color:'#fff', margin:0, flex:1 }}>{quoteToast.msg}</p>
            <button onClick={()=>setQuoteToast(null)} style={{ width:20, height:20, borderRadius:5, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        )}

        {/* Convert confirm modal */}
        {convertConfirm && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
            <div style={{ background:T.bg2, borderRadius:20, width:'min(92vw,480px)', overflow:'hidden', boxShadow:'0 24px 60px rgba(0,0,0,0.35)' }}>
              <div style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', padding:'20px 24px', display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ width:40, height:40, borderRadius:10, background:'rgba(255,255,255,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <div>
                  <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Convert to Invoice</h3>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{convertConfirm.quote_number} · {convertConfirm.client_name}</p>
                </div>
              </div>
              <div style={{ padding:'24px 28px' }}>
                <div style={{ background:darkMode?'rgba(16,185,129,0.08)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(16,185,129,0.25)':'#bbf7d0'}`, borderRadius:12, padding:'16px 20px', marginBottom:20 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}><span style={{ fontSize:12, color:T.text3 }}>Quotation No.</span><span style={{ fontSize:12, fontWeight:700, color:T.text }}>{convertConfirm.quote_number}</span></div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}><span style={{ fontSize:12, color:T.text3 }}>Client</span><span style={{ fontSize:12, fontWeight:700, color:T.text }}>{convertConfirm.client_name}</span></div>
                  <div style={{ display:'flex', justifyContent:'space-between', paddingTop:8, borderTop:`1px solid ${darkMode?'rgba(16,185,129,0.2)':'#d1fae5'}` }}><span style={{ fontSize:13, fontWeight:700, color:'#16a34a' }}>Total Amount</span><span style={{ fontSize:15, fontWeight:800, color:'#16a34a' }}>{fc(convertConfirm.total_amount||0)}</span></div>
                </div>
                <p style={{ fontSize:13, color:T.text2, margin:'0 0 20px', lineHeight:1.6 }}>This will create a new <strong>Tax Invoice</strong> with all items from this quotation.</p>
                <div style={{ display:'flex', gap:12 }}>
                  <button onClick={()=>setConvertConfirm(null)} disabled={converting} style={{ flex:1, padding:'10px 0', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg3, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
                  <button disabled={converting} onClick={async()=>{
                    setConverting(true);
                    try {
                      const q = convertConfirm;
                      const items = typeof q.items==='string'?JSON.parse(q.items||'[]'):(q.items||[]);
                      const res = await fetch(`${BASE}/api/invoices`,{ method:'POST', headers:jsonHeaders, body:JSON.stringify({ client_id:q.client_id, items:items.map((i,idx)=>({ particular:i.description||i.particular||i.service_name||'Service', description:i.description||i.particular||'', sac_code:i.sac_code||'', quantity:1, rate:Number(i.amount||0)-Number(i.discount||0), amount:Number(i.amount||0)-Number(i.discount||0), discount_type:'none', discount_value:0, discount_amount:0, net_amount:Number(i.amount||0)-Number(i.discount||0), item_order:idx, task_id:null, service_id:null })), remarks:q.notes||`Converted from Quotation ${q.quote_number}`, gst_type:settings.is_gst_registered?'cgst_sgst':'none', discount_type:'none', discount_value:0, quotation_id:q.id }) });
                      if(!res.ok){const d=await res.json();showQuoteToast(d.message||'Failed','error');setConverting(false);setConvertConfirm(null);return;}
                      const inv=await res.json();
                      setConvertConfirm(null); setSelectedQuote(null);
                      showQuoteToast(`✅ Invoice ${inv.invoice_number} created!`,'success');
                      await fetchAll();
                    } catch(e){showQuoteToast('Error: '+e.message,'error');}
                    setConverting(false);
                  }} style={{ flex:2, padding:'10px 0', borderRadius:10, border:'none', background:converting?'#15803d':'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:13, fontWeight:700, cursor:converting?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:converting?0.8:1 }}>
                    {converting?'Creating...':'Create Invoice'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── BREADCRUMB + ACTIONS ── */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
              <button onClick={()=>setSelectedQuote(null)} style={{ display:'flex', alignItems:'center', gap:6, border:'none', background:'none', cursor:'pointer', color:'#3b82f6', fontSize:13, fontWeight:500, padding:0 }}
                onMouseEnter={e=>e.currentTarget.style.textDecoration='underline'}
                onMouseLeave={e=>e.currentTarget.style.textDecoration='none'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M5 12l7 7M5 12l7-7"/></svg>
                Quotations
              </button>
              <span style={{ color:T.text3 }}>›</span>
              <span style={{ fontSize:13, fontWeight:500, color:T.text3 }}>Quotation Detail</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <h2 style={{ fontSize:20, fontWeight:800, color:T.text, margin:0 }}>{selectedQuote.quote_number}</h2>
              <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20, color:qst.c, background:qst.bg, textTransform:'capitalize' }}>{QUOTE_STATUS[selectedQuote.status||'draft']?.label||'Draft'}</span>
            </div>
            <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>{selectedQuote.client_name} · {selectedQuote.title}</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            {/* WhatsApp */}
            <button onClick={()=>{ const mobile=selectedQuote.client_mobile||''; const num=mobile.replace(/\D/g,''); if(!num){showQuoteToast('No mobile number for this client','error');return;} const msg=encodeURIComponent(`Dear ${selectedQuote.client_name},

Please find your quotation *${selectedQuote.quote_number}* for ${fc(selectedQuote.total_amount||0)}.
Valid till: ${selectedQuote.valid_till?new Date(selectedQuote.valid_till).toLocaleDateString('en-IN'):'N/A'}

Regards,
${settings.firm_name||'Legal Chanakya'}`); window.open(`https://wa.me/91${num}?text=${msg}`,'_blank'); showQuoteToast('Opening WhatsApp...','info'); }}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'}`, background:darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', color:'#16a34a' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
              WhatsApp
            </button>
            {/* Email */}
            <button disabled={emailSendingQ} onClick={async()=>{ const email=selectedQuote.client_email; if(!email){showQuoteToast('No email for this client','error');return;} setEmailSendingQ(true); try{ const res=await fetch(`${BASE}/api/notification-settings/send-finance-email`,{method:'POST',headers:jsonHeaders,body:JSON.stringify({type:'quotation',to:email,invoice_number:selectedQuote.quote_number,client_name:selectedQuote.client_name,total_amount:selectedQuote.total_amount,balance_due:0,firm_name:settings.firm_name,status:'sent'})}); const d=await res.json(); if(d.success)showQuoteToast(`Email sent to ${email}`,'success'); else showQuoteToast('Failed: '+(d.reason||'Unknown'),'error'); }catch{showQuoteToast('Server error','error');} setEmailSendingQ(false); }}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:emailSendingQ?'not-allowed':'pointer', border:`1px solid ${darkMode?'rgba(37,99,235,0.3)':'#bfdbfe'}`, background:darkMode?'rgba(37,99,235,0.1)':'#eff6ff', color:'#2563eb', opacity:emailSendingQ?0.6:1 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              {emailSendingQ?'Sending...':'Email'}
            </button>
            {/* Edit */}
            {(selectedQuote.source||'crm')==='direct' && !selectedQuote.converted_invoice_id && (
              <button onClick={()=>{ openEdit(selectedQuote); setSelectedQuote(null); }}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </button>
            )}
            {/* Convert to Invoice */}
            {selectedQuote.status==='accepted' && (
              <button onClick={()=>{ if(selectedQuote.converted_invoice_id){showQuoteToast(`Invoice ${selectedQuote.converted_invoice_number||'already'} exists. Delete it first.`,'error');return;} setConvertConfirm(selectedQuote); }}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', border:'none', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', boxShadow:'0 3px 10px rgba(22,163,74,0.25)' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Convert to Invoice
              </button>
            )}
            {/* Delete */}
            <button onClick={async()=>{ if(selectedQuote.converted_invoice_id){showQuoteToast(`Cannot delete — Invoice ${selectedQuote.converted_invoice_number||'already'} exists. Delete invoice first.`,'error');return;} showConfirm('Delete Quotation', `Delete ${selectedQuote.quote_number}? This cannot be undone.`, async () => { await fetch(`${BASE}/api/invoices/quotations/${selectedQuote.id}`,{method:'DELETE',headers}); setSelectedQuote(null); fetchAll(); }); }}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${darkMode?'rgba(239,68,68,0.3)':'#fecaca'}`, background:darkMode?'rgba(239,68,68,0.08)':'#fef2f2', color:'#ef4444' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
              Delete
            </button>
            {/* Download PDF — direct single-click download, no print dialog */}
            <button onClick={()=>handlePrint(selectedQuote)}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div style={{ display:'flex', gap:20, alignItems:'flex-start' }}>

          {/* LEFT — Quotation detail */}
          <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:14 }}>

            {/* Client + Quotation Info */}
            <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, overflow:'hidden', boxShadow:darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.05)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', borderBottom:`1px solid ${T.border}` }}>
                <div style={{ padding:'20px 24px', borderRight:`1px solid ${T.border}` }}>
                  <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 8px', fontWeight:600 }}>Prepared For</p>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text, margin:0 }}>{selectedQuote.client_name||'—'}</p>
                </div>
                <div style={{ padding:'20px 24px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                    <span style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Quotation No.</span>
                    <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{selectedQuote.quote_number}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                    <span style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Date</span>
                    <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{new Date(selectedQuote.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})}</span>
                  </div>
                  {selectedQuote.valid_till && (
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Valid Till</span>
                      <span style={{ fontSize:13, fontWeight:600, color:new Date(selectedQuote.valid_till)<new Date()?'#ef4444':T.text }}>{new Date(selectedQuote.valid_till).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})}</span>
                    </div>
                  )}
                </div>
              </div>
              {selectedQuote.notes && (
                <div style={{ padding:'12px 24px', background:darkMode?T.bg3:'#fafbff' }}>
                  <p style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px', fontWeight:600 }}>Notes</p>
                  <p style={{ fontSize:13, color:T.text2, margin:0 }}>{selectedQuote.notes}</p>
                </div>
              )}
            </div>

            {/* Items table */}
            <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, overflow:'hidden', boxShadow:darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.05)' }}>
              <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
                <h3 style={{ fontSize:13, fontWeight:700, color:T.text, margin:0 }}>Services / Items</h3>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                  <tr>
                    {['#','Description','Amount','Discount','Net Amount'].map((h,i)=>(
                      <th key={h} style={{ padding:'10px 16px', fontSize:11, fontWeight:700, color:'#fff', textAlign:i>1?'right':'left', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {qItems.map((item,i)=>(
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}`, transition:'background 0.1s' }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'11px 16px', fontSize:12, color:T.text3, width:32 }}>{i+1}</td>
                      <td style={{ padding:'11px 16px', fontSize:13, fontWeight:600, color:T.text }}>{item.description||item.particular||'—'}</td>
                      <td style={{ padding:'11px 16px', fontSize:13, color:T.text2, textAlign:'right' }}>{fc(qItemAmount(item))}</td>
                      <td style={{ padding:'11px 16px', fontSize:13, color:'#ef4444', textAlign:'right' }}>{qItemDiscount(item)>0?`-${fc(qItemDiscount(item))}`:'—'}</td>
                      <td style={{ padding:'11px 16px', fontSize:13, fontWeight:600, color:T.text, textAlign:'right' }}>{fc(qItemAmount(item)-qItemDiscount(item))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display:'flex', justifyContent:'flex-end', padding:'12px 16px', borderTop:`1px solid ${T.border}`, background:darkMode?T.bg3:'#fafbff' }}>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Total Amount</span>
                  <span style={{ fontSize:15, fontWeight:700, color:T.text }}>{fc(selectedQuote.total_amount||0)}</span>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT — Summary sidebar */}
          <div style={{ width:240, flexShrink:0, display:'flex', flexDirection:'column', gap:12 }}>

            <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20, boxShadow:darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.05)' }}>
              <h3 style={{ fontSize:13, fontWeight:700, color:T.text, margin:'0 0 14px' }}>Quotation Summary</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:T.text3 }}>Subtotal</span>
                  <span style={{ fontSize:12, fontWeight:600, color:T.text }}>{fc(qItems.reduce((s,i)=>s+qItemAmount(i),0))}</span>
                </div>
                {qItems.some(i=>qItemDiscount(i)>0) && (
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:12, color:T.text3 }}>Discount</span>
                    <span style={{ fontSize:12, fontWeight:600, color:'#ef4444' }}>-{fc(qItems.reduce((s,i)=>s+qItemDiscount(i),0))}</span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', paddingTop:9, borderTop:`1px solid ${T.border}` }}>
                  <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Total</span>
                  <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{fc(selectedQuote.total_amount||0)}</span>
                </div>
              </div>
            </div>

            <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20, boxShadow:darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.05)' }}>
              <h3 style={{ fontSize:13, fontWeight:700, color:T.text, margin:'0 0 12px' }}>Status & Info</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:12, color:T.text3 }}>Status</span>
                  <span style={{ fontSize:12, fontWeight:600, padding:'2px 10px', borderRadius:20, color:qst.c, background:qst.bg }}>{QUOTE_STATUS[selectedQuote.status||'draft']?.label||'Draft'}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:T.text3 }}>Source</span>
                  <span style={{ fontSize:12, fontWeight:600, color:T.text }}>{(selectedQuote.source||'crm')==='direct'?'Direct':'CRM Lead'}</span>
                </div>
                {selectedQuote.valid_till && (
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:12, color:T.text3 }}>Valid Till</span>
                    <span style={{ fontSize:12, fontWeight:600, color:new Date(selectedQuote.valid_till)<new Date()?'#ef4444':T.text }}>{new Date(selectedQuote.valid_till).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>
                  </div>
                )}
                {selectedQuote.converted_invoice_id && (
                  <div style={{ marginTop:6, paddingTop:8, borderTop:`1px solid ${T.border}`, background:darkMode?'rgba(16,185,129,0.08)':'#f0fdf4', borderRadius:8, padding:10 }}>
                    <p style={{ fontSize:11, color:'#16a34a', fontWeight:700, margin:'0 0 3px' }}>✅ Converted</p>
                    <p style={{ fontSize:12, color:'#16a34a', margin:0 }}>Invoice: <strong>{selectedQuote.converted_invoice_number||'Created'}</strong></p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex:1, minHeight:0, display:"flex", flexDirection:"column" }}>
      {/* HEADER */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <p style={{ fontSize:12, color:T.text3, margin:0 }}>{filtered.length} quotation{filtered.length!==1?'s':''} · CRM Leads + Direct</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => {
            const rows = [['Quote No.','Client','Title','Date','Valid Till','Amount','Source','Status']];
            filtered.forEach(q => rows.push([
              q.quote_number||'', q.client_name||'', q.title||'',
              q.created_at ? new Date(q.created_at).toLocaleDateString('en-IN') : '',
              q.valid_till ? new Date(q.valid_till).toLocaleDateString('en-IN') : '',
              Number(q.total_amount||0).toFixed(2),
              (q.source||'crm')==='direct'?'Direct':'CRM Lead',
              q.status||'draft'
            ]));
            const csv = rows.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
            const blob = new Blob([csv], { type:'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href=url; a.download='quotations.csv'; a.click();
            URL.revokeObjectURL(url);
          }} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button onClick={openNew} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 18px',
            borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)',
            color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer',
            boxShadow:'0 4px 12px rgba(15,31,69,0.3)',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Quotation
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:showFilters?12:20, flexWrap:'wrap' }}>
        <input type="text" placeholder="Search by quote no., client, title..." value={search} onChange={e=>setSearch(e.target.value)}
          style={{ border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, width:280, boxSizing:'border-box' }} />
        <FilterButton activeCount={[filterStatus!=='all'?filterStatus:'', filterSource!=='all'?filterSource:'', filterClient, filterGroup, filterCreatedBy, filterDateFrom, filterDateTo].filter(Boolean).length} onClick={() => setShowFilters(s => !s)} darkMode={darkMode} T={T} />
        {(search||filterStatus!=='all'||filterSource!=='all'||filterClient||filterGroup||filterCreatedBy||filterDateFrom||filterDateTo) && (
          <button onClick={() => { setSearch(''); setFilterStatus('all'); setFilterSource('all'); setFilterClient(''); setFilterGroup(''); setFilterCreatedBy(''); setFilterDateFrom(''); setFilterDateTo(''); }}
            style={{ fontSize:12, color:'#ef4444', padding:'6px 10px', borderRadius:8, border:'1px solid #fecaca', background:'none', cursor:'pointer' }}>Clear</button>
        )}
      </div>
      {showFilters && (
        <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:12, padding:16, marginBottom:20 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Status</label>
              <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={ic}>
                <option value="all">All Status</option>
                {Object.entries(QUOTE_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Source</label>
              <select value={filterSource} onChange={e=>setFilterSource(e.target.value)} style={ic}>
                <option value="all">All Sources</option>
                <option value="crm">CRM Leads</option>
                <option value="direct">Direct</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Client</label>
              <select value={filterClient} onChange={e=>setFilterClient(e.target.value)} style={ic}>
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Group</label>
              <FilterSelect value={filterGroup} onChange={setFilterGroup} placeholder="All Groups" darkMode={darkMode} T={T}
                options={[['','All Groups'],...groupOptions]} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Created By</label>
              <select value={filterCreatedBy} onChange={e=>setFilterCreatedBy(e.target.value)} style={ic}>
                <option value="">All Users</option>
                {creatorOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>From Date</label>
              <input type="date" value={filterDateFrom} onChange={e=>setFilterDateFrom(e.target.value)} style={ic} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>To Date</label>
              <input type="date" value={filterDateTo} onChange={e=>setFilterDateTo(e.target.value)} style={ic} />
            </div>
          </div>
          <p style={{ fontSize:11, color:T.text3, marginTop:10 }}>{filtered.length} quotation(s) match the filters</p>
        </div>
      )}

      {/* Bulk actions bar */}
      {selectedIds.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, padding:'8px 14px', borderRadius:10, background: darkMode?'rgba(239,68,68,0.1)':'#fef2f2', border:`1px solid ${darkMode?'rgba(239,68,68,0.25)':'#fecaca'}` }}>
          <span style={{ fontSize:13, fontWeight:600, color:'#dc2626' }}>{selectedIds.length} quotation{selectedIds.length===1?'':'s'} selected</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setSelectedIds([])} style={{ padding:'6px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:'transparent', color:T.text2, fontSize:12, fontWeight:600, cursor:'pointer' }}>
              Clear selection
            </button>
            <button onClick={() => setShowBulkDelete(true)} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'#dc2626', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              Delete Selected
            </button>
          </div>
        </div>
      )}
      {showBulkDelete && (
        <BulkDeleteModal token={token} entityName="Quotation"
          items={filtered.filter(q => selectedIds.includes(q.id))}
          getLabel={q => q.quote_number || `Quotation #${q.id}`}
          buildDeleteUrl={q => `${BASE}/api/invoices/quotations/${q.id}`}
          warningText="Quotations already converted to an invoice are skipped — delete the invoice first if you need to remove one of those."
          skipIf={q => q.converted_invoice_id ? `already converted to Invoice ${q.converted_invoice_number || '#'+q.converted_invoice_id}` : null}
          onClose={() => setShowBulkDelete(false)}
          onDone={() => { setShowBulkDelete(false); setSelectedIds([]); fetchAll(); }}
          darkMode={darkMode} T={T} />
      )}

      {/* TABLE */}
      <div style={{ flex:1, minHeight:0, background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
        {loading ? (
          <div style={{ padding:'64px 0', textAlign:'center', color:T.text3, fontSize:13 }}>Loading quotations...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:'64px 0', textAlign:'center' }}>
            <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No quotations found</p>
            <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>Create your first quotation or check CRM leads</p>
          </div>
        ) : (
          <div style={{ flex:1, minHeight:0, overflowX:'auto', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
              <tr>
                <th style={{ padding:'10px 12px', width:36 }}>
                  <input type="checkbox"
                    checked={filtered.length > 0 && selectedIds.length === filtered.length}
                    ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < filtered.length; }}
                    onChange={e => setSelectedIds(e.target.checked ? filtered.map(q => q.id) : [])}
                    style={{ cursor:'pointer' }} />
                </th>
                {QUOTE_SORT_COLUMNS.map(col => (
                  <SortableTh key={col.key} label={col.label} sortKey={col.key} activeKey={sortKey} dir={sortDir} onSort={onSort} align={col.key==='total_amount'?'right':'left'} />
                ))}
                <th style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q,i) => {
                const QSM = {
                  draft:    { c:'#64748b', bg: darkMode?'rgba(100,116,139,0.12)':'#f8fafc' },
                  sent:     { c:'#2563eb', bg: darkMode?'rgba(37,99,235,0.12)':'#eff6ff'  },
                  accepted: { c:'#16a34a', bg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'  },
                  rejected: { c:'#dc2626', bg: darkMode?'rgba(220,38,38,0.12)':'#fef2f2'  },
                };
                const st = QSM[q.status]||QSM.draft;
                const total = Number(q.total_amount||q.amount||0);
                const isDirect = (q.source||'crm')==='direct';
                return (
                  <tr key={q.id} style={{ borderBottom:`1px solid ${T.border}`, background: selectedIds.includes(q.id) ? (darkMode?'rgba(239,68,68,0.08)':'#fef2f2') : darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                    onMouseEnter={e => { if (!selectedIds.includes(q.id)) e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = selectedIds.includes(q.id) ? (darkMode?'rgba(239,68,68,0.08)':'#fef2f2') : darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'); }}>
                    <td style={{ padding:'10px 12px' }}>
                      <input type="checkbox" checked={selectedIds.includes(q.id)}
                        onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, q.id] : prev.filter(id => id !== q.id))}
                        style={{ cursor:'pointer' }} />
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <button onClick={()=>setSelectedQuote(q)} style={{ fontSize:13, fontWeight:700, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline' }}>{q.quote_number||'—'}</button>
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <p style={{ fontSize:13, fontWeight:500, color:T.text, margin:0 }}>{q.client_name||'—'}</p>
                      {q.lead_ref && <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>Lead: {q.lead_ref}</p>}
                    </td>
                    <td style={{ padding:'10px 16px', maxWidth:200 }}>
                      <p style={{ fontSize:13, color:T.text2, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{q.title||'—'}</p>
                    </td>
                    <td style={{ padding:'10px 16px', fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>
                      {q.created_at ? new Date(q.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                    </td>
                    <td style={{ padding:'10px 16px', fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>
                      {q.valid_till ? new Date(q.valid_till).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                    </td>
                    <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color:T.text, whiteSpace:'nowrap' }}>
                      {total>0 ? `₹${total.toLocaleString('en-IN',{minimumFractionDigits:2})}` : '—'}
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, color: isDirect?'#7c3aed':'#2563eb', background: isDirect?(darkMode?'rgba(124,58,237,0.12)':'#f5f3ff'):(darkMode?'rgba(37,99,235,0.12)':'#eff6ff') }}>
                        {isDirect?'Direct':'CRM Lead'}
                      </span>
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <select value={q.status||'draft'} onChange={async e => {
                          if (q.converted_invoice_id) { showQuoteToast(`Locked — Invoice ${q.converted_invoice_number||'#'+q.converted_invoice_id} already created. Delete the invoice first.`, 'error'); return; }
                          await handleStatusChange(q, e.target.value);
                          await fetchAll();
                        }}
                          style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:20, border:'none', cursor:'pointer', outline:'none', color:st.c, background:st.bg }}>
                          {Object.entries(QUOTE_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                        </select>
                        {q.status === 'accepted' && (
                          <button onClick={async () => {
                            if (q.converted_invoice_id) {
                              showQuoteToast(`Already converted — Invoice ${q.converted_invoice_number || '#'+q.converted_invoice_id} exists. Delete that invoice first to reconvert.`, 'error');
                              return;
                            }
                            setConvertConfirm(q);
                          }} style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20, border:'none', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', cursor:'pointer', whiteSpace:'nowrap' }}>
                            → Invoice
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <button onClick={()=>handlePrint(q)} title="Download PDF"
                          style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:'#3b82f6', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.12)':'#eff6ff'}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                        </button>
                        {isDirect && !q.converted_invoice_id && (
                          <button onClick={()=>openEdit(q)} title="Edit"
                            style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                            onMouseLeave={e => e.currentTarget.style.background='none'}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        )}
                        <button onClick={()=>{ if(q.converted_invoice_id){ showQuoteToast(`Cannot delete — Invoice ${q.converted_invoice_number||'already'} created. Delete the invoice first.`,'error'); return; } handleDelete(q.id); }} title="Delete"
                          style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:q.converted_invoice_id?T.text3:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center', cursor:q.converted_invoice_id?'not-allowed':'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = q.converted_invoice_id ? 'none' : (darkMode ? 'rgba(239,68,68,0.12)' : '#fef2f2'); }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>


      {/* GLOBAL QUOTE TOAST — visible from table and detail view */}
      {quoteToast && !selectedQuote && (
        <div style={{ position:'fixed', bottom:32, left:'50%', transform:'translateX(-50%)', zIndex:99999, display:'flex', alignItems:'center', gap:12, padding:'14px 22px', borderRadius:14, background:quoteToast.type==='success'?'linear-gradient(135deg,#16a34a,#15803d)':quoteToast.type==='error'?'linear-gradient(135deg,#dc2626,#b91c1c)':'linear-gradient(135deg,#0f1f45,#1a3a6b)', boxShadow:'0 8px 32px rgba(0,0,0,0.25)', minWidth:280, maxWidth:520 }}>
          <div style={{ width:30, height:30, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {quoteToast.type==='success'
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              : quoteToast.type==='error'
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/></svg>}
          </div>
          <p style={{ fontSize:13, fontWeight:600, color:'#fff', margin:0, flex:1 }}>{quoteToast.msg}</p>
          <button onClick={()=>setQuoteToast(null)} style={{ width:22, height:22, borderRadius:6, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* CONVERT TO INVOICE CONFIRM MODAL */}
      {convertConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'min(92vw,480px)', overflow:'hidden', boxShadow:'0 24px 60px rgba(0,0,0,0.35)' }}>
            {/* Header */}
            <div style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', padding:'20px 24px', display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:'rgba(255,255,255,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </div>
              <div>
                <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Convert to Invoice</h3>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{convertConfirm.quote_number} · {convertConfirm.client_name}</p>
              </div>
            </div>
            {/* Body */}
            <div style={{ padding:'24px 28px' }}>
              <div style={{ background:darkMode?'rgba(16,185,129,0.08)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(16,185,129,0.25)':'#bbf7d0'}`, borderRadius:12, padding:'16px 20px', marginBottom:20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:12, color:T.text3 }}>Quotation No.</span>
                  <span style={{ fontSize:12, fontWeight:700, color:T.text }}>{convertConfirm.quote_number}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:12, color:T.text3 }}>Client</span>
                  <span style={{ fontSize:12, fontWeight:700, color:T.text }}>{convertConfirm.client_name}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', paddingTop:8, borderTop:`1px solid ${darkMode?'rgba(16,185,129,0.2)':'#d1fae5'}` }}>
                  <span style={{ fontSize:13, fontWeight:700, color:'#16a34a' }}>Total Amount</span>
                  <span style={{ fontSize:15, fontWeight:800, color:'#16a34a' }}>₹{Number(convertConfirm.total_amount||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                </div>
              </div>
              <p style={{ fontSize:13, color:T.text2, margin:'0 0 20px', lineHeight:1.6 }}>
                This will create a new <strong>Tax Invoice</strong> with all items from this quotation. The invoice number will be auto-generated. The quotation will remain unchanged.
              </p>
              <div style={{ display:'flex', gap:12 }}>
                <button onClick={() => setConvertConfirm(null)} disabled={converting}
                  style={{ flex:1, padding:'10px 0', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg3, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  Cancel
                </button>
                <button disabled={converting} onClick={async () => {
                  setConverting(true);
                  try {
                    const q = convertConfirm;
                    const items = typeof q.items === 'string' ? JSON.parse(q.items||'[]') : (q.items||[]);
                    const res = await fetch(`${BASE}/api/invoices`, {
                      method:'POST', headers: jsonHeaders,
                      body: JSON.stringify({
                        client_id: q.client_id,
                        items: items.map((i,idx) => ({
                          particular:      i.description || i.particular || i.service_name || 'Service',
                          description:     i.description || i.particular || '',
                          sac_code:        i.sac_code || '',
                          quantity:        1,
                          rate:            Number(i.amount||0) - Number(i.discount||0),
                          amount:          Number(i.amount||0) - Number(i.discount||0),
                          discount_type:   'none', discount_value: 0,
                          discount_amount: 0,
                          net_amount:      Number(i.amount||0) - Number(i.discount||0),
                          item_order:      idx, task_id: null, service_id: null,
                        })),
                        remarks: q.notes || `Converted from Quotation ${q.quote_number}`,
                        gst_type: settings.is_gst_registered ? 'cgst_sgst' : 'none',
                        discount_type: 'none', discount_value: 0,
                        quotation_id: q.id,
                      })
                    });
                    if (!res.ok) { const d = await res.json(); showQuoteToast(d.message||'Conversion failed','error'); setConverting(false); setConvertConfirm(null); return; }
                    const inv = await res.json();
                    setConvertConfirm(null);
                    setSelectedQuote(null);
                    showQuoteToast(`✅ Invoice ${inv.invoice_number} created for ₹${Number(inv.total_amount).toLocaleString('en-IN')}!`,'success');
                    await fetchAll();
                  } catch(e) { showQuoteToast('Error: '+e.message,'error'); }
                  setConverting(false);
                }} style={{ flex:2, padding:'10px 0', borderRadius:10, border:'none', background: converting?'#15803d':'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:13, fontWeight:700, cursor:converting?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:converting?0.8:1 }}>
                  {converting
                    ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" style={{ animation:'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Creating Invoice...</>
                    : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>Yes, Create Invoice</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW / EDIT FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full shadow-2xl flex flex-col" style={{ maxWidth:'1100px', height:'92vh' }}>

            {/* ── MODAL HEADER ── */}
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 flex-shrink-0" style={{ background:'#0f1f45' }}>
              <div className="flex items-center gap-4">
                {/* Smart Calendar Icon */}
                <div className="w-11 h-11 bg-white bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                    <rect x="3" y="4" width="18" height="18" rx="3"/>
                    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                    <circle cx="12" cy="15" r="1" fill="white"/>
                    <line x1="9" y1="15" x2="9.01" y2="15" strokeWidth="2.5"/><line x1="15" y1="15" x2="15.01" y2="15" strokeWidth="2.5"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{editQuote ? 'Edit Quotation' : 'New Quotation'}</h3>
                  <p className="text-xs text-white text-opacity-60 mt-0.5">
                    Quote No.: <strong className="text-white">{editQuote?.quote_number || nextNumber || 'Auto-generated'}</strong>
                  </p>
                </div>
              </div>
              <button onClick={()=>setShowForm(false)}
                className="w-9 h-9 rounded-xl bg-white bg-opacity-10 hover:bg-opacity-20 flex items-center justify-center transition">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* ── MODAL BODY ── */}
            <div style={{ flex:1, overflowY:'auto', padding:32, scrollbarWidth:'thin', background:T.bg3 }}>

              {/* Section 1: Quotation Details */}
              <div style={{ marginBottom:32 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                  <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>1</div>
                  <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Quotation Details</h4>
                  <div style={{ flex:1, height:1, background:T.border }}/>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
                  <div style={{ gridColumn:'span 2' }}>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Client <span style={{ color:'#ef4444' }}>*</span></label>
                    <select value={form.client_id} onChange={e=>setForm(f=>({...f,client_id:e.target.value}))}
                      style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }}>
                      <option value="">Select client...</option>
                      {clients.map(c=><option key={c.id} value={c.id}>{c.client_name}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn:'span 2' }}>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Subject / Title</label>
                    <input type="text" placeholder="e.g. Annual GST Compliance Package"
                      value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}
                      style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                  </div>
                  <div>
                    <SmartDatePicker label="Valid Till" value={form.valid_till}
                      onChange={v=>setForm(f=>({...f,valid_till:v}))}
                      placeholder="Select valid till date" darkMode={darkMode} T={T} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Status</label>
                    <select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}
                      style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }}>
                      {Object.entries(QUOTE_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn:'span 2' }}>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Internal Notes</label>
                    <input type="text" placeholder="Optional notes for this quotation..."
                      value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}
                      style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                  </div>
                </div>
              </div>

              {/* Section 2: Service Items */}
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                  <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700, flexShrink:0 }}>2</div>
                  <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Service Items</h4>
                  <div style={{ flex:1, height:1, background:T.border }}/>
                  <button onClick={addItem} style={{
                    display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                    borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer',
                    border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`,
                    background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', color:'#3b82f6',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add Row
                  </button>
                </div>

                {/* Items Table */}
                <div style={{ border:`1px solid ${T.border}`, borderRadius:16, overflow:'visible', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.3)':'0 2px 8px rgba(15,31,69,0.06)' }}>
                  {/* Table Header */}
                  <div className="grid text-xs font-semibold text-white px-4 py-3" style={{ gridTemplateColumns:'2.5fr 1fr 0.7fr 0.7fr 0.8fr 36px', background:'#0f1f45' }}>
                    <div>Service / Description</div>
                    <div className="text-center">SAC Code</div>
                    <div className="text-right">Rate (₹)</div>
                    <div className="text-right">Discount (₹)</div>
                    <div className="text-right">Net Amount (₹)</div>
                    <div></div>
                  </div>

                  {/* Item Rows */}
                  {form.items.map((item, i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'2.5fr 1fr 0.7fr 0.7fr 0.8fr 36px', padding:'10px 16px', borderBottom: i<form.items.length-1?`1px solid ${T.border}`:'none', alignItems:'center', background: darkMode&&i%2!==0?T.bg3:'transparent', transition:'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                      onMouseLeave={e => e.currentTarget.style.background=darkMode&&i%2!==0?T.bg3:'transparent'}>

                      {/* Description + Service Picker */}
                      <div className="pr-3" style={{ position:'relative' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <input type="text" placeholder="Type service description..."
                            value={item.description} onChange={e=>updateItem(i,'description',e.target.value)}
                            style={{ flex:1, background:'transparent', border:'none', outline:'none', fontSize:13, color:T.text, minWidth:0 }} />
                          {/* Service Picker Button */}
                          <button type="button"
                            onClick={()=>{ setServiceSearch(''); setOpenServicePicker(openServicePicker===i?null:i); }}
                            style={{
                              flexShrink:0, display:'flex', alignItems:'center', gap:5,
                              padding:'5px 10px', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer',
                              border: openServicePicker===i ? '1px solid #3b82f6' : `1px solid ${T.border2}`,
                              background: openServicePicker===i ? (darkMode?'rgba(59,130,246,0.15)':'#eff6ff') : T.bg2,
                              color: openServicePicker===i ? '#3b82f6' : T.text3,
                            }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            Pick
                          </button>
                        </div>

                        {/* Service Dropdown */}
                        {openServicePicker === i && (
                          <div style={{
                            position:'absolute', left:0, top:'calc(100% + 6px)',
                            background:T.bg2, border:`1px solid ${T.border2}`,
                            borderRadius:16, boxShadow: darkMode?'0 16px 48px rgba(0,0,0,0.6)':'0 16px 48px rgba(15,31,69,0.18)',
                            zIndex:9999, width:384,
                          }}
                            onMouseLeave={()=>setOpenServicePicker(null)}>
                            {/* Search */}
                            <div style={{ padding:12, borderBottom:`1px solid ${T.border}` }}>
                              <div style={{ position:'relative' }}>
                                <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                <input type="text" placeholder="Search services..."
                                  value={serviceSearch} onChange={e=>setServiceSearch(e.target.value)}
                                  style={{ width:'100%', paddingLeft:32, paddingRight:12, paddingTop:8, paddingBottom:8, border:`1px solid ${T.border}`, borderRadius:8, fontSize:13, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }}
                                  autoFocus />
                              </div>
                            </div>
                            {/* List */}
                            <div style={{ maxHeight:224, overflowY:'auto', scrollbarWidth:'none', padding:'6px 0' }}>
                              {filteredServices.length === 0 ? (
                                <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No services found</p>
                              ) : filteredServices.map(sv => (
                                <button key={sv.id} type="button"
                                  onClick={()=>{
                                    updateItem(i, 'description', sv.name);
                                    updateItem(i, 'sac_code', sv.sac_code||sv.service_code||'');
                                    updateItem(i, 'amount', sv.default_amount||sv.base_price||sv.rate||'');
                                    setOpenServicePicker(null);
                                    setServiceSearch('');
                                  }}
                                  style={{ width:'100%', textAlign:'left', padding:'10px 16px', border:'none', background:'transparent', cursor:'pointer', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, transition:'background 0.1s' }}
                                  onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                  <div style={{ minWidth:0 }}>
                                    <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sv.name}</p>
                                    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:3 }}>
                                      {sv.service_code && (
                                        <span style={{ fontFamily:'monospace', fontSize:11, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color:T.text3, padding:'1px 6px', borderRadius:4 }}>
                                          {sv.service_code}
                                        </span>
                                      )}
                                      {sv.category && <span style={{ fontSize:11, color:T.text3 }}>{sv.category}</span>}
                                    </div>
                                  </div>
                                  {(sv.default_amount||sv.base_price||sv.rate) && (
                                    <span style={{ fontSize:12, fontWeight:700, color:'#16a34a', flexShrink:0, marginTop:2 }}>
                                      ₹{Number(sv.default_amount||sv.base_price||sv.rate).toLocaleString('en-IN')}
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* SAC Code */}
                      <div style={{ padding:'0 8px' }}>
                        <input type="text" placeholder="SAC code"
                          value={item.sac_code||''} onChange={e=>updateItem(i,'sac_code',e.target.value)}
                          style={{ width:'100%', background:'transparent', border:'none', outline:'none', fontSize:13, color:T.text2, textAlign:'center', fontFamily:'monospace' }} />
                      </div>

                      {/* Rate */}
                      <div style={{ padding:'0 8px' }}>
                        <input type="number" placeholder="0.00"
                          value={item.amount} onChange={e=>updateItem(i,'amount',e.target.value)}
                          style={{ width:'100%', background:'transparent', border:'none', outline:'none', fontSize:13, color:T.text, textAlign:'right' }} />
                      </div>

                      {/* Discount */}
                      <div style={{ padding:'0 8px' }}>
                        <input type="number" placeholder="0.00"
                          value={item.discount} onChange={e=>updateItem(i,'discount',e.target.value)}
                          style={{ width:'100%', background:'transparent', border:'none', outline:'none', fontSize:13, color:'#f97316', textAlign:'right' }} />
                      </div>

                      {/* Net */}
                      <div style={{ padding:'0 8px', textAlign:'right', fontSize:13, fontWeight:700, color:T.text }}>
                        ₹{Number(Number(item.amount||0)-Number(item.discount||0)).toLocaleString('en-IN',{minimumFractionDigits:2})}
                      </div>

                      {/* Remove */}
                      <div style={{ display:'flex', justifyContent:'center' }}>
                        {form.items.length > 1 && (
                          <button onClick={()=>removeItem(i)}
                            style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:T.text3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                            onMouseEnter={e => { e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'; e.currentTarget.style.color='#ef4444'; }}
                            onMouseLeave={e => { e.currentTarget.style.background='none'; e.currentTarget.style.color=T.text3; }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Table Footer — Total */}
                  <div style={{ display:'grid', gridTemplateColumns:'2.5fr 1fr 0.7fr 0.7fr 0.8fr 36px', padding:'12px 16px', borderTop:`2px solid ${T.border}`, background:T.bg3 }}>
                    <div style={{ gridColumn:'span 4', textAlign:'right', fontSize:13, fontWeight:600, color:T.text3, paddingRight:12 }}>Grand Total</div>
                    <div style={{ textAlign:'right', fontSize:15, fontWeight:800, color:T.text, padding:'0 8px' }}>
                      ₹{calcTotal().toLocaleString('en-IN',{minimumFractionDigits:2})}
                    </div>
                    <div/>
                  </div>
                </div>
              </div>
            </div>

            {/* ── MODAL FOOTER ── */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 32px', borderTop:`1px solid ${T.border}`, background:T.bg2, flexShrink:0, borderRadius:'0 0 20px 20px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:'#10b981' }}/>
                <span style={{ fontSize:13, color:T.text3 }}>Total Amount:</span>
                <span style={{ fontSize:17, fontWeight:800, color:T.text }}>₹{calcTotal().toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
              </div>
              <div style={{ display:'flex', gap:12 }}>
                <button onClick={()=>setShowForm(false)} style={{
                  padding:'10px 20px', borderRadius:10, border:`1px solid ${T.border2}`,
                  background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
                }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                onMouseLeave={e => e.currentTarget.style.background=T.bg2}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving} style={{
                  padding:'10px 28px', borderRadius:10, border:'none',
                  background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff',
                  fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:8,
                  opacity: saving?0.6:1, boxShadow:'0 4px 12px rgba(15,31,69,0.3)',
                }}>
                  {saving && <span style={{ width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
                  {saving ? 'Saving...' : editQuote ? 'Save Changes' : 'Create Quotation'}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// BankStatementsTab is defined above (with its own supporting modals) and
// used to live under Finance's own "Bank Statements" tab — that tab is
// removed from Finance now that Accounting owns it (see pages/Accounting.js),
// but the component itself stays here rather than being physically moved,
// since it closes over several other Finance-local helpers (BizCategoryDropdown,
// BankAccountSelect, PaymentModeDropdown, ensureImportStyles, toISODateLoose,
// resolveOrCreateClient, the module-level BASE constant, etc.) that would
// otherwise all need duplicating or exporting individually.
export { BankStatementsTab };
export default Finance; 